/**
 * Three.js render layer.
 *
 * Replaces the Pixi sprite renderer. The simulation is untouched and
 * still deterministic — this reads RunState and draws it, exactly as the
 * 2D layer did, so replays and the headless balance sim are unaffected.
 *
 * Why 3D at all: the reference class ships lit, shaded, skeletally
 * animated characters. A 2D sprite renderer cannot reach that no matter
 * how good the polygons are, because the gap is lighting, depth and
 * animation, not draw calls.
 *
 * Budget discipline:
 *  - The squad is real skinned meshes, but CAPPED. Skinned characters are
 *    the expensive thing on screen; the honest count stays in the HUD.
 *  - Enemies are InstancedMesh with simple geometry: hundreds of bodies
 *    for one draw call.
 *  - Tracers are a second InstancedMesh.
 */
import {
  Scene, PerspectiveCamera, WebGLRenderer, Group, Object3D, Color, IcosahedronGeometry,
  HemisphereLight, DirectionalLight, AmbientLight, PointLight,
  InstancedMesh, Mesh, Matrix4, Quaternion, Vector3, Vector2, Euler, Box3,
  BoxGeometry, PlaneGeometry, CircleGeometry, CylinderGeometry, SphereGeometry, TorusGeometry,
  RingGeometry,
  BufferGeometry, BufferAttribute, InstancedBufferAttribute,
  MeshStandardMaterial, MeshPhysicalMaterial, MeshBasicMaterial, DoubleSide,
  AnimationMixer, AnimationClip, SkinnedMesh, LoopRepeat, LoopOnce,
  SRGBColorSpace, NoToneMapping, PCFSoftShadowMap, CanvasTexture,
  Sprite, SpriteMaterial, AdditiveBlending, TextureLoader, RepeatWrapping, FogExp2,
} from 'three';
import { Layers, PMREMGenerator, ShaderMaterial,
  type Material, type Bone } from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { createCrowd, type CrowdRig } from './crowd';
import { addRimLight } from './entityMaterial.js';
import { shapeRun, shapeFire, shapeDeath, shapeHit, stripRootMotion } from './clip-shape.js';
import { retarget } from './retarget';
import { settle } from './ease.js';
import { createWorld } from '../render/environment';
import { tuning } from '../render/tuning.js';

/**
 * Objects on this layer -- and ONLY these -- bloom.
 *
 * The previous attempt used a luminance THRESHOLD over the whole frame.
 * Against this game's high-key art (a bright sky, a pale concrete road, a
 * sunlit town) most of the picture sits above any threshold that still
 * catches a muzzle flash, so the entire frame glowed: the "broken TV".
 * A threshold cannot separate "bright because it emits light" from
 * "bright because it is a white road in sunshine" -- that is a property of
 * the OBJECT, not of its pixel value. So the selection is explicit: the
 * bloom pass renders the scene with everything not on this layer painted
 * black, blurs that, and adds it back. The sky and the road are
 * structurally incapable of blooming.
 */
export const BLOOM_LAYER = 1;
/**
 * Selective-bloom tuning, ONE place per the render-quality-pass spec.
 * The bloom pass renders ONLY BLOOM_LAYER objects (see above) against a
 * cleared black background, so THRESHOLD has nothing bright-but-unlit
 * left to reject -- it stays 0 and the layer assignment is the entire
 * selection mechanism. STRENGTH/RADIUS are the two knobs that matter.
 */
export const BLOOM_THRESHOLD = 0;
// FRAMING FREEZE LIFTED (2026-09-02): the 2026-08-25 freeze below was
// explicitly scoped to "this session is framing only" and was never
// restored afterward -- A-52's gate-panel glow() fix (2026-09-01) shipped
// on top of it and was silently inert as a result, since strength=0 means
// the bloom composite contributes nothing no matter what's enrolled via
// glow(). Restored to 0.35, founder-graded too strong -> 0.10, still too
// strong -> 0.05. Each step a real look-at-it correction, not a re-guess.
//
// FRAMING FREEZE (founder, 2026-08-25): 0 -- this session is framing only
// (camera pitch/height/horizon, spawn Y, obstacle lane), and bloom is
// explicitly out. The selective-bloom plumbing (layer masking, quarter-res
// target, additive composite) is left in place, just silent at strength 0.
export const BLOOM_STRENGTH = 0.05;
export const BLOOM_RADIUS = 0.5;
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { fireHalfWidth, volleySize, applyGateOp, Ev, EVENT_STRIDE, TICK_RATE, Phase, Outcome, type RunState,
  TRUE_BOSS_SCALE, BOSS_SCALE, BEAST_SCALE } from '@bonwarsy/run-engine';

/**
 * Rim-light colours, one per ART_BIBLE.md §Palette role (the twelve exact
 * swatches stay founder-blocked; these four are the unblocked ROLES,
 * reusing hex values already established elsewhere in this file for the
 * same concept rather than inventing new ones — Iron Rule 4). Silhouette
 * separation only: the base texture/colour of every entity is untouched.
 */
// NO TINT (founder directive, 2026-08-30): the 2026-08-30 palette pass
// that moved Vanguard's tint off red/orange is reverted along with every
// other body tint in this file. RIM_FRIENDLY is a dynamic light-response
// accent, not a body tint, and tracks the selected character's own
// original colour (config/balance/characters.csv) via setTeamColor.
const RIM_FRIENDLY = 0xff7a6e; // squad, squad crowd, freed allies — default (Vanguard) rim accent
const RIM_THREAT = 0xe8f0ff; // fodder, elites, bosses — pale cool, desaturated-threat-appropriate
const RIM_OBJECTIVE = 0x8fe9ff; // monolith — matches buildAetherSled's own reserved aether cyan

/**
 * How many soldiers get a real skeleton. These are the FRONT ranks: they
 * animate, they carry the muzzles the flares fire from, and they play the
 * death performance. Everything behind them is instanced (see MAX_DRAWN),
 * so this number is a skeleton budget, not a crowd size.
 */
const MAX_SOLDIERS = 24;
/**
 * Total soldiers that can appear. Ranks past MAX_SOLDIERS are drawn as
 * INSTANCED frozen poses rather than as skinned meshes with their own
 * skeletons, so the whole squad is visible for a handful of draw calls.
 * Before this, a squad of 240 showed 26 bodies -- the crowd multiplying is
 * the entire hook of the genre and it stopped being visible in about forty
 * seconds.
 */
const MAX_DRAWN = 260;
/** How many frozen poses the crowd cycles through, per animation state. */
const CROWD_POSES = 6;
/** Pose steps per second for the instanced ranks. Matches a marching cadence. */
const CROWD_FPS = 9;
/** Where in the walk cycle the horde silhouette is frozen. */
const POSE_T = 0.34;
/** Baked strides per movement clip. Four reads as walking; two as twitching. */
const MASS_PHASES = 4;
/** Stride cycle rate, phases per second. */
const MASS_FPS = 7;
const MAX_ENEMIES = 4200;
// UI LAW (founder, 2026-08-20): raised 700 -> 2000, under the sim's own
// projectile_pool_cap (3200, rules.csv) with headroom left. 700 was the
// real "sparse dots" root cause at high squad size -- REAL concurrent
// projectiles already existed past 700 and were simply never drawn once
// the render-side cap truncated the loop below (`ti < MAX_TRACERS`), not
// a density problem in the sim or the geometry.
const MAX_TRACERS = 2000;
/** Bodies in flight after death. */
const MAX_CORPSES = 420;

/** World units -> scene units. The sim's corridor is ~7.2 wide. */
const S = 1;
/**
 * LATERAL scale, applied to x only. The corridor read cramped: two lanes of
 * packed horde inside 7.2 units left no room to see a gap open, so choosing
 * a lane was guesswork. Widening x alone keeps bodies their own size and the
 * depth foreshortening intact -- scaling S would have stretched the run
 * length too. The sim is untouched, so the wall law still holds: every
 * position maps linearly and the rails move out by the same factor.
 */
const SX = S * 1.875;

/** Same API the Pixi FX layer exposed, backed by instanced 3D geometry. */
export interface Fx3D {
  hit(x: number, y: number, n: number, tint: number): void;
  /**
   * @param ddx,ddy the killing blow's unit direction, straight from
   *                Ev.Death (0,0 for a splash kill, which has no blow to
   *                follow). Debris sprays in a cone along this line from an
   *                impact point offset toward it, instead of a ring from
   *                the body's centre.
   */
  death(x: number, y: number, radius: number, tint: number, dark: number,
    ddx?: number, ddy?: number): void;
  /**
   * Hand a killed body to the corpse pool so it goes DOWN, not away.
   *
   * @param radius   the enemy def's radius. The corpse is sized from this
   *                 through the SAME function the living body uses, so a
   *                 body cannot change size at the moment it dies.
   * @param overkill damage dealt / health it had left. 1 = the round that
   *                 exactly finished it; >2 = it was massively over-killed.
   *                 Picks the death archetype.
   * @param offAxis  how far off the shooter's centre line the body was, in
   *                 corridor units and signed, so a body caught from the
   *                 side spins out toward the side it was hit from. Falls
   *                 back to deciding launch direction when ddx/ddy is the
   *                 zero vector (a splash kill has no blow to follow).
   * @param instanceIdx the dying body's own enemy-pool index (M4) — picks
   *                 which of the three death clips this corpse plays, so
   *                 bodies dying in the same tick fall differently.
   * @param ddx,ddy  the killing blow's unit direction, straight from
   *                 Ev.Death. Drives the corpse's initial launch direction
   *                 and spin sign, so it snaps away from where the shot
   *                 came from instead of a random side.
   */
  kill(x: number, y: number, tier: number, radius: number, power: number,
    overkill?: number, offAxis?: number, instanceIdx?: number,
    ddx?: number, ddy?: number): void;
  /**
   * A body that slipped past the squad line (Ev.Breach, AUDIT A-13) --
   * hands it to the corpse pool upright and still moving, so it runs off
   * the frame instead of vanishing the instant the sim frees its slot.
   */
  breach(x: number, y: number, tier: number, radius: number): void;
  burst(x: number, y: number, n: number, tint: number, power: number): void;
  /**
   * IMPACT DEBRIS (render-quality-pass, item 4): angular chunks thrown
   * along (ddx,ddy), gravity-integrated, tumbling, distinct from the
   * spark/gore particle pool `hit`/`death`/`burst` use. Used at boss
   * damage-state transitions, on every kill, and on wreckage breaks
   * (monolith cracking).
   */
  debris(x: number, y: number, n: number, tint: number, ddx?: number, ddy?: number, power?: number): void;
  ring(x: number, y: number, tint: number, to: number, life?: number): void;
  muzzle(x: number, y: number, angle: number, scale: number): void;
  tracer(x: number, y: number, angle: number, scale: number, tint: number): void;
  label(x: number, y: number, text: string, tint: number, size: number, rise?: number): void;
}

/**
 * Fixed-timestep interpolation snapshot (founder, 2026-09-05, "fix C" --
 * the stutter diagnosed as uninterpolated sim sampling). Owned by the
 * render layer (main.ts), NEVER by RunState -- Iron Rule 1 stands, a run
 * still replays from config+seed+inputLog alone; this is presentation
 * smoothing over ticks that already happened, not a new source of
 * simulation truth. Scoped tightly to the two things worth smoothing
 * this round: `worldY` (ground UV scroll, pier recycle) and the squad's
 * own position (`playerX`/`playerY`), which the camera derives from --
 * interpolating one without the other would trade judder for the squad
 * swimming against the ground, so both move together or neither does.
 * Enemies/projectiles/pickups/gates are NOT covered (tasks/todo.md logs
 * this as a known remaining gap, not silently dropped).
 */
export interface TickSnapshot {
  worldY: number;
  playerX: number;
  playerY: number;
}

export interface SceneHandles {
  /**
   * `alpha` is the fraction (0..1, CLAMPED -- an overshooting
   * accumulator must never extrapolate) of a tick already elapsed since
   * `prev` was captured. `prev` holds the specific fields above as they
   * stood immediately before the most recent `stepRun()` this frame (or
   * the last one from a prior frame, if this frame fired zero ticks --
   * that's what removes the frozen frame, not a bug). Interpolated
   * value = `prev.field + (s.field - prev.field) * alpha`.
   */
  render(s: RunState, dtMs: number, lean: number, alpha: number, prev: TickSnapshot): void;
  resize(): void;
  readonly domElement: HTMLCanvasElement;
  /** True once the character GLB has loaded and soldiers are real meshes. */
  readonly ready: boolean;
  readonly fx: Fx3D;
  /** Fire a muzzle flare + light pulse at the firing line. */
  flash(streams: number): void;
  /** Paint the squad (skinned + instanced crowd) in the team colour. */
  setTeamColor(hex: number): void;
  /** Re-lay the lane furniture when an isle runs a different lane count. */
  setLanes(n: number): void;
  /** Impact scale of the CURRENT weapon class, for the shell's hit fx. */
  gunBoom(): number;
  /** Block theatre: mass-scaled break moment, for juice/audio in main. */
  onBlockBreak(cb: (mass: number) => void): void;
  /**
   * M7 zoom punch (ANIMATION_DOCTRINE.md, spec'd: "on titan entry and on
   * each Colossus stage"). `strength` is added to the existing eventPunch
   * accumulator (capped, decays over ~0.5-0.7s) -- same push-in-and-lift
   * mechanism camKick already uses for per-kill impact, sized for a rarer,
   * bigger beat.
   */
  punchCamera(strength: number): void;
  /**
   * I9 near-death feedback: a red edge vignette and a desaturation, both
   * already computed every frame by juice.ts's updateNearDeath() — this
   * just gives the live renderer a place to read them.
   */
  setDanger(vignette: number, desaturate: number): void;
  /** Frames of white flash remaining per enemy pool slot. */
  readonly enemyFlash: Int32Array;
  /** Frames of spawn grow-in remaining per enemy pool slot (L2). */
  readonly enemyGrow: Int32Array;
  /**
   * Register a non-lethal hit so the body REACTS to it.
   *
   * @param slot     enemy pool index — the same index as `enemyFlash`
   * @param severity damage / (damage + health left): 0 a graze, 1 lethal
   * @param fromX    shooter corridor x, so the lean points off the blow
   * @param fromY    shooter corridor y
   * @param atX      the struck body's corridor x
   * @param atY      the struck body's corridor y
   */
  enemyHit(slot: number, severity: number, fromX: number, fromY: number,
    atX: number, atY: number): void;
  /**
   * Start an elite/boss actor's death beat (a knockback-and-fall held for
   * ~0.6s after the sim frees the enemy, then released back to the pool).
   * The instanced horde tiers (defIdx<=2) use `fx.kill` instead and never
   * call this.
   *
   * @param defIdx    the dying enemy's def index, straight from Ev.Death
   * @param isBoss    per-spawn boss-ness (s.eIsBoss===1)
   * @param x,y       corridor position at the moment of death
   * @param ddx,ddy   the blow's unit direction, straight from Ev.Death
   * @param radius    the enemy def's radius, for the actor's living scale
   * @param trueBoss  the chapter's own boss (config isBoss), never a
   *                  mid-run beast -- picks TRUE_BOSS_SCALE over BOSS_SCALE
   *                  for the death-beat's fall.
   * @param overkill  damage dealt / health it had left, same measure `kill`
   *                  takes -- sizes how much of the knockback and lean the
   *                  death beat snaps in on its very first frame instead of
   *                  easing in from a standing pose.
   */
  killActor(defIdx: number, isBoss: boolean, x: number, y: number,
    ddx: number, ddy: number, radius: number, trueBoss: boolean,
    overkill?: number): void;
  /**
   * D9: the render layer's pools (soldiers, corpses, particles, labels,
   * gate panels, camera-feel accumulators) are built ONCE and reused for
   * the whole page lifetime — they carry no knowledge of which run they
   * belong to. `newRun()` always builds a genuinely fresh `RunState`, but
   * without this call the OLD run's transient per-object state (a
   * soldier mid-death-performance, its `dyingFor` timer and the rotation
   * twist that came with it) keeps animating on top of the new run's
   * tick 0 until its own timer happens to expire. Every restart path
   * must call this — see `newRun()` in main.ts, the one place all of
   * them already converge.
   */
  resetForNewRun(): void;
}

/**
 * GLTFLoader strips reserved characters from node names, so the rig's
 * `mixamorig:LeftHand` arrives as `mixamorigLeftHand`. Match on the
 * suffix rather than on an exact name that depends on the loader's
 * sanitising rules — and exclude finger bones, which also end in the
 * hand name with a digit appended.
 */
/**
 * The Mixamo bone-name prefix a loaded rig ships (`mixamorig`, `mixamorig10`,
 * ...). Two rigs of this family bind a clip natively once tracks are
 * renamed from one prefix to the other (B3a) — no full retarget needed.
 * Shared by every borrow site so the rename rule lives in exactly one place.
 */
function prefixOfRoot(root: Object3D): string {
  let pfx = '';
  root.traverse((o) => {
    if (pfx === '') { const m = /^(mixamorig\d*)(?=[A-Z_])/.exec(o.name); if (m) pfx = m[1]!; }
  });
  return pfx;
}

/**
 * Clone a donor clip onto a target rig of the same Mixamo family, renaming
 * the bone-name prefix on every track (B3a — binds natively, no retarget).
 * Returns null if the donor has no clip of that name.
 */
function borrowClip(
  donorClips: AnimationClip[], donorRoot: Object3D, targetRoot: Object3D,
  sourceName: string, destName = sourceName,
): AnimationClip | null {
  const src = donorClips.find((c) => c.name === sourceName);
  if (!src) return null;
  const c = src.clone();
  const from = prefixOfRoot(donorRoot);
  const to = prefixOfRoot(targetRoot);
  if (from && to && from !== to) {
    for (const tr of c.tracks) tr.name = tr.name.replace(new RegExp(`^${from}`), to);
  }
  c.name = destName;
  return c;
}

/**
 * Freeze a skinned mesh at one instant of a clip into plain geometry.
 *
 * This is how a crowd gets drawn without a skeleton per body: pose the rig
 * once at load, read the skinned vertex positions out, and instance the
 * result. Used for the enemy horde and for squad ranks past the skinned
 * front, which is what lets 240 soldiers exist on screen instead of 26.
 */
function bakePose(
  scene: Object3D, clip: AnimationClip | undefined, t: number,
  forcedRot?: number,
): { geo: BufferGeometry; mat: MeshStandardMaterial; rot: number } | null {
  if (clip) {
    const mx = new AnimationMixer(scene);
    mx.clipAction(clip).play();
    mx.setTime(t);
  }
  scene.updateWorldMatrix(true, true);

  let out: { geo: BufferGeometry; mat: MeshStandardMaterial; rot: number } | null = null;
  // Bake the BODY. Two rules, in order: prefer a SKINNED mesh over a rigid
  // prop, then the largest. "Largest wins" alone filled the slinger tier
  // with its ring-stack prop — a torus carries more vertices than a
  // low-poly body, so the whole tier rendered as marching orange rings.
  // (And the v2 characters carry a separate weapon mesh — Sam's node tree
  // is literally [Pistol, Sam] — which is why first-visited was never
  // safe either.)
  let best: SkinnedMesh | null = null;
  let bestVerts = -1;
  let bestSkinned = false;
  scene.traverse((o) => {
    const m = o as SkinnedMesh;
    if (!(m as unknown as { isMesh?: boolean }).isMesh) return;
    const skinned = (m as unknown as { isSkinnedMesh?: boolean }).isSkinnedMesh === true;
    const n = m.geometry?.getAttribute('position')?.count ?? 0;
    if ((skinned && !bestSkinned) || (skinned === bestSkinned && n > bestVerts)) {
      bestVerts = n; best = m; bestSkinned = skinned;
    }
  });
  scene.traverse((o) => {
    const m = o as SkinnedMesh;
    if (out || m !== best) return;
    const srcGeo = m.geometry;
    const posAttr = srcGeo.getAttribute('position');
    const baked = new Float32Array(posAttr.count * 3);
    const v = new Vector3();
    const skinned = (m as unknown as { isSkinnedMesh?: boolean }).isSkinnedMesh === true;
    if (skinned) m.skeleton.update();
    for (let k = 0; k < posAttr.count; k++) {
      v.fromBufferAttribute(posAttr, k);
      if (skinned) m.applyBoneTransform(k, v);
      baked[k * 3] = v.x; baked[k * 3 + 1] = v.y; baked[k * 3 + 2] = v.z;
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(baked, 3));
    const uv = srcGeo.getAttribute('uv');
    if (uv) geo.setAttribute('uv', uv.clone());
    if (srcGeo.index) geo.setIndex(srcGeo.index.clone());
    geo.applyMatrix4(m.matrixWorld);

    // Stand it up if it is prone. For a humanoid the smallest extent is
    // depth when upright and height when prone; testing depth against
    // height alone misses anything lying along X.
    //
    // When several PHASES of one clip are baked, the caller passes the
    // rotation decided for phase 0 (packed as a step list, 2 bits each):
    // mid-stride extents differ enough that per-phase decisions can
    // disagree, and a crowd whose walk frames point different ways
    // strobes between orientations.
    geo.computeBoundingBox();
    let bb = geo.boundingBox!;
    let rot = 0;
    if (forcedRot !== undefined) {
      let steps = forcedRot;
      while (steps > 0) {
        const step = steps & 3;
        if (step === 1) geo.rotateX(-Math.PI / 2);
        else if (step === 2) geo.rotateZ(Math.PI / 2);
        steps >>= 2;
      }
      rot = forcedRot;
      geo.computeBoundingBox();
      bb = geo.boundingBox!;
    } else {
      let shift = 0;
      for (let guard = 0; guard < 2; guard++) {
        const w2 = bb.max.x - bb.min.x, h2 = bb.max.y - bb.min.y, d2 = bb.max.z - bb.min.z;
        if (h2 >= d2 * 1.9 && h2 >= w2 * 1.9) break;
        if (d2 >= w2) { geo.rotateX(-Math.PI / 2); rot |= 1 << shift; }
        else { geo.rotateZ(Math.PI / 2); rot |= 2 << shift; }
        shift += 2;
        geo.computeBoundingBox();
        bb = geo.boundingBox!;
      }
    }
    geo.translate(0, -bb.min.y, 0);
    // Normalise to unit height so callers size in world units.
    geo.computeBoundingBox();
    const th = geo.boundingBox!.max.y - geo.boundingBox!.min.y;
    if (th > 1e-4) geo.scale(1 / th, 1 / th, 1 / th);
    geo.computeVertexNormals();

    const srcMat = m.material as MeshStandardMaterial;
    out = {
      geo,
      mat: new MeshStandardMaterial({
        map: srcMat.map ?? null, color: 0xffffff, roughness: 0.85,
      }),
      rot,
    };
  });
  return out;
}

/**
 * Remove a prop fused into a skinned body ABOVE the head line: drop every
 * triangle whose three vertices all sit above `frac` of the bind-pose
 * height. Used for the slinger, whose ring-stack shares the body mesh.
 */
function cropFusedTopProp(root: Object3D, frac: number): void {
  root.traverse((o) => {
    const m = o as SkinnedMesh;
    if ((m as unknown as { isSkinnedMesh?: boolean }).isSkinnedMesh !== true) return;
    const geo = m.geometry;
    const pos = geo.getAttribute('position') as BufferAttribute;
    geo.computeBoundingBox();
    const bb = geo.boundingBox!;
    const line = bb.min.y + (bb.max.y - bb.min.y) * frac;
    const idx = geo.index;
    const keep: number[] = [];
    const tris = idx ? idx.count / 3 : pos.count / 3;
    for (let t = 0; t < tris; t++) {
      const a = idx ? idx.getX(t * 3) : t * 3;
      const b = idx ? idx.getX(t * 3 + 1) : t * 3 + 1;
      const c = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
      if (pos.getY(a) > line && pos.getY(b) > line && pos.getY(c) > line) continue;
      keep.push(a, b, c);
    }
    geo.setIndex(keep);
    geo.computeBoundingBox();
    geo.computeBoundingSphere();
  });
}

function findBone(root: Object3D, suffixes: string[]): Object3D | null {
  // Tries rig conventions in order: Mixamo has explicit Hand bones, the
  // Quaternius rig ends at LowerArm.L / LowerArm.R (dots stripped to
  // LowerArmL by GLTFLoader's name sanitiser).
  for (const suffix of suffixes) {
    let hit: Object3D | null = null;
    root.traverse((o) => {
      if (hit) return;
      if (o.name.endsWith(suffix)) hit = o;
    });
    if (hit) return hit;
  }
  return null;
}

interface Soldier {
  root: Object3D;
  mixer: AnimationMixer;
  actions: Record<string, ReturnType<AnimationMixer['clipAction']>>;
  current: string;
  /** This body's fixed place in the animation cycle, 0..1. */
  phase: number;
  /** Seconds left of a death performance before the body is recycled. */
  dyingFor: number;
  /** Seconds left of the join pop-in (C8/I10), 0 once settled. */
  joinFor: number;
  /**
   * The two hands gripping the rifle. In a rifle stance the LEFT hand
   * supports the barrel and the RIGHT hand is on the grip, so
   * (left - right) is the barrel axis and the muzzle sits just beyond
   * the left hand. Reading the bones means the flare tracks the actual
   * animation — recoil, lean, death — instead of a guessed offset.
   */
  lh: Object3D | null;
  rh: Object3D | null;
  /** The rig's own weapon node — the grip-accurate mount for class guns. */
  gunMount: Object3D | null;
  /** Which weapon class model is currently mounted. */
  mounted: string;
}

/** How long a fallen soldier stays on the deck before the slot is reused. */
/** A muzzle flare is a spark, not a lamp — about five frames at 60fps. */
const MUZZLE_LIFE_S = 0.11;

/**
 * Boss shell-damage checkpoints across the whole fight (was 3: intact/
 * cracked/breaking, i.e. only 2 real transitions). 12 gives a checkpoint
 * roughly every 8.3% of health -- on isle 1's own measured ~10-15s DPS
 * check, that's a legible "did that land" moment every ~1-1.5s instead of
 * twice per fight.
 */
const BOSS_DMG_STATES = 12;
/**
 * TRUE BOSS VISUAL SCALE (spectacle pass, 2026-09-01) -- render-layer ONLY.
 * `TRUE_BOSS_SCALE` (packages/run-engine/src/constants.ts) is NOT a pure
 * cosmetic number: `config.ts` derives the boss's actual gameplay hit
 * radius and melee stop-distance directly from it
 * (`radius: TRUE_BOSS_SCALE * RADIUS_PER_SCALE`), measured and calibrated
 * against the real attack-swing reach (see that constant's own long
 * derivation comment). Bumping it would silently move the boss's attack
 * range and re-open exactly the class of bug this session's own first
 * commit fixed ("boss never played its attack clip" -- a scale/reach
 * mismatch). This multiplier instead scales ONLY the drawn mesh (`sc`
 * below and its two other consumption sites), matching the reference
 * captures' far larger titan silhouette, while `def.radius`/step.ts's
 * collision and attack-stop-distance math are completely untouched --
 * verified by grep: this constant is read nowhere outside this render
 * file. 1.5x is a deliberately measured, moderate bump, not the full
 * ~4-5x a literal kaiju-scale match would need -- reaching that fully
 * needs the sim-side radius/attack-range recalibrated in lockstep (its
 * own dedicated pass, flagged separately), not a render-only multiplier
 * pushed past the point the visual silhouette and the real hit-circle
 * plausibly still agree.
 */
// SCALED DOWN (founder finding, 2026-09-02): 1.5x read as too large live --
// 0.8x of that value, not a re-guess.
const TRUE_BOSS_VISUAL_MULT = 1.2;

/**
 * C8/I10 (genre-teardown-corridor.md): a new squad member pops in with a
 * burst, not a lerp — the gain must read as a discrete event. 0.2s sits at
 * the top of M1's 120-200ms crossfade band, so the pop is felt but never
 * reads as a state-transition snap.
 */
const JOIN_DUR_S = 0.2;

/**
 * Which way world +x points on screen.
 *
 * The chase camera sits BEHIND the squad and looks along +z (position
 * `pz - 10.5`, target `pz + 13`). A right-handed camera looking down -z
 * has +x on the right; looking down +z mirrors that, so world +x renders
 * to the LEFT. Anything converting a screen gesture into a corridor
 * position must go through this, or dragging right moves the squad left.
 *
 * The render layer owns the camera, so the render layer declares the
 * sign. -1 means "screen right is world -x".
 */
export const SCREEN_X_SIGN = -1;

/**
 * THE UNIT OF SCALE. Every other body in the corridor is sized relative
 * to a squad soldier, because "in proportion" has to mean proportional to
 * something. Previously enemies were sized by an unrelated formula
 * (0.38 * max(0.95, radius * 2.4)) that had no relationship to the squad
 * at all, so the ladder the design calls for -- grunts smaller, elites
 * near-equal, bosses far larger -- was true only by coincidence.
 *
 * The run now starts with ONE soldier, so that soldier has to carry the
 * screen on its own; it is sized to read as a character rather than as a
 * game piece.
 */
// 0.96 — the founder's +33% bulk pass (2026-08-13): at phone size the
// fight read as mosquitos. The game rationale is legibility of the core
// exchange — oversized hero proportions are what makes one soldier
// falling readable at 400px, which is the whole reason the contact rule
// exists on screen. Formation spacing derives from this constant, so the
// squad spreads with its bodies and nothing clips.
// +0.22 BULK PASS (founder, 2026-09-04): "now that the angle works, you
// can increase size of soldiers and enemies." The steeper camera
// (CAM_HEIGHT 7.5->10.5 above) opened vertical headroom this constant
// didn't have before -- formation spacing (FILE_GAP/RANK_GAP below) and
// every enemy/boss/beast scale term all derive from this ONE constant
// (see the block comment above `1.05 * (radius / 1.05) * FODDER_VISUAL_BULK`
// at the fodder scale sites), so bumping it here scales soldiers AND
// enemies together and keeps their formation-spacing math consistent
// with the new body size -- exactly the single-lever pattern the prior
// +33% pass already established.
// +0.20 BULK PASS (founder, 2026-09-04): "increase the size of the
// soldiers and enemy zombies by 0.20 their current size" -- same single
// lever as the +0.22 pass above, multiplied on top of it (1.24 * 1.20).
const SOLDIER_SCALE = 1.488;
/**
 * HORDE BULK (founder finding, live playtest, 2026-09-04): "the zombie
 * horde needs to be one notch bulkier." Render-layer only, deliberately
 * NOT applied to `def.radius` (`enemies.csv`) — that field is the real
 * collision/contact hitbox, and touching it would move balance, not just
 * how the horde looks. Same pattern as `TRUE_BOSS_VISUAL_MULT` (a purely
 * cosmetic scale multiplier layered on top of the authored collision
 * size). A modest bump, not a redesign — "one notch," not a new body.
 */
const FODDER_VISUAL_BULK = 1.12;
/**
 * Founder 2026-08-14: squad bodies to 0.8x of the bulk-pass size —
 * SQUAD ONLY. Enemies keep sizing off SOLDIER_SCALE unchanged, so the
 * horde grows RELATIVE to the squad rather than the whole frame zooming.
 * Applied everywhere the squad's footprint derives from its body:
 * formation pitch, body scale, foot lift, contact shadow.
 */
const SQUAD_MULT = 0.864;
/**
 * Extra girth on x and z only. A uniformly scaled humanoid reads spindly
 * at corridor distance; widening without heightening is what makes a body
 * read as heavy rather than as tall. Applied to the skinned front rank
 * AND the GPU-skinned ranks behind it, or the squad becomes two armies of
 * different builds at the boundary.
 */
const SOLDIER_BULK = 1.12;
/**
 * Horde body size, as a fraction of what the size formula returns.
 *
 * The squad has to be the biggest thing on its own side of the fight. At
 * parity the corridor read as a scrum of interchangeable bodies; pulling
 * the horde down to two thirds puts the size ladder back — a soldier out-
 * tops fodder, and only an elite or a boss out-tops a soldier.
 *
 * GROWN (founder, 2026-08-19, "the enemies are too small... bodies read
 * as a thin ribbon"). This constant has not moved since it was introduced
 * (2026-08-13, `1b005cfe`) — confirmed by history, not assumed: zero
 * commits touch it, including all three of this session's own camera/
 * render passes. The "thin ribbon" read is the camera pull-back
 * (CAM_HEIGHT/CAM_BACK, this same session) shrinking every body on
 * screen together, not this constant moving out from under it. Measured
 * live before this change (`?bot=1`, nearest front-rank
 * rift_skitterer -- spawn_weight 100, the dominant body): ~40px tall at
 * wave 5, ~39px at wave 15, against an 844px viewport (~4.6%).
 *
 * 0.65 -> 0.80 (+23% linear, ~+51% silhouette AREA -- area is what a
 * "mass", not a ribbon, actually reads by). Solved against the SAME
 * design-intent ceiling the comment above states, not just picked
 * bigger: at 0.80, the weakest fodder (skitterer, shape factor 0.987)
 * lands at 2.05 world units, still fractionally under a soldier's own
 * ~2.11 (`SOLDIER_SCALE`*`SQUAD_MULT`*`SOLDIER_WORLD_H`) -- "a soldier
 * out-tops fodder" still holds at the weakest tier, and every heavier
 * tier (shape factor scales with `radius`) grows proportionally more
 * from the same multiplier, same ladder preserved throughout. Measured
 * after: see deploy report.
 */
const HORDE_SIZE = 0.864;
/**
 * How tall a soldier stands in WORLD units. Every donor rig is scaled to
 * this, so the formation, the fire line, the corpse pool and the camera
 * framing all stay valid when the character art is swapped.
 * 2.59 — 1.95 grown by the founder's +33% (2026-08-13). Enemies size off
 * this via massBodyScale, so horde and squad grow together and the horde
 * stays shorter than the men holding the line.
 */
const SOLDIER_WORLD_H = 2.59;
/**
 * D7: ceiling on the per-kill camera impact accumulator (`camKick`).
 * UNCHANGED at 0.9 — the fix is the per-kill gain (below) and the decay
 * rate, not this ceiling. At the new gain, ordinary play never gets near
 * 0.9: measured peak on a real wave-5 capture (124 kills, one a mass
 * burst) was 0.0005 of viewport height, 8x under the 0.4% budget, because
 * fast decay (`CAM_KICK_DECAY`) clears most of each kill's contribution
 * before the next one lands. This ceiling stays as a backstop for the
 * pathological case gain/decay do NOT cover — many kills landing in the
 * SAME tick (an AOE, say), before any decay has a chance to run at all —
 * and 0.9 was already sized for that case, so lowering it further has no
 * effect on the behaviour this defect is about.
 */
const CAM_KICK_MAX = 0.9;
/** D7: decay rate so camKick clears to <1% of peak within 150ms of the last kill. */
const CAM_KICK_DECAY = 30.7;
/**
 * FRAMING PASS, STEP 1 REVISED (founder directive, this session) —
 * SUPERSEDES the horizon-driven step 1 above (deployed as `e31baf1d`,
 * explicitly "informational only, not graded"). The founder measured the
 * game's own two reference stills (docs/reference/Key-image-1.png,
 * key-image-2.png) pixel-by-pixel and found the REAL biggest gap wasn't
 * horizon position at all: squad frame-fraction (front-rank-to-rear-rank,
 * including body height) was 5.7% of frame height against a reference
 * range of 21-56% — five prior D1 rounds of angle-only tuning never
 * touched this, "because it is not an angle problem." Pitch/horizon
 * placement (the previous step) is a real but SECOND-ORDER gap next to
 * this one.
 *
 * FOV IS NO LONGER FROZEN this round (was frozen through every prior D1
 * round). DERIVED, not iterated by eye, using the same standalone
 * Three.js-lookAt + perspective-project reimplementation as the previous
 * step (scripts/_diag-frame-budget.mjs), calibrated against a REAL
 * `soldierFrame()` reading on the deployed build (bodyHeight01=0.157 at
 * the then-current H=7/B=10.2/F=21/fov=57 — implying a world body height
 * of ~2.2 units, back-solved from that one measurement and cross-checked:
 * the model's own predicted front-rank screenY, 0.875, matched the
 * measured 0.876).
 *
 * TWO CANDIDATES, single-lever each, so the trade is a number and not a
 * feeling:
 *   A. DISTANCE ONLY (CAM_BACK 10.2 -> lower, fov held at 57): needs
 *      CAM_BACK ~8.0 to clear 0.18 (predicted bodyFrac 0.194). COST: only
 *      the near field grows — a fixed-fov camera magnifies what's CLOSE
 *      without magnifying what's FAR, so the horde (already reading small
 *      per the reference gap) grows no bigger while the squad in front of
 *      it grows a lot, worsening the near/far size mismatch rather than
 *      fixing it.
 *   B. FOV ONLY (CAM_BACK held at 10.2, fov narrowed from 57): needs
 *      fov~50° to clear 0.18 (predicted bodyFrac 0.191). COST: a uniform
 *      zoom — near AND far content magnify together, preserving their
 *      size ratio (closer to what the references show: a large squad AND
 *      a large sustained crowd, not one growing at the other's expense)
 *      — but at the SAME back-distance a narrower fov shows less lane
 *      width at the edges, tightening how much of the corridor/isles is
 *      visible left-right.
 *
 * IMPLEMENTED: a BLEND, not either extreme — CAM_BACK 10.2->8.5 (17%
 * closer) and fov 57->50 (12% narrower), splitting both costs rather than
 * taking one lever to its full extent. Predicted bodyFrac 0.198 (10%
 * margin over the 0.18 floor — the single-lever candidates above cleared
 * the floor by under 10%, too thin against this model's own ~4%
 * calibration error to trust blind). CAM_LOOKAT_FORWARD re-solved
 * afterward (21.0 -> 7.0) to bring the front rank back into the NEW
 * squad-position target (0.71-0.92) — changing distance/fov moves the
 * squad's screen position too, not just its size, so forward-reach had to
 * move with it. CAM_HEIGHT (7.0) untouched — pitch was already addressed
 * by the previous step and isn't this step's lever.
 *
 * Every OTHER row (horizon, isles, monolith, enemy mass, dead ground) may
 * have moved as a side effect of distance/fov changing — reported in the
 * re-measured audit table, not fixed here; that's explicitly out of scope
 * this step.
 */
/**
 * FRAMING PASS, STEP 2a — DRAFTED then REVERTED same session, before
 * deploy. A height/lookAt solve WAS derived (H=4.0/F=14.0, satisfying
 * front-rank, horizon AND squad-frame-fraction simultaneously — the math
 * is preserved below since the derivation itself is still correct and may
 * be wanted later), but the founder's next message redirected: "STEP 2 —
 * engagement distance... The squad is fixed; do not touch camera
 * constants" — camera is explicitly FROZEN for that step, so this reverts
 * to step 1's own values (H=7.0, F=7.0) rather than shipping an untouched,
 * ungraded camera change alongside unrelated sim work. CAM_BACK=8.5 and
 * CAM_FOV=50 (step 1's actual levers) are unaffected either way.
 *
 * PRESERVED DERIVATION (not deployed): solved jointly with
 * CAM_LOOKAT_FORWARD using the same closed-form projection model as step
 * 1, extended with a horizon term (world Y=-7.5 at a very large Z, its
 * asymptotic screen position). For each candidate height,
 * CAM_LOOKAT_FORWARD was re-solved to hold the front rank at the centre
 * of its target band (screen01.y=0.80):
 *
 *   H=3.0  F=33.6  bodyFrac=0.270  horizon=0.431  (all three satisfied)
 *   H=4.0  F=14.0  bodyFrac=0.255  horizon=0.321  (all three satisfied)
 *   H=5.5  F=8.5   bodyFrac=0.226  horizon=0.170  (all three satisfied)
 *   H=5.75 F=8.1   bodyFrac=0.221  horizon=0.146  (horizon just OUT — <0.15)
 *   H=7.0  F=6.9   bodyFrac=0.194  horizon=0.030  (step 1's value — horizon OUT)
 *
 * A height DOES exist that satisfies all three simultaneously — valid
 * range roughly H∈[3.0, 5.5]. If camera unfreezes again, H=4.0/F=14.0 is
 * the standing recommendation (middle of the valid range, not an edge).
 */
// HORIZON FIX (framing pass, 2026-08-18, step 3): measured horizon_y
// 0.031-0.035 against FRAME_SPEC §10's floor of 0.062 in six independent
// captures (both viewports, three wave states, alive and dead) -- a
// constant undershoot, not gameplay-state noise, so this is the camera's
// to fix. horizon_y is set by CAM_HEIGHT (the preserved derivation
// comment below this constant, from the last time it was unfrozen,
// already found the slope: lower H -> horizon moves DOWN-frame, i.e.
// higher y01). CAM_LOOKAT_FORWARD is re-solved alongside it, same as
// that derivation, to hold front_rank_y roughly still while H moves --
// front rank is not the thing that's broken (measured IN band, or within
// 0.007 of it, in every one of the six captures).
// PULLED BACK FOR ENVIRONMENT (founder, 2026-08-19, item 3, "the camera
// is too close... the archipelago is a strip at the edges"). The
// horizon band this file's own history tuned against is retired
// (FRAME_SPEC.md §13, no sea) -- the only surviving gate is the front-
// rank band (screen01.y 0.695-0.830, FRAME_SPEC.md §10). Measured live
// (`window.__scene.projectWorld`/`soldierFrame()`, wave3) rather than
// solved in closed form, per this project's own measure-first discipline:
//   H=6.6  B=8.5  F=7.4  -> sky 12.8% of frame, front rank 0.796 (before)
//   H=7.5  B=13.0 F=9.0  -> sky 19.6% of frame, front rank 0.717 (shipped)
//   H=8.6  B=16.5 F=10.0 -> sky 21.3% of frame, front rank 0.686 (FAILS
//     the 0.695 floor for another 1.7 points of sky -- diminishing
//     returns past the shipped pose, not worth trading band compliance
//     for). Landed on the middle candidate: a real, measured 53% relative
//     gain in sky fraction (12.8% -> 19.6%) with front rank still 0.022
//     inside the floor, comfortable margin rather than sitting on the
//     line.
// FRAMING FREEZE (founder, 2026-08-25): matching docs/reference/lastz/
// f_05997.png -- 7.0 -> 10.0. Computed via this file's own baselineScreenY
// closed form (see that function's doc comment) before touching pixels:
// at 7.0 a body at world depth 100 (a plausible spawn distance) projects
// to screen01y 0.255 -- comfortably BELOW the upper third the reference
// wants. At 10.0 the same depth lands at 0.153, and the asymptotic
// horizon itself moves from 0.183 to 0.043 (just off the top edge, which
// is what lets a spawn-distance body read as "near the vanishing point"
// rather than needing genuinely infinite depth to get there).
// RE-SOLVED AN EIGHTH TIME (founder, 2026-08-29): the seventh pass
// (9.5/9.0) fixed the horizon-spawn regression but over-corrected on
// drama -- founder, playing the live build in a fresh incognito window
// (ruling out any caching): "there is some change in difficulty but
// nothing visually." 9.5/9.0 is only ~14-18% closer than the pre-
// regression 11.0/11.0; not a large enough move to read as different at
// a glance, per the teardown's own "camera is the single biggest lever."
// Pushed back toward the sixth pass's distance (7.5/7.0, the value that
// actually read as dramatic in screenshots) but this time solved the FOV
// FOR that distance instead of reusing a FOV solved for a different one
// (the sixth pass's bug: FOV68 was never re-derived for 7.5/7.0's own
// horizon-spawn requirement). Solved numerically: FOV=58 at 7.5/7.0 lands
// depth-60 groundY at +0.05 (safely off-screen-to-barely-emerging, same
// target band as the seventh pass) AND depth-90 groundY at +0.016 (still
// positive) AND monolith-crown clearance at depth 18-22 comes out to
// 0.057-0.075 -- MORE margin than the seventh pass had, not less, because
// solving FOV for the actual horizon target this time (rather than
// reusing an old value) closed the gap properly instead of overshooting
// it. Verify against a real screenshot on both isles before pushing
// further either direction -- this is the third camera-distance walk in
// one session; the failure mode each time was skipping that check.
// CAMERA-ANGLE ATTEMPT (founder directive, 2026-09-04, explicit risk
// accepted): "camera needs the matching angle to the reference." Prior
// pitch was 32 deg below horizontal (computed from the constants below);
// the reference reads noticeably steeper. Raised CAM_HEIGHT alone rather
// than touching CAM_BACK/CAM_LOOKAT_FORWARD too, to keep this a
// single-variable change against nine prior tuning passes -- then
// RE-SOLVED CAM_FOV numerically (not reused) for the new height, holding
// the exact same depth-60/groundY=0 target this file's own eighth pass
// established (0.0683, reproduced from the CURRENT shipped camera before
// changing anything, not copied from a possibly-stale comment). Result:
// pitch 32 -> 41.4 deg, FOV 58 -> 72.7. `monolithDressingYOffset()` and
// every other consumer of these constants re-derive live, so this is not
// expected to silently desync them -- but the monolith-crown headroom
// this same history warns about DOES shrink at a steeper angle (checked:
// a rough crown-position proxy moved 0.126 -> 0.171, meaning less margin,
// not none) -- verify against a real screenshot before trusting this,
// same rule every prior pass here already learned the hard way.
const CAM_HEIGHT = 10.5;
const CAM_BACK = 7.0;
/**
 * D1 (superseded framing, kept for the historical note): "how far
 * up-corridor the camera aims, past the squad's own position... larger
 * pushes near content DOWN in frame." Re-solved alongside CAM_HEIGHT/
 * CAM_BACK above (2026-08-19, item 3) to hold the front-rank band while
 * pulling back -- see that comment for the measured candidates.
 *
 * RE-SOLVED AGAIN, ANALYTICALLY, 2026-08-20 (capture harness blocked by
 * concurrent-session memory pressure, not this file): closed-form via the
 * same baselineScreenY formula this file already uses elsewhere,
 * calibrated against the CURRENT camera's own front-rank depth (solved
 * as -2.726 world units from the requirement that the shipped camera's
 * front rank already sits in [0.695, 0.830] -- it does). At the new
 * CAM_HEIGHT/CAM_BACK, 9.0 put the early-squad front rank at 0.854
 * (over the 0.830 ceiling); 6.87 holds both early (0.815) and late-squad
 * (0.778) in band, and drops the horizon (sky fraction) from 14.8% to
 * 9.6% as a side effect of pulling the pitch down slightly, not fighting
 * it. Predicted, not captured -- verify against a still when the
 * machine is quiet.
 *
 * COMPOSITION FIX, RE-SOLVED A THIRD TIME (founder, 2026-08-20): the
 * reference (docs/reference/lastz) never converges to an in-frame
 * vanishing point -- the corridor is still 20-26% of frame width where
 * the top edge clips it, ours resolved cleanly to a point at y=0.096.
 * Closing that gap needs a STEEPER pitch (a lower horizon), not a
 * shallower one -- verified by sweeping the closed-form projection
 * before touching this constant: raising CAM_LOOKAT_FORWARD (or lowering
 * CAM_HEIGHT) both INCREASE horizonY, pulling the vanishing point further
 * INTO frame, the wrong direction. 6.87 -> 2.0 (down, not up) is what
 * actually pushes horizonY negative (-0.057, off-frame) while landing
 * top-edge corridor width at 23.5% (inside the 15-25% target) and
 * holding the front rank at 0.716 (still inside lower-third and close to
 * the existing [0.695, 0.830] band). CAM_HEIGHT held at 7.0 -- lowering
 * it moves the same direction as raising this constant did, away from
 * the goal, confirmed by the same sweep.
 *
 * RE-SOLVED A FOURTH TIME, GOAL CHANGED (founder, 2026-08-20): the prior
 * entry optimised for corridor convergence; the founder's judgement from
 * live play was that the squad sat at ~55% of frame height over an empty
 * bottom third, and convergence stopped being the priority ("ignore the
 * vanishing-point rule from earlier"). 2.0 -> 12.0 (up, reversing the
 * prior conclusion on purpose) pushes the front rank toward the bottom of
 * frame instead: analytically, foot.y 0.716 -> 0.945, head.y 0.509 -> 0.72
 * at CAM_HEIGHT=7.0/CAM_BACK=11.0 (unchanged). Predicted, not captured --
 * verify against a still when the machine is quiet.
 *
 * RE-SOLVED A FIFTH TIME (founder, 2026-08-25): measured against
 * docs/reference/lastz -- the ad frames hold the squad at ~0.68 of frame
 * height with a third of the frame as road behind it; ours (at 12.0,
 * CAM_HEIGHT=11.0/CAM_BACK=11.0 from the intervening framing pass)
 * measured 0.933, pinned to the bottom edge. Swept all three candidate
 * levers via baselineScreenY before touching this constant: CAM_BACK to
 * the same footY target moves road width -40.9%, CAM_HEIGHT +32.6% --
 * both rescale the whole frame because they translate/re-pitch the
 * camera. This constant is a pure rotation (position unchanged), so a
 * fixed-depth slice of road doesn't resize, only reframes -- same target
 * footY, road width moves only -6.0%. 12.0 -> 3.0 lands footY at 0.680.
 */
const CAM_LOOKAT_FORWARD = 4.7;
/**
 * FRAMING PASS, STEP 1 REVISED: unfrozen this round only (see CAM_HEIGHT's
 * comment above) — half of the squad-scale fix. 57 -> 50, a uniform zoom
 * that magnifies near AND far content together (unlike CAM_BACK alone,
 * which only grows what's close) — the half of the blend that keeps the
 * horde from shrinking relative to the squad while the squad grows.
 */
// RE-SOLVED AGAIN, ALONGSIDE CAM_HEIGHT/CAM_BACK's SEVENTH PASS (founder,
// 2026-08-29): 68 was solved for the 7.5/7.0 position specifically (to
// stop the monolith crown/dressing clipping at that closer distance). Now
// that CAM_HEIGHT/CAM_BACK walked back to 9.5/9.0, re-solved alongside
// them as one system rather than left stale. RE-SOLVED AGAIN for
// CAM_HEIGHT/CAM_BACK's eighth pass (7.5/7.0): solving numerically for
// depth-60 groundY=+0.05 at THIS distance lands FOV back at 58 again --
// a coincidence of the math, not a reused value (the sixth pass's actual
// bug was reusing 68, a FOV solved for the seventh pass's 9.5/9.0
// distance, without re-deriving it for 7.5/7.0). Monolith crown/dressing
// headroom must be re-checked via `previewDiag()` against a real build
// before this ships, same as every prior FOV pass here.
// RE-SOLVED for CAM_HEIGHT's camera-angle attempt above, 2026-09-04:
// numerically solved (binary search against this file's own
// baselineScreenY formula, not reused from any prior pass) so that
// depth-60 groundY holds at 0.0683 -- the value CAM_HEIGHT=7.5 itself
// produced before this change -- at the new CAM_HEIGHT=10.5. Verify
// against a real screenshot before trusting this; same rule every prior
// FOV pass here already learned the hard way.
const CAM_FOV = 72.7;

/**
 * DYNAMIC FRAMING (founder, 2026-08-19) — bounded offsets applied ON TOP
 * of the four constants above, not a replacement for them. CAM_HEIGHT/
 * CAM_BACK/CAM_LOOKAT_FORWARD/CAM_FOV stay the baseline pose FRAME_SPEC.md
 * calibrated; these are the max deltas a dynamic term may add, each small
 * enough on its own to hold every band in FRAME_SPEC.md §10 (verified:
 * horizon_y sampled at squad 1/10/20 and through a forced boss encounter,
 * `docs/DECISIONS.md`).
 */
/** Max additional back-distance at high squad count (diminishing, sqrt). */
const CAM_SQUAD_PULL_MAX = 1.8;
/** Squad size the pull curve treats as "maxed out" — beyond this it holds. */
const CAM_SQUAD_PULL_REF = 20;
/**
 * Boss shot: how much FURTHER BACK and HIGHER at full blend — inverted
 * from the first pass (founder, 2026-08-19, after docs/reference/
 * zw-shooting/): that reference's own boss/big-ability beats pull the
 * camera UP and BACK, steeper pitch toward top-down, not down and in.
 *
 * FLOOR EXEMPTION (founder, 2026-08-19): pitch and horizon_y are the same
 * viewing-angle variable in this camera's fixed-lookAt-target geometry —
 * still true, but the target itself moved. The horizon band this
 * exemption was written against is ITSELF retired (FRAME_SPEC.md §13, no
 * sea) — see this constant's next entry.
 *
 * PULLED BACK TOO FAR (founder, 2026-08-19, item 3, same date as the
 * ordinary-play pull-back): -52.24 deg (or -51.6 deg against the
 * pulled-back baseline, live-measured -45.6 to -45.8 across several
 * checks this same round) reads as full top-down and "loses the fight."
 * Target: -35 to -40 deg, midpoint -37.5. Solved from this camera's own
 * pitch formula (unchanged geometry, just smaller deltas):
 *   pitch = atan2(CAM_HEIGHT + R - 0.2, CAM_BACK + squadPull + P + CAM_LOOKAT_FORWARD)
 * Held CAM_BACK/CAM_LOOKAT_FORWARD at their item-3 ordinary-play values
 * (13.0/9.0) and solved R/P for the -37.5 deg target, then verified live
 * (forced boss encounter, `window.__scene.soldierFrame().camera`, pitch
 * computed from position/lookAt): R=11.5, P=2.2 measured -37.9 deg at
 * full blend, squad still on-screen — inside the 35-40 deg band with
 * margin either side. Do NOT "fix" this back toward -50+ without the
 * founder asking again; that reading was explicitly rejected this round.
 */
const CAM_BOSS_BACK_PULL = 2.2;
const CAM_BOSS_HEIGHT_RAISE = 11.5;
/** Boss counts as "near" inside this many world-units of corridor depth. */
const CAM_BOSS_NEAR_DY = 16;
/**
 * BOSS REVEAL PULSE (spectacle pass, 2026-09-01). One-shot, additive, on
 * TOP of the settled camBossT wide shot above -- fires only on the exact
 * frame `bossNear` flips false->true, pulling the camera LOWER and CLOSER
 * for a brief "looks up at something huge" beat before the wide overview
 * (needed for actually PLAYING the fight, per the -37.9deg measurement
 * two comments up) takes over. Decays fast (DECAY_PER_SEC) so it reads as
 * a punch-in, not a lingering angle that would fight the tuned overview.
 * Values are deliberately modest: HEIGHT_DIP/BACK_PULL_IN are well inside
 * CAM_BOSS_HEIGHT_RAISE/CAM_BOSS_BACK_PULL's own magnitude, so even at
 * bossRevealT=1 stacked with camBossT already rising, the camera never
 * goes below CAM_HEIGHT or closer than the squad's own render bounds.
 */
const BOSS_REVEAL_HEIGHT_DIP = 3.0;
const BOSS_REVEAL_BACK_PULL_IN = 2.5;
const BOSS_REVEAL_DECAY_PER_SEC = 1.1;

/**
 * ISLE-COMPLETE BEAT (founder, 2026-08-19, item 3 dynamism): CAMERA_GRAMMAR.md
 * catalogued `cinematic-paths` ("a scripted, non-reactive shot") as not
 * implemented, and named exactly this trigger as where it would fit — "a
 * boss-death or run-end beat if the founder ever wants... no such beat
 * exists today." One now does. `main.ts` already holds the live scene on
 * a boss-kill outcome for `ISLE_COMPLETE_BEAT_MS` (2200ms) before cutting
 * to the stats screen — this reads that same state (`Phase.Ended` +
 * `Outcome.BossKilled`) directly off `RunState` rather than adding a
 * second signal, and blends in a slow, wide "trophy" pull: higher and
 * further back than ordinary play, but a fraction of the boss-engagement
 * shot's own magnitude (that one is a combat cut, this one is a settled
 * establishing shot of the field the squad just cleared) — Keren's
 * constraint (motion is punctuation, not decoration) means this fires
 * once, at a beat the run itself just produced, not on a timer or a
 * loop. Ramped in slower than boss-blend's own IN rate (0.55 vs 2.4) on
 * purpose: a combat cue should read as a cut, a victory beat should read
 * as a settle.
 */
const CAM_ISLE_COMPLETE_BACK_PULL = 5.0;
const CAM_ISLE_COMPLETE_HEIGHT_RAISE = 6.5;

/**
 * Visual identity per gun, keyed by weapons.csv `vfx_profile`.
 *
 * Ten weapons that differ only in damage/count/cooldown all rendered as
 * the same orange bolt, so upgrading was a number changing in the HUD.
 * A gun has to LOOK like what it does: a beam is a long thin lance of
 * light, flak is short and fat, a missile is a stubby body with a wide
 * halo. `w` scales girth, `len` scales length, and the three colours are
 * the additive core / mid / halo.
 */
interface GunLook {
  core: number; mid: number; halo: number;
  /** Girth and length multipliers on the shared dart geometry. */
  w: number; len: number; glow: number;
  /** Bore flash size at the muzzle. */
  flare: number;
  /** Impact burst size where the round lands. */
  boom: number;
  /**
   * How this family BEHAVES, not just what colour it is. Every gun used to
   * fire the same dart at a different size and tint, so ten weapon classes
   * read as one weapon with a palette swap. Shape drives the flight path and
   * the shape of the destruction, which is what actually distinguishes a
   * shotgun from a laser at a glance.
   */
  shape: 'kinetic' | 'spiral' | 'beam' | 'shell' | 'rocket' | 'lob' | 'sweep' | 'slam';
}
/**
 * TEN WEAPON CLASSES, each with a distinct read.
 *
 * Every gun previously drew as the same orange dart at a different size,
 * which made an upgrade a number in the HUD rather than a visible change
 * of weapon. A class is defined by four things the eye actually picks up:
 * the colour of the energy, the SHAPE of the round (short and fat vs long
 * and thin), the size of the bore flash, and the size of the impact.
 */
const GUN_LOOKS: Record<string, GunLook> = {
  // Kinetic: hot brass, short rounds, small flare, small impact.
  // UI LAW (founder, 2026-08-20): long thin yellow-white streaks, was a
  // short stubby amber-orange dart -- w down (thinner), len up (longer),
  // glow up (brighter), core/mid/halo moved into the yellow-white family.
  // DEFAULT_LOOK only (rifle) -- the other nine weapon classes keep their
  // own distinct colours untouched.
  rifle:   { core: 0xffffff, mid: 0xfff066, halo: 0xffe066, w: 0.62, len: 1.6, glow: 1.6, flare: 0.9, boom: 0.7, shape: 'kinetic' },
  mk:      { core: 0xfff0c0, mid: 0xffb02c, halo: 0xff5a0a, w: 0.8, len: 0.7,  glow: 1.1, flare: 0.8, boom: 0.6, shape: 'kinetic' },
  // Shotgun: fat, short, huge bore flash, wide impact.
  shotgun: { core: 0xffdca0, mid: 0xff8a18, halo: 0xd93300, w: 1.9, len: 0.45, glow: 1.3, flare: 1.9, boom: 1.4, shape: 'shell' },
  // Cannon: heavy slug, slow-reading, big everything.
  cannon:  { core: 0xfff4d8, mid: 0xffc247, halo: 0xc25a00, w: 2.3, len: 1.1,  glow: 1.5, flare: 2.1, boom: 1.9, shape: 'shell' },
  // Tesla (VFX-overhaul audit, 2026-08-20): was 'spiral' -- a corkscrew
  // dart that read as visual noise, not the "calmer beam that kills
  // through-and-through" the weapon's own pierce=10 already implies.
  // Reshaped as a real beam (the already-working laser/beam path), kept
  // on its own cyan-blue family so it doesn't collide with laser's pink
  // or beam's violet once those two widen below.
  tesla:   { core: 0xf2fbff, mid: 0x6fd8ff, halo: 0x1a72e0, w: 1.1, len: 2.4,  glow: 1.9, flare: 1.1, boom: 1.2, shape: 'beam' },
  // Laser vs beam (2026-08-20): reported indistinguishable and "too
  // thin" at 0.5/0.85 width and pink-0xff5bd0/lavender-0xba7cff -- too
  // close in both axes at once. Widened both; pushed beam off lavender
  // into a clearly separate deep violet-blue family so the two read as
  // different weapons, not two thicknesses of the same one.
  laser:   { core: 0xffffff, mid: 0xff2fb8, halo: 0x9b17c8, w: 0.8, len: 6.5,  glow: 2.4, flare: 0.6, boom: 0.9, shape: 'beam' },
  beam:    { core: 0xffffff, mid: 0x8a3aff, halo: 0x2d0a80, w: 1.15, len: 8.0, glow: 2.7, flare: 0.7, boom: 1.1, shape: 'beam' },
  // Flak: stubby shells, moderate bore, very wide airburst.
  flak:    { core: 0xfff0c8, mid: 0xff8434, halo: 0xcf2a08, w: 2.1, len: 0.5,  glow: 1.5, flare: 1.5, boom: 2.2, shape: 'shell' },
  // Missiles: fat bodies, long flame trail, enormous detonation.
  missile: { core: 0xffe8cf, mid: 0xff8a3a, halo: 0xff2a00, w: 2.7, len: 1.5,  glow: 2.0, flare: 1.6, boom: 3.0, shape: 'rocket' },
  // Mortar: the heaviest of all.
  mortar:  { core: 0xffffff, mid: 0xffd24a, halo: 0xff3fa0, w: 3.1, len: 1.8,  glow: 2.8, flare: 2.4, boom: 3.6, shape: 'lob' },
  // MELEE. The blade is a wide flat arc of light that crosses the lane;
  // the hammer is a short thick slab that lands with a shockwave. Both
  // die a body-length out, so they read as REACH rather than range.
  blade:   { core: 0xffffff, mid: 0x7fe8ff, halo: 0x1f8ce0, w: 5.5, len: 0.30, glow: 3.0, flare: 2.2, boom: 1.6, shape: 'sweep' },
  hammer:  { core: 0xfff4d0, mid: 0xffb43a, halo: 0xff5a12, w: 4.2, len: 0.42, glow: 2.6, flare: 2.8, boom: 3.2, shape: 'slam' },
};
const DEFAULT_LOOK = GUN_LOOKS['rifle']!;
/**
 * THE BOSS BECOMES A SPECTACLE (founder, 2026-08-24; Z Route: Redemption
 * reference). The chapter's own boss (config `isBoss` -- stormhulk in isle
 * 12, never a mid-run beast, same distinction the death-beat and camera
 * code already make) reads bigger again on top of BOSS_SCALE, so it dwarfs
 * even a beast rather than matching one.
 *
 * RE-SOLVED TWICE (founder finding, 2026-08-25). First pass measured the
 * silhouette at SPAWN depth (dz=24) via this file's own baselineScreenY
 * projection and picked 7.0 for 33.1% there -- wrong frame: the player
 * actually sees the boss at its STOP distance, which the melee fix
 * (step.ts TRUE_BOSS_MELEE_REACH) later moved from reach 3.0 to 1.2,
 * roughly 2.3x closer in camera distance, so 7.0 filled most of the
 * frame in real play. baselineScreenY has now mispredicted this twice
 * and is not trusted for this constant again -- chosen instead by real
 * 560x940 captures with the boss forced to TRUE_BOSS_MELEE_REACH (its
 * actual on-screen distance) and measured by eye against
 * docs/reference/lastz/IMG_3802.PNG (the boss-tier mech reference,
 * ~33% of frame height there). Swept 1.5/2.5/3.5 at that distance
 * (docs/reference/compare/c5-scale-*.png) -- 3.5 measured at ~307px of
 * 940 (32.7%), the closest match.
 *
 * F2 AUDIT (2026-08-26): `TRUE_BOSS_SCALE`, `BOSS_SCALE`, `BEAST_SCALE` and
 * the ratio that turns a scale into a CONTACT radius (`RADIUS_PER_SCALE`)
 * now live in `packages/run-engine/src/constants.ts` and are imported from
 * there, not redeclared here -- `config.ts` reads the SAME constants to
 * compute `enemies.csv`'s stormhulk/gale_colossus `radius` at load time,
 * so render scale and sim contact radius can no longer drift out of hand-
 * sync. See that file's own comment for the gale_colossus history this
 * fixed (its render scale used to be the flat generic `BOSS_SCALE`,
 * unrelated to its own contact radius, because it routes through the
 * BEAST clamp in step.ts and the stormhulk-only work never reached it).
 */
/** A soldier's actual height in world units — the yardstick for the horde. */
const SOLDIER_HEIGHT = 1.26;
/**
 * How far one cycle of the run clip carries a body at playback rate 1, in
 * world units. M2's no-skating law is rate = worldSpeed / STRIDE_M.
 *
 * MEASURED, not estimated. A run clip is authored in place, so during ground
 * contact the planted foot travels backward relative to the hips by exactly
 * one stride; that displacement, taken as a fraction of body height, is the
 * stride and is scale-invariant. Measured on this rig: 53.4% of body height,
 * which against SOLDIER_WORLD_H = 2.59 is 1.38 units per cycle.
 *
 * The previous value, 2.7, was an ESTIMATE from "a human runs about 3 m/s"
 * and it shipped to the deployed build unverified. It was 95% too large, so
 * the clip played at roughly half the rate the world moved and every soldier
 * skated. An eyeballed constant replaced by an underived one is not progress
 * — the lesson is that a derivation is not a measurement.
 */
const STRIDE_M = 1.38;
/** Rank/file spacing, in soldier-widths, so bulk never causes overlap. */
const FILE_GAP = 0.70, RANK_GAP = 0.78;
const DEATH_HOLD_S = 1.5;
/**
 * How many soldiers may be mid-death at once.
 *
 * The cap, not the duration, is what bounds the over-draw: with 24 skinned
 * slots and no cap, one bad wave puts 24 dying bodies on a formation of
 * three. Four reads as "we are taking losses" and never doubles the squad.
 */
const MAX_DYING = 4;
/** Crossfade time. Long enough to not pop, short enough to stay reactive. */
const FADE_S = 0.16;

export async function createScene(
  host: HTMLElement, arenaW: number, laneCount: number, skyIndex = 0,
): Promise<SceneHandles> {
  let lanesNow = Math.max(1, laneCount);
  /**
   * `?hideents=1` (dev-only, founder, 2026-09-05: "test the theory
   * before building" -- the residual-hitch investigation, whether
   * un-interpolated enemies/projectiles/pickups/gates snapping at tick
   * cadence inside an otherwise-interpolated frame reads as a hitch).
   * Forces enemies, corpses, elite/boss actors, projectiles, pickups
   * (gems) and gates (monolith rigs + panels) invisible every frame,
   * AFTER their normal update logic runs -- their sim-driven positions
   * still update underneath, only the visible result is suppressed, so
   * nothing about the sim or the update path changes, only what's
   * drawn. Same pattern as `?tune=1`; never active without the query
   * param, ships inert.
   */
  const HIDE_ENTITIES = new URLSearchParams(location.search).get('hideents') === '1';
  const renderer = new WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(host.clientWidth, host.clientHeight);

  renderer.outputColorSpace = SRGBColorSpace;
  // ACES is a FILM curve: it exists to make renders look photographic, and
  // it does that by desaturating saturated colour and rolling highlights
  // off toward grey. That is the opposite of the brief. Cineon keeps far
  // more chroma at the top end, and the exposure goes up because the frame
  // was reading dark on real devices.
  // NO TONE MAPPING. The backdrop is a finished painting loaded straight
  // from disk, and a tone mapper is a colour transform applied to every
  // pixel on the way out — including that painting. Cineon in particular
  // lifts contrast and apparent saturation, which is exactly the
  // "saturated version of the original image" that came back from the
  // founder. Deleting the grade from the composite pass did not help,
  // because this was a SECOND grade, one stage further down, and it was
  // the one still touching the sky.
  //
  // With the linear->sRGB encode fixed (OutputPass, below), tone mapping is
  // a deliberate look choice rather than a lever yanked to fix a decode
  // bug. NoToneMapping keeps the backdrop EXACTLY as authored.
  //
  // RENDER-QUALITY-PASS REVERT (founder finding, 2026-08-25): commit
  // 97d0e6d tried ACESFilmicToneMapping specifically to make a warm key
  // survivable (the theory: ACES's highlight rolloff would stop a warm
  // light from clipping pale albedo the way NoToneMapping's raw linear
  // multiply did on 2026-08-19). Deployed and judged by eye: the whole
  // frame -- deck, sky, squad -- read washed out and near-white. The
  // theory did not hold up against the actual art direction; reverted
  // rather than re-tuned, since this exact pairing (tone mapping change +
  // warm key) is now the SECOND time it has been tried and rejected on
  // this file. Do not re-attempt without a different mechanism.
  renderer.toneMapping = NoToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;
  host.appendChild(renderer.domElement);
  // HITCH INVESTIGATION, continued: `autoReset` (default true) resets
  // `renderer.info` before EVERY internal `renderer.render()` call --
  // and `finalComposer.render()` makes several per logical frame (one
  // per pass: RenderPass draws the real scene, then the grade and
  // OutputPass shader passes each draw one fullscreen triangle). With
  // autoReset on, no read of `renderer.info` taken AFTER the whole
  // chain finishes can ever see more than the LAST pass's numbers --
  // moving the read from an external rAF hook to an internal snapshot
  // (previous commit) did not fix this, because the counts were never
  // accumulating across passes in the first place. Reset manually,
  // once, at the top of each logical frame instead.
  renderer.info.autoReset = false;

  const scene = new Scene();

  // INTEGRATION FIX (founder verdict on 0c9a697b): "the background is
  // empty" — R8 v3 deleted the primitive floating islands on the grounds
  // that the painted panorama's own islands carried the depth, and the
  // char-pipeline merge deleted `render/environment.ts` (the sky dome,
  // sea, cloud drift, isle bob, waterfalls) to remove two banned items
  // (worn asphalt, lamp posts). Two individually-defensible deletions,
  // and the painted panorama alone does not actually read as a living
  // sky — nobody graded the sum. `createWorld()` is harvested back from
  // `char-pipeline` (see that file's own header for the full cut
  // rationale): shader sky dome, animated sea, floating archipelago with
  // waterfalls and gulls. It does NOT rebuild a causeway, piers, kerbs
  // or lamps — this game already has its own (below), already re-dressed
  // and already verified, and duplicating it would conflict rather than
  // "keep everything else." The static `./scenery/*.jpg` panorama this
  // replaces is no longer loaded.
  // CAM_HEIGHT/CAM_BACK/CAM_LOOKAT_FORWARD are declared below, but as
  // module-level `const`s they're already initialised by the time this
  // function runs — passed in so environment.ts's backdrop plates can face
  // the camera correctly without a second, driftable copy of these numbers.
  const skyWorld = createWorld(skyIndex, CAM_HEIGHT, CAM_BACK, CAM_LOOKAT_FORWARD, CAM_FOV);
  scene.add(skyWorld.group);

  // IMAGE-BASED LIGHTING. Without an environment probe a PBR material has
  // nothing to reflect, so every metal in the scene rendered BLACK -- which
  // is why the charge station's podium had to be faked with low metalness
  // and a self-glow, and why nothing in the corridor read as a material.
  // RoomEnvironment synthesises a probe on the GPU at load, costing no
  // download and no asset, and every MeshStandardMaterial in the game gains
  // real specular response from it.
  {
    const pmrem = new PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = 0.5;
    pmrem.dispose();
  }
  // FOG REMOVED (founder, 2026-08-19, item 3): was 46/122 (near/far),
  // colour `pal.fog` (0xa8cef2 for isle-1) -- a flat blue that matched
  // the OLD procedural sky dome's own horizon colour by construction, so
  // fog fading into that dome was seamless. That premise is gone: the
  // background is now layered painted plates plus an under-deck cloud
  // layer, not one flat-coloured dome, so fog fading to a fixed flat blue
  // no longer matches whatever is actually behind it at any given point
  // -- confirmed live, this was exactly the "visible fog boundary partway
  // up the deck" the founder flagged, a hard cut where the deck's fogged
  // colour (pale blue) meets the unfogged cloud/isle content around it.
  // With the sea gone, fog's other job ("hides the far clip") also loses
  // its case: nothing at the far clip reads as a hard edge worth hiding
  // once the background is continuous cloud/sky rather than a flat plane
  // ending abruptly. Removed outright rather than pushed out -- pushing
  // the SAME flat-colour fog further away still leaves the seam, just
  // later; it does not fix what's wrong with it. Stills before/after in
  // the deploy report.
  //
  // RE-ADDED, LIVE-TUNABLE, DEFAULT OFF (Stage 1, 2026-09-05): uses
  // `skyWorld.palette.fog`, the SAME colour `scene.background` is set to
  // a few lines below -- a non-zero density cannot reproduce the seam
  // above by construction (it blends into a background already that
  // exact colour). `tuning.fogDensity` defaults to 0 (FogExp2 at density
  // 0 contributes nothing, identical to `fog: null`), so nothing changes
  // on screen until the founder actively dials it in via the tuning
  // panel; `render()` below writes `runFog.density` from `tuning` every
  // frame.
  const runFog = new FogExp2(skyWorld.palette.fog, tuning.fogDensity);
  scene.fog = runFog;

  // SCENE BACKGROUND, ROOT CAUSE OF THE BOSS-PITCH VOID (founder,
  // 2026-08-19, item 1): the backdrop plates are fixed-size, fixed-
  // orientation planes sized for the BASELINE camera pose (see
  // `buildBackdropPlate`'s own doc comment) -- at the boss camera's
  // raise+pull pitch, live-checked, `sky`'s own coverage stops at
  // screen01 y=0.594, and `clouds`/`underClouds` are entirely off-frame.
  // Before this session the 900x900 sea plane quietly covered whatever
  // gap that left, at any angle, because it was enormous and flat.
  // Removing the sea (this session, item 1 of the environment rebuild)
  // removed that safety net along with it -- confirmed live: a pure
  // BLACK rectangle, the renderer's own default clear colour, filled the
  // bottom-right quadrant of the boss-moment screenshot. No plate
  // resize can fully solve this for every possible camera pose (a flat,
  // fixed-orientation plane has a hard edge somewhere no matter how big
  // it gets); a scene background colour cannot fail to cover a pixel,
  // by construction. Set to the palette's own horizon tone so it blends
  // with whatever plate edge it sits behind rather than introducing a
  // fourth colour.
  scene.background = new Color(skyWorld.palette.fog);

  // A wider lens exaggerates convergence, which is the strongest depth cue
  // a fixed camera has: the rails visibly rush to a point and bodies at the
  // far end are dramatically smaller than those at the near end.
  // 56, was 68: the wide lens exaggerated the road's splay and made both
  // armies read small and far — the disproportion the founder named. The
  // reference frames sit near a normal lens.
  const camera = new PerspectiveCamera(CAM_FOV, 1, 0.1, 260);

  // --- POST: grade ---------------------------------------------------------
  // FRAME-TIME FIX (founder-reported, ~8.9ms of a ~10.6ms frame): this used
  // to run a SECOND full scene render through `bloomComposer` (its own
  // RenderPass + UnrealBloomPass) for a selective-bloom composite, gated by
  // `darkenNonBloom`/`restoreMaterial` traversing the ENTIRE scene graph
  // twice a frame (once to black out every non-glowing mesh's material,
  // once to restore it) so the bloom pass would isolate only the glowing
  // bits. The comment below ("NO full-screen bloom... removed deliberately")
  // already recorded that the bloom composite itself was pulled for being
  // wrong for this art direction -- `bloomComposer.render()` was never
  // actually called anywhere in this file. So every frame was still paying
  // for the double scene-graph traversal and the material-swap churn (which
  // forces three.js to rebind/recompile shader state) for a pass that no
  // longer contributed anything to what's on screen. Confirmed by direct
  // A/B (`?noPost=1` bypass, both before this fix): removing exactly this
  // traversal, and nothing from `finalComposer`'s own three real passes
  // (RenderPass, the depth grade below, OutputPass), takes the frame from
  // ~10.6ms median to within the target. `glow()` and BLOOM_LAYER are kept
  // — they're near-free (one layer-flag set per glowing object) and every
  // existing call site (bolts, muzzle flares, holograms, the monolith
  // fissure) still marks its intent; only the now-pointless render/traverse
  // machinery that consumed it is gone.
  const bloomLayers = new Layers();
  bloomLayers.set(BLOOM_LAYER);
  /**
   * Enrol an object (and its children) in the bloom layer. No composite
   * reads this layer today (see above) — kept as an intent marker at
   * existing call sites, and because the layer flag itself costs nothing.
   */
  const glow = (o: Object3D): void => { o.traverse((c) => c.layers.enable(BLOOM_LAYER)); };

  const finalComposer = new EffectComposer(renderer);
  finalComposer.addPass(new RenderPass(scene, camera));
  /**
   * DEPTH GRADE — the cinematic depth cue as one cheap fullscreen pass:
   * the near field (squad, road, blocks) gets a warm lift and micro-
   * contrast, the far field (horizon, skyline) goes cool, lifted and
   * SOFT (a 4-tap blur standing in for depth-of-field), plus a gentle
   * vignette. The camera is a fixed shoulder rig, so SCREEN Y is a
   * faithful distance proxy — no depth buffer, no second scene render,
   * phone-safe. Runs in LINEAR space (before OutputPass, per 8b) and is
   * deliberately subtle: a grade, not a filter.
   */
  const gradePass = new ShaderPass({
    uniforms: {
      tDiffuse: { value: null },
      // NEAR-DEATH FEEDBACK (I9, genre-teardown-corridor.md: "vignette AND
      // desaturation"). juice.ts's updateNearDeath() computes both every
      // frame from squad danger and always has -- but the only consumer
      // was renderer.ts's Renderer class, a leftover 2D Pixi overlay
      // `new Renderer(` never appears anywhere, so main.ts's actual live
      // render path (this Three.js scene) never read either value. Wired
      // onto the one fullscreen grade pass that already exists instead of
      // building a second post pipeline.
      uVignette: { value: 0 },
      uDesaturate: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D tDiffuse;
      uniform float uVignette;
      uniform float uDesaturate;
      varying vec2 vUv;
      void main() {
        vec3 c = texture2D(tDiffuse, vUv).rgb;
        float far = smoothstep(0.48, 0.86, vUv.y);
        float near = smoothstep(0.52, 0.14, vUv.y);
        vec3 soft = (
          texture2D(tDiffuse, vUv + vec2( 0.0016, 0.0)).rgb +
          texture2D(tDiffuse, vUv + vec2(-0.0016, 0.0)).rgb +
          texture2D(tDiffuse, vUv + vec2(0.0,  0.0016)).rgb +
          texture2D(tDiffuse, vUv + vec2(0.0, -0.0016)).rgb) * 0.25;
        c = mix(c, soft, far * 0.50);
        c = mix(c, c * vec3(0.975, 1.0, 1.035) + vec3(0.0, 0.002, 0.007), far * 0.32);
        c = mix(c, c * vec3(1.045, 1.005, 0.960), near * 0.40);
        c = mix(c, (c - 0.5) * 1.06 + 0.5, near * 0.45);
        // VIBRANCE — the whole frame, not a band: the sharp saturated
        // register the direction calls for, applied last so every layer
        // (world, bodies, fx) rides the same lift.
        float lum = dot(c, vec3(0.299, 0.587, 0.114));
        c = mix(vec3(lum), c, 1.16);
        float d = distance(vUv, vec2(0.5, 0.42));
        c *= 1.0 - smoothstep(0.55, 0.95, d) * 0.10;
        // Danger desaturation, applied to the post-vibrance colour so it
        // reads as the screen losing colour, not as a weaker vibrance lift.
        float dangerLum = dot(c, vec3(0.299, 0.587, 0.114));
        c = mix(c, vec3(dangerLum), uDesaturate);
        // Danger vignette: a red-tinted edge darkening distinct from the
        // permanent 0.10-max composition vignette above, which never
        // moves and isn't a danger signal.
        float dangerEdge = smoothstep(0.20, 0.85, d) * uVignette;
        c = mix(c, c * vec3(1.0, 0.32, 0.30), dangerEdge * 0.55);
        c *= 1.0 - dangerEdge * 0.30;
        gl_FragColor = vec4(c, 1.0);
      }`,
  });
  finalComposer.addPass(gradePass);
  /**
   * OUTPUT PASS — the linear->sRGB encode, and the ROOT CAUSE of every
   * colour complaint in this project.
   *
   * EffectComposer renders the scene into a HALF-FLOAT LINEAR render
   * target. `renderer.outputColorSpace = SRGBColorSpace` does NOT apply to
   * that: it governs rendering straight to the canvas. A ShaderPass built
   * on a raw ShaderMaterial writes whatever it samples, and unless its
   * fragment shader includes <colorspace_fragment> nothing ever encodes
   * linear back to sRGB on the way out.
   *
   * This pass used to be a hand-written copy that did exactly that —
   * copied linear values onto an sRGB canvas with no encode. An image
   * missing its linear->sRGB encode renders DARKER AND MORE SATURATED,
   * which is precisely, and only, what was reported: a saturated, darkened
   * version of a backdrop that is correct on disk.
   *
   * It also explains why nothing fixed it. The composite grade was deleted
   * — not the cause. The tone mapper was swapped three times — a raw
   * ShaderPass ignores `renderer.toneMapping` too, so those swaps never
   * reached the composited frame at all. Deck, fog, hemisphere and key
   * were all recoloured to compensate for a decode fault, which is why
   * every one of them made some other part of the frame worse.
   *
   * OutputPass is three's own answer: it applies the renderer's tone
   * mapping AND the output colour-space transform, and it tracks changes
   * to either. It must be LAST.
   */
  finalComposer.addPass(new OutputPass());

  /**
   * SELECTIVE BLOOM (render-quality-pass, item 1). Two prior attempts are
   * on record and both are avoided here, differently:
   *
   * 1. A full-screen luminance-threshold bloom was tried and removed: this
   *    art direction is HIGH-KEY (sunlit near-white deck, bright pastel
   *    sky), so any threshold that still catches a muzzle flash also
   *    catches half the frame -- "the broken TV". This pass never
   *    thresholds the real frame. It renders a SECOND time, camera
   *    layer-masked to BLOOM_LAYER only (bloomLayers, above) against a
   *    cleared black background -- the sky, the deck, every non-glowing
   *    body is invisible to this render because it was never enrolled via
   *    glow(), not because its pixels failed a brightness test. Only what
   *    was already marked as light-emitting can ever appear in the bloom
   *    buffer.
   * 2. A wired-up selective bloom WAS built once (RenderPass +
   *    UnrealBloomPass in a second composer) and cost ~8.9ms of a ~10.6ms
   *    frame -- but the cost was `darkenNonBloom`/`restoreMaterial`
   *    traversing the ENTIRE scene graph twice a frame to blackout/restore
   *    every non-bloom material, forcing shader rebinds. Camera-layer
   *    masking replaces that: three's layer test is a per-object bitmask
   *    check with no material mutation and no recompile, and the bloom
   *    scene render only rasterizes the handful of objects actually on
   *    BLOOM_LAYER (bolts, muzzle flares, holograms, the monolith fissure,
   *    the boss's emissive core) -- not the whole graph painted black.
   *
   * threshold/strength/radius are BLOOM_THRESHOLD/STRENGTH/RADIUS, defined
   * once at the top of the file next to BLOOM_LAYER.
   */
  const bloomComposer = new EffectComposer(renderer);
  bloomComposer.renderToScreen = false;
  bloomComposer.addPass(new RenderPass(scene, camera));
  const bloomPass = new UnrealBloomPass(new Vector2(1, 1), BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD);
  bloomComposer.addPass(bloomPass);
  const bloomCompositePass = new ShaderPass({
    uniforms: { tDiffuse: { value: null }, tBloom: { value: null } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D tDiffuse;
      uniform sampler2D tBloom;
      varying vec2 vUv;
      void main() {
        vec3 base = texture2D(tDiffuse, vUv).rgb;
        vec3 bloom = texture2D(tBloom, vUv).rgb;
        gl_FragColor = vec4(base + bloom, 1.0);
      }`,
  });
  // Reads bloomComposer's own output target directly rather than going
  // through renderToScreen -- same technique three's own selective-bloom
  // example uses. Composited in LINEAR space, before OutputPass, so the
  // added glow gets the same tone-map/encode treatment as everything else
  // instead of blowing straight past it.
  bloomCompositePass.uniforms['tBloom']!.value = bloomComposer.renderTarget2.texture;
  finalComposer.insertPass(bloomCompositePass, 1);


  // --- lighting: one key with shadows, sky/ground bounce, a little fill.
  // A WARM KEY AND A COLD SKY, both pushed hard. The frame read dark and
  // blue-grey because one pale key was doing all the work against a cool
  // hemisphere. Colour comes from lights disagreeing with each other:
  // a warm sun on one side, a saturated sky bounce on the other, so every
  // body is lit warm on one cheek and cool on the other instead of flatly
  // grey all over.
  // NEUTRAL DAYLIGHT KEY, ROOT CAUSE (founder, 2026-08-19, item 2, fourth
  // "deck is yellow" round): every prior fix compensated the DECK'S OWN
  // albedo against a light that stayed warm — the founder's explicit
  // instruction this round is to stop doing that and cool the light
  // itself. 0xffe9c4 is R=255/G=233/B=196, R-B=59, a strongly amber sun;
  // at intensity 2.9 (normalized R=1.0/G=0.91/B=0.77 * 2.9) it multiplied
  // every lit surface warm regardless of that surface's own colour — the
  // real cause of every "still beige" re-measurement in the comment block
  // below `deckMat`. Set to 0xffffff (D65, R=G=B, the sRGB neutral white
  // point — "neutral daylight" has no colour to compensate for) and
  // dropped intensity 2.9 -> 2.3: unchanged, a neutral colour at 2.9 still
  // overdrives every bright albedo (soldiers' pale trim, kerb) toward
  // clipped white with NoToneMapping's linear multiply and no rolloff;
  // 2.3 was the highest step that held the sampled highlights below 250
  // on live re-check (see deploy report for before/after RGB).
  // RENDER-QUALITY-PASS REVERT (founder finding, 2026-08-25): commit
  // 97d0e6d re-tried a warm key (0xfff1e0, R-B=31, about half the amber
  // of the 0xffe9c4 rejected above) on the theory that pairing it with
  // ACESFilmicToneMapping's highlight rolloff would avoid the same
  // clipped-white-deck failure. Deployed and judged by eye: washed out
  // again, same failure mode as before. This is now the second time a
  // warm key has been tried and rejected against this deck material --
  // treat neutral white as load-bearing, not a placeholder.
  const key = new DirectionalLight(0xffffff, 2.3);
  key.position.set(-9, 18, 10);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.near = 1;
  key.shadow.camera.far = 60;
  key.shadow.camera.left = -14;
  key.shadow.camera.right = 14;
  key.shadow.camera.top = 22;
  key.shadow.camera.bottom = -10;
  key.shadow.bias = -0.0012;
  scene.add(key, key.target);
  // INTEGRATION FIX (founder verdict on e8a25db8, second look, still
  // correct with the sky dome restored): brightened alongside the fog
  // fix above -- same "washed out" complaint. Colours now come from the
  // sky dome's own palette (`skyWorld.palette.hemiSky`/`hemiGround`)
  // rather than a hand-picked hex, for the same one-palette reason the
  // fog colour changed.
  const hemi = new HemisphereLight(skyWorld.palette.hemiSky, skyWorld.palette.hemiGround, 1.4);
  scene.add(hemi);
  // A COLD RIM from the opposite side. One light makes a diorama; two
  // disagreeing lights make a picture, and the rim is what separates a
  // dark body from a dark background without lifting the whole frame.
  // Camera-side fill, cool against the warm key. Its job is that no body
  // between the lens and the horde is ever a featureless silhouette.
  const rim = new DirectionalLight(0x9fd6ff, 1.9);
  rim.position.set(10, 7, -14);
  /**
   * THE HERO LIGHT — a fill that follows the squad and nothing else.
   *
   * The soldier rig ships a near-black uniform, and three multiplies
   * map * color * light: against a diffuse that dark, scene lighting and
   * tinting both bottom out and the player renders as a silhouette on a
   * pale road. Moving the key off the squad's back helped the horde and
   * not him.
   *
   * A short-range light parented to the formation is the standard answer
   * — it lifts the one body that has to stay readable at all times
   * without raising the exposure of the whole corridor. `distance` keeps
   * it off the road and off the horde.
   */
  const heroFill = new PointLight(0xfff0dd, 9, 9, 2);
  scene.add(heroFill);
  scene.add(rim, rim.target);
  // AmbientLight removed: the IBL probe now supplies the fill it was
  // faking, and running both flattened and clipped every pale surface.

  // ---------------------------------------------------------------- world
  const world = new Group();
  scene.add(world);

  const DECK_LEN = 260;
  // WARM CARVED STONE, NOT ASPHALT (founder, 2026-08-19). The mid-grey
  // neutral material below (this comment's own prior history, preserved
  // in git blame) was itself the wrong target: `docs/ART_BIBLE.md` BANS
  // asphalt outright ("no roads, no asphalt, no lane markings, no
  // guardrails") and grey-with-a-grain-shader is exactly that prop,
  // still present under a different name. Both key images show warm
  // carved stone, not grey. Sampled key-image-2's causeway directly
  // (open, sunlit, flat stone -- avoided the carved-glyph band, cast
  // shadow and any figure): six points clustered tightly at RGB
  // (253,195,140), hex #FDC38C, naive luminance 80.8%. That figure is a
  // PEAK HIGHLIGHT, not a paintable albedo -- this file's own history
  // (three prior rounds, same lesson) already found that feeding a
  // photo's brightest sunlit pixel straight into an albedo double-counts
  // the scene's own key light and clips. Solved instead for a base in
  // the SAME FAMILY as `buildWall`'s quarried stone (0xb0a79c,
  // ~66% luminance) and the palette's `rock`/`cliff` fields (`rock`
  // 0xc9b696 ~72%, `cliff` 0xb7a284 ~65%) -- the corridor should belong
  // to the same material world the isles and the reward wall already
  // do, not introduce a fourth, unrelated stone colour. 0xa8927a
  // (168,146,122, ~59% naive luminance, R-B=46 -- warm, same hue
  // direction as the reference, headroom left under this scene's key
  // light (neutral 0xffffff@2.3) + hemisphere so it does not clip the
  // way the photo's own peak pixel would). Verified live after the
  // change: sampled rendered deck RGB (see deploy report) -- see also
  // `deckMat.onBeforeCompile` below, unchanged, for why the grain STAYS
  // (carved stone has surface texture too; only the base colour and the
  // "reads as tarmac" complaint were ever about hue, not about texture).
  const deckMat = new MeshStandardMaterial({ color: 0xa8927a, roughness: 0.88, metalness: 0.0 });
  // REAL SURFACE TEXTURE, NOT A FLAT FILL (founder, 2026-08-19, "a solid
  // fill with a faint procedural speckle -- no aggregate, no wear, no
  // variation across the surface... a TEXTURE MAP, which the deck does
  // not have"). Generated (`scripts/gen-deck-texture.py`, PIL+numpy, no
  // deck photo exists in docs/reference/ to key off of) -- one 512x512
  // albedo (~10.4KB webp) and one matching 512x512 normal map (~56.8KB
  // webp), ~67.2KB total. The normal map is finite differences of the
  // same height field the albedo speckle/streak are built from, so bump
  // detail lines up with colour variation. Every layer is placed with
  // WRAPPED (toroidal) distance, so the tile is seamless by construction
  // -- checked by tiling both maps 2x2 and inspecting the seams directly
  // (see deploy report) before this touched the deck.
  //
  // A THIRD LAYER (low-frequency blotches, for the reference's own
  // "staining, tonal shift across width") WAS baked into this tile and
  // is NOT any more -- found live, per the DoD's own instruction to
  // check for tiling and say so, that it aliased into a visible
  // horizontal "corduroy" band pattern down the receding corridor.
  // Isolated by testing layers alone (speckle-only and streak-only both
  // stayed clean under 15-40x tiling + Lanczos downsample in plain PIL,
  // zero 3D involved; the blotch layer alone reproduced the banding even
  // after cutting its amplitude 15->6) -- a smooth low-frequency signal
  // aliases under repeated tiling regardless of amplitude, because the
  // STRUCTURE repeats identically every tile, not just its magnitude.
  // That role now lives in `onBeforeCompile` below as a world-space
  // sum-of-sines (see that comment), which cannot alias by construction
  // since world position never tiles. `anisotropy` (below) was tried
  // first and made no measurable difference to the banding, THEN turned
  // out to have a real, measured frame-time cost of its own (see that
  // constant's own comment) -- not the free improvement it was assumed
  // to be, and shipped disabled.
  //
  // `RepeatWrapping` + `repeat` tiles the 512px tile to roughly 3 world
  // units per repeat (deck width 13.5u -> repeat.x 4.5; DECK_LEN 260u ->
  // repeat.y ~86.7) -- small enough that individual flecks stay legible
  // near the camera, far short of the "one giant stretched image" a
  // repeat of (1,1) would show, and safe now that the tile itself
  // carries no low-frequency content to alias.
  const maxAniso = renderer.capabilities.getMaxAnisotropy();
  const deckAlbedo = new TextureLoader().load('./deck-albedo.webp');
  deckAlbedo.colorSpace = SRGBColorSpace;
  deckAlbedo.wrapS = deckAlbedo.wrapT = RepeatWrapping;
  deckAlbedo.repeat.set(4.5, DECK_LEN / 3);
  // PERFORMANCE REGRESSION FOUND AND ISOLATED LIVE, NOT SHIPPED BLIND
  // (founder, 2026-08-19, "check p95 not just median -- you found a real
  // regression there this round"): `anisotropy = maxAniso` +
  // `normalScale.set(0.55, 0.55)` together measured 47/299 frames over
  // 30ms (p95 34-50ms, max up to 200ms) against a clean baseline of 0
  // frames over 20ms. Bisected one variable at a time (both off -> clean;
  // anisotropy alone -> 9/299 over 30ms; normalScale alone -> 10/299) --
  // each costs a real, measurable amount alone, and together the cost
  // compounds well past their sum, not just adds. Anisotropic filtering
  // was never what fixed the tiling artifact either (that was the
  // blotch-layer removal, see this file's own history above) -- it was
  // an untested "free improvement" that was not actually free. Shipping
  // both OFF: the albedo map (aggregate speckle, wear streaks, tiling
  // verified clean) is what item 1 actually asked for and is unaffected;
  // the normal map FILE still exists (generated, byte cost still
  // real and reported) but its lighting contribution is disabled rather
  // than shipped at a measured, real frame-time cost nobody asked to pay.
  deckAlbedo.anisotropy = 1;
  const deckNormal = new TextureLoader().load('./deck-normal.webp');
  deckNormal.wrapS = deckNormal.wrapT = RepeatWrapping;
  deckNormal.repeat.set(4.5, DECK_LEN / 3);
  deckNormal.anisotropy = 1;
  deckMat.map = deckAlbedo;
  deckMat.normalMap = deckNormal;
  deckMat.normalScale.set(0.0, 0.0);
  void maxAniso;
  // `map` MULTIPLIES `material.color` (three.js: diffuseColor *=
  // sampledDiffuseColor) -- the texture already carries the full 0xa8927a
  // family baked into its own pixels, so leaving color at 0xa8927a would
  // square it (168/255 twice -> ~0.43, a visibly darker, desaturated
  // result nobody asked for). White is the neutral multiplier that lets
  // the texture's own baked colour render as itself.
  deckMat.color.set(0xffffff);
  // ASPHALT GRAIN (founder, 2026-08-19, item 5, "the road reads as
  // paint"). Procedural per-fragment speckle via `onBeforeCompile`, not a
  // texture: a hash of world-space XZ position never repeats at
  // floating-point resolution, so there is no tiling seam at any camera
  // position (a real texture tiled across DECK_LEN=260 world units would
  // have to repeat many times over that span and would show one). Zero
  // added bytes -- the cost is a handful of extra ALU ops per fragment
  // (one dot product, one sin, one fract), gated behind the same distance
  // falloff so far fragments skip even that. Falloff uses `vViewPosition`,
  // which meshphysical's shader already computes for every fragment
  // (lighting needs it) -- no extra varying, no per-frame JS-side uniform
  // update required, unlike a camera-position uniform would need. Faded
  // out with distance (smoothstep 6..34 world units, roughly the visible-
  // near-deck band under CAM_HEIGHT/CAM_BACK below) both because the
  // founder asked for "gone at distance" and because a fixed-frequency
  // per-pixel pattern shown at range would drop below one screen pixel
  // and shimmer/alias rather than read as smooth road.
  // FAR-EDGE FADE (founder, 2026-08-19, item 2, "the deck runs to a point
  // and stops... must fade or be occluded into cloud, never terminate
  // visibly"). Three options were weighed: shorten DECK_LEN (rejected by
  // the founder explicitly -- deletes the space rather than using it);
  // a distant landmark billboard (needs a new hand-authored asset this
  // session has no image-gen tool to produce); fill the reach with
  // environment and dissolve the seam (chosen -- reuses machinery this
  // session already has: the returning island plates, item 4, now
  // populate the sky the far deck used to run bare under, and THIS fade
  // removes the box's own hard geometric edge so there is no seam left to
  // populate around). The deck's own far edge is world-Z 240 (box centre
  // 110 + half-length 130, DECK_LEN=260).
  //
  // ZONE NARROWED, CONFIRMED THEN FIXED (founder, next turn, "the sides
  // are washed out"). The founder's own hypothesis was right: the fade
  // is a function of world-Z ALONE, applied uniformly across the full
  // width of the mesh at that depth -- it never distinguished centre from
  // kerb-adjacent. Verified live before touching the code (per
  // instruction, hypothesis confirmed before fixing): pixel-sampled both
  // kerb-adjacent strips and the centre at world-Z 100-235 (wave 5,
  // `window.__scene.projectWorld`) -- solid grey (~166,171,176) at every
  // sampled point Z<185, and BOTH sides fading in lockstep with the
  // centre from Z=185 (172,192,203) through Z=235 (189,239,255). The
  // original 185-240 zone is 55 world units deep -- at this camera's
  // shallow pitch that is NOT a razor-thin sliver at the vanishing point,
  // it is a real ~10-15%-of-frame-width band, wide enough for "the sides"
  // (the kerb-to-cloud boundary within that band) to visibly wash toward
  // pale blue instead of staying a solid grey-to-cloud cut. Narrowed to
  // 228-240 (the last 12 units, not 55) so the fade is confined to the
  // genuine terminus -- every wave's actual enemy reach (world-Z ~56-99,
  // item 3's report) and the entire ordinary sightline up to Z~228 now
  // render as solid, undimmed deck meeting cloud cleanly at the kerb.
  deckMat.transparent = true;
  deckMat.transparent = true;
  deckMat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vGrainWorldPos;')
      .replace('#include <worldpos_vertex>',
        '#include <worldpos_vertex>\n  vGrainWorldPos = worldPosition.xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vGrainWorldPos;')
      .replace('#include <dithering_fragment>', `
  float grainDist = length( vViewPosition );
  float grainFalloff = 1.0 - smoothstep( 6.0, 34.0, grainDist );
  if ( grainFalloff > 0.0 ) {
    vec2 gp = vGrainWorldPos.xz * 5.0;
    float gn = fract( sin( dot( gp, vec2( 12.9898, 78.233 ) ) ) * 43758.5453 );
    gl_FragColor.rgb += ( gn - 0.5 ) * 0.05 * grainFalloff;
  }
  // STAINING / TONAL SHIFT, WORLD-SPACE, NOT TILED (founder, 2026-08-19,
  // item 1 -- see scripts/gen-deck-texture.py's own comment for the
  // full story: this role was originally baked into the tiled albedo as
  // a third, low-frequency noise layer, and THAT aliased into a visible
  // "corduroy" band pattern down the receding corridor no matter how far
  // its amplitude was cut, because a repeating low-frequency signal
  // aliases under compression regardless of magnitude. Moved here
  // instead, exactly like the grain two lines up: a smooth sum-of-sines
  // over the ACTUAL world position, incommensurate frequencies along X
  // (gentle side-to-side shift across the 13.5-unit width, matching the
  // reference's "tonal shift across width") and Z (a much longer period
  // than the 260-unit deck, so it never visibly repeats along its
  // length). World position never tiles, so this is structurally immune
  // to the exact aliasing class that broke the first attempt -- not
  // distance-gated like the grain, because a slow, large-scale tonal
  // shift is exactly the kind of low-frequency content that should
  // survive to the horizon, unlike fine speckle.
  float stainX = sin( vGrainWorldPos.x * 0.35 + 1.7 ) + sin( vGrainWorldPos.x * 0.13 + 4.1 );
  float stainZ = sin( vGrainWorldPos.z * 0.045 + 0.6 ) + sin( vGrainWorldPos.z * 0.017 + 2.3 );
  gl_FragColor.rgb += ( stainX + stainZ ) * 0.017;
  gl_FragColor.a *= 1.0 - smoothstep( 228.0, 240.0, vGrainWorldPos.z );
  #include <dithering_fragment>`);
  };
  const deck = new (class extends Object3D {})();
  const deckMesh = new InstancedMesh(new BoxGeometry(arenaW * SX, 0.5, DECK_LEN), deckMat, 1);
  deckMesh.receiveShadow = true;
  const m4 = new Matrix4();
  deckMesh.setMatrixAt(0, m4.makeTranslation(0, -0.25, DECK_LEN / 2 - 20));
  deckMesh.instanceMatrix.needsUpdate = true;
  deck.add(deckMesh);
  world.add(deck);

  // Causeway rails: a solid kerb plus a carved stone cap. R8 re-dress: this
  // used to be a highway guardrail (cool grey kerb, neon-cyan cap rail) --
  // cyan is R7's RESERVED objective colour, so a glowing cyan rail running
  // the whole corridor competed with the one thing that is supposed to own
  // that hue. Warm quarried stone (matches `buildWall`'s block colour) plus
  // a gold-amber inlay reads as the carved rail in the key image instead.
  //
  // BORDER, RE-DRESSED PALE BLUE (founder, 2026-08-19): the gold-amber
  // inlay (0xd9b877, emissive 0x6b4a12) read as yellow.
  //
  // CLIPPED TWICE before this landed, both caught by pixel-sampling the
  // actual render rather than trusting naive-luminance arithmetic:
  // 0xc7dcf0 (emissive 0x3a6a8a/0.4) rendered pure (255,255,255); a
  // second, dimmer pass (0x5c86a4, emissive 0x14222c/0.2) still clipped
  // the BLUE channel alone to 255 — glossy (roughness 0.45) blue albedo
  // catches a specular highlight from the cool hemisphere/fog on top of
  // the warm key, and blue has less headroom than red/green under this
  // NoToneMapping rig regardless of the base colour's own brightness.
  // Fixed on the THIRD measurement, not a third guess: pushed roughness
  // to 0.85 (matte, same as the deck/kerb — kills the specular source
  // rather than chasing its colour) and darkened further (0x46697e,
  // emissive 0x0c161c/0.15). Sampled clean in the captured render:
  // (108,152,174) — no channel clips, reads as a clear light blue against
  // both the water (measured alongside it, ~(20,135,195), more saturated/
  // dark) and the neutral grey deck (~(196,194,188)). Distinct from
  // RIM_OBJECTIVE's reserved saturated cyan (0x8fe9ff) and from the sky
  // palette (`environment.ts` isle-1: horizon 0xa8cef2 — `seaShallow`/
  // `seaDeep` no longer exist, deleted with the sea shader itself).
  //
  // YELLOW LINE REMOVED (founder, 2026-08-19): the kerb (this block) had its
  // own material, `railMat` (0xa89a86, warm quarried-stone tan), left over
  // from before the cap was re-dressed pale blue above it — pixel-sampled
  // off a live render at (176,154,124), distinctly warm against both the
  // neutral deck (~197,195,190) and the cap right above it (~108,152,174).
  // Founder's call: remove it rather than tune a fourth hex. The kerb now
  // reuses `capMat` directly — one continuous pale-blue rail, no seam,
  // nothing new to verify for clipping since capMat is already sampled
  // clean.
  //
  // PALETTE LIFT (founder, 2026-08-19): 0x46697e rendered at ~38% naive
  // luminance — key-image-2 has no dark structural accent at all (the
  // only saturated element is one deliberate teal beacon; every rail/
  // stone surface is pale). Lifted ~1.5x per channel, same cool lean
  // (B > R) preserved: 0x699ebd. Verified live after the change, see
  // deploy report.
  const capMat = new MeshStandardMaterial({
    color: 0x699ebd, emissive: 0x0c161c, emissiveIntensity: 0.15, roughness: 0.85,
  });
  for (const side of [-1, 1]) {
    // A KERB, not a wall. At 1.1 units against a 1.15-unit soldier the
    // barrier stood shoulder-high the whole length of the corridor and the
    // road read as the bottom of a box -- the horizon, the water and the
    // town were all cut off behind it. Waist height keeps the road solid
    // (the physics law is enforced in the sim regardless) while opening
    // the view the width was widened to show.
    const kerb = new InstancedMesh(new BoxGeometry(0.42, 0.42, DECK_LEN), capMat, 1);
    kerb.castShadow = true; kerb.receiveShadow = true;
    kerb.setMatrixAt(0, m4.makeTranslation(side * (arenaW / 2) * SX, 0.21, DECK_LEN / 2 - 20));
    kerb.instanceMatrix.needsUpdate = true;
    const cap = new InstancedMesh(new BoxGeometry(0.5, 0.09, DECK_LEN), capMat, 1);
    cap.setMatrixAt(0, m4.makeTranslation(side * (arenaW / 2) * SX, 0.46, DECK_LEN / 2 - 20));
    cap.instanceMatrix.needsUpdate = true;
    world.add(kerb, cap);
  }

  // PIERS (founder, 2026-08-19): "engraved edges, structures rising from
  // the deck, gates and pillars punctuating its length" -- key-image-2's
  // one element with real height, the thing that actually breaks the
  // corridor's dead-flat horizon line from a distance (report this same
  // date: "the single element that buys the most... piers"). ONE
  // InstancedMesh, per the founder's own spec -- a shaft box and a
  // capital box are merged into a single BufferGeometry
  // (`mergeGeometries`) rather than built as two separate meshes, so N
  // pillars stay exactly one draw call, not two. Sits ON the kerb line,
  // `side * (arenaW/2) * SX` = the SAME x the kerb/cap already use
  // (render-X +/-6.75) -- per this date's own report, the wall law's
  // playable range is render-X [-4.875, +4.875], so anything AT the kerb
  // line already sits 1.875 units clear of it, no runtime check needed
  // because no sim body can ever reach there. Reuses `deckMat` rather
  // than a new material: same warm carved-stone family as the deck it
  // rises from (the point of this whole pass), and it inherits that
  // material's far-edge fade and grain for free -- a pier at Z=204 (the
  // last one before the fade zone starts at 228) dissolves into cloud
  // exactly like the deck does, no separate fade logic to write.
  // PIER RECYCLING (founder, Stage 1 of the run-scene motion plan,
  // 2026-09-05): "kerb geometry" + "prop spawn/despawn" bound to run
  // speed. `pierMesh`/`PIER_COUNT`/`PIER_Z_START` are hoisted out of
  // this block (were local to it, one-shot placement) so `render()`
  // below can re-place every instance each frame as a function of
  // `s.worldY` -- despawning behind the squad and spawning back in
  // ahead is the SAME operation as sliding each instance's Z by the
  // wrapped travel distance, not a separate pool/lifecycle system.
  // These piers already sit ON the kerb line (`side * (arenaW/2) * SX`,
  // the same X the kerb/cap use) per the comment above this block, so
  // recycling them also satisfies "kerb geometry" -- no second geometry
  // system, no reintroduction of the banned road-debris props (Iron
  // Rule 7e) this same block's neighbour comment documents removing.
  // PIER_MAX_COUNT (founder, 2026-09-05, "add pierCount to the panel
  // alongside propSpawnDistance"): an InstancedMesh's instance buffer is
  // fixed at construction, so a LIVE count slider needs headroom
  // allocated up front. 20 is the panel's own slider max
  // (render/tuning.ts) -- instances beyond the live `tuning.pierCount`
  // for a given frame are zero-scaled in render(), not left at a stale
  // position, so raising the buffer size costs draw-call-free idle
  // instances, never a visible artifact.
  // PIER_WRAP_FLOOR (founder correction, 2026-09-05, "forward motion
  // stutters/glitches intermittently"): the wrap arithmetic's floor was
  // PIER_Z_START=15, well INSIDE the frustum -- at baseline camera Z
  // ~1.8 (pz=playerY*S=8.8 fixed all run, minus CAM_BACK=7.0), a pier at
  // Z=15 is ~13.2 units out and ~100px tall (2.3-unit pier height,
  // camera FOV 72.7deg, 844px viewport), one of the most visible things
  // on screen. Reaching that floor teleports it to the far end of the
  // cycle (~z=231 at default density, ~6px tall) in one frame -- a
  // large, close object popping to a speck, recurring roughly every
  // span/pierCount/scrollSpeed = 216/8/3 = 9s at defaults. That is the
  // stutter; confirmed by evidence, not the two other candidates
  // checked alongside it (deck albedo/normal V-tiling measured
  // near-seamless -- mean/max abs diff 1.5/8 of 255 top-vs-bottom row
  // for albedo; the normal map's own seam, 7.4/64, doesn't matter
  // either way since `deckMat.normalScale` is (0,0), disabling its
  // lighting contribution entirely; and `main.ts`'s frame() calls
  // `scene3d.render()` once per rAF with whatever `state` the fixed-
  // step accumulator produced that frame, no worldY interpolation
  // between ticks -- real and worth tracking separately, but it reads
  // as constant micro-judder under frame-time variance, not a
  // describable recurring "pop," so it is NOT fixed here).
  //
  // Fix: move the wrap floor behind the camera instead of just inside
  // it. Camera Z = playerY*S(8.8) - dynBack, and dynBack's own worst
  // case is CAM_BACK(7.0) + CAM_SQUAD_PULL_MAX(1.8) +
  // CAM_BOSS_BACK_PULL(2.2) = 11.0 (full squad, mid-boss), giving a
  // worst-case camera Z of -2.2 -- more forward than baseline, not
  // less. -15 clears that by 12.8 units in every camera state this
  // file computes, so the teleport always happens on geometry the
  // camera has already passed, never rendered regardless of near-clip
  // specifics (behind the eye, not just off to the side).
  const PIER_Z_START = 15, PIER_WRAP_FLOOR = -15, PIER_MAX_COUNT = 20;
  let pierMesh!: InstancedMesh;
  {
    const shaftGeo = new BoxGeometry(0.55, 2.0, 0.55).translate(0, 1.0, 0);
    const capitalGeo = new BoxGeometry(0.75, 0.3, 0.75).translate(0, 2.15, 0);
    const pierGeo = mergeGeometries([shaftGeo, capitalGeo]);
    shaftGeo.dispose(); capitalGeo.dispose();
    // Spacing ~27 units (within the "24-30" the report proposed), paired
    // left/right: originally 8 positions from Z=15 (just past spawn) to
    // Z=204 (the last one still short of the 228-240 far-edge fade), 16
    // instances total -- against the measured 47-127 draw call /
    // 249K-2.14M triangle baseline (see deploy report), noise against
    // either figure. Spacing is `tuning.propSpawnDistance` and count is
    // `tuning.pierCount` (Stage 1 tunables, defaults 27/8) -- both read
    // fresh every frame in render(), not just at construction, so the
    // founder's live sliders actually move something.
    pierMesh = new InstancedMesh(pierGeo, deckMat, PIER_MAX_COUNT * 2);
    // castShadow FALSE, measured not assumed: `castShadow=true` here (16
    // instances of a shared TRANSPARENT material, `deckMat.transparent`
    // for the far-edge fade) turned a clean 16.7ms/frame build into
    // 79-of-251 sampled frames over 30ms, several 60-130ms -- shadow-map
    // casting from a transparent instanced mesh is real per-frame cost in
    // this renderer, not a one-time compile hitch (confirmed by isolating
    // the flag with everything else unchanged, and by diffing against the
    // pre-pier commit, which stayed clean at max 18.6ms). `receiveShadow`
    // alone still lets the piers pick up shading from the key light and
    // whatever else casts onto them, which is what actually reads on
    // screen; casting their OWN shadow onto the deck is the part that
    // cost 30-100ms and was never asked for.
    pierMesh.castShadow = false; pierMesh.receiveShadow = true;
    // Placeholder placement -- render()'s first frame overwrites every
    // instance from live tuning before anything is drawn.
    let pi = 0;
    for (let i = 0; i < PIER_MAX_COUNT; i++) {
      const z = PIER_Z_START + i * 27;
      for (const side of [-1, 1]) {
        pierMesh.setMatrixAt(pi++, m4.makeTranslation(side * (arenaW / 2) * SX, 0, z));
      }
    }
    pierMesh.instanceMatrix.needsUpdate = true;
    world.add(pierMesh);
  }

  /** Widest isle (3 lanes = 2 dividers) — kept for callers below, though R8
   * deletes the divider mesh itself (see note at the old dashed strip). */
  const MAX_LANES = 3;

  // R8 (Iron Rule 7e), BACKDROP HISTORY, kept for the sequence of mistakes
  // it records rather than deleted:
  //   v1/v2 deleted the coastal-highway photo backdrop and the primitive
  //   floating islands it replaced them with never actually read as rock
  //   (`tasks/lessons.md` L-12/L-13).
  //   v3 replaced the flat gradient with a painted panorama assembled
  //   from `docs/reference/key-image-2.png` itself, feathering two clean
  //   regions of the source (islands on the left, light rays on the
  //   right) together.
  //   The integration merge then deleted char-pipeline's
  //   `render/environment.ts` wholesale to remove two banned items (worn
  //   asphalt, lamp posts), which also removed real, independently
  //   gradeable world motion the panorama alone could not replace — graded
  //   as its own regression ("the background is empty").
  // CURRENT STATE: `createWorld()` (top of this function) is harvested
  // back, background-only (no causeway/lamps — this game's own causeway
  // is below, already re-dressed). The static panorama JPGs are no
  // longer loaded. `docs/reference/key-image-2.png` remains the R7
  // palette source and the DoD reference image; it is no longer the
  // backdrop asset itself.

  // No sea plane. A 600x600 quad under the deck cut a hard horizontal seam
  // across the backdrop wherever its top edge met the sky image -- and the
  // sky image already contains water, so the plane was covering the very
  // thing it was imitating. The deck now floats over the painting, which is
  // both the intended fiction and one less surface to keep in palette.

  // ------------------------------------------------------------- entities
  /**
   * Enemies render in two tiers, because a horde and a boss have opposite
   * needs. The MASS is instanced static geometry in bind pose — one draw
   * call for hundreds of real character silhouettes, which is how shipped
   * games do crowds. ELITES and BOSSES are genuine skinned actors with
   * their own clips; there are only ever a few and they are what the eye
   * is on.
   */
  const massBounds: string[] = [];
  /** Per tier: one InstancedMesh per baked walk phase, cycled at draw. */
  const massTiers: InstancedMesh[][] = [];
  /**
   * DEATH PHYSICS. A body used to be removed from the instanced horde the
   * frame it died -- it simply blinked out mid-stride while a puff of
   * particles played, which reads as a rendering glitch rather than as a
   * kill. Every death now hands the body to this pool, which flings it
   * back along the shot, spins it, drops it under gravity and sinks it
   * into the deck. Same baked geometry, so it costs one extra draw call
   * per tier and no new art.
   */
  /**
   * One corpse mesh PER BAKED PHASE, per tier — not one per tier.
   *
   * Every corpse in the game was `phases[0].geometry`: walk-cycle frame
   * zero, tumbling. Four bodies falling in the same frame were four copies
   * of one silhouette, which is what makes a death read as a prop being
   * recycled rather than as a body going down. The phases are already
   * baked and already paid for, so using all of them costs three extra
   * draw calls per tier and no new art.
   */
  const corpseTiers: InstancedMesh[][] = [];
  /**
   * Half-width of a tier's body in GEOMETRY units. The bake is normalised
   * to unit height, so this is a fraction of the body's height and stays
   * correct at any world scale.
   *
   * The rail law is enforced on the rendered body's EDGE, not its centre
   * (Iron Rule 8). Anything that shoves a body sideways — a stagger, a
   * death impulse, a corpse bounce — has to know how wide the body is, or
   * the clamp holds the centre on the deck and lets the shoulders through
   * the kerb.
   */
  const massHalfW: number[] = [];
  /**
   * How many bodies the GPU crowd can hold in one draw call — living plus
   * dead. Observed peak is ~230 alive with a few dozen corpses; the
   * headroom is for later isles, and it costs one instance buffer, not one
   * draw call each.
   */
  const CROWD_CAP = 1200;
  /**
   * ONE GPU CROWD PER TIER, built from the game's own enemy meshes.
   *
   * The single-rig experiment used Ch24 from the Mixamo combat pack
   * because it carried a 9-clip library. Measured, not argued: its diffuse
   * averages RGB 63,52,70 — a dark navy outfit — so all three tiers
   * rendered as one black silhouette and no tint could rescue them,
   * because three multiplies the tint INTO that darkness.
   *
   * The original grunt / wretch / slinger meshes are bright, distinct and
   * actually look like the creatures they are. They carry only a `walk`
   * clip, so the attack/impact/death library is lost — but the thing that
   * made the horde read as 1990s was the FLIPBOOK, not the clip count, and
   * the GPU rig fixes that either way: continuous 30fps motion, per-body
   * phase, and a stride retimed to the body's real speed. Colour and
   * silhouette now cost one draw call per tier instead of one for all.
   */
  const hordes: (CrowdRig | null)[] = [];
  const hordeClips: Set<string>[] = [];
  /** Body half-width as a fraction of its own height, for the rail law. */
  let crowdHalfW = 0.24;
  /**
   * Rig metres -> world units, so `massBodyScale` keeps meaning the same
   * thing it meant for the baked path. The old horde geometry was
   * normalised to unit height at bake; this rig is not, so without the fit
   * factor the whole crowd renders at whatever scale Mixamo exported in.
   */
  const crowdFit: number[] = [1, 1, 1];
  /** The squad's back ranks, on the same GPU rig as the horde. */
  let squadCrowd: CrowdRig | null = null;
  let squadClips = new Set<string>();
  /**
   * NO TINT (founder directive, 2026-08-30): soldiers and horde ship in
   * their own original asset colour, full stop -- no per-instance tint of
   * any kind. This used to carry a team-colour multiply (and before that,
   * a brightness gain to compensate a near-black rig); both are gone. If
   * a rig reads too dark at this scene's lighting, that is a LIGHTING
   * problem to solve with real light response, not a colour multiply.
   */
  const squadTint = new Color(0xffffff);
  /**
   * The three authored impact clips (M4). Severity still scales the
   * MAGNITUDE of a reaction (duration, lean, shove — see `enemyHit`); which
   * of these three plays is chosen per-instance instead, so a volley that
   * hits several bodies in the same tick does not play one clip in unison
   * across the whole rank (M4's whole reason to exist: "never a phase-offset
   * copy" applies to WHICH clip plays, not only to its timing offset).
   */
  const HIT_VARIANTS = ['impact', 'impact2', 'impact3'];
  /**
   * The three authored death clips (M4 "M4 variant sources (fodder)"):
   * `death` and `death2` are the donor rig's own two takes, `die` is
   * `character.glb`'s squad-death mocap borrowed onto the fodder rig — a
   * genuinely different clip, not a phase-offset copy of the first two.
   */
  const DEATH_VARIANTS = ['death', 'death2', 'die'];
  /**
   * NO TINT (founder directive, 2026-08-30): every crowd body pushes this
   * neutral white as its per-instance colour -- a true no-op multiply, so
   * the raw asset texture is what actually renders.
   */
  const WHITE_TINT = new Color(1, 1, 1);
  /** Seconds of crossfade into and out of a reaction clip. M1: 120-200ms. */
  const REACT_IN = 0.13, REACT_OUT = 0.19;
  /**
   * THE one place a mass body's world height is decided.
   *
   * It was decided in two places, and they disagreed by a factor of THREE.
   * The living body was sized here; the corpse was handed a separate
   * number computed in main.ts as `0.72 * (0.70 + min(0.32, r * 0.26))`.
   * For a skitterer that is 1.81 units alive and 0.57 dead — so every body
   * in the game collapsed to a third of its size on the frame it died,
   * which is why a kill read as the body being deleted and a puff of
   * particles being played over the gap. The corpse pool was working the
   * whole time; it was drawing bodies too small to find.
   *
   * Both callers now read this, so the living and the dead cannot drift
   * apart again.
   */
  function massBodyScale(radius: number): number {
    // Base 0.80 (was 0.74) in the 2026-08-13 bulk pass: fodder keeps its
    // shorter-than-a-soldier ratio but stops reading as insects. The cap
    // is unchanged, so brutes still out-top the line.
    //
    // HORDE_SIZE then pulls the whole family down together, so the ladder
    // between fodder, heavies and brutes is preserved while the squad
    // becomes the biggest thing on its own side of the fight.
    return SOLDIER_WORLD_H * (0.80 + Math.min(0.62, radius * 0.55)) * HORDE_SIZE;
  }
  const cX = new Float32Array(MAX_CORPSES), cY = new Float32Array(MAX_CORPSES);
  const cZ = new Float32Array(MAX_CORPSES);
  const cVx = new Float32Array(MAX_CORPSES), cVy = new Float32Array(MAX_CORPSES);
  const cVz = new Float32Array(MAX_CORPSES);
  const cSpin = new Float32Array(MAX_CORPSES), cRot = new Float32Array(MAX_CORPSES);
  const cLife = new Float32Array(MAX_CORPSES), cMax = new Float32Array(MAX_CORPSES);
  const cScale = new Float32Array(MAX_CORPSES);
  const cTier = new Int8Array(MAX_CORPSES).fill(-1);
  /** Which baked silhouette this body fell in. */
  const cPhase = new Int8Array(MAX_CORPSES);
  /** Resting yaw, so a heap of bodies does not all lie the same way. */
  const cYaw = new Float32Array(MAX_CORPSES);
  /** How it died — a DeathArch. Drives the impulse AND the settle. */
  const cArch = new Int8Array(MAX_CORPSES);
  /**
   * Which of the three M4 death clips this corpse plays (index into
   * `DEATH_VARIANTS`), chosen ONCE at spawn from the dying body's own pool
   * index — never re-rolled per frame, and never from `cArch`'s physics
   * archetype, which is a different axis (how the body falls, not which
   * mocap plays under the fall).
   */
  const cVariant = new Int8Array(MAX_CORPSES);
  let cCur = 0;

  /**
   * HOW A BODY DIES, not merely that it did.
   *
   * One impulse formula for every kill meant a rifle round and a mortar
   * shell disposed of a body identically — flung up-corridor, spinning, at
   * a speed that only scaled. These are four different events, chosen from
   * what actually killed the body, and each settles differently.
   */
  const DeathArch = {
    /** Heavy weapon, square on: thrown back up-corridor and lifted. */
    Blown: 0,
    /** Just enough damage: the legs go and the body folds where it stood. */
    Crumple: 1,
    /** Killed off the centre line: whipped round its own axis and skidded. */
    SpinOut: 2,
    /** Killed mid-stride: carries its own momentum down onto its face. */
    Pitch: 3,
    /**
     * Not a death at all — a body that slipped past the squad line (AUDIT
     * A-13). Stays upright and keeps its own heading instead of tumbling;
     * see `breach()` below and its branch in the corpse update loop.
     */
    Breach: 4,
  } as const;

  /**
   * CONTACT SHADOWS.
   *
   * The directional key casts a real shadow, but a soft 1024-map shadow at
   * corridor distance is a detached grey smear — it says "there is a light
   * over there", not "this body is standing ON this road". What grounds a
   * body is the small, dark, tight patch directly under it, and every
   * shipped game of this kind draws one.
   *
   * One instanced quad, one radial-gradient texture, one draw call for the
   * whole corridor: the squad, the horde, the dead and the props.
   */
  const contactTex = (() => {
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d')!;
    const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    // Tight and dark in the middle, gone by the edge. A linear falloff
    // reads as a disc; this reads as contact.
    grad.addColorStop(0, 'rgba(0,0,0,0.62)');
    grad.addColorStop(0.45, 'rgba(0,0,0,0.34)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 64, 64);
    const t = new CanvasTexture(c);
    t.colorSpace = SRGBColorSpace;
    return t;
  })();
  const MAX_CONTACT = 2000;
  const contacts = new InstancedMesh(
    new PlaneGeometry(1, 1),
    new MeshBasicMaterial({
      map: contactTex, transparent: true, depthWrite: false,
      // Under the bodies but over the road, and never z-fighting with it.
      polygonOffset: true, polygonOffsetFactor: -2,
    }),
    MAX_CONTACT);
  contacts.frustumCulled = false;
  contacts.count = 0;
  contacts.renderOrder = 1;
  world.add(contacts);
  let contactCount = 0;
  /** Lay one contact patch on the deck. `r` is its radius in world units. */
  function contact(cx2: number, cz: number, r: number, strength = 1): void {
    if (contactCount >= MAX_CONTACT) return;
    tmpV.set(cx2, 0.015, cz);
    tmpE.set(-Math.PI / 2, 0, 0, 'YXZ');
    tmpQ.setFromEuler(tmpE);
    tmpS.set(r * 2, r * 2, 1);
    contacts.setMatrixAt(contactCount, tmpM.compose(tmpV, tmpQ, tmpS));
    tmpColor.setScalar(strength);
    contacts.setColorAt(contactCount, tmpColor);
    contactCount++;
  }

  // enemy-slinger.glb REPLACED (Z-Route quality push, "monsters need
  // variety" -- founder finding, live playtest): confirmed via direct
  // GLB inspection before touching anything that `enemy-slinger.glb` and
  // `enemy-wretch.glb` are built from the SAME source FBX
  // (build-enemies.ts's own CAST array, both list "Standing Torch Melee
  // Attack 05.fbx") -- mote_slinger and gale_wretch are mechanically
  // distinct enemy types that were rendering as the IDENTICAL model.
  // `enemy-prisoner.glb` is a genuinely different Mixamo character (the
  // user's own local Scary Zombie Pack, "Prisoner B Styperek"), built
  // with the same recipe already proven this session on the guardian
  // (no simplify(), no draco() -- both silently corrupted the asset;
  // see build-enemy-prisoner.mjs's own header for the full history).
  const MASS_ASSETS = ['./enemy-grunt.glb', './enemy-wretch.glb', './enemy-prisoner.glb'];
  // boss slot (i=1): boss-colossus.glb -> boss-titan-mech.glb -> boss-clown.glb
  // (founder directive, 2026-08-31: "the big bold cigar smoking boss" --
  // the same clown character already shipped as the elite tier, at
  // TRUE_BOSS_SCALE. boss-clown.glb is a straight copy of enemy-brute.glb
  // as it stood before this change (walk+attack already grafted, see
  // scripts/build-clown-brute.ts); no rebuild needed, the render loop's
  // own scale multiplier does the enlarging. enemy-brute (i=0) moves on
  // to a different character below so the elite tier isn't just a small
  // copy of the boss.
  const ACTOR_ASSETS = ['./enemy-brute.glb', './boss-clown.glb'];
  const enemiesFallbackMat = new MeshStandardMaterial({ color: 0xffffff, roughness: 0.8 });
  addRimLight(enemiesFallbackMat, { rimColor: RIM_THREAT }); // rim only — same instancing limitation
  const enemies = new InstancedMesh(new BoxGeometry(0.4, 0.8, 0.4),
    enemiesFallbackMat, MAX_ENEMIES);
  enemies.castShadow = true;
  world.add(enemies);

  /**
   * Fire is THREE additive layers, not one box. A single thin quad reads
   * as a 1992 sprite; a hot white core inside a saturated mid inside a
   * wide soft halo reads as energy, because that is how real bright
   * things fall off. All additive, all depth-write off, so overlapping
   * streams accumulate into a genuine glow instead of z-fighting.
   */
  // A tracer is a DART: pointed at the leading end, widening to a short
  // tail. Cylinders of even thickness are what made a dense volley read as
  // stacked white bars -- there was no direction in the shape and nothing
  // separating one bolt from the next. A cone gives both, and stays
  // readable when thirty of them are in the air at once.
  // WHY THE SHOOTING COULD NOT BE SEEN.
  //
  // All three bolt layers were ADDITIVE. Additive blending adds light to
  // what is already there — which does nothing at all over a pale sunlit
  // road, because those pixels are already near the top of the range. The
  // fire was mathematically present and optically absent, and the little
  // that did survive read as a wash rather than as a round. That wash is
  // what looks like bloom.
  //
  // So only the CORE stays additive: a small hot centre that blows out on
  // purpose. The mid and the halo are NORMAL-blended saturated colour, so
  // a bolt is an opaque coloured object against the road no matter how
  // bright the road is.
  //
  // Radii stay close to the originals. Doubling them was an overcorrection
  // — a round at the muzzle is only a metre from the lens, so a fat cone
  // there covers the shooter's whole torso. The blending was the bug; the
  // size never was.
  const boltCore = new InstancedMesh(
    new CylinderGeometry(0.011, 0.058, 1, 6),
    new MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1,
      blending: AdditiveBlending, depthWrite: false }),
    MAX_TRACERS);
  const boltMid = new InstancedMesh(
    new CylinderGeometry(0.024, 0.112, 1, 6),
    new MeshBasicMaterial({ color: 0xffa326, transparent: true, opacity: 0.98,
      depthWrite: false }),
    MAX_TRACERS);
  const boltHalo = new InstancedMesh(
    new CylinderGeometry(0.048, 0.198, 1, 6),
    new MeshBasicMaterial({ color: 0xff4a00, transparent: true, opacity: 0.55,
      depthWrite: false }),
    MAX_TRACERS);
  for (const m of [boltHalo, boltMid, boltCore]) { m.frustumCulled = false; world.add(m); glow(m); }

  /**
   * BEAMS. A laser is not a fast bullet, and drawing it as one is why the
   * energy classes read the same as the kinetic ones however bright their
   * tint was: at any speed a travelling dart is still a dart. A beam is a
   * CONTINUOUS line from the barrel to whatever it is burning, present for
   * as long as the trigger is down, with no travel to watch.
   *
   * Two layers -- a white core and a wide coloured halo -- because a single
   * quad reads as a painted stripe, and the halo is what makes it look hot.
   */
  const MAX_BEAMS = 96;
  const beamCore = new InstancedMesh(new CylinderGeometry(0.030, 0.030, 1, 6),
    new MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.95,
      blending: AdditiveBlending, depthWrite: false }), MAX_BEAMS);
  const beamHalo = new InstancedMesh(new CylinderGeometry(0.105, 0.105, 1, 6),
    new MeshBasicMaterial({ color: 0x9b6bff, transparent: true, opacity: 0.34,
      blending: AdditiveBlending, depthWrite: false }), MAX_BEAMS);
  for (const m of [beamHalo, beamCore]) {
    m.frustumCulled = false; m.count = 0; world.add(m); glow(m);
  }

  /**
   * MORTAR SHADOW. A lobbed shell needs a ground contact cue or it just
   * looks like it's floating -- the shadow is what sells "this is going
   * to land HERE," not the arc alone. Flat disc, laid on the deck, no
   * bloom (a glowing shadow reads as a light, not a shadow).
   */
  const MAX_MORTAR_SHADOWS = 64;
  const mortarShadow = new InstancedMesh(
    new CircleGeometry(1, 16),
    new MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.4,
      depthWrite: false }),
    MAX_MORTAR_SHADOWS);
  mortarShadow.frustumCulled = false; mortarShadow.count = 0; world.add(mortarShadow);
  /** Last known real (sim) position + travel of each live 'lob' round, by
   * projectile pool index -- carried frame to frame so a round that
   * disappears (expires at range OR hits something) can be told apart
   * from one that's still in flight, and so a MISS (expired at range,
   * no Ev.Hit fired for it) still gets an impact burst at the position
   * it actually reached. A hit already bursts via Ev.Hit in main.ts; this
   * only fires for the case that event never covers. */
  const lobLastPos = new Map<number, { x: number; z: number; travel: number }>();
  const tracers = boltCore; // kept for the older count bookkeeping

  // Muzzle flares: billboards that pop for a few frames at each barrel.
  const MUZZLES = 64;
  const muzzleTex = (() => {
    const c = document.createElement('canvas'); c.width = c.height = 128;
    const g = c.getContext('2d')!;
    const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    grd.addColorStop(0, 'rgba(255,255,245,1)');
    grd.addColorStop(0.3, 'rgba(255,196,80,0.85)');
    grd.addColorStop(1, 'rgba(255,120,20,0)');
    g.fillStyle = grd; g.fillRect(0, 0, 128, 128);
    const t = new CanvasTexture(c); t.colorSpace = SRGBColorSpace; return t;
  })();
  const muzzles: Sprite[] = [];
  const muzzleLife = new Float32Array(MUZZLES);
  /**
   * Which barrel each live flare belongs to. A flare left at the world
   * point where it was emitted drifts off the rifle as the squad moves;
   * pinning it to its barrel for its whole (very short) life keeps it on
   * the gun under any motion.
   */
  const muzzleBarrel = new Int16Array(MUZZLES);
  /**
   * Where each living soldier's muzzle actually is, refreshed as the
   * formation is laid out. Flares fired from the formation CENTRE read as
   * one cluster of light no matter how many rifles are firing; the whole
   * point of a growing squad is that you can see every barrel light up.
   */
  const barrels: { x: number; y: number; z: number }[] = [];
  let barrelCount = 0;
  const handL = new Vector3();
  const handR = new Vector3();
  const muzzleV = new Vector3();
  /** Streams asked for since the last rendered frame; see flash(). */
  let pendingStreams = 0;
  for (let i = 0; i < MUZZLES; i++) {
    const sp = new Sprite(new SpriteMaterial({
      map: muzzleTex, transparent: true, blending: AdditiveBlending, depthWrite: false,
    }));
    sp.visible = false;
    world.add(sp); glow(sp);
    muzzles.push(sp);
  }
  let muzzleCur = 0;

  // One shared light that pulses with the volley: muzzle flash should
  // LIGHT the squad, not just draw on top of it.
  const gunLight = new PointLight(0xffb64a, 0, 9, 2);
  gunLight.position.set(0, 1.2, 0);
  world.add(gunLight);
  let gunFlash = 0;
  let frameNo = 0;
  /**
   * RIM_FRIENDLY fix (2026-08-30): every `addRimLight(x, {rimColor:
   * RIM_FRIENDLY})` call site pushes its returned live Color reference
   * here, so `setTeamColor` (below) can update every friendly rim light
   * in place when the player picks a non-default character -- previously
   * this was a load-time-only constant, stuck on whichever character was
   * default at construction regardless of live selection.
   */
  const friendlyRimColors: Color[] = [];
  /** Advances the horde's baked walk cycle. */
  let strideClock = 0;
  /** Integral of dt * squadRate — the one clock every marcher obeys. */
  let strideT = 0;
  /** vfx_profile of the gun in hand, for fx the shell drives. */
  let lastProfile = '';

  const gemMat = new MeshStandardMaterial({
    color: 0x7dffd0, emissive: 0x2fd0a0, emissiveIntensity: 1.5, roughness: 0.2,
  });
  const gems = new InstancedMesh(new SphereGeometry(0.16, 6, 5), gemMat, 220);
  world.add(gems);

  // ---------------------------------------------------------------- squad
  const squadGroup = new Group();
  world.add(squadGroup);
  /**
   * SQUAD FOOTPRINT RING (quality-audit vs docs/reference, R12): both
   * competitor reference sets mark the squad's ground position with a
   * glowing ring -- an immediate "this is your army, here" anchor. Ours
   * had none; soldiers stood on bare deck. A normalized unit ring (radius
   * 1, thin), rescaled every frame to the formation's own computed
   * footprint (never a fixed size) so it grows and shrinks with squad
   * size exactly the way the formation itself does.
   */
  const footprintRing = new Mesh(
    new RingGeometry(0.88, 1.0, 48),
    new MeshBasicMaterial({
      color: RIM_FRIENDLY, transparent: true, opacity: 0.5,
      side: DoubleSide, depthWrite: false,
    }),
  );
  footprintRing.rotation.x = -Math.PI / 2;
  footprintRing.renderOrder = 2;
  world.add(footprintRing);
  glow(footprintRing);
  /**
   * SQUAD-GROWTH CAGE (Z-ROUTE BUILD PLAN, Priority 4, 2026-09-03): the
   * reference marks a new-recruit moment with a bright rectangular
   * outline flashing around the freshly-expanded formation — "look at
   * your new SHAPE," not just a point-source pickup burst. `Ev.PickedUp`
   * already has real per-pickup feedback (colors/audio/particles,
   * main.ts), but nothing highlights the formation itself. Detected
   * locally (squad size increasing frame-over-frame) rather than piped
   * from an event, so it needs no sim/event-schema change — reuses the
   * SAME footprint position/radius math `footprintRing` already computes
   * every frame, just a second, brighter, briefly-visible ring.
   */
  const cageRing = new Mesh(
    new RingGeometry(0.94, 1.0, 48),
    new MeshBasicMaterial({
      color: 0x7fe8ff, transparent: true, opacity: 0, side: DoubleSide, depthWrite: false,
    }),
  );
  cageRing.rotation.x = -Math.PI / 2;
  cageRing.renderOrder = 3;
  world.add(cageRing);
  glow(cageRing);
  let cageFlashT = 0;
  let prevSquadSize = -1;
  /**
   * The crowd behind the skinned front ranks: one InstancedMesh per frozen
   * pose, per state. Cycling poses by index stops the mass reading as one
   * rigid block, which a single frozen pose would.
   */
  const crowd: Record<string, InstancedMesh[]> = {};
  const crowdCount: Record<string, number> = {};
  const soldiers: Soldier[] = [];
  /**
   * Feet-to-ground lift for the loaded rig, in world units. Set when the
   * character loads; the formation reads it every frame. Zero until then,
   * which is correct -- there is nothing to place yet.
   */
  let rigFootOffset = 0;
  /** Crowd instance scale for the loaded rig (see SOLDIER_WORLD_H). */
  let rigFit = 1;
  let ready = false;

  const loader = new GLTFLoader();
  // NO setDecoderPath. This is deliberate and load-bearing.
  //
  // three@0.185 resolves its Draco decoder through
  // `new URL('../libs/draco/...', import.meta.url)`, so Vite emits the
  // wrapper and the wasm into /assets with content hashes — same-origin and
  // immutable. That is exactly what the service worker's cache-first rule
  // already covers.
  //
  // This line previously pointed at
  // `https://www.gstatic.com/draco/versioned/decoders/1.5.7/`, which broke
  // the offline promise: sw.js returns early for any cross-origin request,
  // so the decoder was never cached. Every Draco asset — all 20 under gen/,
  // including the monolith the whole game is about, plus the root gun-*.glb
  // fallbacks — silently fell back to procedural placeholders once the
  // player was offline. The installed icon promised a game and delivered
  // boxes. It also put a third-party CDN on every cold start.
  //
  // Leaving the default alone fixes both. Do not re-add a decoder path
  // unless it is same-origin.
  const dracoLoader = new DRACOLoader();
  loader.setDRACOLoader(dracoLoader);

  try {
    const gltf = await loader.loadAsync('./character.glb');
    const src = gltf.scene;
    src.traverse((o) => {
      const sm = o as SkinnedMesh;
      if (sm.isSkinnedMesh) { sm.castShadow = true; sm.frustumCulled = false; }
      // WASHED-OUT MATERIAL FIX (Z-Route quality push, found while
      // chasing an unrelated guardian-texture bug, then confirmed to
      // reach the squad's OWN model too by direct inspection before
      // touching this): `character.glb`'s material ships metallic=0.4 —
      // a diffuse Mixamo skin/uniform texture guessed as part-metallic by
      // the FBX->glTF converter, which reads as pale and washed out
      // under this scene's strong lighting instead of showing its real
      // texture (the same root cause already found and fixed once this
      // session for the boss, and now for the guardian). The squad is
      // the one body on screen every second of every run; if this bug
      // reaches it too, it reaches everything.
      const m = (o as Mesh).material as MeshStandardMaterial | MeshStandardMaterial[] | undefined;
      if (m) for (const mat of Array.isArray(m) ? m : [m]) {
        if ((mat as MeshStandardMaterial).isMeshStandardMaterial) (mat as MeshStandardMaterial).metalness = 0;
      }
    });
    // The chibi head-shrink and body-stretch are GONE. They were a
    // correction for an authored-cartoon rig; the character is now a
    // realistic Mixamo body (~1.87m, human proportions) and deforming it
    // would only undo what the pack already gets right.
    //
    // FIT THE RIG TO THE WORLD, generically. The pipeline may hand this
    // loader any donor rig, and two things about a donor are never
    // guaranteed: how tall it is, and where its origin sits. This Mixamo
    // body measures 1.87 units with its origin at the HIPS, so dropped in
    // unadjusted every soldier is buried to the waist in the deck -- feet
    // through a solid, which the physics law forbids outright.
    //
    // So: measure the bind pose once, scale to a known world height, and
    // lift the feet to y=0. Swapping the donor rig then costs nothing.
    const fitBox = new Box3().setFromObject(src);
    const rigH = Math.max(0.001, fitBox.max.y - fitBox.min.y);
    const RIG_FIT = SOLDIER_WORLD_H / rigH;
    rigFit = RIG_FIT;
    rigFootOffset = -fitBox.min.y * RIG_FIT;
    const clips = gltf.animations;

    /**
     * THE SQUAD HAS NO AUTHORED FLINCH — borrow one.
     *
     * character.glb ships idle/run/shoot/die and nothing for being hit, which
     * is why M5's most important feedback event had no clip to play and the
     * FSM fell back to the base state. horde.glb's `impact` is the same
     * Mixamo skeleton family, so it binds after a prefix rename and nothing
     * else (B3a). This is not a new asset — it is a clip already in the
     * project, already logged, reaching a second rig.
     */
    try {
      const donorHit = await loader.loadAsync('./horde.glb');
      const hit = borrowClip(donorHit.animations as AnimationClip[], donorHit.scene, src, 'impact', 'hit');
      if (hit) clips.push(hit);
    } catch (err) {
      console.error('squad hit clip unavailable; flinch falls back to base', err);
    }

    // M3 SHAPING + stripRootMotion, computed HERE (before the front-rank
    // soldiers are built) rather than after them.
    //
    // INTEGRATION FIX (founder verdict on bf97a5bb): `shaped` used to be
    // computed AFTER the soldiers loop below and was used ONLY by
    // `createCrowd` (the back-rank instanced crowd) — the front-rank
    // SKINNED soldiers loop built its `actions` straight from the RAW
    // `clips` array, never touched by shapeRun/shapeFire/shapeDeath/
    // shapeHit or stripRootMotion at all. `hipWorld()` samples exactly
    // those front-rank soldiers, so every prior stripRootMotion fix
    // (applied only to `shaped`, only consumed by the back ranks) was
    // invisible to the founder's own screenshots — the visible squad was
    // never shaped or stripped, in any state, across every prior round.
    // `shaped` now feeds BOTH the front ranks and the back-rank crowd
    // (unchanged), so root motion is stripped on the actual clip that
    // renders, not just on a copy something else consumes.
    const shaped: AnimationClip[] = (clips as AnimationClip[]).map((c) => {
      if (c.name === 'run') return shapeRun(stripRootMotion(c), 1);
      if (c.name === 'shoot') return shapeFire(stripRootMotion(c));
      if (c.name === 'die') return shapeDeath(stripRootMotion(c));
      if (c.name === 'hit') return shapeHit(stripRootMotion(c));
      // WALK (2026-08-30 founder asset, a strafe clip): stripRootMotion
      // only, no shapeRun -- shapeRun sets a forward pitch-lean, which is
      // right for a run and wrong for a side-step that should stay
      // upright. Root motion still needs stripping for the same reason
      // every other locomotion clip gets it: the deck does not scroll.
      if (c.name === 'walk') return stripRootMotion(c);
      return c;
    });

    // NO TINT (founder directive, 2026-08-30): the soldier ships in its
    // own original asset colour -- no desaturate-then-recolour regrade,
    // no team hex multiply. One cloned material shared by every soldier,
    // not one per body, so the (colour-neutral) rim patch below and any
    // physical-property tuning stays a single call.
    let teamMat: MeshStandardMaterial | null = null;
    src.traverse((o) => {
      const sm = o as SkinnedMesh;
      if (!sm.isSkinnedMesh) return;
      if (!teamMat) {
        teamMat = (sm.material as MeshStandardMaterial).clone();
        teamMat.roughness = 0.66;
        teamMat.metalness = 0.0;
        // The realistic kit is a DARK uniform. Multiplied by a tint under
        // this scene's soft key it came out near-black -- the squad read as
        // silhouettes. A self-lit floor lifts it into readable colour
        // without washing the texture out.
        // NEUTRAL fill, not team-coloured. A red emissive at 0.34 stacked on
        // the red tint and the squad came out as flat pink mannequins with
        // the texture washed away -- brighter, but less legible than the
        // silhouettes it was meant to fix. A dim grey lift raises the dark
        // uniform into view and leaves the diffuse doing the describing.
        // Back to a whisper. 0.95 was stacked up over three passes to
        // drag the squad out of a darkness that was a DECODE FAULT, not a
        // lighting one. With the encode fixed it bleaches the uniform.
        teamMat.emissive = new Color(0x6e757e);
        teamMat.emissiveIntensity = 0.18;
        // Covers BOTH L-17 paths in one call: squadCrowd clones `src`
        // AFTER this runs and shares materials by reference (Object3D
        // .clone() does not deep-clone materials), so the back-rank
        // instanced crowd inherits the same rim-patched teamMat for free.
        friendlyRimColors.push(addRimLight(teamMat, { rimColor: RIM_FRIENDLY }));
      }
      sm.material = teamMat;
    });

    // Front-rank soldiers: rim only (inverted-hull outline reverted,
    // 2026-08-18 — failed three times on the actor pool; the pre-outline
    // build renders correctly, so this is a removal, not another tune).
    for (let i = 0; i < MAX_SOLDIERS; i++) {
      const root = cloneSkinned(src);
      // Uniform: a real human body must not be stretched to fit.
      root.scale.setScalar(RIG_FIT);
      const mixer = new AnimationMixer(root);
      const actions: Soldier['actions'] = {};
      // Play the SHAPED clip (M3 shaping + stripRootMotion), keyed by the
      // ORIGINAL state name: `shaped[j]`'s own `.name` has a `:m3` suffix
      // (every shape function appends it), but every FSM lookup elsewhere
      // (`sol.actions[name]`, `actions['shoot']`) keys on the bare state
      // name — `shaped` and `clips` are the same length in the same
      // order (a 1:1 `.map()`), so `clips[j].name` is the stable key.
      for (let j = 0; j < clips.length; j++) {
        const name = clips[j]!.name;
        const a = mixer.clipAction(shaped[j]!);
        a.setLoop(name === 'die' ? LoopOnce : LoopRepeat, Infinity);
        if (name === 'die') a.clampWhenFinished = true;
        actions[name] = a;
      }
      // Per-soldier phase offset so the formation breathes instead of
      // moving as one rigid block.
      const shoot = actions['shoot'];
      if (shoot) { shoot.play(); shoot.time = (i * 0.137) % 1; }
      // Unused slots start HIDDEN. Left visible, every slot above the
      // starting squad size would fire a death on the very first frame.
      root.visible = false;
      squadGroup.add(root);
      soldiers.push({
        root, mixer, actions, current: 'shoot',
        // IN STEP (founder 6c.2, 2026-08-14): the formation marches as
        // ONE. The irrational per-body phase this replaced read as a
        // shuffle — "one goes first the other later". A whisper of
        // file-stagger (<=50ms) keeps it organic without breaking step.
        phase: 0,
        dyingFor: 0,
        joinFor: 0,
        lh: findBone(root, ['LeftHand', 'HandL', 'LowerArmL']),
        rh: findBone(root, ['RightHand', 'HandR', 'LowerArmR']),
        // GUN SOCKET. The Quaternius rig shipped a 'Pistol' node to parent
        // into; a Mixamo body has no prop nodes at all, only bones. So the
        // socket is CREATED on the right hand: an empty Object3D carrying
        // the grip offset, which then inherits the hand's animation exactly
        // as an authored prop node would.
        gunMount: (() => {
          const existing = root.getObjectByName('Pistol');
          if (existing) return existing;
          const hand = findBone(root, ['RightHand', 'HandR', 'mixamorigRightHand']);
          if (!hand) return null;
          const socket = new Object3D();
          socket.name = 'GunSocket';
          // Forward along the palm and a little out of it, so the weapon
          // reads as held rather than growing from the wrist.
          socket.position.set(0.06, 0.02, 0.10);
          // FIX (gun-orientation report, 2026-09-04): the old (0, PI/2, 0)
          // put the barrel diagonally across the chest -- measured via
          // window.__scene.gunWorld()'s world-space bounding box, which
          // showed side/forward and up/forward extent ratios of 0.28/0.51
          // (the barrel angled ~28 degrees sideways and pitched steeply
          // up). A sweep of the six axis-aligned quarter-turns found
          // (0, 0, PI/2) minimizes both (0.18/0.36) -- the correct roll
          // for this rig's rest-pose hand-bone axes, empirically measured
          // rather than guessed from a screenshot.
          socket.rotation.set(0, 0, Math.PI / 2);
          hand.add(socket);
          return socket;
        })(),
        mounted: '',
      });
    }
    // Freeze a few poses per state for the instanced crowd. Baking is one
    // pass over ~7k vertices per pose, once, at load.
    // ONE orientation decision for the whole crowd. Nine bakes (three
    // states x three poses) each deciding their own stand-up rotation is
    // how soldiers past the skinned ranks appeared UPSIDE DOWN at larger
    // squad sizes: an aim pose with raised arms fails the height-dominance
    // test and gets flipped. The first bake decides; every other pose and
    // state replays its exact rotation.
    // Only states the rig can actually pose. Baking an 'idle' the model
    // never had silently produced a set of DEATH poses.
    for (const state of ['shoot', 'run', 'walk']) {
      const clip = clips.find((c) => c.name === state);
      if (!clip) continue;
      const meshes: InstancedMesh[] = [];
      for (let k = 0; k < CROWD_POSES; k++) {
        const t = clip ? (clip.duration * (k + 0.5)) / CROWD_POSES : 0;
        // Forced-no-rotation for the same reason as the horde: authored
        // upright, and the heuristic misfires on toon proportions.
        const baked = bakePose(cloneSkinned(src), clip as AnimationClip | undefined, t, 0);
        if (!baked) continue;
        const inst = new InstancedMesh(baked.geo, baked.mat, MAX_DRAWN);
        inst.frustumCulled = false;
        inst.castShadow = true;
        inst.count = 0;
        squadGroup.add(inst);
        meshes.push(inst);
      }
      crowd[state] = meshes;
      crowdCount[state] = 0;
    }

    // THE SQUAD'S BACK RANKS WERE A FLIPBOOK TOO. Six frozen poses cycled
    // at nine frames a second, which is the same 1990s read as the horde
    // and lands on the bodies the player is actually watching. Same GPU
    // rig, same one draw call, and now the ranks behind the skinned front
    // hold their rifles and stride like the front does.
    //
    // A FRESH clone: the bake poses the skeleton it is handed, so handing
    // it the same scene the 24 skinned soldiers were cloned from would
    // leave them standing in whatever frame the bake stopped on.
    // M3 SHAPING, APPLIED BEFORE THE BAKE.
    //
    // This is the line that puts four sessions of clip work into the actual
    // game rather than a harness. B3c measured the cost of baking shaped
    // curves at 2.3% on hip-bob and 0.0% on the death fall, because the bake
    // samples at 30fps — the source's own authoring rate — so the shaping
    // survives into the instanced ranks intact.
    //
    // The RAW clips are kept alongside: the A/B toggle needs both, and a
    // shaping mistake must never be destructive to the source. `shaped`
    // itself is now computed above, before the front-rank soldiers loop —
    // see the note there for why it moved.
    try {
      squadCrowd = createCrowd(cloneSkinned(src), shaped, MAX_DRAWN);
      if (squadCrowd) {
        squadGroup.add(squadCrowd.mesh);
        squadClips = new Set(squadCrowd.clipNames);
      }
    } catch (err) {
      console.error('squad crowd failed; back ranks stay on baked poses', err);
    }
    ready = true;
  } catch (err) {
    console.error('character.glb failed to load; squad will be missing', err);
  }

  // --- enemy art -------------------------------------------------------
  interface Actor {
    root: Object3D; mixer: AnimationMixer; busy: boolean;
    /**
     * THE BOSS BECOMES A SPECTACLE. Per-slot material clones (see the
     * pool-creation loop for why a clone is required) and the state this
     * actor's shell is CURRENTLY tinted to -- -1 means "not a true boss,
     * default colour", so a slot that only ever plays an elite or a beast
     * never allocates a numeral or touches its own material.
     */
    bodyMats: { mat: MeshStandardMaterial; color0: Color }[];
    dmgState: number;
    hpSprite: Sprite;
    hpLabel: string;
    /**
     * ACTOR GAIT (founder finding, 2026-08-25): boss-colossus.glb and
     * enemy-brute.glb each ship exactly one clip, 'attack' -- there is no
     * locomotion of their own to fall back on, unlike the crowd tiers
     * (which the MASS path already handles, see the 'walk' borrow in the
     * MASS_ASSETS loop). `walkAction`/`attackAction` are null when the
     * clip-borrow failed (donor missing, or this asset turns out not to be
     * a Mixamo rig) -- the slot then keeps whatever `g.animations[0]`
     * plays, same degraded behaviour as before this fix, rather than
     * throwing on a missing action.
     */
    walkAction: ReturnType<AnimationMixer['clipAction']> | null;
    attackAction: ReturnType<AnimationMixer['clipAction']> | null;
    gait: 'walk' | 'attack' | '';
  }
  const actorPools: Actor[][] = [];
  /**
   * DEATH BEAT (elite/boss actors only). The instanced horde tiers get a
   * real corpse-physics tumble (`kill()` below, `defIdx<=2`); a skinned
   * actor had none at all -- its pool slot is only ever claimed while the
   * sim's own `s.ep.alive[i]` is true, so the frame after death nothing
   * claims it and the general sweep hides it (`if (!a.busy) root.visible
   * = false`), same frame the particles land. A five-soldier-tall boss
   * popping out of existence is the opposite of the impact its death is
   * supposed to read as.
   *
   * `killActor` below holds ONE actor out of the normal per-frame claim
   * cycle for DEATH_HOLD_SEC, driving a knockback-and-fall from the blow
   * direction Ev.Death already carries (ddx/ddy), then releases it back
   * to the pool. Presentation only -- the sim's own pool slot is already
   * freed by the time this runs; nothing here is read by the sim.
   */
  interface DyingActor {
    actor: Actor; t: number; x0: number; z0: number; sc: number;
    kux: number; kuz: number; fallSign: number;
    /** Fraction of the knockback+lean already applied on frame 0, sized off
     *  the killing blow's overkill -- see killActor's SNAP_FRAC note. */
    snapFrac: number;
  }
  const dyingActors: DyingActor[] = [];
  const DEATH_HOLD_SEC = 0.6;
  const DEATH_KNOCK_DIST = 1.0;

  async function loadEnemyArt(): Promise<void> {
    // NO TINT (founder directive, 2026-08-30): each mass tier ships in its
    // own original asset colour -- no per-tier hue/saturation/brightness
    // canvas regrade. Tier identity comes from using genuinely distinct
    // source assets per tier, not from a filter painted over a shared one.

    // Mass tiers: bake ONE POSE from the walk clip into a static geometry
    // and instance that.
    //
    // These used to instance the mesh in BIND pose, because the build step
    // stripped the skin entirely. Bind pose is a T-POSE -- what a rig looks
    // like before anyone has posed it -- so the horde came at the player as
    // hundreds of scarecrows with their arms straight out. It is never a
    // shipping pose, and instancing is not a reason to ship it.
    //
    // Baking costs one pass over ~7k vertices per mesh, once, at load. The
    // horde is then still a single instanced draw call with no skeletons
    // to update, and it reads as a body mid-stride.
    for (const [i, url] of MASS_ASSETS.entries()) {
      try {
        const g = await loader.loadAsync(url);
        const clip = g.animations[0] as AnimationClip | undefined;

        // Several PHASES of the movement clip, baked to static geometry and
        // cycled per instance at draw time. One frozen pose is why the
        // horde GLIDED down the corridor -- positionally advancing, dead to
        // the eye. Cycling four strides is the cheapest walk that reads.
        const phases: InstancedMesh[] = [];
        for (let k = 0; k < MASS_PHASES; k++) {
          const t = clip ? clip.duration * ((k + POSE_T) / MASS_PHASES) : 0;
          // Rotation FORCED to none: the v2 characters are authored
          // upright in glTF, and the stand-up heuristic misfires on squat
          // toon proportions -- a chubby zombie is as wide as it is tall,
          // so the "height must dominate" test knocked the whole horde
          // flat on its face. The heuristic exists for world-baked FBX
          // rigs, which no longer feed this path.
          const baked = bakePose(cloneSkinned(g.scene), clip, t, 0);
          if (!baked) break;
          // THE SLINGER'S CROWN. The slinger's authored mesh fuses a
          // stack of throwing-rings ABOVE its head into the body geometry
          // (one skinned mesh, verified with gltf-transform). Animated it
          // reads as gear; frozen into a marching pose it reads as four
          // zombies wearing orange wedding cakes. Crop every triangle that
          // sits wholly above the head line and re-normalise, so the tier
          // ships the body the eye expects.
          if (i === 2) {
            const geo = baked.geo;
            const posA = geo.getAttribute('position') as BufferAttribute;
            const CROP = 0.74; // head line, in unit-height space
            const keep: number[] = [];
            const idx = geo.index;
            const triCount = idx ? idx.count / 3 : posA.count / 3;
            for (let tr = 0; tr < triCount; tr++) {
              const a = idx ? idx.getX(tr * 3) : tr * 3;
              const b = idx ? idx.getX(tr * 3 + 1) : tr * 3 + 1;
              const c = idx ? idx.getX(tr * 3 + 2) : tr * 3 + 2;
              if (posA.getY(a) > CROP && posA.getY(b) > CROP && posA.getY(c) > CROP) continue;
              keep.push(a, b, c);
            }
            geo.setIndex(keep);
            geo.computeBoundingBox();
            const hh = geo.boundingBox!.max.y - geo.boundingBox!.min.y;
            if (hh > 1e-4) geo.scale(1 / hh, 1 / hh, 1 / hh);
          }
          // Lean the baked body a few degrees toward the camera. The
          // shamble pose pitches the torso away, and from the play camera
          // the horde read as the tops of its own heads; this presents
          // face and chest instead. Presentation-only cheat, standard for
          // hero renders.
          baked.geo.rotateX(0.20);
          // RIM ONLY, no outline (founder, 2026-08-17): these instances
          // touch and overlap at horde density — a shared inverted hull
          // reads as one blob outline around the cluster, not per-body
          // silhouettes. The reference itself shows the packed mass as a
          // dark readable blob, not outlined individuals; rim alone is
          // what separates overlapping bodies without that artifact.
          addRimLight(baked.mat, { rimColor: RIM_THREAT });
          const inst = new InstancedMesh(baked.geo, baked.mat, MAX_ENEMIES);
          // PER-INSTANCE COLOUR (structural fix, 2026-08-30): this pool
          // previously had NO way to tint an individual instance --
          // burn conversion (R10) and atmospheric depth-haze (below)
          // could only reach the separate, much smaller `hc` crowd-rig
          // pool (reacting bodies only), leaving the vast majority of
          // the horde (everything simply marching, not currently
          // reacting to a hit) untouched by either. `instanceColor`
          // defaults to white (1,1,1) for every instance until written,
          // so this is a pure addition -- nothing renders differently
          // until a later frame actually calls setColorAt.
          inst.material.vertexColors = true;
          inst.instanceColor = new InstancedBufferAttribute(new Float32Array(MAX_ENEMIES * 3).fill(1), 3);
          inst.castShadow = true;
          inst.frustumCulled = false;
          inst.count = 0;
          world.add(inst);
          phases.push(inst);
          // MEASURE THE BODY, once, after every geometry op that changes
          // its footprint. This is the number the rail law is enforced
          // against, so it has to come from the shipped geometry rather
          // than from a guess about how wide a zombie is.
          baked.geo.computeBoundingBox();
          const bb2 = baked.geo.boundingBox!;
          const half = Math.max(Math.abs(bb2.max.x), Math.abs(bb2.min.x));
          massHalfW[i] = Math.max(massHalfW[i] ?? 0, half);
          if (k === 0) {
            massBounds.push(`${url.replace('./', '').replace('.glb', '')} `
              + `w=${(bb2.max.x - bb2.min.x).toFixed(2)} `
              + `h=${(bb2.max.y - bb2.min.y).toFixed(2)} `
              + `d=${(bb2.max.z - bb2.min.z).toFixed(2)}`);
          }
        }
        if (phases.length > 0) {
          massTiers[i] = phases;
          // ONE corpse mesh PER PHASE. All four share a single faded
          // material, so the dead still recolour independently of the
          // living, but a body can fall in any of the four silhouettes it
          // might have been standing in. Previously every corpse in the
          // game was phase 0.
          const corpseMat = new MeshStandardMaterial({
            map: (phases[0]!.material as MeshStandardMaterial).map ?? null,
            color: 0xb0a89e, roughness: 1, transparent: true, opacity: 1,
          });
          // RIM ONLY, no outline — corpses pile up the same way the fodder
          // that dropped them did, same touching-instances limitation.
          addRimLight(corpseMat, { rimColor: RIM_THREAT });
          const cms: InstancedMesh[] = [];
          for (const ph of phases) {
            const cm = new InstancedMesh(ph.geometry, corpseMat, MAX_CORPSES);
            // A body on the deck casts a shadow like every other body on
            // the deck. The living horde cast; the dead did not, so a
            // corpse lay in the road with no shadow under it — the exact
            // "floating" read the physics law exists to prevent.
            cm.castShadow = true;
            cm.frustumCulled = false;
            cm.count = 0;
            world.add(cm);
            cms.push(cm);
          }
          corpseTiers[i] = cms;
        }
      } catch (err) {
        console.error(`enemy tier ${i} failed to load`, err);
      }
    }

    // THE CLIP DONOR. horde.glb is a Mixamo rig carrying the combat
    // library — walk, run, two attacks, three impacts, two deaths. Its
    // BODY is never drawn; only its animation is taken. The enemies keep
    // the Quaternius meshes that actually look right.
    //
    // Loaded here, before the ACTOR pool below, rather than after it (its
    // original position, alongside the MASS_ASSETS loop that also reads
    // it) -- ACTOR GAIT (2026-08-25) needs `donor`'s 'walk' clip for the
    // boss/elite pool too, so the donor has to exist before that pool is
    // built. `loader.loadAsync` results hit the browser's HTTP cache on
    // a repeat call, so nothing double-loads over the network.
    let donor: Awaited<ReturnType<typeof loader.loadAsync>> | null = null;
    try {
      donor = await loader.loadAsync('./horde.glb');
    } catch (err) {
      console.error('clip donor failed to load; the horde keeps its one walk', err);
    }
    // THE THIRD DEATH VARIANT. M4 wants three death clips per tier, never a
    // phase-offset copy of one source — `character.glb`'s own `die` is a
    // genuinely different mocap take, reachable because the whole Mixamo
    // family binds on a prefix rename (ASSET-LOG "M4 variant sources"). No
    // new asset: this file is already loaded for the squad above; loading it
    // again here hits the browser's HTTP cache, not the network.
    let charDonor: Awaited<ReturnType<typeof loader.loadAsync>> | null = null;
    try {
      charDonor = await loader.loadAsync('./character.glb');
    } catch (err) {
      console.error('character.glb unavailable as a death donor; fodder keeps two deaths', err);
    }

    // Actors: a small pool of real skinned characters per elite type.
    for (const [i, url] of ACTOR_ASSETS.entries()) {
      const pool: Actor[] = [];
      try {
        const g = await loader.loadAsync(url);
        // NO TINT (founder directive, 2026-08-30): brute and boss ship in
        // their own original asset colour -- no per-pool hue/saturation/
        // brightness canvas regrade.
        // RIM LIGHT REMOVED FROM THE ACTOR POOL (2026-08-18): a real scene
        // DirectionalLight's grazing-angle mix flattened this pool's
        // (then-regraded) material to a near-uniform pale value. Instead
        // ELITE gets a VIEW-DEPENDENT fresnel accent injected straight into
        // its own shader via onBeforeCompile, below -- driven by camera
        // angle, not by any scene Light, so it cannot repeat that failure.
        g.scene.traverse((o) => {
          const m = o as SkinnedMesh;
          if (m.isSkinnedMesh) { m.castShadow = true; m.frustumCulled = false; }
          // WASHED-OUT MATERIAL FIX, same root cause as the squad and the
          // guardian: confirmed by direct inspection (not assumed) that
          // `enemy-brute.glb` ships metallic=0.4 -- almost certainly the
          // REAL explanation behind this file's own older comment just
          // above ("a real scene DirectionalLight... flattened this
          // pool's material to a near-uniform pale value"), which was
          // worked around by removing the rim light rather than by
          // finding this. `boss-colossus.glb` was already independently
          // fixed to metallic=0 at some point (confirmed by inspection);
          // this brings enemy-brute/boss-clown to the same state.
          const mats = m.material ? (Array.isArray(m.material) ? m.material : [m.material]) : [];
          for (const mat of mats) {
            if ((mat as MeshStandardMaterial).isMeshStandardMaterial) (mat as MeshStandardMaterial).metalness = 0;
          }
        });
        if (i === 0) {
          // ELITE (brute): emissive accent + rim, per the material-tier
          // spec, as a fresnel term added on top of the existing unlit
          // regraded material -- see the comment above for why this,
          // not a scene rim light.
          g.scene.traverse((o) => {
            const m = o as SkinnedMesh;
            if (!m.isSkinnedMesh) return;
            const mats = Array.isArray(m.material) ? m.material : [m.material];
            for (const mm of mats) {
              const basic = mm as MeshBasicMaterial;
              basic.onBeforeCompile = (shader) => {
                shader.uniforms['rimColor'] = { value: new Color(0xff5a3a) };
                shader.vertexShader = shader.vertexShader
                  .replace('#include <common>', '#include <common>\nvarying vec3 vFresnelNormal;\nvarying vec3 vFresnelView;')
                  .replace('#include <begin_vertex>', '#include <begin_vertex>\nvFresnelNormal = normalize(normalMatrix * normal);\nvFresnelView = normalize(-(modelViewMatrix * vec4(position, 1.0)).xyz);');
                shader.fragmentShader = shader.fragmentShader
                  .replace('#include <common>', '#include <common>\nuniform vec3 rimColor;\nvarying vec3 vFresnelNormal;\nvarying vec3 vFresnelView;')
                  .replace('#include <dithering_fragment>', `
                    float rimFresnel = pow(1.0 - max(dot(vFresnelNormal, vFresnelView), 0.0), 2.6);
                    gl_FragColor.rgb += rimColor * rimFresnel * 0.9;
                    #include <dithering_fragment>`);
              };
              basic.needsUpdate = true;
            }
          });
        }
        // FRAMING FREEZE REVERT (founder, 2026-08-25): the boss tier's
        // MeshPhysicalMaterial (transmission/thickness/ior + emissive
        // core) is out of scope for a framing-only session -- back to the
        // pre-97d0e6d unlit MeshBasicMaterial clone, same as the elite
        // tier and the same as this pool used before the render-quality
        // pass touched it. Elites: regraded material + fresnel rim accent
        // above (unaffected -- not part of this revert), no outline
        // (outline reverted 2026-08-18 -- failed three times on this pool).
        //
        // ACTOR GAIT. enemy-brute.glb ships exactly one clip, 'attack' --
        // no locomotion of its own, unlike the crowd tiers. Its own
        // bind pose does not match a Mixamo biped's, so borrowing a walk
        // by bone-name rename (BOSS FIX, founder finding, 2026-08-26) drove
        // its limbs through rotations authored for a human and read as
        // fallen/sideways for the whole approach -- never fixable by a
        // rename, a real retarget was out of scope. It keeps ONLY its own
        // native 'attack' (the fallback path below, `walkAction ??
        // attackAction`, already exists for exactly this case).
        //
        // Founder-supplied Mixamo characters (2026-08-30/31: the zombie
        // packs, the clown, pumpkinHulk) are proper Mixamo-family rigs
        // with their OWN walk clip grafted on at build time -- no borrow
        // needed, so it is read directly here rather than hard-coded
        // null. Same stripRootMotion
        // as attack, below: the deck does not scroll (Iron Rule pattern,
        // see clip-shape.ts's own doc comment) -- an un-stripped walk
        // clip creeps the body forward across the cycle and resets it at
        // the loop point, which is exactly a visible "back and forth"
        // glitch on top of whatever position the sim itself is driving.
        const walkClipRaw = g.animations.find((c) => c.name === 'walk') ?? null;
        const walkClip = walkClipRaw ? stripRootMotion(walkClipRaw) : null;
        const attackClipRaw = g.animations.find((c) => c.name === 'attack') ?? g.animations[0];
        const attackClip = attackClipRaw ? stripRootMotion(attackClipRaw) : undefined;
        for (let k = 0; k < 6; k++) {
          const rootObj = cloneSkinned(g.scene);
          rootObj.visible = false;
          const mixer = new AnimationMixer(rootObj);
          const walkAction = walkClip ? mixer.clipAction(walkClip) : null;
          const attackAction = attackClip ? mixer.clipAction(attackClip) : null;
          walkAction?.setLoop(LoopRepeat, Infinity);
          attackAction?.setLoop(LoopRepeat, Infinity);
          // Approach is the default state; attack only inside contact
          // range (the inReach check in the render loop switches this).
          // Falls back to attackAction alone (the pre-fix behaviour) if
          // the walk borrow failed -- never silently plays nothing.
          const startAction = walkAction ?? attackAction;
          if (startAction) { startAction.play(); startAction.time = k * 0.31; }
          world.add(rootObj);
          // THE BOSS BECOMES A SPECTACLE: per-slot material clones. This
          // pool's 6 slots share one asset (boss-colossus.glb) across BOTH
          // the true chapter boss and a mid-run beast -- SkeletonUtils.clone
          // does not deep-clone materials, so tinting a shared material for
          // one slot's damage state would bleed onto every other slot
          // playing this asset. Cloned once here, never mutated after.
          //
          // CORRECTED (2026-09-02): this was believed to be MeshBasicMaterial
          // (unlit) -- checked at runtime, it is actually MeshStandardMaterial
          // (see the shine-fix comment below). Damage-state tinting below
          // still only touches `.color`, matching every existing call site;
          // `.emissive` is real on this material now but unused here.
          const bodyMats: Actor['bodyMats'] = [];
          rootObj.traverse((o) => {
            const m = o as SkinnedMesh;
            if (m.isSkinnedMesh) {
              const mats = Array.isArray(m.material) ? m.material : [m.material];
              // SHINE FIX (founder finding, 2026-09-02): the comment below
              // claimed this asset's material was "MeshBasicMaterial
              // (unlit, deliberately)" -- checked at runtime, not assumed,
              // and it is actually `MeshStandardMaterial`, carrying
              // whatever metalness/roughness the source GLB happened to
              // bake in. That mismatch is exactly why the boss read as
              // shiny/plasticky rather than matte like a real Mixamo
              // character: nothing here was ever clamping it. Force matte
              // on the CLONE (never the shared source) -- zero metalness,
              // near-max roughness -- so it looks right regardless of
              // what the export pipeline set.
              const cloned = mats.map((mm) => {
                const sm = (mm as MeshStandardMaterial).clone();
                sm.metalness = 0;
                sm.roughness = 0.92;
                return sm;
              });
              m.material = cloned.length === 1 ? cloned[0]! : cloned;
              for (const cm of cloned) bodyMats.push({ mat: cm, color0: cm.color.clone() });
            }
          });
          pool.push({
            root: rootObj, mixer, busy: false, bodyMats, dmgState: -1,
            hpSprite: makeBossHpSprite(), hpLabel: '',
            walkAction, attackAction, gait: walkAction ? 'walk' : 'attack',
          });
        }
      } catch (err) {
        console.error(`actor ${i} failed to load`, err);
      }
      actorPools[i] = pool;
    }

    // A GPU crowd per tier. The baked flipbook above stays loaded as the
    // fallback: if the injected shader fails on some device, a horde of
    // frozen poses is a bad frame and an invisible horde is no game.


    for (const [i, url] of MASS_ASSETS.entries()) {
      try {
        const g = await loader.loadAsync(url);
        // (The Quaternius slinger needed cropFusedTopProp here — its mesh
        // fused a ring-stack above the head. The Mixamo cast the founder
        // reinstated on 2026-08-14 carries no such prop; cropping a clean
        // body would take its head off. The helper stays for any future
        // donor that ships fused headgear.)
        // RETARGET, because not one bone name is shared between these two
        // skeletons and a name graft binds zero channels. Rest-pose-
        // relative, in world space, so a clip authored on a T-posed Mixamo
        // rig means the same thing on a squat goblin. Proven on one enemy
        // in crowd-test.html before it came anywhere near the game.
        let clips = g.animations as AnimationClip[];
        if (donor) {
          // WHICH MAP? The Quaternius table binds Quaternius targets; a
          // MIXAMO target (the founder's reinstated cast) shares the
          // donor's own skeleton, so the map is identity over the rig's
          // actual bones. Feeding the Quaternius table to a Mixamo rig
          // bound 3 fuzzy pairs and every grafted clip shredded the body
          // — caught by crowd-test.html, mapped=3.
          let isMixamoRig = false;
          g.scene.traverse((o) => {
            if ((o as Bone).isBone && /^mixamorig/i.test(o.name)) isMixamoRig = true;
          });
          // A MIXAMO rig ships its OWN authored clip and grafts nothing
          // through `retarget()`: the isolation harness proved these rigs
          // clean through the GPU crowd on their own clips and SHREDDED on
          // every RETARGETED clip (identity-mapped included). So `retarget()`
          // stays skipped for this family — Quaternius-class rigs keep the
          // mapped retarget verified for them.
          //
          // That is not the same guard as "no combat clips reach this rig".
          // `retarget()` and the prefix-rename BORROW (B3a, `borrowClip`,
          // proven on the squad's own `hit` clip above) are different
          // mechanisms: same-family Mixamo clips bind NATIVELY once their
          // track names carry the target's own prefix, no retarget skeleton
          // math involved to shred anything. `clip-bind` verified 53/53
          // tracks bind natively across all four rigs. This is what was
          // actually missing — the retarget skip left `clips` as ONLY the
          // rig's own file (walk, sometimes one attack), so `impact*` /
          // `death*` never reached `hordeClips` and every hit/death check
          // against them was silently always false.
          if (isMixamoRig && donor) {
            const donorClips = donor.animations as AnimationClip[];
            const own = new Set(clips.map((c) => c.name));
            // STALE ASSUMPTION FIXED (founder finding, 2026-09-04:
            // "movements are so static, stuck in the mud"). The comment
            // this replaced assumed enemy-wretch/enemy-slinger's OWN clip
            // WAS `attack`, so plain `attack` was deliberately left off
            // this borrow list. Re-inspected the shipped files directly
            // (Anchor on disk, not on docs): `enemy-wretch.glb` and
            // `enemy-grunt.glb` on disk today carry ONLY `walk`/`run` --
            // no `attack` at all, on either. `hcClips.has('attack')` in
            // the render loop's gait selector below is therefore false
            // for every mass tier that isn't a beast/boss, so the horde
            // NEVER plays an attack clip on contact -- it just keeps
            // walking through the squad. `horde.glb` (this same donor)
            // carries a plain `attack` clip, same Mixamo bone family,
            // proven to native-bind here already for attack2/impact*/
            // death* -- added to the list so it binds too.
            for (const name of ['attack', 'attack2', 'impact', 'impact2', 'impact3', 'death', 'death2', 'walk']) {
              if (own.has(name)) continue;
              const c = borrowClip(donorClips, donor.scene, g.scene, name);
              if (c) clips.push(c);
            }
            if (charDonor && !own.has('die')) {
              const dieClip = borrowClip(
                charDonor.animations as AnimationClip[], charDonor.scene, g.scene, 'die');
              if (dieClip) clips.push(dieClip);
            }
          }
          const r = isMixamoRig
            ? { mapped: -1, clips: [] as AnimationClip[],
              missingTarget: [] as string[], missingSource: [] as string[] }
            : retarget(g.scene, donor.scene, donor.animations as AnimationClip[]);
          if (r.mapped > 0) {
            // LOCOMOTION IS NOT TAKEN FROM THE RETARGET.
            //
            // The combat clips (attacks, impacts, deaths) transfer cleanly
            // and were checked one body per clip against the source rig
            // playing the same clip at the same instant. Walk and run do
            // NOT: they fold the body forward at the waist, and they are
            // the two clips carrying a full pelvis rotation cycle. Four
            // candidate causes were ruled out by measurement — a
            // world-space hip translation track, a root-frame mismatch
            // (the rigs' hips agree to 6 degrees, so there is none), the
            // hip translation entirely, and a scaled-matrix quaternion
            // extraction. The cause is still open.
            //
            // So the rig keeps its OWN authored walk, which was made for
            // this body and looks right, and `run` is simply absent — the
            // gait selector falls back to walk on its own. Better a horde
            // that walks correctly than one that crawls.
            const LOCOMOTION = new Set(['walk', 'run']);
            const own = new Set(clips.map((c) => c.name));
            clips = [...clips,
              ...r.clips.filter((c) => !own.has(c.name) && !LOCOMOTION.has(c.name))];
          } else {
            console.error(`retarget bound 0 bones for ${url}`,
              r.missingTarget, r.missingSource);
          }
        }
        // Same fix as the guardian (`guardian-parasite.glb`), same root
        // cause: this Mixamo-family FBX->glTF conversion guesses a
        // non-zero metallic value for what is really a flat diffuse
        // zombie-skin texture, which reads as a washed-out, near-uniform
        // blob under this scene's strong lighting instead of the real
        // texture. `enemy-prisoner.glb` ships from the same converter,
        // same source pack -- forced off here too rather than waiting to
        // rediscover the same bug live a second time.
        g.scene.traverse((o) => {
          if (!(o as Mesh).isMesh) return;
          const m = (o as Mesh).material as MeshStandardMaterial | MeshStandardMaterial[];
          for (const mat of Array.isArray(m) ? m : [m]) {
            if ((mat as MeshStandardMaterial).isMeshStandardMaterial) {
              (mat as MeshStandardMaterial).metalness = 0;
            }
          }
        });
        const rig = createCrowd(g.scene, clips, CROWD_CAP);
        hordes[i] = rig;
        hordeClips[i] = new Set(rig?.clipNames ?? []);
        if (rig) {
          world.add(rig.mesh);
          // Measured from the POSED skeleton during the bake, not the
          // bind-pose box — a T-pose measurement put half-width at half
          // the body's height and shoved every body off the kerb.
          crowdHalfW = Math.max(crowdHalfW, rig.halfW);
          crowdFit[i] = SOLDIER_WORLD_H / rig.bodyH;
        }
      } catch (err) {
        console.error(`crowd tier ${i} failed; that tier stays on baked poses`, err);
      }
    }
  }
  await loadEnemyArt();

  // ------------------------------------------------------------------ gate
  /**
   * The armour this gate STARTED with, so the panel can show how far through
   * it the player is. The live pool only carries what is left.
   */
  function gateArmor(s: RunState, i: number): number {
    const group = s.config.gateGroups[s.gGroup[i]];
    const lane = group?.lanes.find((l) => l.laneIndex === s.gLane[i]);
    return lane?.armor ?? 0;
  }

  // UI LAW (founder, 2026-08-20): SATURATED RED/GREEN, heavy white
  // numerals, thick black outline -- reference docs/reference/lastz. Was
  // blue-vs-red (this function's own prior comment: "read at a glance...
  // blue vs red is friend vs threat"); superseded, not layered on top of.
  // Shared by gates AND monolith rewards so costs and rewards read as one
  // visual language, per the same instruction.
  // ISLE-12 LEGIBILITY PASS (2026-08-22): the panel this paints onto is now
  // a full lane-width band segment, not a small 3.2-unit floating sign, and
  // its aspect ratio varies with lane width/count -- `aspect` (default 2,
  // the old fixed 256x128 ratio, kept for callers like the monolith counter
  // that still want a compact sign) sizes the canvas to MATCH the eventual
  // display shape so the numeral is centred, undistorted and generously
  // padded rather than stretched to fill a wide band. Base resolution
  // doubled (128 -> 192 tall) and the numeral's own share raised (62px/224
  // max-width -> 132px/336 max-width) because the old size went soft and
  // read as a small logo once stretched across the bigger geometry.
  function gateTexture(label: string, good: boolean, aspect = 2): CanvasTexture {
    // RES 2.5x (quality-audit, SDF-alternative pass): the zoom-punch camera
    // hits up to 2.0x on Monolith/TitanFreed -- this canvas is baked once
    // per unique label and reused, so the resolution bump costs nothing
    // per-frame, only a bigger one-time texture. 192 was tuned for the
    // default camera distance and pixelated under that punch; 2.5x gives
    // real margin over it. Every pixel constant below scales with it so
    // the panel's proportions are unchanged, only crisper.
    const RES = 2.5;
    const H = 192 * RES;
    const W = Math.max(H, Math.round(H * aspect));
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const g = c.getContext('2d')!;
    const grd = g.createLinearGradient(0, 0, 0, H);
    if (good) { grd.addColorStop(0, 'rgba(64,220,110,0.97)'); grd.addColorStop(1, 'rgba(16,150,58,0.97)'); }
    else { grd.addColorStop(0, 'rgba(255,80,70,0.97)'); grd.addColorStop(1, 'rgba(192,20,20,0.97)'); }
    g.fillStyle = grd;
    g.fillRect(0, 0, W, H);
    g.strokeStyle = '#ffffff'; g.lineWidth = 13 * RES;
    g.strokeRect(7 * RES, 7 * RES, W - 14 * RES, H - 14 * RES);
    g.textAlign = 'center'; g.textBaseline = 'middle';
    // The numeral itself stays compact (capped at 336px) even on a wide
    // band -- the colour panel fills the rest of the segment's width, the
    // digits don't stretch to match it.
    const maxW = Math.min(W - 48 * RES, 336 * RES);
    let px = 132 * RES;
    g.font = `900 ${px}px ui-sans-serif, system-ui, sans-serif`;
    const w = g.measureText(label).width;
    if (w > maxW) {
      px = Math.max(30 * RES, Math.floor(px * (maxW / w)));
      g.font = `900 ${px}px ui-sans-serif, system-ui, sans-serif`;
    }
    // Heavy black outline, THEN white fill -- same order hpTexture already
    // uses for the boss numeral, matched here for one shared type treatment.
    g.lineJoin = 'round';
    g.strokeStyle = 'rgba(8,10,14,0.95)'; g.lineWidth = Math.max(9 * RES, px * 0.16);
    const cx = W / 2, cy = H / 2 + 6 * RES;
    g.strokeText(label, cx, cy);
    g.fillStyle = '#ffffff';
    g.fillText(label, cx, cy);
    const t = new CanvasTexture(c);
    t.colorSpace = SRGBColorSpace;
    return t;
  }
  /**
   * Remaining HP over each beast and the chapter boss, in big outlined
   * numerals. Without it a 106,000hp boss and a 2,000hp beast look the
   * same and the player has no way to know whether they are winning the
   * fight -- which is the entire tension of the finale.
   */
  const hpTags: { sp: Sprite; text: string }[] = [];
  for (let i = 0; i < 6; i++) {
    const sp = new Sprite(new SpriteMaterial({ transparent: true, depthWrite: false, depthTest: false }));
    sp.visible = false;
    sp.renderOrder = 30;
    world.add(sp);
    hpTags.push({ sp, text: '' });
  }
  // UI LAW (founder, 2026-08-20): same type treatment as the gate signs --
  // saturated panel, heavy white numerals, thick black outline. Was
  // transparent-background outline-only; the panel is new, the outline was
  // already this shape and is kept as-is.
  function hpTexture(text: string): CanvasTexture {
    // RES 2.5x, same reasoning/margin as gateTexture's own comment.
    const RES = 2.5;
    const c = document.createElement('canvas');
    c.width = 384 * RES; c.height = 128 * RES;
    const g = c.getContext('2d')!;
    const grd = g.createLinearGradient(0, 0, 0, 128 * RES);
    grd.addColorStop(0, 'rgba(255,80,70,0.97)'); grd.addColorStop(1, 'rgba(192,20,20,0.97)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 384 * RES, 128 * RES);
    g.strokeStyle = '#ffffff'; g.lineWidth = 9 * RES;
    g.strokeRect(5 * RES, 5 * RES, 374 * RES, 118 * RES);
    g.textAlign = 'center'; g.textBaseline = 'middle';
    let px = 92 * RES;
    g.font = `900 ${px}px ui-sans-serif, system-ui, sans-serif`;
    if (g.measureText(text).width > 340 * RES) {
      px = Math.floor(px * (340 * RES / g.measureText(text).width));
      g.font = `900 ${px}px ui-sans-serif, system-ui, sans-serif`;
    }
    g.lineJoin = 'round';
    g.strokeStyle = 'rgba(8,10,14,0.95)'; g.lineWidth = 14 * RES;
    g.strokeText(text, 192 * RES, 68 * RES);
    g.fillStyle = '#ffffff';
    g.fillText(text, 192, 68);
    const t = new CanvasTexture(c);
    t.colorSpace = SRGBColorSpace;
    return t;
  }


  /**
   * How many bodies are bearing down in each lane, floated over the
   * horde -- the reference ad's 205 / 284 / 692 read. Density alone does
   * not tell the player whether the left lane is twice the right one, and
   * that comparison IS the decision the corridor is built around.
   */
  const laneTags: { sp: Sprite; text: string }[] = [];
  for (let i = 0; i < 4; i++) {
    const sp = new Sprite(new SpriteMaterial({ transparent: true, depthWrite: false, depthTest: false }));
    sp.visible = false; sp.renderOrder = 28;
    world.add(sp);
    laneTags.push({ sp, text: '' });
  }

  /**
   * THE AETHER SLED. R8 (Iron Rule 7e / ASSET_PIPELINE.md "two entries the
   * new standard invalidates"): the GATE_VEHICLE reward used to park an
   * armoured tank -- tracks, turret, mantlet, exhaust stacks -- in front of
   * the formation. "R8 bans modern vehicles outright and requires deletion
   * rather than restyling"; that GLB source (`tank.glb`) never actually
   * shipped (the loader 404'd and the procedural fallback below was what
   * every build actually rendered), so this is a like-for-like replacement,
   * not a regression: same footprint, same parked-in-front behaviour, same
   * two render call sites (parked prop + prize hologram), but built from
   * the permitted Sky Archipelago vocabulary -- quarried stone hull, aether
   * conduits, no wheels, no barrel, no exhaust.
   */
  // MOVED UP FROM ITS ORIGINAL DECLARATION SITE (founder audit, 2026-09-04):
  // this session's own single-source-of-truth fix made buildAetherSled()
  // read `genCache` (below, was declared much later in the file). That was
  // harmless for buildArmourPrize() (only ever called lazily, from inside
  // the render loop) but buildAetherSled(1) is called EAGERLY, synchronously,
  // a few lines below this function's own definition, at scene-setup time --
  // before `const genCache` further down in the file had run at all. A
  // `const` referenced before its own declaration executes is a hard
  // ReferenceError in a temporal dead zone, not a silent undefined: this
  // broke `main()` completely on every load, which is what actually made
  // the game "very slow" to the point of having "no purpose to play" --
  // it was crashing before window.__bonwarsy was ever assigned, not running
  // slowly. Confirmed via CDP pageerror + this exact file's own sourcemap
  // against the live production bundle, not guessed.
  const genCache = new Map<string, Group>();
  function buildAetherSled(scale = 1): Group {
    // SINGLE SOURCE OF TRUTH, same reasoning and same `.catch`-safe
    // `loadGen` guarantee as `buildArmourPrize()` above -- checked once
    // here so every one of this function's 4 call sites (monolith
    // preview, gate-wall station preview, AND the actually-deployed
    // field vehicle) gets the real asset the moment `reward-vehicle.glb`
    // exists, with no further code changes anywhere.
    const gen = genCache.get('reward-vehicle');
    if (gen) { const c = gen.clone(true) as Group; c.scale.multiplyScalar(scale); return c; }
    const g = new Group();
    const stone = new MeshStandardMaterial({ color: 0xb0a79c, roughness: 0.9, metalness: 0.02 });
    const dark = new MeshStandardMaterial({ color: 0x5b5248, roughness: 0.85, metalness: 0.05 });
    const aether = new MeshStandardMaterial({
      color: 0x8fe9ff, roughness: 0.3, metalness: 0.1,
      emissive: new Color(0x35c9e8), emissiveIntensity: 1.1,
    });
    const add = (m: Mesh, x: number, y: number, z: number, rx = 0, rz = 0): Mesh => {
      m.position.set(x, y, z); m.rotation.x = rx; m.rotation.z = rz;
      m.castShadow = true; g.add(m); return m;
    };

    // Quarried-stone hull, tapered prow.
    add(new Mesh(new BoxGeometry(1.30, 0.42, 2.05), stone), 0, 0.62, 0);
    add(new Mesh(new BoxGeometry(1.10, 0.44, 0.55), stone), 0, 0.68, 1.05, -0.55);
    // Skirt, in place of fenders -- no wheels, the sled reads as hovering.
    for (const sx of [-1, 1]) add(new Mesh(new BoxGeometry(0.14, 0.10, 1.9), dark), sx * 0.70, 0.42, 0);
    // Aether conduits running the hull's length, in place of tracks.
    for (const sx of [-1, 1]) {
      add(new Mesh(new CylinderGeometry(0.07, 0.07, 1.9, 8), aether), sx * 0.78, 0.40, 0, 0, Math.PI / 2);
    }
    // Twin aether-core turrets, in place of the gun turret.
    for (const sx of [-0.22, 0.22]) {
      add(new Mesh(new CylinderGeometry(0.20, 0.24, 0.30, 10), stone), sx, 1.02, -0.15);
      add(new Mesh(new SphereGeometry(0.15, 10, 8), aether), sx, 1.24, -0.15);
    }
    // Rear conduit vents, in place of exhaust stacks.
    for (const sx of [-0.34, 0.34]) {
      add(new Mesh(new CylinderGeometry(0.05, 0.07, 0.22, 8), aether), sx, 0.92, -0.98);
    }

    g.scale.setScalar(scale);
    return g;
  }

  const vehicle = new Group();
  vehicle.visible = false;
  world.add(vehicle);
  vehicle.add(buildAetherSled(1));
  /** Loaded gun models by vfx profile, normalised to ~0.9 length. */
  const gunModels: Record<string, Group> = {};
  // PARALLEL, NOT SEQUENTIAL (founder finding, live playtest, 2026-09-05:
  // "the gun? just 1?" -- for the first ~25-30s of a run, only a handful
  // of these had actually loaded. This loop used to be a single `for`
  // with an `await` inside it: one profile's network fetch had to fully
  // resolve before the NEXT one's request was even sent, so 12 profiles
  // took roughly 12x one profile's load time in sequence. A player who
  // reached weapon tier 4+ within that window (very plausible -- this
  // isle's own median clear time is ~53s) got an empty-handed soldier
  // for whatever tier the sequential queue hadn't reached yet, which is
  // exactly what a "held opposite and just one gun" report from a fast
  // early upgrade looks like. Kicking off every profile's fetch at once
  // means total load time is bounded by the SLOWEST single asset, not
  // the sum of all twelve.
  void Promise.all(Object.keys(GUN_LOOKS).map(async (profile) => {
      try {
        // GENERATED FIRST. This loader still pointed at the old blaster
        // pack, whose twelve entries are near-identical toy shapes -- so
        // every class read the same in the hand no matter how distinct its
        // bolt and its impact were, and the two melee classes had no file
        // at all and fell back to boxes. The generated set is twelve
        // deliberately different silhouettes; use it, and keep the pack
        // only as the fallback.
        const g = await loader.loadAsync(`./gen/gun-${profile}.glb`)
          .catch(() => loader.loadAsync(`./gun-${profile}.glb`));
        const m = g.scene;
        m.traverse((o) => { (o as Mesh).castShadow = true; });
        const bb = new Box3().setFromObject(m);
        const size = new Vector3(); bb.getSize(size);
        // MELEE RE-THEME (VFX-overhaul audit, 2026-08-20): normalizing
        // only against x/z assumed every model's long axis lies roughly
        // horizontal, true for ten rifle-shaped guns and false for a
        // sword/hammer authored with its long axis along Y -- that model
        // never got shrunk to fit, which is the concrete mechanism behind
        // "soldiers holding a giant sword pointed at the sky." Scoped to
        // the two melee profiles only; the other ten keep their measured
        // x/z-only normalization unchanged.
        const meleeShape = GUN_LOOKS[profile]?.shape;
        const isMelee = meleeShape === 'sweep' || meleeShape === 'slam';
        const sc = isMelee
          ? 0.95 / Math.max(0.0001, size.x, size.y, size.z)
          : 0.95 / Math.max(0.0001, size.z, size.x);
        const wrap = new Group();
        m.position.set(
          -(bb.min.x + size.x / 2), -(bb.min.y + size.y / 2), -(bb.min.z + size.z / 2));
        wrap.add(m);
        wrap.scale.setScalar(sc);

        gunModels[profile] = wrap;
      } catch (err) {
        // WAS SILENT (founder audit, 2026-09-05): blade and hammer never
        // populated gunModels at all, in EVERY session, and this bare
        // catch is why nobody could see why -- logged now so the next
        // failure (this one or a new one) names itself instead of
        // reading as "no gun" with no further clue.
        console.error(`gun model load failed for profile "${profile}"; buildGun fallback stays`, err);
      }
    }));

  /**
   * A real WALL, not a poster.
   *
   * The reference blocks the lane with a stack of tyres carrying its
   * armour value, and hangs the prize above it on a lit podium. A flat
   * label sprite could never sell "shoot this down" -- nothing was
   * physically in the way. Same idea in geometry: stacked rings, a
   * glowing disc, and the reward turning slowly above it.
   */
  /**
   * The rock wall: a stack of stone blocks filling the lane, which loses
   * blocks as it is shot through.
   *
   * This is the dilemma made physical. A percentage on a floating sign asks
   * the player to do arithmetic; a wall of rock with a hole punched through
   * the middle of it tells them how far they have got at a glance, from the
   * corner of an eye already busy with the other lane. The reward turning
   * above it says what is on the far side.
   *
   * Blocks fall away top-first and outside-in, so the last stone standing is
   * bottom-centre and the wall opens like a breach rather than dissolving
   * evenly. They sit ON the deck and span the lane -- a floating rock or one
   * hanging past the rail would break the same law the bodies obey.
   */
  const ROCK_COLS = 5, ROCK_ROWS = 3;
  function buildWall(): Group {
    const g = new Group();
    // ISLE-12 LEGIBILITY PASS (2026-08-22): 0.62 -> 0.92 lane-width share.
    // A wall that occupies most but not all of its lane reads as a prop
    // beside the road, not an obstacle blocking it -- the gap at each edge
    // is what let the wall recede into "small plate at the horizon" instead
    // of a foreground object the player is visibly driving at.
    const laneW = (arenaW / MAX_LANES) * SX * 0.92;
    const bw = laneW / ROCK_COLS, bh = 0.56;
    for (let r = 0; r < ROCK_ROWS; r++) {
      for (let c = 0; c < ROCK_COLS; c++) {
        // Warm quarried stone, LIGHTER than the deck it stands on. The
        // first pass used a cool dark grey and the wall read as a black bar
        // across the lane from the play camera -- the same failure as the
        // tyre stack this replaced. A self-lit floor keeps the blocks off
        // black when the lane falls into shadow, which is when a dark prop
        // stops looking like an object and starts looking like a hole.
        const shade = 0xb0a79c + ((r * 7 + c * 13) % 5) * 0x060606;
        const m = new Mesh(
          new BoxGeometry(bw * 0.86, bh * 0.86, 0.52 + ((c + r) % 3) * 0.05),
          new MeshStandardMaterial({
            color: shade, roughness: 0.92, metalness: 0.02,
            emissive: new Color(0x4a423a), emissiveIntensity: 0.55,
          }));
        m.position.set((c - (ROCK_COLS - 1) / 2) * bw, bh * 0.5 + r * bh,
          ((r + c) % 2) * 0.04);
        // A slight per-block tilt: quarried stone stacked by hand, not a
        // machined grid.
        m.rotation.z = (((r * 5 + c * 3) % 7) - 3) * 0.012;
        m.castShadow = true;
        m.name = 'rock';
        g.add(m);
      }
    }
    const disc = new Mesh(new CylinderGeometry(0.72, 0.72, 0.06, 20),
      new MeshBasicMaterial({ color: 0xffd76b, transparent: true, opacity: 0.75,
        blending: AdditiveBlending, depthWrite: false }));
    disc.position.y = 1.32;
    g.add(disc);
    const under = new Mesh(new CylinderGeometry(0.5, 0.58, 0.05, 18),
      new MeshBasicMaterial({ color: 0xffe9a8, transparent: true, opacity: 0.22,
        blending: AdditiveBlending, depthWrite: false }));
    under.position.y = 0.04;
    g.add(under);
    return g;
  }

  /**
   * Procedural weapon silhouettes, one per class.
   *
   * The crate used to advertise its prize as the text "MK REPEATER" --
   * a name means nothing at a glance and looks like debug output. These
   * are chunky readable shapes with emissive accents: a shotgun's fat
   * twin bores or a mortar's stubby mouth is recognisable from across the
   * corridor, which is what makes wanting one immediate.
   */
  const GUN_ASSET: Record<string, string> = {
    rifle: 'gun-rifle', mk: 'gun-mk', shotgun: 'gun-shotgun', cannon: 'gun-cannon',
    tesla: 'gun-tesla', laser: 'gun-laser', beam: 'gun-beam', flak: 'gun-flak',
    missile: 'gun-missile', mortar: 'gun-mortar', blade: 'gun-blade', hammer: 'gun-hammer',
  };
  function buildGun(profile: string): Group {
    const gen = genCache.get(GUN_ASSET[profile] ?? '');
    if (gen) return gen.clone(true) as Group;
    const g = new Group();
    const look = GUN_LOOKS[profile] ?? DEFAULT_LOOK;
    const steel = new MeshStandardMaterial({ color: 0x39404d, roughness: 0.45, metalness: 0.65 });
    const accent = new MeshStandardMaterial({
      color: look.mid, roughness: 0.3, metalness: 0.4,
      emissive: new Color(look.halo), emissiveIntensity: 0.7,
    });
    const add = (m: Mesh, x: number, y: number, z: number, rx = 0): void => {
      m.position.set(x, y, z); m.rotation.x = rx; m.castShadow = true; g.add(m);
    };
    add(new Mesh(new BoxGeometry(0.20, 0.17, 0.62), steel), 0, 0, 0);
    add(new Mesh(new BoxGeometry(0.13, 0.26, 0.15), steel), 0, -0.19, -0.16);
    switch (profile) {
      case 'shotgun':
      case 'flak':
        for (const sx of [-0.075, 0.075]) {
          add(new Mesh(new CylinderGeometry(0.062, 0.07, 0.72, 10), accent),
            sx, 0.03, 0.62, Math.PI / 2);
        }
        break;
      case 'cannon':
      case 'mortar':
        add(new Mesh(new CylinderGeometry(0.15, 0.11, 0.95, 12), accent), 0, 0.05, 0.72, Math.PI / 2);
        add(new Mesh(new CylinderGeometry(0.19, 0.19, 0.16, 12), steel), 0, 0.05, 1.16, Math.PI / 2);
        break;
      case 'laser':
      case 'beam':
      case 'tesla':
        add(new Mesh(new CylinderGeometry(0.045, 0.045, 1.15, 10), accent), 0, 0.04, 0.76, Math.PI / 2);
        for (const sz of [0.45, 0.72, 0.99]) {
          add(new Mesh(new BoxGeometry(0.30, 0.035, 0.06), steel), 0, 0.04, sz);
        }
        break;
      case 'blade':
        // A long single-edged blade on a short grip.
        add(new Mesh(new BoxGeometry(0.07, 0.30, 1.55), accent), 0, 0.16, 0.85);
        add(new Mesh(new BoxGeometry(0.34, 0.08, 0.10), steel), 0, 0.03, 0.16);
        break;
      case 'hammer':
        // A stubby haft under a heavy head.
        add(new Mesh(new CylinderGeometry(0.06, 0.06, 1.0, 8), steel), 0, 0.05, 0.5, Math.PI / 2);
        add(new Mesh(new BoxGeometry(0.44, 0.42, 0.52), accent), 0, 0.05, 1.05);
        break;
      case 'missile':
        for (const sx of [-0.13, 0.13]) {
          for (const sy of [-0.06, 0.10]) {
            add(new Mesh(new CylinderGeometry(0.055, 0.055, 0.6, 8), accent),
              sx, sy, 0.5, Math.PI / 2);
          }
        }
        break;
      default:
        add(new Mesh(new CylinderGeometry(0.055, 0.06, 0.9, 10), accent), 0, 0.03, 0.68, Math.PI / 2);
        add(new Mesh(new BoxGeometry(0.10, 0.09, 0.26), steel), 0, 0.13, 0.10);
        add(new Mesh(new BoxGeometry(0.16, 0.20, 0.24), steel), 0, -0.05, -0.44);
        break;
    }
    g.scale.setScalar(0.92);
    return g;
  }

  /**
   * One wall rig per concurrent crate: the tyre stack, plus a slot the
   * prize model is swapped into. Pooled, because a crate is on screen for
   * a few seconds and rebuilding geometry per gate would churn.
   */
  const walls: {
    root: Group; prize: Group | null; kind: string;
    rocks: Mesh[]; standing: number;
  }[] = [];
  // ISLE-12 PRE-PLACEMENT (2026-08-22): 4 -> 6, so all 3 of chapter 12's
  // groups can carry a physical wall/station simultaneously (up to 2 of
  // chapter 12's 3 groups are barrier-type) once they're all visible from
  // run start, with headroom to spare.
  for (let i = 0; i < 6; i++) {
    const root = buildWall();
    root.visible = false;
    world.add(root);
    // Destruction order: top row first, and outside-in within each row, so
    // the breach opens through the middle.
    const rocks = (root.children.filter((o) => o.name === 'rock') as Mesh[])
      .slice()
      .sort((a, b) => (b.position.y - a.position.y)
        || (Math.abs(b.position.x) - Math.abs(a.position.x)));
    walls.push({ root, prize: null, kind: '', rocks, standing: rocks.length });
  }

  /**
   * THE CHARGE STATION — the reference genre's lane-winning machine.
   *
   * A crate that is "a sign with a number" asks nothing of the imagination.
   * The reference ads stage the same mechanic as an event: a metal podium
   * holds a HOLOGRAM of the prize — you can see the gun you would be
   * winning, ghost-blue and turning, the whole time you fight for it — and
   * a capacitor plug at its base is the thing you actually shoot. As charge
   * flows in, the capacitor heats from cold blue to hot magenta, and the
   * moment it is full the hologram MATERIALISES into the real thing.
   *
   * Rocks stay for barriers (a wall you break is honest work); the station
   * is for prizes, because a prize should be visible while it is still
   * out of reach — that is what makes the other lane worth abandoning.
   */
  function holoify(src: Object3D): Group {
    // cloneSkinned (SkeletonUtils.clone), not a plain .clone(true): the
    // latter silently breaks skinning on a real bone-rigged GLB (this
    // file's own established finding, `borrowClip`/guardian creation
    // comments) -- every caller here used to be procedural/static
    // geometry where that never mattered, until the gate-wall "ally"
    // prize hologram started passing it a real rigged clone (founder
    // finding, 2026-09-04: "hollogramic geometric shape... like a
    // glitch"). SkeletonUtils.clone degrades to an ordinary deep clone
    // for objects with no skeleton, so this is a no-op for every other
    // existing call site.
    const g = cloneSkinned(src) as Group;
    g.traverse((o) => {
      const m = o as Mesh;
      if ((m as Mesh).isMesh) {
        // One material PER MESH: materialisation solidifies the projection
        // part by part, so each part owns its opacity and wireframe state.
        // NORMAL blending, not additive. Additive over this game's bright
        // daytime sky saturates to white -- the "hologram" rendered as a
        // featureless pale slab hanging over the town. Normal blending with
        // a cyan tint keeps the SHAPE readable, which is the only thing the
        // ghost exists to communicate.
        m.material = new MeshBasicMaterial({
          color: 0x3fc8ff, transparent: true, opacity: 0.5, depthWrite: false,
        });
        m.castShadow = false;
      }
    });
    return g;
  }

  /**
   * The ally prize: a hulking silhouette with claws. Procedural, so the
   * hologram can advertise a MONSTER without cloning a 60-bone actor
   * four times per frame.
   */
  function buildAllyPrize(): Group {
    const g = new Group();
    const hide = new MeshStandardMaterial({ color: 0x7a5cc8, roughness: 0.7 });
    const dark = new MeshStandardMaterial({ color: 0x4a3585, roughness: 0.8 });
    friendlyRimColors.push(addRimLight(hide, { rimColor: RIM_FRIENDLY }));
    friendlyRimColors.push(addRimLight(dark, { rimColor: RIM_FRIENDLY }));
    const torso = new Mesh(new BoxGeometry(0.9, 1.0, 0.62), hide);
    torso.position.y = 0.9; g.add(torso);
    const head = new Mesh(new BoxGeometry(0.5, 0.42, 0.5), dark);
    head.position.set(0, 1.55, 0.1); g.add(head);
    for (const sx of [-0.62, 0.62]) {
      const arm = new Mesh(new BoxGeometry(0.3, 0.95, 0.3), hide);
      arm.position.set(sx, 0.85, 0.08); arm.rotation.z = sx > 0 ? -0.2 : 0.2; g.add(arm);
      const claw = new Mesh(new BoxGeometry(0.36, 0.3, 0.36), dark);
      claw.position.set(sx * 1.16, 0.32, 0.12); g.add(claw);
    }
    for (const sx of [-0.28, 0.28]) {
      const leg = new Mesh(new BoxGeometry(0.32, 0.5, 0.34), dark);
      leg.position.set(sx, 0.25, 0); g.add(leg);
    }
    g.scale.setScalar(1.05);
    return g;
  }

  /** The armour prize: a shield shell with a glowing rim. */
  function buildArmourPrize(): Group {
    // SINGLE SOURCE OF TRUTH (founder audit, 2026-09-04): "you keep finding
    // bugs and different codes and these changes don't land" -- root cause
    // was the SAME reward-kind having its visual decided independently at
    // every call site (monolith preview, gate-wall station, field-ally
    // rig each picked their own placeholder). The 'ally' kind is fixed
    // now (a real asset, checked once here). Armour and vehicle are NOT
    // fixed yet (still plain sphere/torus and box/cylinder primitives --
    // no Meshy asset exists for them yet, blocked on an invalid API key)
    // -- but every CALL SITE of this function already goes through it, so
    // checking `genCache` here, once, means the day `reward-shield.glb`
    // exists, every one of this function's callers gets the real asset
    // with zero further changes, the same guarantee the 'ally' kind now
    // has. `loadGen('reward-shield', ...)` below is safe to call before
    // the asset exists: its own `.catch` leaves `genCache` unset and this
    // function keeps returning the procedural placeholder, unchanged.
    const gen = genCache.get('reward-shield');
    if (gen) return gen.clone(true) as Group;
    const g = new Group();
    const shell = new Mesh(new SphereGeometry(0.62, 18, 12),
      new MeshStandardMaterial({
        color: 0x9fd8ff, roughness: 0.25, metalness: 0.2, transparent: true,
        opacity: 0.55, emissive: new Color(0x2f7fd8), emissiveIntensity: 0.8,
      }));
    shell.position.y = 0.75; g.add(shell);
    const rim = new Mesh(new TorusGeometry(0.62, 0.06, 10, 32),
      new MeshStandardMaterial({
        color: 0xcfeaff, emissive: new Color(0x5ad7ff), emissiveIntensity: 1.8, roughness: 0.3,
      }));
    rim.position.y = 0.75; g.add(rim);
    return g;
  }
  interface Station {
    root: Group; capMat: MeshStandardMaterial; ring: Mesh;
    holoSlot: Group; realSlot: Group; holo: Group | null; real: Group | null;
    /**
     * The solid copy that MATERIALISES inside the hologram: its meshes are
     * revealed one by one as charge rises, so the prize is visibly taking
     * shape mid-fight. This is the station's hook -- your investment has a
     * body, and you watch it grow.
     */
    solid: Group | null; solidMeshes: Mesh[]; holoMeshes: Mesh[];
    kind: string; wasCharged: boolean;
  }
  const stations: Station[] = [];
  loadGen('station', 2.8);
  // PREPARED, NOT YET GENERATED (founder audit, 2026-09-04): these two
  // asset ids are already defined in scripts/gen-assets-ai.ts, blocked
  // on an invalid MESHY_API_KEY. Calling loadGen for a file that doesn't
  // exist yet is safe -- its own `.catch` leaves genCache unset and
  // buildArmourPrize()/buildAetherSled() keep returning their current
  // procedural placeholder. The day the key works and the generator
  // runs, both reward kinds upgrade everywhere they're used with no
  // further code change.
  loadGen('reward-shield', 1.5);
  loadGen('reward-vehicle', 2.0);
  // ISLE-12 PRE-PLACEMENT (2026-08-22): 4 -> 6, matching `walls`' own
  // raise -- see its comment.
  for (let i = 0; i < 6; i++) {
    const g = new Group();
    // Low metalness ON PURPOSE: there is no environment map in this scene,
    // and a metalness-0.8 surface with nothing to reflect renders BLACK --
    // the podium came out as a dark drum. Bright dielectric plus a faint
    // self-glow is what actually reads as polished metal here.
    const metal = new MeshStandardMaterial({
      color: 0xdfe6ee, metalness: 0.25, roughness: 0.42,
      emissive: new Color(0x9fb4cc), emissiveIntensity: 0.22,
    });
    const dark = new MeshStandardMaterial({ color: 0x8a97a6, metalness: 0.3, roughness: 0.55 });
    const base = new Mesh(new CylinderGeometry(1.30, 1.42, 0.85, 24), metal);
    base.position.y = 0.425; base.castShadow = true; g.add(base);
    // From the raised play camera the TOP is most of what the player sees
    // of this machine; a dark grille there read as a black hole in the
    // road. A bright self-lit platform disc is the fix.
    const grille = new Mesh(new CylinderGeometry(1.02, 1.02, 0.06, 24), dark);
    grille.position.y = 0.88; g.add(grille);
    const platform = new Mesh(new CylinderGeometry(0.94, 0.94, 0.05, 24),
      new MeshStandardMaterial({
        color: 0xe8f2fc, roughness: 0.4, metalness: 0.1,
        emissive: new Color(0x9fd0f0), emissiveIntensity: 0.5,
      }));
    platform.position.y = 0.92; g.add(platform);
    // The lit rim: idles cyan, floods hot as the capacitor charges.
    const ringMat = new MeshStandardMaterial({
      color: 0x9fe8ff, emissive: new Color(0x2fb9e8), emissiveIntensity: 1.6, roughness: 0.35,
    });
    const ring = new Mesh(new TorusGeometry(1.18, 0.07, 10, 40), ringMat);
    ring.rotation.x = Math.PI / 2; ring.position.y = 0.86; g.add(ring); glow(ring);
    // The capacitor plug — THE TARGET. Lies toward the squad so it is the
    // nearest, most shootable part of the machine.
    const capMat = new MeshStandardMaterial({
      color: 0x3a62d8, emissive: new Color(0x2242c8), emissiveIntensity: 1.2,
      metalness: 0.4, roughness: 0.35,
    });
    const cap = new Mesh(new CylinderGeometry(0.52, 0.52, 1.35, 18), capMat);
    cap.rotation.x = Math.PI / 2;
    cap.position.set(0, 0.52, -1.85);
    cap.castShadow = true; g.add(cap);
    const capTip = new Mesh(new CylinderGeometry(0.22, 0.22, 0.26, 12), metal);
    capTip.rotation.x = Math.PI / 2; capTip.position.set(0, 0.52, -2.62); g.add(capTip);
    // Holo light cone under the projection.
    const coneMat = new MeshBasicMaterial({
      color: 0x66d8ff, transparent: true, opacity: 0.14,
      blending: AdditiveBlending, depthWrite: false,
    });
    const cone = new Mesh(new CylinderGeometry(2.4, 0.6, 3.6, 20, 1, true), coneMat);
    cone.position.y = 2.7; g.add(cone);
    const holoSlot = new Group(); holoSlot.position.y = 3.0; g.add(holoSlot);
    const realSlot = new Group(); realSlot.position.y = 2.35; g.add(realSlot);
    g.visible = false;
    world.add(g);
    stations.push({
      root: g, capMat, ring, holoSlot, realSlot,
      holo: null, real: null, solid: null, solidMeshes: [], holoMeshes: [],
      kind: '', wasCharged: false,
    });
  }

  // --- MONOLITHS ----------------------------------------------------------
  /**
   * The rock that stands beside you.
   *
   * Built as a cluster of boulders rather than one box so the cracks have
   * somewhere to open, with a glowing fissure that widens as the counter
   * falls and the titan's hologram standing over it. The counter itself is
   * a world-space label -- five figures, unmissable, exactly the read the
   * reference gives its rock.
   */
  interface MonolithRig {
    root: Group; rocks: Mesh[]; fissure: Mesh; holo: Group | null;
    holoMeshes: Mesh[]; titan: number;
    /** The generated rock once it loads; the boxes are only a placeholder. */
    model: Group | null;
    /**
     * THE COUNTER, as its own persistent sprite rather than a pooled
     * damage label. The pooled path redrew it every 4 frames with a
     * 46-tick life, so the single most-read element of the reference
     * frame FADED AND ROSE like hit text -- and at 35 units out, a
     * 54px label is a speck. This one never fades, never rises, is
     * never occluded by the crowd, and rescales with camera distance
     * so it stays readable from the moment the rock is raised.
     */
    counter: Sprite; counterVal: number;
    /** Block-theatre state (docs/animation-bible.md). */
    lastHp: number; lastAlive: boolean; lastOp: number;
    lastWX: number; lastWZ: number;
    /** L7: seconds into the raise-from-deck entrance. */
    raiseT: number;
    /** THE PRIZE, SHOWN (founder): the hologram of what breaking pays. */
    preview: Group | null; previewOp: number; previewMeshes: Mesh[];
    /**
     * AETHER WARD CRYSTALS (founder finding, 2026-08-27): "the rocks have
     * to go... creative and pretty reward holders, not cracked open
     * black rocks." Real fix is the generation prompt (gen-assets-ai.ts,
     * re-dressed to a crystalline aether ward) -- but regenerating the
     * actual mesh needs a MESHY_API_KEY nobody has set in THIS session.
     * These are additive, procedural, and render immediately without
     * waiting on that: angular crystal shards jutting from the existing
     * rock, brightening with `broken` same as every other dressing
     * element already does. Purely decorative -- never touches monoH,
     * the fissure, or anything the fit/measurement pipeline depends on,
     * so it carries none of the risk a full model swap would.
     */
    crystals: Mesh[];
    /**
     * ONE-BONE SPRING (ART_BIBLE.md M6, spec'd, never built until now):
     * the titan hologram had a slow idle drift but no REACTION to being
     * hit -- every other reactive element in the game (soldiers, the
     * horde, the boss) flinches; the one object the player stares at for
     * most of a level didn't. A single scalar spring-damper (impulse on
     * chip, decays back to 0) driving a scale punch, not a bone or a
     * cloth sim -- exactly the cheap technique the spec calls for.
     */
    holoSpringVel: number; holoSpringOffset: number;
    /**
     * GUARDIAN (Z-ROUTE BUILD PLAN, Priority 1, 2026-09-03): when the
     * rigged, animated titan source is available (`riggedSrc`, the same
     * asset the field ally titan already walks around in), the titan
     * reveal renders as an OPAQUE, ATTACKING guardian standing on the
     * rock instead of the translucent solidifying ghost `holo` still
     * carries — a living creature actively defending the loot, matching
     * the reference's own guardian-mini-boss beat. `holo`/`holoMeshes`
     * are untouched and still used for the ghost fallback (no rigged
     * source loaded yet, or a kind with no rig at all) so nothing
     * regresses if the GLB hasn't finished loading. Never both at once.
     */
    guardian: Group | null; guardianMixer: AnimationMixer | null;
    /**
     * Constant offset between the guardian's own origin and its feet,
     * established once at creation (see the creation site's own comment
     * for why this can't just be re-derived from `monoH` each frame) —
     * the per-frame position update adds this to the frame's own target
     * foot height instead of re-measuring a bounding box every tick.
     */
    guardianFootOffset: number;
    /** The measure-and-normalise fit scale from creation — the per-frame
     * spring-punch multiplies THIS, rather than overwriting it outright
     * the way a bare `setScalar(1 + spring)` would. */
    guardianBaseScale: number;
  }
  const monoRigs: MonolithRig[] = [];
  /**
   * The generated rock, loaded once and cloned per rig.
   *
   * It replaces a five-box stack. A box cluster cannot be lit into looking
   * like stone no matter what its material says, because the silhouette is
   * the tell -- flat faces and right angles read as a crate at any distance.
   * The asset carries albedo, normal and metallic-roughness maps, so it now
   * has surface as well as shape.
   */
  /**
   * Generated assets, loaded once and cloned per use.
   *
   * Every entry replaces a function that built the object out of boxes and
   * cylinders. A procedural primitive cannot be lit into looking like
   * stone or steel because the SILHOUETTE is the tell -- flat faces and
   * right angles read as a crate at any distance, whatever the material
   * says. These carry albedo, normal and metallic-roughness maps, so they
   * have surface as well as shape.
   *
   * Each is normalised on arrival: fitted by its LONGEST axis (a generator
   * returns whatever proportions it likes, and fitting by height turned a
   * wide rock into a pebble) and grounded so it stands on the deck.
   */
  function loadGen(name: string, targetSpan: number, rim = false): void {
    void loader.loadAsync(`./gen/${name}.glb`).then((g) => {
      const src = g.scene;
      const bb = new Box3().setFromObject(src);
      const span = Math.max(
        bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z);
      const fit = targetSpan / Math.max(0.001, span);
      src.scale.setScalar(fit);
      // Ground AND centre (see the monolith loader): an off-centre source
      // mesh otherwise renders displaced from every rig that clones it.
      src.position.set(
        -(bb.min.x + (bb.max.x - bb.min.x) / 2) * fit,
        -bb.min.y * fit,
        -(bb.min.z + (bb.max.z - bb.min.z) / 2) * fit);
      src.traverse((o) => { if ((o as Mesh).isMesh) (o as Mesh).castShadow = true; });
      // TITANS/ALLIES: rim only (inverted-hull outline reverted,
      // 2026-08-18 — failed three times on the actor pool; the
      // pre-outline build renders correctly). Patched on the CACHED
      // source, so every clone (reward hologram preview AND the walking
      // freed ally, both drawn from this same genCache entry) inherits
      // it for free.
      if (rim) {
        src.traverse((o) => {
          const mesh = o as Mesh;
          if (!mesh.isMesh) return;
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const mm of mats) friendlyRimColors.push(addRimLight(mm as Material, { rimColor: RIM_FRIENDLY }));
        });
      }
      // WRAPPED, like the tank and gun loaders. The fit lives on the inner
      // node so a caller's scale.setScalar on the clone COMPOSES with the
      // normalisation instead of deleting it. Unwrapped, the block theatre's
      // rig.model.scale.setScalar(bulk) reset the monolith to raw generator
      // scale — which is why the forge rock rendered invisible while its
      // fissure, counter and hologram (siblings, never rescaled) all drew.
      const wrap = new Group();
      wrap.add(src);
      genCache.set(name, wrap);
    }).catch(() => { /* the procedural placeholder stays; the game still runs */ });
  }
  // Titans are the hero pieces and are sized to tower over the squad —
  // 5.4 span (founder 2026-08-14: rewarded monsters must be HUGE; with
  // the squad at 0.8x, a freed titan now stands several soldiers tall).
  for (const t of ['titan-saurian', 'titan-mech', 'titan-bulwark', 'titan-artillery']) {
    loadGen(t, 5.4, true);
  }
  // Weapons are held in a hand and also hang over a crate as the prize, so
  // they are fitted to a rifle's real length rather than a hero's height.
  for (const w of ['rifle', 'mk', 'shotgun', 'cannon', 'tesla', 'laser',
    'beam', 'flak', 'missile', 'mortar', 'blade', 'hammer']) {
    loadGen(`gun-${w}`, 0.95);
  }
  let monoModel: Group | null = null;
  /**
   * Founder rejection, item 1 (post-Part-A): "no hologram visible on the
   * rock." Measured, not assumed, via `previewDiag()` (window.__scene) —
   * TWO independent bugs, neither a D3 regression (D3 never touched this
   * code):
   *
   * 1. VERTICAL FRAMING. At the pre-fix offsets (monoH+0.95 preview,
   *    monoH+1.4 holo) both projected screen01.y < 0 (above the top edge)
   *    at every position tested. The vertical frustum budget above the
   *    rock's own crown has shrunk to nearly nothing since the D1 rounds
   *    raised camera height/pitch repeatedly — nobody re-checked whether a
   *    hologram floating ABOVE an already-near-the-top-edge crown still
   *    landed in frame. Lowering it enough to clear the top edge also put
   *    it BELOW the crown — depth-occluded by the rock's own opaque
   *    boulders (D4's exact "wrong side of the rock" shape, confirmed via
   *    a still: on-screen by the numbers, invisible in the frame) — fixed
   *    by also pulling it to the rock's NEAR face (negative Z, D4's own
   *    sign convention) so it composites in front instead of needing to
   *    clear above the crown.
   * 2. SCALE. `buildPreview()`'s op-1 (bodies) branch applied a local
   *    `m.scale.setScalar(2.2)` to the REAL crowd geometry, which then
   *    compounded with the enclosing group's own `monoH * 0.38` scale into
   *    ~9.4x — a hologram 9+ world units tall, taller than the rock hiding
   *    it, its pivot at the feet meaning nearly the entire body rendered
   *    above the frustum regardless of the Y fix. The x2.2 was only ever
   *    exercised against the real geometry (skipped for the sphere
   *    fallback), so it was never checked together with the scale that
   *    also applies to it. Removed — the group scale alone is "scale with
   *    the rock."
   *
   * Camera constants are frozen/out of scope this round, so both fixes
   * live here. ONE named constant per axis per group — every consumer
   * (initial placement AND the per-frame bob) reads the same value, so
   * they cannot drift apart the way the L-14/L-18 class of bug already has
   * twice in this file.
   *
   * FRAMING PASS STEP 2c (founder directive): those two Y offsets were
   * iteration-fit against `previewDiag()` at ONE camera setting — exactly
   * the fragility flagged in `tasks/defects.md`'s protective note, and it
   * cashed in the moment step 2a moved the camera again (bbox clipped
   * above frame). Replaced with `monolithDressingYOffset()` below: a
   * CLOSED-FORM re-derivation of the camera's own baseline projection
   * (same lookAt + perspective math the live camera uses, reading
   * CAM_HEIGHT/CAM_BACK/CAM_LOOKAT_FORWARD/CAM_FOV directly), evaluated
   * live per frame at each rig's own depth. If those constants move again,
   * this recomputes with them automatically — nothing to re-iterate.
   */
  const TOP_MARGIN_SCREEN01_Y = 0.05;
  const _bslCam = new Vector3(), _bslLook = new Vector3(), _bslPoint = new Vector3(),
    _bslZAxis = new Vector3(), _bslXAxis = new Vector3(), _bslYAxis = new Vector3(), _bslD = new Vector3();
  /** Screen01.y for a world point (0, worldY, depthRelToPlayer), using the
   * camera's BASELINE formula (position/lookAt with every per-frame jitter
   * term — camLead/breath/camKick — zeroed). Not the live camera: a small,
   * stable re-derivation of it, so dressing offsets don't jitter frame to
   * frame the way reading the live camera object here would. */
  function baselineScreenY(worldY: number, depthRelToPlayer: number): number {
    _bslCam.set(0, CAM_HEIGHT, -CAM_BACK);
    _bslLook.set(0, 0.2, CAM_LOOKAT_FORWARD);
    _bslPoint.set(0, worldY, depthRelToPlayer);
    _bslZAxis.copy(_bslCam).sub(_bslLook).normalize();
    _bslXAxis.set(0, 1, 0).cross(_bslZAxis).normalize();
    _bslYAxis.copy(_bslZAxis).cross(_bslXAxis);
    _bslD.copy(_bslPoint).sub(_bslCam);
    const halfFovY = (CAM_FOV * Math.PI / 180) / 2;
    const vz = _bslD.dot(_bslZAxis);
    const ndcY = _bslD.dot(_bslYAxis) / (-vz) / Math.tan(halfFovY);
    return (1 - ndcY) / 2;
  }
  /** The world-Y offset (added to `monoH`) that lifts a floating dressing
   * object just clear of the rock's own crown, toward a fixed top-of-frame
   * safety margin. If the crown itself is already at or above the margin
   * (no room left above it — a real, disclosed limit, not chased further
   * this round), clamps to the crown (offset 0) rather than pushing past
   * it and re-triggering D4's occlusion shape.
   *
   * FRACTION RETUNED 2026-08-18 (was `available / 2`, i.e. centred midway
   * between crown and margin): correct when `monoH` was ~10.9, but after
   * the height fix (`monoH` ~4.1-4.2) that same midpoint target pushed the
   * preview's own TOP -- not just its pivot -- back off the top of frame
   * (bboxScreenY.min as low as -0.08 at dz=10). The preview's pivot is its
   * OWN base, so lifting it further than a small clearance above the crown
   * moves its far larger visible extent further up too. `0.05` (measured,
   * not guessed) is the largest fraction of the OLD margin band that still
   * lands the preview's own top edge at or above the 0.03 floor at every
   * tested distance -- close to the crown reads as "sitting on the rock,"
   * which is also what was asked for. */
  function monolithDressingYOffset(monoHVal: number, depthRelToPlayer: number): number {
    const crownScreenY = baselineScreenY(monoHVal, depthRelToPlayer);
    const available = crownScreenY - TOP_MARGIN_SCREEN01_Y;
    if (available <= 0) return 0;
    const slope = baselineScreenY(monoHVal + 1, depthRelToPlayer) - crownScreenY;
    if (slope >= 0) return 0; // degenerate (shouldn't happen for a downward-pitched camera)
    const targetScreenY = crownScreenY - available * 0.05;
    return (targetScreenY - crownScreenY) / slope;
  }
  // A Y low enough to land on screen also puts the hologram BELOW the
  // rock's own crown — i.e. depth-occluded by the opaque boulders behind
  // it (confirmed via a still: on-screen by the numbers, invisible in the
  // frame, D4's exact "wrong side of the rock" shape). Pulled to the NEAR
  // face (negative Z, same sign convention D4 already established) so it
  // composites IN FRONT of the rock instead of needing to clear above it.
  // Z (unlike Y) is a rock-geometry constraint, not a camera one — it does
  // NOT need to be camera-derived, so it stays a plain constant.
  const MONOLITH_PREVIEW_Z_OFFSET = -3.2;
  const MONOLITH_HOLO_Z_OFFSET = -2.8;
  /**
   * The rock's real standing height once the model is measured. Everything
   * dressed onto the block — fissure, counter, prize hologram — anchors to
   * THIS, not to a constant tuned for the old box stack: the generated rock
   * is wide and low, and dressing hung at box-stack heights floated in the
   * air two rock-heights above it.
   */
  let monoH = 5.0;
  void loader.loadAsync('./gen/monolith.glb').then((g) => {
    const src = g.scene;
    // NORMALISE BY MEASURED SIZE. A generated mesh arrives at whatever
    // scale and origin the generator chose; the corridor needs a rock of a
    // known height standing on the deck, so it is fitted rather than
    // trusted.
    const bb = new Box3().setFromObject(src);
    // Fit by the LARGEST dimension, not by height. A generator returns
    // whatever proportions it likes, and this rock came back wide and low:
    // normalised by height it scaled to a pebble at the kerb instead of a
    // mass blocking the lane. Sizing the longest axis guarantees the thing
    // reads as big whichever way it was authored.
    const rawW = bb.max.x - bb.min.x, rawH = bb.max.y - bb.min.y,
      rawD = bb.max.z - bb.min.z;
    const span = Math.max(rawW, rawH, rawD);
    // FOUNDER SPEC (2026-08-14): the rock FILLS its lane. Anisotropic on
    // purpose: width pinned to the 6.75-unit lane (6.6 leaves the seam
    // visible), depth capped so it reads as a block across the road.
    // Everything dressed on it (fissure, counter, hologram) anchors to
    // the measured monoH, so the spec is one place, not five.
    //
    // HEIGHT MULTIPLIER, RETUNED 2026-08-18 (measured defect, not a
    // tuning pass): this was `2.5`, producing monoH ~10.9 -- at chapter
    // 1's own spawn depth (dz=22) the crown was off the top of frame
    // entirely (screen-y <= 0), height ~0.32-0.47 of frame against the
    // reference band's 0.057-0.130, and monolithDressingYOffset() could
    // never lift the reward hologram into view because there was no
    // frame margin above the crown to lift it INTO. `0.94` is a scale-down
    // by the exact ratio a closed-form solve of the same camera math this
    // file already uses (`baselineScreenY`) required to land monoH ~4.12 --
    // top edge ~0.166 (>= the 0.05 minimum), height ~0.15 of frame at
    // dz=22 (inside the deliberately-above-reference 0.10-0.20 band: a
    // rock should still read heavier than the reference's low case).
    // Verified live post-change; see the commit this comment ships with
    // for the actual measured numbers, which is the number that closes
    // this, not the derivation above.
    const MONOLITH_HEIGHT_MULT = 0.94;
    const fitX = 6.6 / Math.max(0.001, rawW);
    const fitZ = Math.min(fitX, 5.2 / Math.max(0.001, rawD));
    const fitY = (MONOLITH_HEIGHT_MULT * 6.4 / Math.max(0.001, span));
    src.scale.set(fitX, fitY, fitZ);
    // Ground on Y and CENTRE on x/z. Grounding alone left the mesh wherever
    // the generator put it relative to its own origin — the visible rock
    // hung a full radius off the rig, so the fissure clung to its edge and
    // the contact shadow lay on bare road beside it.
    src.position.set(
      -(bb.min.x + rawW / 2) * fitX,
      -bb.min.y * fitY,
      -(bb.min.z + rawD / 2) * fitZ);
    src.traverse((o) => { if ((o as Mesh).isMesh) (o as Mesh).castShadow = true; });
    // MONOLITH — cyan objective role (ART_BIBLE §Palette, reserved hue).
    // Rim only (inverted-hull outline reverted, 2026-08-18 — failed three
    // times on the actor pool; the pre-outline build renders correctly).
    src.traverse((o) => {
      const mesh = o as Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      // FOUNDER FINDING (2026-08-28, second pass): "3 random colors...
      // looks cheap and too shiny... I want something lighter, like
      // transparent crystal." Tinting the Meshy export's own opaque
      // metallic-roughness surface (the 2026-08-28 first pass: metalness
      // 0, a flat color lift) fixed the metallic mirror-sheen but not the
      // underlying problem -- an OPAQUE painted stone can never read as
      // "crystal," because a crystal's whole visual identity is light
      // passing THROUGH it, and the export's own baked texture paints
      // several unrelated hues (warm sandstone base, cyan facets) that
      // fight each other no matter how the surface is lit. Replaced
      // outright with a real transmissive glass material -- one pale hue,
      // lit by the scene's existing environment probe (RoomEnvironment,
      // already active for IBL) instead of a painted texture. The source
      // map is dropped on purpose: keeping it would keep the color clash
      // this is meant to fix.
      const swapped = mats.map(() => new MeshPhysicalMaterial({
        color: 0xeaf7ff,
        metalness: 0,
        roughness: 0.32,
        transmission: 0.72,
        ior: 1.4,
        thickness: 1.1,
        attenuationColor: new Color(0xbdeeff),
        attenuationDistance: 2.2,
        clearcoat: 0.25,
        clearcoatRoughness: 0.12,
      }));
      mesh.material = Array.isArray(mesh.material) ? swapped : swapped[0]!;
      for (const mm of swapped) addRimLight(mm as Material, { rimColor: RIM_OBJECTIVE });
    });
    monoH = rawH * fitY;
    const monoD = rawD * fitZ;
    // Wrapped for the same reason as loadGen: the theatre scales the model
    // for its chipped-bulk read, and that scale must compose with the fit.
    const wrap = new Group();
    wrap.add(src);
    monoModel = wrap;
    // Rigs built before the load finishes swap their placeholder now, and
    // re-dress to the measured rock: fissure sized into the silhouette and
    // moved to the CAMERA face (this camera looks down +z, so the front of
    // a block is its -z side), counter dropped to just above the crown.
    for (const rig of monoRigs) {
      if (rig.model) continue;
      const m = wrap.clone(true) as Group;
      rig.root.add(m);
      rig.model = m;
      for (const r of rig.rocks) r.visible = false;
      rig.fissure.geometry.scale(1.6, (monoH * 0.72) / 3.6, 1);
      // On the CAMERA face of the measured rock, not at a constant depth
      // tuned for the old 4.4 fit — inside the silhouette it never reads.
      rig.fissure.position.set(0, monoH * 0.42, -monoD * 0.46);
      // SECONDARY ELEMENT (2026-08-18): the counter used to float at
      // monoH+1.15 -- almost the same height as the reward preview
      // (monoH+offset, offset~1.5) -- so a five-figure number and the
      // hologram competed for the same band of sky and the number won on
      // sheer screen size. Dropped to just above the crown, clearly BELOW
      // the preview floating higher above it: near the rock, not in
      // competition with what's floating over it.
      rig.counter.position.y = monoH + 0.35;
    }
  }).catch(() => { /* placeholder boxes stay; the rig still works */ });
  /** Soft vertical glow for the monolith fissure — same radial-gradient
   * technique as `contactTex` above, elongated so the bright core reads
   * as a lit crack rather than a hard-edged box. */
  const fissureGlowTex = (() => {
    const c = document.createElement('canvas');
    c.width = 32; c.height = 128;
    const g = c.getContext('2d')!;
    const grad = g.createLinearGradient(0, 0, 32, 0);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.5, 'rgba(255,255,255,1)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 32, 128);
    // Fade the top/bottom of the crack too, so it reads as a core with a
    // soft extent rather than a bar cut off at a hard top and bottom edge.
    g.globalCompositeOperation = 'destination-in';
    const vgrad = g.createLinearGradient(0, 0, 0, 128);
    vgrad.addColorStop(0, 'rgba(255,255,255,0)');
    vgrad.addColorStop(0.18, 'rgba(255,255,255,1)');
    vgrad.addColorStop(0.82, 'rgba(255,255,255,1)');
    vgrad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = vgrad;
    g.fillRect(0, 0, 32, 128);
    g.globalCompositeOperation = 'source-over';
    const t = new CanvasTexture(c);
    t.colorSpace = SRGBColorSpace;
    return t;
  })();
  for (let i = 0; i < 4; i++) {
    const g = new Group();
    const rocks: Mesh[] = [];
    // Five boulders stacked into a mass ~2.7 wide, 3.4 tall.
    // Taller than the horde it stands beside: a rock the crowd hides is a
    // rock the player never decides about.
    const spec: [number, number, number, number, number, number][] = [
      [0, 1.05, 0, 2.6, 2.1, 2.3],
      [-0.58, 2.65, 0.15, 1.9, 1.7, 1.8],
      [0.66, 2.5, -0.12, 1.6, 1.5, 1.55],
      [0.05, 3.8, 0.05, 1.35, 1.3, 1.3],
      [-0.15, 4.65, -0.1, 0.9, 0.85, 0.85],
    ];
    for (const [x, y, z, sx, sy, sz] of spec) {
      const m = new Mesh(new BoxGeometry(sx, sy, sz),
        new MeshStandardMaterial({
          color: 0xbdb4a6, roughness: 0.94, metalness: 0.02,
          emissive: new Color(0x6b6055), emissiveIntensity: 0.55,
        }));
      m.position.set(x, y, z);
      m.rotation.set(((x * 7) % 1) * 0.12, ((y * 5) % 1) * 0.5, ((z * 9) % 1) * 0.1);
      m.castShadow = true;
      g.add(m); rocks.push(m);
    }
    // The fissure: a core of light glowing through a crack in the rock,
    // widening as it is chipped -- the "energy blade cutting in" read.
    //
    // INTEGRATION FIX (founder verdict on 64af0c6b, reported twice): a
    // hard-edged BoxGeometry with a flat additive material has a crisp
    // rectangular silhouette against the rock -- exactly what reads as a
    // rendering artifact rather than as a lit core, regardless of colour.
    // Swapped for a camera-facing plane carrying a soft radial-gradient
    // texture (same technique as the contact-shadow blob, `contactTex`
    // above): bright centre, soft falloff, no hard box edge to read as a
    // bug. `fissureGlowTex` is a shared module-level texture; per-rig
    // tint/opacity/scale still work exactly as before via `.material`.
    //
    // D4 FIX: that swap was never actually visible. The camera sits
    // behind the player looking toward +Z (camera.position.z = pz -
    // CAM_BACK, lookAt z = pz + CAM_LOOKAT_FORWARD) — it is always on
    // the -Z side of the rig. A default PlaneGeometry's front face
    // normal is +Z, so a plane placed at local z=+1.25 (the FAR/back
    // face of the boulder mass, per the rock spec below) shows the
    // camera its back face (culled by the material's default FrontSide)
    // while also sitting behind ~1-2 units of opaque boulder along the
    // camera's sightline. Two independent reasons for one symptom: the
    // "soft-gradient fix" never rendered a single pixel, on any build.
    // Moved to the NEAR face (z=-1.25, proud of the boulders' front
    // surface at this height, see spec z-ranges above) and set
    // `side: DoubleSide` so it isn't re-hidden by the same mistake if
    // the camera angle ever changes again.
    const fissure = new Mesh(new PlaneGeometry(0.6, 3.6),
      new MeshBasicMaterial({
        map: fissureGlowTex,
        color: 0xffc44a, transparent: true, opacity: 0.9,
        blending: AdditiveBlending, depthWrite: false, side: DoubleSide,
      }));
    fissure.position.set(0, 2.5, -1.25);
    g.add(fissure); glow(fissure);
    // AETHER WARD CRYSTALS. See MonolithRig.crystals' own comment: additive
    // decoration only, angular shards erupting from the rock at varying
    // height/angle so the cluster reads as grown, not stamped. Elongated
    // via non-uniform scale on a plain low-poly icosahedron (module-level
    // `shardGeo`, used by the debris system, is declared further down this
    // file and isn't in scope yet at this point in the setup pass -- a
    // second small geometry instance per rig is negligible next to a
    // handful of monolith slots).
    const crystalGeo = new IcosahedronGeometry(0.34, 0);
    const crystals: Mesh[] = [];
    const CRYSTAL_LAYOUT: readonly [number, number, number, number, number][] = [
      // x, y, z, yaw, height-stretch
      [-1.4, 2.1, -0.3, 0.4, 2.6],
      [1.1, 1.6, 0.6, -0.7, 1.9],
      [0.2, 3.0, -0.8, 1.3, 3.1],
      [-0.6, 1.2, 1.1, 2.4, 1.5],
      [1.5, 2.6, -0.2, -1.6, 2.2],
    ];
    for (const [cx, cy, cz, yaw, stretch] of CRYSTAL_LAYOUT) {
      const cm = new Mesh(crystalGeo, new MeshStandardMaterial({
        color: 0xbfeeff, transparent: true, opacity: 0.5, roughness: 0.15, metalness: 0.05,
        // Quality-audit M-tier: 0.9 read as barely-there against daylight
        // ambient at capture distance (they sit small against the rock and
        // the much larger prize hologram already dominates the same read).
        // Bumped, not re-verified pixel-for-pixel -- eyeball on next pass.
        //
        // Re-audited 2026-09-02 (founder finding, live capture): that bump
        // over-corrected. At 1.3 base emissive/0.82 opacity, sitting at
        // the SAME height as the titan hologram they're meant to merely
        // decorate, the crystals became the dominant read and the actual
        // monster prize (rig.holoMeshes, this file) was reduced to a
        // barely-visible smear behind them -- exactly the "creative,
        // pretty reward holder" framing this array exists for, drowning
        // out the reward itself. Pulled back down; still brightens with
        // damage (below) so the "fight is visibly working" read survives.
        emissive: new Color(0x4fd8ff), emissiveIntensity: 0.75,
      }));
      cm.position.set(cx, cy, cz);
      cm.rotation.set(0.3, yaw, 0.15);
      cm.scale.set(0.85, stretch, 0.85);
      cm.userData['baseStretch'] = stretch;
      cm.castShadow = true;
      g.add(cm); glow(cm);
      crystals.push(cm);
    }
    // The counter plate. depthTest off: five figures the horde can walk in
    // front of is a counter the player loses exactly when the street is
    // fullest, which is when the decision is being made.
    const counter = new Sprite(new SpriteMaterial({
      transparent: true, depthWrite: false, depthTest: false,
    }));
    counter.position.set(0, 6.1, 0);
    counter.renderOrder = 30;
    g.add(counter);
    g.visible = false;
    world.add(g);
    monoRigs.push({
      root: g, rocks, fissure, holo: null, holoMeshes: [], titan: -1,
      model: null, counter, counterVal: -1,
      lastHp: -1, lastAlive: false, lastOp: -1, lastWX: 0, lastWZ: 0, raiseT: 9,
      preview: null, previewOp: -1, previewMeshes: [], crystals,
      holoSpringVel: 0, holoSpringOffset: 0,
      guardian: null, guardianMixer: null, guardianFootOffset: 0, guardianBaseScale: 1,
    });
  }

  // ================= BLOCK THEATRE (docs/animation-bible.md) =============
  // Renderer-only choreography for the forge blocks: escalating chips,
  // pre-break anticipation, the break burst, payload celebrations, and
  // the fizzle. The sim stays untouched; everything here reads state
  // deltas and settles physically (Iron Rule 8: shards land, nothing
  // teleports — a freed titan MARCHES to the war).
  /** Per-payload identity. Index = mOp (BLOCK_* in step.ts). */
  // FOUNDER FINDING (2026-08-28): "3 random colors... looks cheap" -- index
  // 5 (BLOCK_RACE) had no entry here and fell through to the generic
  // `?? 0xb47aff` default, a purple with no relation to the rock's own
  // palette. Isle 1 AND isle 12 are entirely BLOCK_RACE, so every fissure
  // crack on both isles was glowing an off-family purple against the
  // crystal's own cyan/white -- one of the three clashing hues, alongside
  // the rock's warm stone base and its cyan crystal facets. Given its own
  // entry in the same cyan family as RIM_OBJECTIVE / the crystal shards, so
  // the crack reads as part of one object instead of a fourth competing
  // color.
  const PAYLOAD_TINT = [0xb47aff, 0x35e0b8, 0xffb84a, 0x57a8ff, 0xc9d24f, 0x8fe9ff];
  const BLOCK_RACE_OP = 5;
  const shardGeo = new IcosahedronGeometry(0.34, 0);
  const shardMat = new MeshStandardMaterial({ color: 0xbdb4a6, roughness: 0.95 });
  interface Shard { m: Mesh; vx: number; vy: number; vz: number; sx: number; sz: number; life: number }
  const shards: Shard[] = [];
  for (let i = 0; i < 16; i++) {
    const m = new Mesh(shardGeo, shardMat.clone());
    m.visible = false; m.castShadow = true;
    world.add(m);
    shards.push({ m, vx: 0, vy: 0, vz: 0, sx: 0, sz: 0, life: 0 });
  }
  let shardCursor = 0;
  function burstShards(wx: number, wz: number, tint: number, n: number): void {
    for (let k = 0; k < n; k++) {
      const sh = shards[shardCursor]!; shardCursor = (shardCursor + 1) % shards.length;
      sh.m.visible = true;
      sh.m.position.set(wx, 1.2 + (k % 3) * 0.6, wz);
      sh.m.scale.setScalar(0.6 + ((k * 7) % 5) * 0.16);
      (sh.m.material as MeshStandardMaterial).color.set(0xbdb4a6);
      (sh.m.material as MeshStandardMaterial).emissive.set(tint);
      (sh.m.material as MeshStandardMaterial).emissiveIntensity = 0.25;
      (sh.m.material as MeshStandardMaterial).opacity = 1;
      (sh.m.material as MeshStandardMaterial).transparent = true;
      const ang = (k / n) * Math.PI * 2 + 0.4;
      const sp = 3.2 + ((k * 13) % 7) * 0.55;
      sh.vx = Math.cos(ang) * sp; sh.vz = Math.sin(ang) * sp * 0.55;
      sh.vy = 4.2 + ((k * 5) % 4) * 0.9;
      sh.sx = ((k * 11) % 7 - 3) * 1.8; sh.sz = ((k * 17) % 5 - 2) * 1.8;
      sh.life = 2.2;
    }
  }
  /** Payload comet streaks: the cause flying to its effect. */
  interface Streak { sp: Sprite; fx: number; fz: number; tx: number; tz: number; t: number; dur: number }
  const streaks: Streak[] = [];
  for (let i = 0; i < 12; i++) {
    const sp = new Sprite(new SpriteMaterial({
      transparent: true, depthWrite: false, blending: AdditiveBlending,
    }));
    sp.visible = false; sp.scale.setScalar(0.9);
    world.add(sp);
    streaks.push({ sp, fx: 0, fz: 0, tx: 0, tz: 0, t: 0, dur: 0.5 });
  }
  let streakCursor = 0;
  const streakTex = new Map<number, CanvasTexture>();
  function streakTexture(tint: number): CanvasTexture {
    let t = streakTex.get(tint);
    if (t) return t;
    const c = document.createElement('canvas'); c.width = c.height = 64;
    const g = c.getContext('2d')!;
    const grad = g.createRadialGradient(32, 32, 2, 32, 32, 30);
    const hex = `#${tint.toString(16).padStart(6, '0')}`;
    grad.addColorStop(0, '#ffffff'); grad.addColorStop(0.35, hex);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad; g.fillRect(0, 0, 64, 64);
    t = new CanvasTexture(c); t.colorSpace = SRGBColorSpace;
    streakTex.set(tint, t);
    return t;
  }
  function launchStreaks(fx0: number, fz0: number, tx: number, tz: number, n: number, tint: number): void {
    for (let k = 0; k < n; k++) {
      const st = streaks[streakCursor]!; streakCursor = (streakCursor + 1) % streaks.length;
      st.sp.material.map = streakTexture(tint);
      st.sp.material.needsUpdate = true;
      st.sp.visible = true;
      st.fx = fx0 + (k % 3 - 1) * 0.5; st.fz = fz0;
      st.tx = tx + (k % 4 - 1.5) * 0.6; st.tz = tz;
      st.t = -k * 0.07; st.dur = 0.55;
    }
  }
  /**
   * CONTACT-HIT FLASH (founder, 2026-09-05: "contact hit visuals and
   * effects"). The existing per-hit burst (`fx.hit`, below) is real and
   * confirmed firing (window.__scene.perf().particlesAlive climbs on
   * every impact) but is built from small 3D spheres -- from this file's
   * own steep top-down camera, a small sphere foreshortens to almost
   * nothing, which is a real, separate reason a hit can under-read even
   * with the particle-size fix earlier this session. A billboarded,
   * camera-facing sprite reads at its full size from ANY angle, which a
   * 3D particle cannot -- the same reason `streaks`/`gunArc`/every
   * pickup-preview icon in this file already uses a Sprite instead of
   * geometry for anything that has to read clearly at a glance. Reuses
   * `streakTexture()` (same white-hot radial gradient, already cached by
   * tint) rather than authoring a second texture generator.
   */
  interface HitFlash { sp: Sprite; t: number; dur: number; size: number }
  const hitFlashes: HitFlash[] = [];
  for (let i = 0; i < 20; i++) {
    const sp = new Sprite(new SpriteMaterial({
      transparent: true, depthWrite: false, depthTest: false,
      blending: AdditiveBlending,
    }));
    sp.visible = false;
    sp.renderOrder = 12;
    world.add(sp);
    hitFlashes.push({ sp, t: 0, dur: 0.14, size: 1 });
  }
  let hitFlashCursor = 0;
  function spawnHitFlash(x: number, y: number, size: number, tint: number): void {
    const hf = hitFlashes[hitFlashCursor]!;
    hitFlashCursor = (hitFlashCursor + 1) % hitFlashes.length;
    hf.sp.material.map = streakTexture(tint);
    hf.sp.material.needsUpdate = true;
    hf.sp.visible = true;
    hf.sp.position.set((x - arenaW / 2) * SX, 1.0, y * S);
    hf.size = size;
    hf.sp.scale.setScalar(size * 1.3);
    hf.t = 0;
  }
  /**
   * THE PRIZE, SHOWN (founder, 2026-08-14: "the rewards shouldn't show a
   * box — show whatever you are going to get"). Every block levitates a
   * hologram of its actual payload: the gun you'll carry, the aether sled
   * that'll fight beside you, the shield you'll wear, the soldiers who'll
   * join. Same solidify-with-damage law as the titan ghost.
   */
  /** A baked soldier silhouette for the bodies-preview, when loaded. */
  function squadCrowdPreviewGeo(): import('three').BufferGeometry | null {
    const pool = crowd['shoot'] ?? crowd['run'];
    return pool && pool[0] ? pool[0].geometry : null;
  }
  function buildPreview(op: number, value: number): Group {
    const g = new Group();
    if (op === 2) {
      const gun = genCache.get('gun-mk') ?? genCache.get('gun-rifle');
      if (gun) { const m = gun.clone(true) as Group; m.scale.multiplyScalar(2.6); g.add(m); }
    } else if (op === 4) {
      const t = buildAetherSled(0.9);
      t.scale.multiplyScalar(1.35);
      g.add(t);
    } else if (op === 3) {
      // FOUNDER FINDING (2026-08-27): this was a flat translucent dome --
      // this function's OWN doc comment says "the shield you'll wear",
      // but nothing here ever built a shield. `buildArmourPrize()`
      // (defined for the charge-station reward system, same emissive
      // shell + glowing rim look this file already uses elsewhere for
      // "here is the thing you're earning") is exactly that shield and
      // was simply never wired to the monolith's own preview. Reused
      // wholesale rather than redesigned from nothing.
      const shield = buildArmourPrize();
      shield.scale.multiplyScalar(1.9);
      g.add(shield);
    } else if (op === 1 || op === BLOCK_RACE_OP) {
      // The soldiers you are buying: baked crowd silhouettes in a rank.
      // BLOCK_RACE (isle 12's payload) pays out via gainSquad exactly like
      // BLOCK_BODIES does (step.ts breakBlock) -- it has never granted a
      // titan ally, so it shows the same soldier icon, not a creature.
      //
      // ITEM 1 FIX (founder rejection of Part A, "no hologram visible"):
      // this local x2.2 compounded with the outer group's own
      // `monoH * 0.38` scale (below, applied to the whole preview) into a
      // combined ~9.4x on the REAL crowd geometry — a preview 9+ world
      // units tall, nearly twice the rock's own height, its pivot at the
      // feet meant almost the entire body rendered above the frustum's
      // top edge (measured: `previewDiag()`'s bboxScreenY topped out at
      // -0.275, more than a quarter-frame above screen). The x2.2 was
      // only ever exercised against the REAL geometry (the `if (geo)`
      // guard skips it for the sphere fallback), so it was never checked
      // together with the group scale that also applies to it — the
      // group scale alone is "scale with the rock," this was double
      // counting it.
      const geo = squadCrowdPreviewGeo();
      const n = Math.max(1, Math.min(6, Math.round(value)));
      for (let k = 0; k < n; k++) {
        const m = new Mesh(geo ?? new SphereGeometry(0.4, 10, 8),
          new MeshBasicMaterial({ color: 0x35e0b8, transparent: true, opacity: 0.6 }));
        m.position.set((k % 3 - 1) * 1.1, 0, Math.floor(k / 3) * -1.0);
        g.add(m);
      }
    }
    return g;
  }

  /** Break bridge to juice/audio in main (mass-scaled kick + slow-mo). */
  let onBlockBreakCb: ((mass: number) => void) | null = null;
  /** Fresh break site: the titan/vehicle spawn eases FROM here. */
  const lastBreak = { wx: 0, wz: 0, at: -10, op: -1 };
  /** Per-ally visual ease-in (march from the block, never teleport). */
  const allyEase: ({ fx: number; fz: number; t: number } | null)[] = [];
  /** L6a: the won gun arcs from the block to the squad. */
  const gunArc = { obj: null as Group | null, t: 0, fx: 0, fz: 0 };
  /** L6b: the tank slides in from the block, never pops at the flank. */
  const vehSlide = { t: 9, fx: 0, fz: 0 };
  // =======================================================================

  /** Four titan silhouettes — the variety the monoliths advertise. */
  const TITAN_ASSET = ['titan-saurian', 'titan-mech', 'titan-bulwark', 'titan-artillery'];
  /**
   * D3: kind 1 ("MECH: upright, blocky, shoulder cannons" — see the
   * fallback geometry below) is the literal humanoid war-machine ART_BIBLE
   * names outright ("modern vehicles of any kind" / the re-dress map's
   * `mech -> aether Colossus`) — the one kind reported and the one
   * ART_BIBLE names. No re-dressed aether-Colossus material exists yet
   * — that is the identity pass, explicitly out of scope this round —
   * so per the founder's instruction ("an empty slot is better than
   * banned vocabulary"), kind 1 is redirected here to kind 0 (saurian,
   * visually confirmed non-mech, already approved, already loaded)
   * rather than shipped un-re-dressed a fifth time.
   *
   * Kinds 0, 2 and 3 are UNCHANGED — four-kind reward variety is a
   * design decision, not a render-layer one, and it is not this fix's
   * call to make. Kinds 2 ("bulwark") and 3 ("artillery") were found,
   * while investigating this, to ALSO read as machine-coded on sight —
   * filed as D10, not fixed here; see tasks/defects.md.
   *
   * This is the ONLY place the redirect lives for the render layer's
   * mesh/asset selection; `s.mTitan[i]`/`s.lKind[i]` (the sim's own
   * values) are read and passed through this before being used for
   * anything visual, at both call sites (hologram preview, field ally)
   * below. The sim's stored kind is untouched — this is a render-layer
   * substitution, not a sim change, so the event/state hash trail is
   * unaffected.
   */
  function renderTitanKind(simKind: number): number {
    // MOVEMENT OVER THEME-PURITY (founder finding, 2026-09-02): of the 4
    // titan kinds, only kind 1 (mech, `titan-mech-rigged.glb`) and kind 2
    // (bulwark, `titan-bulwark-rigged.glb`) have an actual rigged walk/
    // attack animation -- `RIGGED_TITAN` below. Kind 1 is separately
    // redirected away (banned "mech" vocabulary, no re-dress asset yet),
    // and kinds 0/3 have never had a rig at all, so every ally titan that
    // used to land on kind 0 (the pre-existing mech->0 redirect) rendered
    // as a genuinely STATIC mesh -- no walk cycle, no attack reaction,
    // ever. Redirecting everything to kind 2 trades D10's "reads as an
    // armoured robot" identity concern (real, still open, needs a
    // re-dress asset) for an ally that actually moves and fights, which
    // is the more severe defect right now. Revert to `k===1?0:k` if a
    // rigged, on-theme replacement for kind 0/3 ever ships.
    return 2;
  }
  function buildTitan(kind: number): Group {
    // Generated mesh when it has loaded; the box silhouette below is only a
    // placeholder so a slow network never shows an empty podium.
    const gen = genCache.get(TITAN_ASSET[kind] ?? '');
    if (gen) return gen.clone(true) as Group;
    const g = new Group();
    const mk = (c: number, r = 0.75): MeshStandardMaterial => {
      const m = new MeshStandardMaterial({ color: c, roughness: r, metalness: kind === 1 ? 0.5 : 0.05 });
      friendlyRimColors.push(addRimLight(m, { rimColor: RIM_FRIENDLY })); // see gen.clone() above
      return m;
    };
    const body = mk(kind === 0 ? 0x5f8f4a : kind === 1 ? 0xc9d2dc
      : kind === 2 ? 0x8a7a6a : 0x6a6f9a);
    const dark = mk(kind === 0 ? 0x3d5f30 : kind === 1 ? 0x7d8794
      : kind === 2 ? 0x5d5145 : 0x454a72);
    const add = (m: Mesh, x: number, y: number, z: number): void => {
      m.position.set(x, y, z); m.castShadow = true; g.add(m);
    };
    if (kind === 0) {
      // SAURIAN: long low body, heavy tail, jaws.
      add(new Mesh(new BoxGeometry(1.5, 1.3, 2.6), body), 0, 1.5, 0);
      add(new Mesh(new BoxGeometry(0.95, 0.85, 1.3), dark), 0, 2.0, 1.7);
      add(new Mesh(new BoxGeometry(0.75, 0.35, 0.95), body), 0, 1.62, 2.15);
      add(new Mesh(new BoxGeometry(0.55, 0.5, 2.2), dark), 0, 1.55, -2.1);
      for (const sx of [-0.62, 0.62]) {
        add(new Mesh(new BoxGeometry(0.45, 1.2, 0.5), dark), sx, 0.6, 0.7);
        add(new Mesh(new BoxGeometry(0.42, 1.0, 0.48), dark), sx, 0.5, -0.9);
      }
    } else if (kind === 1) {
      // MECH: upright, blocky, shoulder cannons.
      add(new Mesh(new BoxGeometry(1.5, 1.8, 1.0), body), 0, 2.5, 0);
      add(new Mesh(new BoxGeometry(0.85, 0.7, 0.8), dark), 0, 3.7, 0.1);
      for (const sx of [-1.05, 1.05]) {
        add(new Mesh(new BoxGeometry(0.6, 0.6, 0.6), dark), sx, 3.2, 0);
        add(new Mesh(new CylinderGeometry(0.16, 0.16, 1.6, 8), body), sx, 3.4, 0.9);
        add(new Mesh(new BoxGeometry(0.5, 1.6, 0.5), dark), sx * 0.62, 0.9, 0);
      }
    } else if (kind === 2) {
      // BULWARK: squat, immense, slab shoulders.
      add(new Mesh(new BoxGeometry(2.6, 2.0, 1.6), body), 0, 1.8, 0);
      add(new Mesh(new BoxGeometry(1.0, 0.8, 0.9), dark), 0, 3.2, 0.1);
      for (const sx of [-1.6, 1.6]) {
        add(new Mesh(new BoxGeometry(0.7, 2.0, 0.9), dark), sx, 1.7, 0);
      }
      for (const sx of [-0.7, 0.7]) {
        add(new Mesh(new BoxGeometry(0.7, 1.0, 0.8), dark), sx, 0.5, 0);
      }
    } else {
      // ARTILLERY: a walking platform under a wide battery.
      add(new Mesh(new BoxGeometry(2.4, 0.8, 1.8), body), 0, 1.5, 0);
      for (const sx of [-0.75, 0, 0.75]) {
        add(new Mesh(new CylinderGeometry(0.2, 0.24, 1.9, 8), dark), sx, 2.3, 0.2);
      }
      for (const sx of [-0.85, 0.85]) {
        for (const sz of [-0.6, 0.6]) {
          add(new Mesh(new BoxGeometry(0.4, 1.2, 0.4), dark), sx, 0.6, sz);
        }
      }
    }
    return g;
  }

  // --- allies in the field + the armour bubble ---------------------------
  // The monster you charged, walking the corridor. Same silhouette as the
  // station's advertisement, so the promise and the delivery are visibly
  // the same body.
  /**
   * Ally actors, one rig per titan kind.
   *
   * The hologram advertised a generated titan and then a PURPLE BOX walked
   * out of the rock, because the field actor was still the procedural
   * placeholder while only the advertisement had been switched to the real
   * mesh. The promise and the delivery must be the same body -- that is the
   * entire point of showing the prize before it is earned.
   */
  const allyRigs: Group[][] = [[], [], [], []];
  /**
   * RIGGED titans: mesh plus a real walk cycle, per kind.
   *
   * A generated mesh is a statue however well it is textured, and the
   * procedural gait below is a good imitation of weight but it cannot move
   * a leg. Mixamo's auto-rigger emits the SAME skeleton for every humanoid
   * it processes, which is what let the walk be grafted from an unrelated
   * character onto both titans -- 52 and 46 channels bound.
   *
   * Only the two BIPEDS have this; the saurian and the artillery platform
   * are quadrupeds the auto-rigger cannot take, and they keep the
   * procedural gait.
   */
  // Kind 2 REDIRECTED (founder finding, 2026-09-02): titan-bulwark-rigged
  // was confirmed rigged+animated in isolation, but read as an untextured
  // white mannequin in every live capture -- the asset itself is broken
  // (missing/lost base-colour texture), not the loading code. `boss-
  // colossus.glb` is the game's own PREVIOUS chapter-boss asset (before
  // boss-clown replaced it, see ACTOR_ASSETS' own history comment) --
  // already a real, rigged, textured Mixamo character proven working
  // on screen elsewhere in this game (the gale_colossus beast), so
  // reusing it costs nothing new and cannot inherit the bulwark file's
  // texture problem. Full filenames now (not a base name + assumed
  // "-rigged" suffix), since this asset doesn't follow that convention.
  const RIGGED_TITAN: Record<number, string> = { 1: 'titan-mech-rigged.glb', 2: 'boss-colossus.glb' };
  const riggedSrc = new Map<number, Object3D>();
  const riggedMix: { root: Object3D; mixer: AnimationMixer }[][] = [[], [], [], []];
  for (const [k, file] of Object.entries(RIGGED_TITAN)) {
    void loader.loadAsync(`./${file}`).then((g) => {
      const src = g.scene;
      const bb = new Box3().setFromObject(src);
      const span = Math.max(bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z);
      // 8.0, was 5.0 (founder, 2026-09-02: 1.6x bigger, boss-colossus now
      // being a genuinely working reward should read as a bigger prize).
      // Fitted by longest axis, so a tall body grows in height -- audit
      // kerb overhang at this larger size (Iron Rule 8: shoulders stay
      // inside the rails) since the span this normalises FROM changed
      // (boss-colossus, not the old bulwark), not just the target size.
      const fit = 8.0 / Math.max(0.001, span);
      src.scale.setScalar(fit);
      src.position.y = -bb.min.y * fit;
      src.traverse((o) => {
        const sm = o as SkinnedMesh;
        if (sm.isSkinnedMesh) { sm.castShadow = true; sm.frustumCulled = false; }
      });
      (src as Object3D & { userData: { clips?: AnimationClip[] } }).userData.clips =
        g.animations as AnimationClip[];
      riggedSrc.set(Number(k), src);
    }).catch(() => { /* the static generated mesh stays */ });
  }
  // AMBIENT DISTANT BOSS -- REVERTED (founder, 2026-09-05): "reverse that
  // boss staying at the top of the screen frozen from the get go... it
  // was better how it was" -- also flagged as a physical-plausibility
  // violation, standing outside the road/lane rather than inside it (the
  // static silhouette was never given the rail-clamp every other body in
  // this file gets). Removed outright rather than re-tuned: no static
  // ambient body belongs in this corridor without the same rail-law
  // treatment as everything else, and this one never got it.
  /**
   * GUARDIAN SOURCE (Z-ROUTE BUILD PLAN): a genuinely new, distinct
   * character for the monolith guardian — "Parasite L Starkie", a real
   * Mixamo zombie/mutant from the user's own local Scary Zombie Pack,
   * never used anywhere else in this game (boss-colossus.glb is already
   * reused 3x: the real boss, the field-ally titan, AND the guardian
   * before this change — asset fatigue this specifically answers).
   * Deliberately separate from `riggedSrc`/`RIGGED_TITAN` above: this
   * asset is unproven relative to that shared, widely-depended-on system,
   * so it carries its own isolated load path and cannot affect the field
   * ally or the real boss if something about it is wrong.
   */
  let guardianSrc: Object3D | null = null;
  void (async () => {
    try {
      const [charG, attackG] = await Promise.all([
        loader.loadAsync('./guardian-parasite.glb'),
        loader.loadAsync('./guardian-parasite-attack.glb'),
      ]);
      const src = charG.scene;
      const bb = new Box3().setFromObject(src);
      const span = Math.max(bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z);
      const fit = 1 / Math.max(0.001, span); // normalised to a 1-unit span; the guardian creation site re-fits to its own target height
      src.scale.setScalar(fit);
      src.position.y = -bb.min.y * fit;
      src.traverse((o) => {
        const sm = o as SkinnedMesh;
        if (sm.isSkinnedMesh) { sm.castShadow = true; sm.frustumCulled = false; }
      });
      // borrowClip: same-family Mixamo prefix rename, the RIGHT tool for
      // a same-skeleton transplant (confirmed both files carry
      // "mixamorig:"-prefixed bones before this was written, not
      // assumed) — the fuller world-space `retarget()` exists for a
      // genuinely different rig family (Quaternius), not this case.
      const ownClips = charG.animations as AnimationClip[];
      const attackClip = borrowClip(attackG.animations as AnimationClip[], attackG.scene, src, 'attack');
      let clips = attackClip ? [...ownClips, attackClip] : ownClips;
      // SECOND MONSTER FOR THE FIELD ALLY (founder, 2026-09-04: "add
      // another monster character so there is at least 2 of them"). The
      // parasite only ships `idle` -- fine standing guard on the
      // monolith, not enough to march with the squad for the rest of the
      // run. `horde.glb` is the same shared Mixamo-family donor already
      // proven for the squad's own 'hit' clip (this file, ~line 2520) and
      // every mass-tier enemy's borrowed locomotion -- reloaded here
      // independently (browser HTTP cache, not a second network cost)
      // rather than reaching for the MASS_ASSETS closure's own `donor`,
      // matching that exact same independent-reload pattern.
      try {
        const donorWalk = await loader.loadAsync('./horde.glb');
        const walkClip = borrowClip(donorWalk.animations as AnimationClip[], donorWalk.scene, src, 'walk');
        if (walkClip) clips = [...clips, walkClip];
      } catch (err) {
        console.error('guardian-parasite walk-clip borrow failed; ally use will fall back to idle', err);
      }
      (src as Object3D & { userData: { clips?: AnimationClip[] } }).userData.clips = clips;
      guardianSrc = src;
    } catch (err) {
      console.error('guardian-parasite failed to load; falling back to the field-ally titan model', err);
    }
  })();

  const allyActors: Group[] = [];
  for (let i = 0; i < 8; i++) {
    const a = buildAllyPrize();
    // Big enough to tower over the squad, narrow enough that its shoulders
    // stay inside the rails. At 1.7 the body was ~2.5 units across against
    // a 1.0 wall margin and visibly overhung the kerb -- a physics-law
    // violation the moment it was drawn.
    a.scale.setScalar(0.95);
    a.visible = false;
    world.add(a);
    allyActors.push(a);
  }
  const shieldMat = new MeshBasicMaterial({
    color: 0x5ad7ff, transparent: true, opacity: 0.16,
    blending: AdditiveBlending, depthWrite: false,
  });
  const shieldBubble = new Mesh(new SphereGeometry(1, 24, 16), shieldMat);
  shieldBubble.visible = false;
  world.add(shieldBubble);

  // R8 (Iron Rule 7e): the set-dressing pass that scattered `prop-wreck`,
  // `prop-barrier`, `prop-barrel` and `prop-block` along the kerbs is
  // deleted outright. Its own comment named the intent: "make it a road
  // something terrible already happened on" -- road debris, banned by
  // name in `ART_BIBLE.md` §Sky Archipelago Prop Vocabulary. No sky-isle
  // replacement is substituted here; converging rails plus the floating
  // islands above carry this stretch of the causeway's depth read.
  // INTEGRATION NOTE: char-pipeline independently refined this same block
  // (fixed a rail-overlap bug per its own audit note) rather than deleting
  // it — not adopted, for the same Iron Rule 7e reason as v1 of this note.

  const gatePanels: { mesh: Sprite; label: string }[] = [];
  // ISLE-12 PRE-PLACEMENT (2026-08-22): 8 -> 12, so all 3 of chapter 12's
  // groups (2 lanes each = 6 sign panels) can render simultaneously from
  // run start, with headroom for a 4th group in flight during a handoff.
  for (let i = 0; i < 12; i++) {
    const sp = new Sprite(new SpriteMaterial({ transparent: true, depthWrite: false }));
    sp.visible = false;
    sp.scale.set(3.2, 1.6, 1);
    world.add(sp);
    // Quality-audit vs docs/reference: the reference gate panels carry a
    // glowing border that reads as bold even at thumbnail scale; ours had
    // no bloom at all. Additive only -- doesn't touch layout or gameplay.
    glow(sp);
    gatePanels.push({ mesh: sp, label: '' });
  }
  /**
   * BARRIER PAYOUT (founder, 2026-08-24): a separate, persistent sign
   * above each barrier lane -- see the render loop below for why. Sized
   * the same as `gatePanels` for the same reason (up to one per
   * simultaneously in-flight barrier lane).
   */
  const gatePayoutPanels: { mesh: Sprite; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const sp = new Sprite(new SpriteMaterial({ transparent: true, depthWrite: false }));
    sp.visible = false;
    sp.scale.set(3.2, 1.1, 1);
    world.add(sp);
    gatePayoutPanels.push({ mesh: sp, label: '' });
  }

  // ------------------------------------------------------------------ fx
  // Pooled particles as one instanced draw, plus pooled sprite labels.
  const PARTS = 420;
  // 0.09 -> 0.16 (founder, 2026-09-05: "contact hit is non-existent").
  // The mechanism was never missing (confirmed live via
  // window.__scene.perf().particlesAlive climbing 8->65 the instant
  // combat starts) -- it was invisible. This base radius was authored
  // once, before SOLDIER_SCALE went 1.02->1.488 (+46%) across this same
  // session's own bulk passes; the particle geometry itself never moved,
  // so every hit spark has been getting relatively SMALLER against the
  // bodies it's supposed to read against with every size bump. Bumped
  // past a flat proportional catch-up (which would only be +46%, to
  // ~0.131) because "technically still there, just small" is exactly
  // the failure being reported.
  const partGeo = new SphereGeometry(0.16, 5, 4);
  const partMat = new MeshBasicMaterial({ transparent: true, opacity: 0.95 });
  const parts = new InstancedMesh(partGeo, partMat, PARTS);
  parts.instanceColor = null;
  world.add(parts);
  const pState = {
    x: new Float32Array(PARTS), y: new Float32Array(PARTS), z: new Float32Array(PARTS),
    vx: new Float32Array(PARTS), vy: new Float32Array(PARTS), vz: new Float32Array(PARTS),
    life: new Float32Array(PARTS), max: new Float32Array(PARTS),
    sc: new Float32Array(PARTS), col: new Float32Array(PARTS * 3),
  };
  let pCur = 0;
  const pColor = new Color();

  function emit(
    wx: number, wy: number, vx: number, vy: number, vz: number,
    life: number, tint: number, scale: number,
  ): void {
    const i = pCur; pCur = (pCur + 1) % PARTS;
    pState.x[i] = (wx - arenaW / 2) * SX; pState.y[i] = 0.8; pState.z[i] = wy * S;
    pState.vx[i] = vx; pState.vy[i] = vy; pState.vz[i] = vz;
    pState.life[i] = life; pState.max[i] = life; pState.sc[i] = scale;
    pColor.setHex(tint);
    pState.col[i * 3] = pColor.r; pState.col[i * 3 + 1] = pColor.g; pState.col[i * 3 + 2] = pColor.b;
  }

  /**
   * IMPACT DEBRIS (render-quality-pass, item 4). A second, small instanced
   * pool, deliberately separate from `parts` above: that pool's spheres are
   * tuned as sparks/gore (hot core, additive/soft edges) across a dozen
   * weapon looks, and folding "angular tumbling chunk" into the same
   * geometry+material would change every one of them. This pool exists
   * only for wreckage -- boss shell breaks, kills, monolith cracks.
   */
  const DEBRIS = 96;
  const debrisGeo = new IcosahedronGeometry(0.11, 0);
  const debrisMat = new MeshStandardMaterial({ roughness: 0.92, metalness: 0.05 });
  const debrisMesh = new InstancedMesh(debrisGeo, debrisMat, DEBRIS);
  debrisMesh.instanceColor = null;
  debrisMesh.castShadow = true;
  world.add(debrisMesh);
  const dState = {
    x: new Float32Array(DEBRIS), y: new Float32Array(DEBRIS), z: new Float32Array(DEBRIS),
    vx: new Float32Array(DEBRIS), vy: new Float32Array(DEBRIS), vz: new Float32Array(DEBRIS),
    rx: new Float32Array(DEBRIS), ry: new Float32Array(DEBRIS), rz: new Float32Array(DEBRIS),
    rvx: new Float32Array(DEBRIS), rvy: new Float32Array(DEBRIS), rvz: new Float32Array(DEBRIS),
    life: new Float32Array(DEBRIS), max: new Float32Array(DEBRIS),
    sc: new Float32Array(DEBRIS), col: new Float32Array(DEBRIS * 3),
  };
  let dCur = 0;
  const dColor = new Color();

  /** Spawns `n` chunks at an impact point, velocity along (ddx,ddy). */
  function emitDebris(
    wx: number, wy: number, n: number, tint: number,
    ddx: number, ddy: number, power: number,
  ): void {
    dColor.setHex(tint);
    for (let k = 0; k < n; k++) {
      const i = dCur; dCur = (dCur + 1) % DEBRIS;
      const a = Math.random() * Math.PI * 2;
      const spread = (0.02 + Math.random() * 0.05) * power;
      dState.x[i] = (wx - arenaW / 2) * SX; dState.y[i] = 0.4; dState.z[i] = wy * S;
      dState.vx[i] = ddx * power * 0.09 + Math.cos(a) * spread;
      dState.vy[i] = (0.05 + Math.random() * 0.09) * power;
      dState.vz[i] = ddy * power * 0.09 + Math.sin(a) * spread;
      dState.rx[i] = Math.random() * Math.PI * 2;
      dState.ry[i] = Math.random() * Math.PI * 2;
      dState.rz[i] = Math.random() * Math.PI * 2;
      dState.rvx[i] = (Math.random() - 0.5) * 9;
      dState.rvy[i] = (Math.random() - 0.5) * 9;
      dState.rvz[i] = (Math.random() - 0.5) * 9;
      dState.life[i] = 55 + Math.random() * 30;
      dState.max[i] = dState.life[i];
      dState.sc[i] = (0.55 + Math.random() * 0.75) * power;
      dState.col[i * 3] = dColor.r; dState.col[i * 3 + 1] = dColor.g; dState.col[i * 3 + 2] = dColor.b;
    }
  }

  const LABELS = 28;
  const labels: { sp: Sprite; life: number; max: number; x: number; y: number; z: number; rise: number }[] = [];
  for (let i = 0; i < LABELS; i++) {
    const sp = new Sprite(new SpriteMaterial({ transparent: true, depthTest: false, depthWrite: false }));
    sp.visible = false;
    sp.renderOrder = 10;
    world.add(sp);
    labels.push({ sp, life: 0, max: 1, x: 0, y: 0, z: 0, rise: 0 });
  }
  let lCur = 0;
  function labelTexture(text: string, tint: number): CanvasTexture {
    // RES 2.5x, same reasoning/margin as gateTexture's own comment.
    const RES = 2.5;
    const c = document.createElement('canvas');
    c.width = 256 * RES; c.height = 96 * RES;
    const g = c.getContext('2d')!;
    // Damage reads as a PLATE, not as floating text: a filled rounded panel
    // with heavy white lettering, which is what makes a hit legible over a
    // busy corridor at phone size.
    if (text.startsWith('-')) {
      g.fillStyle = 'rgba(214,44,32,0.95)';
      const rw = Math.min(240 * RES, 96 * RES + text.length * 34 * RES), rh = 72 * RES;
      g.beginPath();
      g.roundRect((256 * RES - rw) / 2, (96 * RES - rh) / 2, rw, rh, 12 * RES);
      g.fill();
      g.strokeStyle = 'rgba(255,190,160,0.9)'; g.lineWidth = 4 * RES; g.stroke();
    }
    g.textAlign = 'center'; g.textBaseline = 'middle';
    // FIT the text before drawing anything. A fixed font size in a fixed
    // canvas width silently CLIPS: "TWIN FANG" came out as "WIN FAN" and
    // "WIDER ARC" as "VIDER AR", so the weapon and upgrade names -- the
    // one thing these labels exist to communicate -- rendered as garbage.
    // Measured first so the outline and the fill use the same size.
    const maxW = 232 * RES;
    let px = 62 * RES;
    g.font = `900 ${px}px ui-sans-serif, system-ui, sans-serif`;
    const w = g.measureText(text).width;
    if (w > maxW) {
      px = Math.max(14 * RES, Math.floor(px * (maxW / w)));
      g.font = `900 ${px}px ui-sans-serif, system-ui, sans-serif`;
    }
    g.lineWidth = Math.max(4 * RES, px * 0.16);
    g.strokeStyle = 'rgba(8,20,40,0.9)';
    g.strokeText(text, 128 * RES, 50 * RES);
    g.fillStyle = `#${tint.toString(16).padStart(6, '0')}`;
    g.fillText(text, 128 * RES, 50 * RES);
    const t = new CanvasTexture(c); t.colorSpace = SRGBColorSpace; return t;
  }

  /**
   * THE BOSS BECOMES A SPECTACLE. "Its remaining HP is a huge numeral
   * painted on its chest" (reference: Z Route: Redemption). Wide rather
   * than square, since an HP count can run to six figures (stormhulk:
   * 720000).
   *
   * RENDER-QUALITY PASS (item 5): "same treatment as the gate numerals",
   * spec'd literally. This function used to run its own dark/gold plate
   * (rgba(10,12,16,0.72) panel, amber stroke, brown text outline, cream
   * fill) -- a real divergence from `gateTexture`/`hpTexture`'s shared UI
   * LAW (founder, 2026-08-20): saturated panel, heavy WHITE numerals,
   * thick BLACK outline. Brought in line with that law here: same red
   * threat-gradient panel, same white strokeRect border, same near-black
   * text outline, same white fill -- only the canvas stays this
   * function's own wider size for six-figure HP counts.
   */
  function bossHpTexture(text: string): CanvasTexture {
    // RES 2.5x, same reasoning/margin as gateTexture's own comment -- the
    // boss numeral is the single most-stared-at element in the run (its
    // own doc comment above), so it's the last place a zoom punch should
    // ever show pixelation.
    const RES = 2.5;
    const W = 512 * RES, H = 160 * RES;
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const g = c.getContext('2d')!;
    const grd = g.createLinearGradient(0, 0, 0, H);
    grd.addColorStop(0, 'rgba(255,80,70,0.97)'); grd.addColorStop(1, 'rgba(192,20,20,0.97)');
    g.fillStyle = grd;
    g.fillRect(0, 0, W, H);
    g.strokeStyle = '#ffffff'; g.lineWidth = 11 * RES;
    g.strokeRect(6 * RES, 6 * RES, W - 12 * RES, H - 12 * RES);
    g.textAlign = 'center'; g.textBaseline = 'middle';
    const maxW = W - 64 * RES;
    let px = 96 * RES;
    g.font = `900 ${px}px ui-sans-serif, system-ui, sans-serif`;
    const w = g.measureText(text).width;
    if (w > maxW) {
      px = Math.max(20 * RES, Math.floor(px * (maxW / w)));
      g.font = `900 ${px}px ui-sans-serif, system-ui, sans-serif`;
    }
    g.lineJoin = 'round';
    g.strokeStyle = 'rgba(8,10,14,0.95)'; g.lineWidth = Math.max(8 * RES, px * 0.16);
    g.strokeText(text, W / 2, H / 2 + 4 * RES);
    g.fillStyle = '#ffffff';
    g.fillText(text, W / 2, H / 2 + 4 * RES);
    const t = new CanvasTexture(c); t.colorSpace = SRGBColorSpace; return t;
  }
  function makeBossHpSprite(): Sprite {
    const sp = new Sprite(new SpriteMaterial({ transparent: true, depthTest: false, depthWrite: false }));
    sp.visible = false;
    sp.renderOrder = 11;
    sp.scale.set(4.2, 1.3, 1);
    world.add(sp);
    return sp;
  }

  const fx: Fx3D = {
    hit(x, y, n, tint) {
      // BILLBOARDED FLASH, on top of the 3D particle burst below (own
      // comment near `hitFlashes`' declaration): reads at full size from
      // this file's steep top-down camera angle, where a small 3D sphere
      // foreshortens toward nothing. Sized off `n` (the same impact-count
      // signal already scaling the particle burst), so a mortar's flash
      // is visibly bigger than a rifle round's, not a fixed decal.
      spawnHitFlash(x, y, (0.55 + Math.min(1.4, n * 0.09)) * tuning.impactFlashScale, tint);
      // DIRECTIONAL. Debris erupts ALONG the shot -- the squad fires up the
      // corridor, so sparks throw forward and outward in a cone rather than
      // puffing evenly in a ring. An omnidirectional burst reads as a
      // decal appearing; a cone reads as something being HIT by something.
      //
      // Two populations, bright-to-dark: a hot fast core that dies quickly,
      // and slower dark debris that lingers. A single flat colour is what
      // makes a hit look like a sticker.
      const look = GUN_LOOKS[lastProfile] ?? DEFAULT_LOOK;
      // The DESTRUCTION is shaped by the weapon too, not only the tracer.
      // A cone of sparks for every class made a mortar shell land like a
      // rifle round -- the moment of impact is where a weapon's character
      // actually lands, and it was the same moment ten times over.
      if (look.shape === 'beam') {
        // Mushroom bloom: a flat disc of ejecta at the surface with a
        // column of light rising out of it and capping over.
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2;
          const cap = i < n * 0.45;
          emit(x, y, Math.cos(a) * (cap ? 0.20 : 0.34),
            cap ? 0.30 + Math.random() * 0.22 : 0.02 + Math.random() * 0.05,
            Math.sin(a) * (cap ? 0.20 : 0.34),
            cap ? 16 + Math.random() * 10 : 24 + Math.random() * 12,
            cap ? look.core : look.mid, (cap ? 0.7 : 1.0) * look.boom);
        }
        return;
      }
      if (look.shape === 'shell' || look.shape === 'lob') {
        // Concussive: a hard flat shockwave of debris thrown outward along
        // the ground, plus heavy chunks that arc and fall.
        for (let i = 0; i < n; i++) {
          const a = Math.random() * Math.PI * 2;
          const chunk = i < n * 0.35;
          const v = (chunk ? 0.16 : 0.42) * look.boom;
          emit(x, y, Math.cos(a) * v, chunk ? 0.26 + Math.random() * 0.28 : 0.03,
            Math.sin(a) * v,
            chunk ? 26 + Math.random() * 18 : 12 + Math.random() * 9,
            chunk ? look.halo : look.core,
            (chunk ? 1.15 : 0.6) * look.boom);
        }
        return;
      }
      if (look.shape === 'rocket') {
        // Fireball: a slow, fat, upward-rolling cloud rather than sparks.
        for (let i = 0; i < n; i++) {
          const a = Math.random() * Math.PI * 2;
          const r = 0.10 + Math.random() * 0.26;
          emit(x, y, Math.cos(a) * r, 0.10 + Math.random() * 0.24,
            Math.sin(a) * r, 30 + Math.random() * 22,
            i < n * 0.4 ? look.core : look.halo,
            (1.0 + Math.random() * 0.9) * look.boom);
        }
        return;
      }
      if (look.shape === 'spiral') {
        // Arc crackle: short bright filaments that snap outward on the
        // horizontal, almost no vertical throw.
        for (let i = 0; i < n; i++) {
          const a = Math.random() * Math.PI * 2;
          const r = 0.30 + Math.random() * 0.34;
          emit(x, y, Math.cos(a) * r, 0.02 + Math.random() * 0.05,
            Math.sin(a) * r, 7 + Math.random() * 6,
            i & 1 ? look.core : look.mid, 0.5 * look.boom);
        }
        return;
      }
      const spread = 0.55;
      for (let i = 0; i < n; i++) {
        const hot = i < n * 0.55;
        const sx = (Math.random() - 0.5) * spread;
        const sy = 0.05 + Math.random() * 0.16;
        // +z is up-corridor (away from the squad); most debris goes that
        // way, a little sprays back toward the shooter.
        const sz = (Math.random() < 0.78 ? 1 : -0.45)
          * (0.10 + Math.random() * 0.30) * look.boom;
        emit(x, y, sx * (hot ? 1.6 : 0.7), sy * (hot ? 1.5 : 1),
          sz * (hot ? 1.5 : 0.8),
          hot ? 9 + Math.random() * 7 : 20 + Math.random() * 16,
          hot ? look.core : look.halo,
          (hot ? 0.55 : 1.0) * (0.7 + Math.random() * 0.6) * look.boom);
      }
    },
    kill(x, y, tier, radius, power, overkill = 1, offAxis = 0, instanceIdx = 0, ddx = 0, ddy = 0) {
      // The camera FEELS the kill — one impact's worth, not a quake. D7:
      // at mass-death, the OLD per-kill gain (0.10) plus the old slow
      // decay (see CAM_KICK_DECAY) meant new kills refreshed camKick
      // faster than it could clear, reading as a sustained earthquake
      // rather than discrete pops. Fixed by cutting the GAIN (0.10 ->
      // 0.018) and speeding the DECAY — the ceiling (CAM_KICK_MAX) is
      // unchanged, see its own comment for why that's still correct.
      // Screen-space budget verified via `shakeTrace()`, not asserted.
      camKick = Math.min(CAM_KICK_MAX, camKick + 0.018 * power * Math.min(2.5, overkill));
      const pool = corpseTiers[tier];
      if (tier < 0 || !pool || pool.length === 0) return;
      const i = cCur; cCur = (cCur + 1) % MAX_CORPSES;
      // WHICH DEATH CLIP (M4), chosen from the dying body's OWN pool index
      // so two bodies dying in the same tick do not fall identically —
      // never re-derived from a die roll, which would let two adjacent
      // deaths draw the same variant by chance. Restricted to whichever
      // variants this tier's rig actually has (a tier still on the matrix
      // fallback has none, and that is fine — the physics tumble below
      // does not read `cVariant` at all).
      const hcK = hordes[tier];
      const hcKClips = hordeClips[tier];
      const availV = hcK && hcKClips
        ? DEATH_VARIANTS.filter((n) => hcKClips.has(n)) : [];
      cVariant[i] = availV.length > 0
        ? DEATH_VARIANTS.indexOf(availV[instanceIdx % availV.length]!) : 0;
      cX[i] = (x - arenaW / 2) * SX;
      cY[i] = 0.15;
      cZ[i] = y * S;
      // The silhouette it was standing in when it died. Four instead of
      // one, so two bodies falling together are not the same body twice.
      cPhase[i] = Math.floor(Math.random() * pool.length);

      // WHICH DEATH THIS IS. Chosen from the blow, not from a die roll:
      // how far past lethal the damage went, and how far off the centre
      // line the body was standing. A shell landing square on reads
      // nothing like the last rifle round finding a body already spent.
      const lateral = Math.abs(offAxis);
      const arch = overkill >= 2.2 && power >= 0.9 ? DeathArch.Blown
        : lateral > 0.55 ? DeathArch.SpinOut
          : overkill <= 1.15 ? DeathArch.Crumple
            : DeathArch.Pitch;
      cArch[i] = arch;
      // The blow's own lateral heading wins when there is one; a splash
      // kill carries ddx=ddy=0 and falls back to the old centre-line
      // heuristic (SpinOut's arch test above already reads offAxis, not
      // this -- unchanged).
      const side = Math.abs(ddx) > 0.001 ? (ddx >= 0 ? 1 : -1) : (offAxis >= 0 ? 1 : -1);
      // Additive lean toward the shot's own direction, not a replacement
      // for the per-death randomness `r` already gives every archetype its
      // variety from -- zero for a splash kill, so nothing changes there.
      const dirLean = ddx * 0.6;
      const r = Math.random();

      switch (arch) {
        case DeathArch.Blown:
          // Thrown off its feet and up-corridor, snapped toward the line
          // the shot travelled. The old behaviour, now reserved for the
          // hits that earn it.
          cVx[i] = ((r - 0.5) + dirLean) * 2.4 * power;
          cVy[i] = (2.6 + r * 2.6) * power;
          cVz[i] = (2.8 + r * 3.4) * power;
          cSpin[i] = ((r - 0.5) + dirLean) * 5.5 * Math.min(1.2, power);
          cMax[i] = 1.25 + r * 0.5;
          break;
        case DeathArch.Crumple:
          // The legs go. Almost no travel, a short drop, settles fast --
          // a body folding where it stood. This is most deaths, and it is
          // the one the old single formula could never show.
          cVx[i] = ((r - 0.5) + dirLean * 0.3) * 0.35;
          cVy[i] = 0.5 + r * 0.5;
          cVz[i] = (r - 0.5) * 0.5;
          cSpin[i] = ((r - 0.5) + dirLean * 0.5) * 1.1;
          cMax[i] = 0.95 + r * 0.35;
          break;
        case DeathArch.SpinOut:
          // Caught off the centre line: whipped round its own axis and
          // skidded sideways, low and fast.
          cVx[i] = side * (1.9 + r * 1.8) * power;
          cVy[i] = (1.0 + r * 0.9) * power;
          cVz[i] = (0.7 + r * 1.4) * power;
          cSpin[i] = side * (3.4 + r * 2.6) * Math.min(1.2, power);
          cMax[i] = 1.15 + r * 0.45;
          break;
        default:
          // Killed mid-stride: its own momentum carries it forward onto
          // its face, so it travels DOWN-corridor, against the shot.
          cVx[i] = ((r - 0.5) + dirLean * 0.5) * 0.8;
          cVy[i] = 0.9 + r * 0.8;
          cVz[i] = -(0.6 + r * 1.1);
          cSpin[i] = 1.6 + r * 1.6;
          cMax[i] = 1.05 + r * 0.4;
          break;
      }
      cRot[i] = 0;
      // Where it comes to rest, latched now so the settle has something
      // to ease toward. A heap of bodies all facing one way is the tell
      // that they were placed rather than dropped.
      cYaw[i] = Math.random() * Math.PI * 2;
      // CORPSE PERSISTENCE COVERS THE FALL. The archetype above times a
      // physics tumble that finishes well under two seconds; a mocap death
      // clip does not know about that budget and can run longer. A life
      // shorter than the clip despawns the body mid-fall, which erases the
      // only thing carrying the variety this whole system exists for. The
      // clip's own length — plus a settle beat — is a FLOOR on the life,
      // never a ceiling: the physics-timed archetype value still wins for
      // whichever is longer.
      if (availV.length > 0 && hcK) {
        const dur = hcK.duration(DEATH_VARIANTS[cVariant[i]!]!);
        if (dur > 0) cMax[i] = Math.max(cMax[i]!, dur + 0.15);
      }
      cLife[i] = cMax[i];
      // A DEAD BODY IS THE SAME SIZE AS THE LIVING ONE. Same function the
      // draw loop sizes the living with — see massBodyScale.
      cScale[i] = massBodyScale(radius);
      cTier[i] = tier;
      // IMPACT DEBRIS on every kill (item 4): a few chunks along the same
      // blow direction the corpse itself just launched from.
      emitDebris(x, y, 4, 0x8a8478, ddx, ddy, Math.min(1.6, 0.6 + power * 0.4));
    },
    /**
     * ESCAPED, NOT KILLED (AUDIT A-13). A body that leaks past the squad
     * line used to vanish the instant the sim freed its pool slot -- a
     * direct violation of the physical-plausibility mandate (nothing may
     * disappear mid-frame). Hands it to the same corpse-tier instancing
     * the dead use, but the `Breach` archetype below keeps it upright and
     * moving on its own heading instead of tumbling, so it reads as
     * running off rather than dying.
     */
    breach(x: number, y: number, tier: number, radius: number) {
      const pool = corpseTiers[tier];
      if (tier < 0 || !pool || pool.length === 0) return;
      const i = cCur; cCur = (cCur + 1) % MAX_CORPSES;
      cX[i] = (x - arenaW / 2) * SX;
      cY[i] = 0;
      cZ[i] = y * S;
      cPhase[i] = Math.floor(Math.random() * pool.length);
      cArch[i] = DeathArch.Breach;
      const r = Math.random();
      // Keeps the heading it already had (down-corridor, past the player
      // -- same sign convention as Pitch's "carries its own momentum
      // down onto its face" above), just faster: it is sprinting away,
      // not stumbling. Small lateral wobble so several bodies breaching
      // together don't read as one copy-pasted three times.
      cVx[i] = (r - 0.5) * 0.7;
      cVy[i] = 0;
      cVz[i] = -(2.6 + r * 1.6);
      cSpin[i] = 0;
      cRot[i] = 0;
      cYaw[i] = Math.PI;
      cMax[i] = 0.55;
      cLife[i] = cMax[i];
      cScale[i] = massBodyScale(radius);
      cTier[i] = tier;
    },
    death(x, y, radius, tint, dark, ddx = 0, ddy = 0) {
      const big = radius >= 1.2, mid = radius >= 0.55;
      const n = big ? 26 : mid ? 13 : 7;
      // The shot's own heading, in scene units -- same conversion killActor
      // uses for its knockback direction. A splash kill (ddx=ddy=0) has no
      // line to throw debris along, so it falls back to the old full ring.
      const kx = ddx * SX, kz = ddy * S;
      const klen = Math.hypot(kx, kz);
      const hasBlow = klen > 0.0001;
      const dux = hasBlow ? kx / klen : 0, duz = hasBlow ? kz / klen : 0;
      // Impact point, not body centre: offset toward the side the round
      // came from -- the entry point, roughly the near-side surface.
      const ix = hasBlow ? x - ddx * radius * 0.35 : x;
      const iy = hasBlow ? y - ddy * radius * 0.35 : y;
      for (let i = 0; i < n; i++) {
        let dx: number, dz: number;
        if (hasBlow) {
          // A cone along the shot line (+-126 deg), not a full circle:
          // debris keeps travelling roughly the way the round was, spread
          // enough to still read as a burst rather than a beam.
          const dev = (Math.random() - 0.5) * Math.PI * 1.4;
          const c = Math.cos(dev), sn = Math.sin(dev);
          dx = dux * c - duz * sn; dz = dux * sn + duz * c;
        } else {
          const a = Math.random() * Math.PI * 2;
          dx = Math.cos(a); dz = Math.sin(a);
        }
        const sp = (big ? 0.16 : 0.09) * (0.5 + Math.random());
        emit(ix, iy, dx * sp, 0.09 + Math.random() * 0.09, dz * sp,
          big ? 46 : 26, i % 3 === 0 ? dark : tint, big ? 2.4 : mid ? 1.4 : 0.9);
      }
      // THE WEAPON SIGNS ITS KILLS. Debris is the body's colour; the flash
      // over it is the GUN's -- a tesla kill sparks blue, a mortar kill
      // blooms white-gold, sized by the class's boom. Ten classes finally
      // differ at the exact place the eye watches: the point of impact.
      const look = GUN_LOOKS[lastProfile] ?? DEFAULT_LOOK;
      const k = Math.min(6, 2 + Math.round(look.boom * 1.6));
      for (let i = 0; i < k; i++) {
        const a = Math.random() * Math.PI * 2;
        const sp = 0.05 * look.boom * (0.6 + Math.random());
        emit(x, y + 0.5, Math.cos(a) * sp, 0.10 + Math.random() * 0.06, Math.sin(a) * sp,
          10 + Math.round(look.boom * 5), i % 2 === 0 ? look.core : look.mid,
          0.8 + look.boom * 0.55);
      }
      // Heavy ordnance leaves a shockwave; small arms must not.
      if (look.boom >= 1.8) {
        for (let i = 0; i < 14; i++) {
          const a = (i / 14) * Math.PI * 2;
          emit(x, y, Math.cos(a) * look.boom * 0.05, 0.02, Math.sin(a) * look.boom * 0.05,
            14, look.halo, 0.9);
        }
      }
    },
    burst(x, y, n, tint, power) {
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        emit(x, y, Math.cos(a) * power, 0.08, Math.sin(a) * power,
          24 + Math.random() * 12, tint, 1);
      }
    },
    debris(x, y, n, tint, ddx = 0, ddy = 0, power = 1) {
      emitDebris(x, y, n, tint, ddx, ddy, power);
    },
    ring(x, y, tint, to, life = 22) {
      for (let i = 0; i < 22; i++) {
        const a = (i / 22) * Math.PI * 2;
        emit(x, y, Math.cos(a) * to * 0.04, 0.02, Math.sin(a) * to * 0.04, life, tint, 0.9);
      }
    },
    muzzle(x, y, _angle, scale) {
      // THE MUZZLE IS WHERE A WEAPON IS IDENTIFIED. Every class used to
      // throw the same three-particle puff at a different tint, and a tint
      // is not a difference -- at speed, under bloom, twelve tints read as
      // one gun. Each family now discharges in its own SHAPE.
      const look = GUN_LOOKS[lastProfile] ?? DEFAULT_LOOK;
      const sc = scale * look.flare;
      switch (look.shape) {
        case 'shell': {
          // SHOTGUN / CANNON: a wide, short cone of debris thrown sideways
          // out of the bore, plus a hot core. This is the spread you can
          // see rather than infer from a damage number.
          for (let i = 0; i < 9; i++) {
            const a = (i / 9) * Math.PI * 2;
            emit(x, y + 0.2, Math.cos(a) * 0.14, 0.04 + Math.random() * 0.03,
              0.16 + Math.abs(Math.sin(a)) * 0.05, 8, look.mid, sc * 1.5);
          }
          emit(x, y + 0.2, 0, 0.02, 0.2, 5, look.core, sc * 2.4);
          break;
        }
        case 'lob': {
          // MORTAR: a fat vertical belch of smoke -- the breech venting,
          // not a flash. It hangs, which is what makes it read as heavy.
          // SCALE CUT 2026-08-18 (was 7 particles at sc*2.0): tuned before
          // the weapon's own real cadence (0.13s cooldown, spread_3's
          // multiple simultaneous rounds) was ever exercised. At squad-
          // scale volley counts the old numbers filled the entire shared
          // 420-particle pool (PARTS) in under 1.5s of sustained fire --
          // every muzzle's smoke overlapping into one solid, undecaying
          // mass rather than a puff per shot. Character kept, weight cut.
          for (let i = 0; i < 3; i++) {
            emit(x, y + 0.15, (Math.random() - 0.5) * 0.09,
              0.10 + Math.random() * 0.07, 0.03, 18, look.halo, sc * 0.55);
          }
          break;
        }
        case 'rocket': {
          // MISSILE: backblast. It fires BACKWARD out of the tube, which no
          // other class does and is instantly recognisable.
          for (let i = 0; i < 6; i++) {
            emit(x, y + 0.18, (Math.random() - 0.5) * 0.10, 0.05, -0.20,
              16, look.halo, sc * 1.7);
          }
          break;
        }
        case 'spiral': {
          // TESLA: arcs that jump off the coil sideways, never forward.
          for (let i = 0; i < 5; i++) {
            const a = Math.random() * Math.PI * 2;
            emit(x, y + 0.25, Math.cos(a) * 0.22, Math.sin(a) * 0.18, 0.04,
              7, i % 2 ? look.core : look.mid, sc * 1.1);
          }
          break;
        }
        case 'beam': {
          // ENERGY: almost nothing at the bore -- the beam itself is the
          // light. A big flash here would only fight it.
          emit(x, y + 0.2, 0, 0.01, 0.06, 4, look.core, sc * 0.8);
          break;
        }
        case 'sweep':
        case 'slam': {
          // MELEE: no bore at all. The flash is at the ARC, wide and low.
          for (let i = 0; i < 6; i++) {
            emit(x, y + 0.3, (Math.random() - 0.5) * 0.45, 0.03, 0.10,
              9, look.core, sc * 1.3);
          }
          break;
        }
        default: {
          // KINETIC: a tight forward flash and a hot brass case ejecting to
          // the side. The case is the tell that this is a cased round.
          for (let i = 0; i < 3; i++) {
            emit(x, y + 0.2, (Math.random() - 0.5) * 0.05, 0.03, 0.14,
              6, i === 0 ? look.core : look.mid, sc * 1.4);
          }
          emit(x, y + 0.22, 0.13 + Math.random() * 0.05, 0.07, -0.03,
            14, 0xffc46a, sc * 0.6);
          break;
        }
      }
    },
    tracer() { /* real tracers are instanced geometry, not particles */ },
    label(x, y, text, tint, size, rise = 0.9) {
      const l = labels[lCur]!; lCur = (lCur + 1) % LABELS;
      l.sp.material.map?.dispose();
      l.sp.material.map = labelTexture(text, tint);
      l.sp.material.needsUpdate = true;
      l.sp.visible = true;
      l.sp.scale.set(size / 22, size / 55, 1);
      l.x = (x - arenaW / 2) * SX; l.y = 1.4; l.z = y * S;
      l.life = 46; l.max = 46; l.rise = rise;
    },
  };

  const enemyFlash = new Int32Array(4096);
  /**
   * L2 spawn grow-in: frames remaining of each body's 0.35s birth scale.
   * A 300-body wall materializing full-size mid-view is the physics law
   * broken at scale; grown-in under the fog veil it is a column marching
   * out of the haze.
   */
  const enemyGrow = new Int32Array(4096);
  const growOf = (i: number): number => {
    const g = enemyGrow[i]!;
    if (g <= 0) return 1;
    const k = 1 - g / 21;
    return 0.3 + 0.7 * k * (2 - k);
  };

  // ------------------------------------------------------- hit reactions
  /**
   * WHAT A BODY DOES WHEN IT IS SHOT AND DOES NOT DIE.
   *
   * Before this, the complete reaction vocabulary for the entire horde was
   * two things: `enemyFlash` painted the body white for a few frames, and
   * its scale was multiplied by 1.18. A body absorbing a mortar round and
   * a body clipped by a rifle played the same 1.18. With peak counts over
   * 220 on screen, that is the single largest block of "moves but does not
   * act" in the frame.
   *
   * The reaction CANNOT be a clip. Every mass rig in this game ships
   * exactly one animation — `walk` — so there is no hit pose, no stagger
   * and no death to play (checked in the GLB, not assumed). What there IS,
   * per body per frame, is an instance matrix and a choice of which baked
   * phase to draw. So the reaction lives in position, rotation, scale and
   * pose SELECTION, which is how crowd tiers do it in shipped games
   * anyway.
   *
   * Everything here is presentation. The sim owns where a body is and when
   * it dies; this offsets the body around that, and hands the offset back
   * to zero. Nothing below is read by the sim.
   */
  const REACT_CAP = 4096;
  /** Seconds left in the current reaction. 0 = the body is just walking. */
  const rTime = new Float32Array(REACT_CAP);
  /** Total duration of the current reaction, for a normalised progress. */
  const rDur = new Float32Array(REACT_CAP);
  /** Which tier of reaction: see ReactKind. */
  const rKind = new Int8Array(REACT_CAP);
  /** Unit-ish axis the blow pushed the body along, in scene units. */
  const rAxX = new Float32Array(REACT_CAP), rAxZ = new Float32Array(REACT_CAP);
  /** Per-reaction randomness, latched once so the body does not shimmer. */
  const rSeed = new Float32Array(REACT_CAP);
  /** The phase this body froze on, for reactions that stop the walk. */
  const rHold = new Int8Array(REACT_CAP);
  /**
   * Which of the three `HIT_VARIANTS` this body's CURRENT reaction plays
   * (M4). Set once per landed hit from a rolling counter — never from
   * severity or from the slot index alone — so two bodies hit in the same
   * tick, or the same body hit twice in a row, do not repeat a clip.
   */
  const rVariant = new Int8Array(REACT_CAP);
  let hitVariantSeq = 0;
  /**
   * ALLY-STRUCK REACTION (render-side only, founder-directed). The sim's
   * ally-damage branch (step.ts, the allies section) never emits Ev.Hit
   * the way a player projectile does, so a non-lethal ally hit produced
   * no stagger at all -- only a kill (via the death event) read as
   * anything. Fixing that on the sim side is Lane B's call; this instead
   * detects the damage from data the render layer already has every
   * frame: an hp drop on a body currently inside an alive ally's strike
   * radius. -1 is "unseen this life" so a freshly spawned occupant of a
   * recycled slot never reads as having taken a hit it didn't take.
   */
  const prevEHp = new Float32Array(REACT_CAP).fill(-1);

  /**
   * Three tiers, because a hit that a body shrugs off and a hit that
   * nearly kills it are not the same event and must not look alike.
   *
   * There is deliberately NO full knockdown. A prone body would still be
   * advanced up the corridor by the sim at walking speed — a corpse
   * skating on its back — and holding it still is a change to how fast the
   * horde closes, which is balance. See the note on `enemyHit`.
   */
  const ReactKind = {
    None: 0,
    /** Shrugged off: a snap of the torso, the walk never breaks. */
    Flinch: 1,
    /** Hurt: the walk STOPS, the body leans off the blow and drifts. */
    Stagger: 2,
    /** Nearly killed: down onto a knee, hand to the deck, then up. */
    Reel: 3,
  } as const;
  const REACT_SECS = [0, 0.17, 0.46, 0.92];
  /**
   * Severity thresholds, as a fraction of the health the body had left.
   * Measured against what the blow actually took from THAT body, so a
   * grunt and a brute both react to "half of what I had left" the same
   * way without the renderer needing to know a single balance number.
   */
  const STAGGER_AT = 0.24, REEL_AT = 0.60;

  /**
   * Register a hit on one enemy pool slot.
   *
   * @param slot     the sim's enemy pool index — same index as enemyFlash
   * @param severity damage / (damage + health remaining), so 1 = lethal
   * @param fromX    the shooter's corridor x, for the direction of the blow
   * @param fromY    the shooter's corridor y
   */
  function enemyHit(slot: number, severity: number, fromX: number, fromY: number,
    atX: number, atY: number): void {
    if (slot < 0 || slot >= REACT_CAP) return;
    const kind = severity >= REEL_AT ? ReactKind.Reel
      : severity >= STAGGER_AT ? ReactKind.Stagger : ReactKind.Flinch;
    // A landed blow never DOWNGRADES an in-flight one. Under a dense
    // volley a body is hit several times a second; letting a grazing
    // round cancel a reel is what would turn the whole system back into
    // a shimmer.
    if (rTime[slot]! > 0 && rKind[slot]! > kind) return;
    // Direction of the blow, in scene units, from shooter toward target.
    const dx = (atX - fromX) * SX, dz = (atY - fromY) * S;
    const len = Math.hypot(dx, dz) || 1;
    rAxX[slot] = dx / len;
    rAxZ[slot] = dz / len;
    rKind[slot] = kind;
    // THE REACTION LASTS AS LONG AS THE ANIMATION DOES. Timing it with a
    // hand-picked constant while a real clip plays underneath is how a
    // body snaps back to walking mid-stagger. The constant survives only
    // as the fallback for the baked-pose path, which has no clip to time
    // against.
    // If the struck body's rig HAS a matching impact clip, the reaction
    // lasts exactly as long as that clip; otherwise it falls back to the
    // constant, which is what times the matrix-expressed reaction on rigs
    // that ship only a walk cycle. Which of the three variants actually
    // plays is chosen per-instance at draw time (M4), not here — so the
    // hold is timed against the LONGEST of the three, never the shortest,
    // or a body could snap back to walking mid-clip.
    const clipSecs = Math.max(0, ...hordes.flatMap(
      (h) => HIT_VARIANTS.map((n) => h?.duration(n) ?? 0)));
    const secs = clipSecs > 0.05 ? clipSecs : REACT_SECS[kind]!;
    rDur[slot] = secs;
    rTime[slot] = secs;
    rSeed[slot] = Math.random();
    rHold[slot] = -1;                    // latched on the first drawn frame
    rVariant[slot] = hitVariantSeq++ % HIT_VARIANTS.length;
  }

  /**
   * Start an elite/boss actor's death beat. Called once per Ev.Death whose
   * defIdx is an actor tier (>2) -- the instanced tiers use `kill()`
   * instead and never reach here.
   *
   * @param isBoss per-spawn boss-ness (s.eIsBoss===1), same test the live
   *   claim loop uses -- def.isBoss alone misses Rift Brute/Gale Colossus.
   * @param simX,simY corridor position at the moment of death (still valid:
   *   poolFree does not clear the position arrays, only the alive flag).
   * @param ddx,ddy the blow's unit direction, straight from Ev.Death.
   * @param radius the enemy def's radius -- same scale formula the living
   *   actor claim uses, so the body does not resize at the moment it dies.
   * @param overkill the killing blow's overkill (damage / health it had
   *   left) -- sizes SNAP_FRAC, below.
   */
  function killActor(
    defIdx: number, isBoss: boolean, simX: number, simY: number,
    ddx: number, ddy: number, radius: number, trueBoss: boolean,
    overkill = 1,
  ): void {
    const poolIdx = isBoss || defIdx === 4 ? 1 : 0;
    const pool = actorPools[poolIdx];
    const actor = pool?.find((p) => !p.busy);
    // No free actor (pool exhausted, or already holding a death) -- the
    // body still vanishes on the general sweep, same as before this
    // existed. Degrading silently here is correct: a missed death beat is
    // a smaller defect than a body that never releases its actor.
    if (!actor) return;
    actor.busy = true;
    const x0 = (simX - arenaW / 2) * SX, z0 = simY * S;
    const kx = ddx * SX, kz = ddy * S;
    const klen = Math.hypot(kx, kz);
    // A splash kill carries no blow direction (ddx=ddy=0) -- fall straight
    // rather than toward a direction that does not exist.
    const kux = klen > 0.0001 ? kx / klen : 0;
    const kuz = klen > 0.0001 ? kz / klen : 1;
    // defIdx===4 is gale_colossus -- same magic index the pool-selection
    // line above already relies on (enemies.csv order is global, not
    // per-chapter, so this is stable across every chapter that spawns it).
    const isBeast = defIdx === 4;
    const sc = SOLDIER_SCALE * (trueBoss ? TRUE_BOSS_SCALE * TRUE_BOSS_VISUAL_MULT : isBeast ? BEAST_SCALE : isBoss ? BOSS_SCALE : 1.05 * (radius / 1.05) * FODDER_VISUAL_BULK);
    // THE BOSS BECOMES A SPECTACLE: the numeral does not survive into the
    // death beat (it reads HP, and this body has none left) or the shell
    // tint (the explosion in main.ts's Ev.Death handler is the read now).
    if (actor.dmgState !== -1) {
      actor.dmgState = -1;
      actor.hpSprite.visible = false;
      for (const bm of actor.bodyMats) bm.mat.color.copy(bm.color0);
    }
    // SNAP, THEN FALL. Frame 0 used to render the body dead upright at its
    // rest pose (slideU/rotation both start at 0), carrying nothing from
    // the blow that killed it for the first ~160ms -- the same "starts
    // from standing" defect `kill()` above had, on the actor path. A harder
    // kill snaps a bigger fraction of the knockback and lean in instantly;
    // the rest still eases in over the hold, same as before.
    const snapFrac = Math.min(0.55, 0.16 + 0.14 * Math.min(2.5, overkill));
    dyingActors.push({
      actor, t: 0, x0, z0, sc, kux, kuz, snapFrac,
      fallSign: Math.random() < 0.5 ? 1 : -1,
    });
  }

  // ----------------------------------------------------------------- loop
  const tmpM = new Matrix4();
  const tmpQ = new Quaternion();
  const tmpV = new Vector3();
  const tmpColor = new Color();
  // NO TINT (founder directive, 2026-08-30): the atmospheric depth haze
  // this used to carry (a pale distance tint on enemy bodies) is removed
  // along with every other per-instance colour. "Spatial real depth" from
  // the original ask still stands as an open item -- it needs a non-tint
  // answer (e.g. real fog geometry/opacity, not a colour multiply) if it
  // gets picked back up.
  const tmpS = new Vector3();
  /** Reused for the mortar's arc-tangent orientation (setFromUnitVectors). */
  const tmpDir = new Vector3();
  const UNIT_Y = new Vector3(0, 1, 0);
  /**
   * Reused rather than allocated. The enemy loop built `new Euler(...)`
   * once per body per frame — 220-odd allocations a frame at peak, all
   * identical and all garbage.
   */
  const tmpE = new Euler();
  const HIDDEN = new Vector3(0, -9999, 0);
  const enemyColor = new Color();

  /**
   * Inside edge of the kerb, in scene units. ONE definition, used by both
   * the reacting living and the corpses in flight — two separate 0.85s in
   * two loops is how a body ends up bouncing off a rail that the living
   * walk straight through.
   */
  const railHalfX = (arenaW / 2) * SX - 0.85;
  /**
   * Worst overhang, in scene units, that a REACTION has put past the kerb
   * this run. Exposed on the debug probe so the audit harness can assert
   * the rail law on the rendered body instead of the frame being argued
   * over. It should never leave zero.
   */
  let reactOverhang = 0;
  /** Damped lateral lead, so the camera arrives at a lane change late. */
  let camLead = 0;
  /**
   * SQUAD ANIMATION STATE MACHINE (M1), keyed off the C3 event bus.
   *
   * Replaces `locoState ? 'run' : 'shoot'` plus a 0.25s hysteresis, which was
   * a movement heuristic: the renderer inferring what happened instead of
   * being told. Now it is told.
   *
   * The split follows the contract rather than fighting it. EVENT_VOCABULARY
   * records that `idle` and `fire` are deliberately NOT evented — they are
   * continuous states derived from sim state, not discrete transitions — so
   * the base state still reads movement, while every TRANSIENT state is
   * driven by exactly one event type and nothing else:
   *
   *   Ev.SquadHit -> 'hit'    Ev.Death (squad) -> 'die'
   *
   * A transient outranks the base until its hold expires, and the crossfade
   * runs on `push(m, rowA, rowB, blend)` — the two-row blend the crowd rig
   * already supported and nothing was using.
   */
  let locoWant = false, locoState = false, locoHold = 0, strideRate = 0.85;
  /** Current and outgoing clip, with the crossfade between them. */
  let fsmState = 'shoot', fsmPrev = 'shoot', fsmFade = 1;
  /** Seconds left on a transient (hit/die) before the base state returns. */
  let fsmHold = 0;
  /** M1: 120-200ms. 150ms sits mid-band and is one shared value. */
  const CROSSFADE_S = 0.15;
  /** How long a flinch owns the body. Three baked frames at BAKE_FPS 30. */
  const HIT_HOLD_S = 0.24;
  function fsmEnter(next: string, holdS: number): void {
    if (next === fsmState) { fsmHold = Math.max(fsmHold, holdS); return; }
    fsmPrev = fsmState; fsmState = next; fsmFade = 0; fsmHold = holdS;
  }
  /** Impact push-back, decayed every frame and topped up by big hits. */
  let camKick = 0;
  /**
   * M7 EVENT PUNCH (ANIMATION_DOCTRINE.md, spec'd -- "zoom punch on titan
   * entry and on each Colossus stage" -- never built until now). A
   * SEPARATE accumulator from camKick on purpose: camKick is tuned for
   * frequent small per-kill nudges (low ceiling, fast decay); a titan
   * reveal or a monolith breaking is a rare, bigger beat that deserves
   * its own headroom rather than fighting camKick's cap. Same
   * mechanism (decaying scalar feeding camera position), reused not
   * reinvented -- see the camera.position lines below.
   */
  let eventPunch = 0;
  const EVENT_PUNCH_MAX = 2.2;
  // I9 near-death feedback (setDanger below); juice.ts already smooths
  // and decays both values, so these are a direct passthrough onto
  // gradePass's uniforms, read once per frame in render().
  let dangerVignette = 0;
  let dangerDesaturate = 0;
  const EVENT_PUNCH_DECAY = 5.2; // slower than CAM_KICK_DECAY -- a beat, not a tap
  /** D7 shake trace — see the render() block that fills this. */
  const shakeTrace = new Float32Array(300);
  let shakeTraceCur = 0;
  /**
   * DYNAMIC FRAMING (founder, 2026-08-19). Presentation only — render-side
   * state, read no differently than camLead/camKick/breath above, so M10
   * (all motion is render-layer, hash trail identical on/off) holds by
   * construction: nothing here is ever written back into RunState.
   *
   * Growth felt in the frame, not just counted in the HUD: camera distance
   * pulls back as the squad grows, diminishing (sqrt, not linear) so early
   * growth reads big and late growth doesn't keep pulling the frame out
   * forever. Damped, not stepped, so a sudden squad jump (a body reward)
   * doesn't snap the frame.
   */
  let camSquadPull = 0;
  /**
   * The boss changes the shot while the chapter's own boss (config isBoss,
   * never a mid-run beast — same distinction the frame-time and boss-death
   * fixes both had to make) is alive and within range. Damped both ways,
   * asymmetrically — faster IN (the beat should read as a cut, not a
   * drift) than OUT (founder: "return smoothly"). CORRECTED COMMENT
   * (2026-09-01): this used to say "lower and closer" -- stale against the
   * live values (CAM_BOSS_HEIGHT_RAISE/CAM_BOSS_BACK_PULL are both
   * positive, added to CAM_HEIGHT/CAM_BACK), which actually pull the
   * camera HIGHER and FURTHER for a wide overview of the whole fight, not
   * a close dramatic angle. Verified against the formula, not the old
   * comment, before writing the reveal pulse below.
   */
  let camBossT = 0;
  /**
   * BOSS REVEAL PULSE (spectacle pass, 2026-09-01). `camBossT` above is a
   * deliberate, founder-tuned WIDE shot for playing the actual fight
   * (higher/further, so the whole encounter stays legible) -- not fought
   * here, only preceded. This is a separate, one-shot pulse that fires
   * the instant `bossNear` first flips true, pulling the camera LOWER and
   * CLOSER for a brief dramatic beat before camBossT's own wide blend
   * settles in, giving a "looks up at something huge, then pulls back to
   * fight it" read instead of jumping straight to the overview. Additive
   * on dynHeight/dynBack, decays on its own; never overrides camBossT.
   */
  let bossRevealT = 0;
  let lastBossNear = false;
  /** ISLE-COMPLETE BEAT blend, see `CAM_ISLE_COMPLETE_BACK_PULL`'s comment. */
  let camIsleCompleteT = 0;
  /**
   * Tracer tip/tail diagnostic (2026-08-18) — filled by the bolt loop
   * each frame, read on demand by `window.__scene.tracerDiag()`. Exists
   * because "tip at pY" and "tail never behind the muzzle" are per-bolt
   * invariants a screenshot can't prove; this lets a harness check every
   * live bolt's actual drawn endpoints against the sim's own `s.pY`.
   */
  let lastMuzzleY = 0;
  const lastBoltDiag: { tip: number; tail: number; muzzleY: number; originX: number }[] = [];
  const shakeRefV = new Vector3(), shakeNeutralV = new Vector3(), shakeRealV = new Vector3();
  /** Same, for corpses in flight. */
  let corpseOverhang = 0;

  function resize(): void {
    const w = host.clientWidth, h = host.clientHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / Math.max(1, h);
    camera.updateProjectionMatrix();
    // The composer owns a render target sized to the canvas. Missing this
    // is how post-processing ends up stretched after a rotation.
    finalComposer.setSize(w, h);
    // QUARTER RESOLUTION (measured, not assumed): the bloom target used to
    // match the canvas 1:1, and `pnpm run perf` (900x1400 headless, max
    // density) went from a pre-existing baseline of p99 106.4ms to p99
    // 164.2ms against its 90ms software-rasterization gate -- almost
    // entirely UnrealBloomPass's own internal mip-chain blur passes
    // running at full canvas resolution. Bloom is a soft blur by
    // construction; it does not need source resolution. Measured a further
    // step down to 1/6 against 1/4: no measurable improvement (p99 122.3ms
    // vs 120.6ms, inside run-to-run noise) -- the remaining cost is a
    // second composer's fixed per-frame overhead, not pixel count, so 1/4
    // is kept for the better bloom quality at the same cost. Note
    // `pnpm run perf`'s gate was ALREADY FAILING on this machine before
    // this task touched the file (baseline 106.4ms > its own 90ms gate) --
    // see the deploy report for that pre-existing finding, which this task
    // did not cause and is out of scope to fix.
    const bw = Math.max(1, Math.round(w / 4)), bh = Math.max(1, Math.round(h / 4));
    bloomComposer.setSize(bw, bh);
    bloomPass.setSize(bw, bh);
    // Backdrop plates are sized from fov/aspect (DoD A#2: full coverage,
    // no gap, at every aspect) — same hook that updates the camera's own
    // aspect, so the two can never drift out of sync.
    skyWorld.resize(camera.aspect, camera.fov);
  }
  resize();

  /** Snapshot of `renderer.info` taken once per logical frame — see the
   * note beside `finalComposer.render()` for why a live external read
   * of `renderer.info` doesn't work with a multi-pass composer chain. */
  const lastFrameInfo = { calls: 0, triangles: 0 };
  /** D1 diagnostic: the lookAt target actually used this frame — three.js
   * does not retain it, so it has to be captured at the call site. */
  const lastLookAt = { x: 0, y: 0, z: 0 };

  function render(s: RunState, dtMs: number, lean: number, alpha: number, prev: TickSnapshot): void {
    // Manual reset (see the note beside `renderer.info.autoReset` above):
    // one reset per logical frame, so the snapshot taken after
    // `finalComposer.render()` accumulates every internal pass instead
    // of only the last one.
    renderer.info.reset();
    const dt = dtMs / 1000;
    // FIXED-TIMESTEP INTERPOLATION (fix C, 2026-09-05): `alpha` is
    // already clamped to [0,1] by the caller; no extrapolation even if
    // the accumulator overshoots. `rPlayerX`/`rPlayerY` feed `px`/`pz`
    // below, which is EVERY squad-position-derived visual in this
    // function (formation layout, camera aim, key/fill lights, the
    // shield-aura position) -- interpolating the definition once here
    // carries through to all of them together, exactly the "both or
    // neither" scope. Every OTHER direct `s.playerX`/`s.playerY` read
    // in this file (enemy-reach checks, muzzle/hit-fx positions) is
    // relative math against non-interpolated enemy/projectile state and
    // stays on raw `s.*` -- interpolating one side of a same-frame
    // comparison would misalign it, not smooth it.
    const rPlayerX = prev.playerX + (s.playerX - prev.playerX) * alpha;
    const rPlayerY = prev.playerY + (s.playerY - prev.playerY) * alpha;
    const rWorldY = prev.worldY + (s.worldY - prev.worldY) * alpha;
    const px = (rPlayerX - arenaW / 2) * SX;
    const pz = rPlayerY * S;

    // LIVE CAMERA FOV (founder, 2026-09-05, ?tune=1 panel): the only one
    // of the four camera constants that isn't read fresh every frame
    // elsewhere already (dynBack/dynHeight/lookAtZ below read
    // tuning.camBack/camHeight/camLookAtForward directly) -- FOV lives on
    // the PerspectiveCamera object itself, set once at construction, so
    // a live edit needs this written + the projection matrix rebuilt
    // every frame. Cheap (a few matrix ops) and a no-op whenever the
    // slider hasn't moved from its default (matches CAM_FOV exactly).
    if (camera.fov !== tuning.camFov) {
      camera.fov = tuning.camFov;
      camera.updateProjectionMatrix();
    }

    // --- squad footprint, computed BEFORE the camera (D1) --------------
    // The camera needs to aim at where the formation actually IS, not at
    // the player's raw position: `cx` below can differ from `px` near the
    // deck edges (the formation's footprint is clamped onto the deck, the
    // player's raw x is not), and a camera that tracks `px` instead of
    // `cx` drifts off-centre exactly there — which is where D1's
    // horizontal symptom was measured. Computed once, read by the camera
    // AND by the rank-laying loops below, so there is one definition of
    // "where the squad's footprint sits" for the whole frame.
    // The WHOLE squad is laid out, not just the skinned pool: `drawn` is
    // what the player sees, `shown` is how much of it gets a skeleton.
    const drawn = Math.min(s.squadSize, MAX_DRAWN);
    const shown = Math.min(drawn, soldiers.length);
    const cols = Math.max(3, Math.min(16, Math.ceil(Math.sqrt(drawn * 1.6))));
    const rows = Math.ceil(drawn / cols);
    // KEEP THE FORMATION ON THE BRIDGE. Only the formation CENTRE was
    // clamped (by the sim, to the corridor), so at the rail the outer
    // files hung over the edge and appeared to stand on air. Two industry
    //-standard moves: compress file spacing when the crowd is wide, and
    // clamp the whole FOOTPRINT to the deck, not its centre.
    const deckHalf = (arenaW / 2) * SX - 0.9;
    // FORMATION PITCH, DERIVED FROM THE BODY'S ACTUAL SIZE.
    //
    // FILE_GAP and RANK_GAP are expressed in SOLDIER-WIDTHS, so the pitch
    // has to be multiplied by every factor that scales the body — and it
    // was only being multiplied by SOLDIER_SCALE. The body is scaled by
    // SOLDIER_SCALE * rigFit, and laterally by SOLDIER_BULK on top of
    // that, so the ranks were short by rigFit * SOLDIER_BULK across and
    // rigFit deep. The soldiers grew and the grid they stand on did not,
    // so they walked into each other: bodies interpenetrating bodies,
    // which is Iron Rule 8 and which reads as the squad multiplying into
    // a pile rather than forming up.
    // CROWD AUTOSCALE (founder 6c.3): past 8 men the bodies ease down to
    // ~0.78x at the 20 cap, and the formation pitch shrinks WITH them
    // (pitch in soldier-widths — Iron Rule 8: a grid that does not track
    // the body is how soldiers interpenetrate). The squad grows in power
    // and presence, not in footprint.
    const crowdScale = drawn <= 8 ? 1 : 1 - 0.22 * Math.min(1, (drawn - 8) / 12);
    const fileStep = FILE_GAP * SOLDIER_SCALE * SQUAD_MULT * crowdScale * rigFit * SOLDIER_BULK * S;
    const rankStep2 = RANK_GAP * SOLDIER_SCALE * SQUAD_MULT * crowdScale * rigFit * S;
    const naturalHalf = ((cols - 1) / 2) * fileStep;
    const squeeze = naturalHalf > deckHalf * 0.62
      ? (deckHalf * 0.62) / naturalHalf : 1;
    const formHalf = naturalHalf * squeeze;
    const cx = Math.max(-deckHalf + formHalf, Math.min(deckHalf - formHalf, px));
    // FORMATION ANCHORED BY ITS CENTRE, NOT ITS FRONT RANK (D1).
    //
    // Ranks laid out backwards from `pz` grow the formation's depth toward
    // the camera as the squad does: at squad 8+ the rear (reserve) ranks
    // are close enough to the camera that their downward viewing angle
    // exceeds the frustum's bottom edge and they read as clipped off the
    // bottom of the frame — worse with every additional rank. Anchoring on
    // the MIDDLE rank instead keeps total depth centred on `pz`, so growth
    // expands symmetrically and the closest rank stays inside frame with
    // ground visible under it.
    const totalRows = Math.ceil(drawn / cols);
    const zAnchor = pz + ((totalRows - 1) * rankStep2) * 0.5;

    // Footprint ring: radius from whichever axis of the formation is
    // larger (width or depth), plus a small margin so the ring reads as
    // enclosing the squad rather than clipping its outer files.
    footprintRing.visible = drawn > 0;
    if (drawn > 0) {
      const halfDepth = (totalRows * rankStep2) * 0.5;
      const footR = Math.max(formHalf, halfDepth, 0.6) * 1.22;
      footprintRing.position.set(cx, 0.03, zAnchor);
      footprintRing.scale.setScalar(footR);
      // CAGE FLASH: squad size growing (any source -- recruit pickup,
      // barrier payout, THE DIAL) re-triggers a short, bright pulse on
      // the same footprint. `prevSquadSize` starts at -1 so a fresh run
      // never fires one on its own initial squad_start.
      if (prevSquadSize >= 0 && s.squadSize > prevSquadSize) cageFlashT = 0.4;
      prevSquadSize = s.squadSize;
      if (cageFlashT > 0) {
        cageFlashT = Math.max(0, cageFlashT - dt);
        cageRing.visible = true;
        cageRing.position.set(cx, 0.04, zAnchor);
        // Pulses slightly larger than the steady-state footprint ring so
        // it reads as "expanding to meet the new shape," not a duplicate
        // of the ring that's already there every frame.
        cageRing.scale.setScalar(footR * (1.08 + (0.4 - cageFlashT) * 0.35));
        (cageRing.material as MeshBasicMaterial).opacity = (cageFlashT / 0.4) * 0.85;
      } else {
        cageRing.visible = false;
      }
    }

    // Chase camera: behind and above, looking down the corridor.
    // Higher and further back than a chase cam. The horde IS the game, so
    // the frame has to show it arriving from the far end of the corridor
    // rather than only the moment it reaches you.
    // Lower and closer than a map view, pitched down the corridor: the
    // squad sits near the bottom of frame with the road running away from
    // it, which is what gives the shot its depth.
    // Closer and lower: the fight fills the frame instead of sitting in
    // the middle distance. Depth still reads because the spawn line stays
    // inside the fog band.
    // Low and close. Toon characters are squat: from a high camera they
    // read as domes -- the tops of their own heads. Dropping the eye to
    // just above squad height puts FACES and silhouettes on screen, which
    // is where all the character in these models lives.
    // Dropped and pulled back with the wider deck: at the old height the
    // extra width fell outside the frustum, so widening the bridge would
    // have cropped its own rails.
    // Raised and pulled back, looking further down the corridor: the
    // reference ads shoot from ~35-40 degrees so the whole horde COLUMN is
    // on screen at once -- the threat reads as a river, not a front line.
    // The old low eye (4.0 looking at +15) framed faces beautifully and
    // showed forty metres of empty road; the run's actual story -- what is
    // coming and what stands between -- happened off screen.
    // Raised and pulled back: at 5.8 units the camera sat almost level
    // with the squad, so the corridor foreshortened into a wall and the
    // lanes stopped reading as separate places to stand -- which is the
    // whole decision the game is built on.
    // THE REFERENCE ANGLE. Those frames look steeply DOWN a long road: the
    // camera is high and close, the horizon sits in the top third, and the
    // corridor runs away to a vanishing point. Ours sat low and looked
    // nearly flat along the deck, which foreshortens the road into a wall
    // and is why the depth never read.
    // A CAMERA THAT REACTS. A rig welded to the squad's position renders a
    // technically correct frame with no weight in it: nothing anticipates,
    // nothing settles, and every moment of the run photographs the same.
    // Three things, all damped so none of them reads as a wobble.
    //
    // LEAD: the frame drifts the way the squad is going, and arrives there
    // slightly late, so a lane change is something the camera answers
    // rather than something it was already pointing at.
    camLead += (lean - camLead) * Math.min(1, dt * 3.4);
    // BREATH: a slow, tiny drift on two incommensurable periods, so the
    // frame is never mechanically still and never visibly cycles.
    const breath = Math.sin(strideClock * 0.37) * 0.055
      + Math.sin(strideClock * 0.71 + 1.3) * 0.03;
    // IMPACT: heavy hits push the camera back along its own axis and let
    // it spring home. Fed by the same events that drive the shake, decayed
    // here so a burst of kills accumulates instead of restarting.
    //
    // D7: rate 7.5 left ~32% of peak still present 150ms after the last
    // kill (e^(-0.15*7.5)=0.32) — at mass-death, where kills land faster
    // than that, the value never actually clears and reads as continuous
    // shake rather than discrete pops. CAM_KICK_DECAY is chosen so under
    // 1% of peak remains at 150ms (e^(-0.15*30.7)=0.0099).
    camKick *= Math.exp(-dt * CAM_KICK_DECAY);
    eventPunch *= Math.exp(-dt * EVENT_PUNCH_DECAY);

    // GROWTH, FELT: distance pulls back with squad size, diminishing
    // (sqrt — the first few bodies move the frame, the last few barely
    // do) and damped, so a body-reward's instant squadSize jump doesn't
    // snap the shot.
    {
      const squadFrac = Math.min(1, Math.max(0, (drawn - 1) / (CAM_SQUAD_PULL_REF - 1)));
      const squadPullTarget = Math.sqrt(squadFrac) * tuning.dynBackMax;
      camSquadPull += (squadPullTarget - camSquadPull) * Math.min(1, dt * 1.0);
    }
    // THE BOSS CHANGES THE SHOT: higher and further back while the
    // CHAPTER boss (config isBoss — never a mid-run beast; the same
    // distinction the boss-death and frame-time work both had to draw,
    // since s.eIsBoss alone conflates the two) is alive and within
    // CAM_BOSS_NEAR_DY. Inverted from the first pass (founder, after
    // docs/reference/zw-shooting/): that reference's boss/big-ability
    // beats pull the camera UP and BACK, steeper toward top-down, not
    // down and in. Faster in than out: a cut reads as a beat, a drift
    // reads as a fade.
    //
    // OUT-RATE RETUNED (founder, 2026-08-19): raising CAM_BOSS_HEIGHT_RAISE
    // to 18.0 for the floor-exemption pass (see FRAME_SPEC.md §12) made the
    // old out-rate (0.8) miss the "return within 3s" DoD -- an exponential
    // decay's settling time to a fixed absolute threshold doesn't scale
    // with amplitude, so the same rate that felt smooth at raise=1.0 took
    // 4.74s to clear the horizon floor at raise=18.0. Measured (Playwright,
    // forced boss death, horizonY01 polled every 50ms): 0.8 -> 4.74s,
    // 1.6 -> 2.38s. Shipped 1.6, verified under the 3s DoD with margin.
    {
      let bossNear = false;
      const bi = s.bossIdx;
      if (bi >= 0 && s.ep.alive[bi] && s.config.enemies[s.eDefIdx[bi]]!.isBoss) {
        bossNear = (s.eY[bi]! - s.playerY) < CAM_BOSS_NEAR_DY;
      }
      const bossTarget = bossNear ? 1 : 0;
      camBossT += (bossTarget - camBossT) * Math.min(1, dt * (bossNear ? 2.4 : 1.6));
      // BOSS REVEAL PULSE: fire once, exactly on the false->true edge, not
      // every frame bossNear stays true (which would just re-trigger a
      // full-strength pulse continuously and never let it read as a
      // single beat).
      if (bossNear && !lastBossNear) bossRevealT = 1;
      lastBossNear = bossNear;
      bossRevealT = Math.max(0, bossRevealT - dt * BOSS_REVEAL_DECAY_PER_SEC);
    }
    // ISLE-COMPLETE BEAT: see `CAM_ISLE_COMPLETE_BACK_PULL`'s doc comment.
    // Reads the same Ended+BossKilled state `main.ts` already holds the
    // scene on, no second signal. Slow in (0.55, a settle) so it never
    // reads as a cut the way the boss-blend's own 2.4 does; a touch
    // faster out (1.4) so a fresh run's reset is not sluggish if the
    // hard `resetForNewRun()` zero somehow isn't reached first.
    {
      const complete = s.phase === Phase.Ended && s.outcome === Outcome.BossKilled;
      const target = complete ? 1 : 0;
      camIsleCompleteT += (target - camIsleCompleteT) * Math.min(1, dt * (complete ? 0.55 : 1.4));
    }
    const dynBack = tuning.camBack + camSquadPull + camBossT * CAM_BOSS_BACK_PULL
      + camIsleCompleteT * CAM_ISLE_COMPLETE_BACK_PULL
      - bossRevealT * BOSS_REVEAL_BACK_PULL_IN;
    const dynHeight = tuning.camHeight + camBossT * CAM_BOSS_HEIGHT_RAISE
      + camIsleCompleteT * CAM_ISLE_COMPLETE_HEIGHT_RAISE
      - bossRevealT * BOSS_REVEAL_HEIGHT_DIP;

    // THE STUDIED-ADS FRAME (founder directive 6c.1, 2026-08-14): the
    // reference corridor ads shoot LOW and CLOSE — the squad fills the
    // bottom fifth as readable individuals, the road is a tight V with
    // the vanishing point in the upper third, and the horde is met, not
    // surveyed. 11.5/-10.4/+27 was a drone shot; 8.2/-7.6/+17 is a
    // shoulder. Verified against captured frames, with the world depth
    // cut 90->58 in the same package so enemies enter close.
    // Dropped and aimed deeper (founder: camera angle and depth,
    // surgically): the eye sits lower over the squad's shoulder and looks
    // further down the road, so the corridor's V drives the frame and the
    // world band (sea, isles, sky) widens — depth from composition, not
    // from fog. Verified against frames: squad in the bottom fifth,
    // vanishing point in the upper third.
    // D7 SHAKE TRACE, measured not asserted. Screen-space displacement
    // caused BY camKick alone, decoupled from player movement/breath/lean:
    // project the same reference point through the neutral (camKick=0)
    // camera and the real one, in the SAME frame, and record the
    // screen01 distance between them. Two `updateMatrixWorld` calls, one
    // reference-point projection each — camKick is usually 0 (most
    // frames have no kill), so this is cheap in the common case and the
    // cost is worth a claim that's checkable instead of asserted.
    // D1: height/back-distance/pitch used to be FROZEN per founder
    // instruction; now they carry the squad-pull/boss-shot deltas above,
    // read as `dynHeight`/`dynBack` rather than the raw constants — same
    // discipline as before, just two named numbers instead of none. Three
    // consumers (the shake-trace neutral camera, the shake-trace real
    // camera, and the real render camera) all read the same
    // `dynHeight`/`dynBack`, so there is no risk of the three drifting
    // apart the way D1's own camera position/lookAt once drifted from its
    // shake-trace shadow.
    const lookAtX = cx * 0.97 + camLead * 2.1;
    const lookAtY = 0.2 + breath * 0.5;
    const lookAtZ = pz + tuning.camLookAtForward;

    {
      shakeRefV.set(cx, 0.3, pz);
      camera.position.set(cx * 1.0 + camLead * 1.05, dynHeight + breath, pz - dynBack);
      camera.lookAt(lookAtX, lookAtY, lookAtZ);
      camera.updateMatrixWorld(true);
      shakeNeutralV.copy(shakeRefV).project(camera);
      camera.position.set(cx * 1.0 + camLead * 1.05,
        dynHeight + breath + camKick * 0.30 + eventPunch * 0.30,
        pz - dynBack - camKick * 0.55 - eventPunch * 0.55);
      camera.lookAt(lookAtX, lookAtY, lookAtZ);
      camera.updateMatrixWorld(true);
      shakeRealV.copy(shakeRefV).project(camera);
      const dx = ((shakeRealV.x - shakeNeutralV.x) / 2) * camera.aspect;
      const dy = (shakeRealV.y - shakeNeutralV.y) / 2;
      shakeTrace[shakeTraceCur] = Math.hypot(dx, dy);
      shakeTraceCur = (shakeTraceCur + 1) % shakeTrace.length;
    }

    camera.position.set(
      cx * 1.0 + camLead * 1.05,
      dynHeight + breath + camKick * 0.30 + eventPunch * 0.30,
      pz - dynBack - camKick * 0.55 - eventPunch * 0.55);
    lastLookAt.x = lookAtX;
    lastLookAt.y = lookAtY;
    lastLookAt.z = lookAtZ;
    camera.lookAt(lastLookAt.x, lastLookAt.y, lastLookAt.z);
    // Roll into the lead. A few tenths of a degree is below conscious
    // notice and is most of what separates a camera from a tripod.
    //
    // rotateZ, NOT `rotation.z = ...`. This camera looks down +z, and
    // three's XYZ Euler decomposition of that orientation carries z ~= PI.
    // Assigning rotation.z absolutely therefore does not add a small roll
    // — it DELETES a half-turn, and the entire game renders upside down.
    // rotateZ multiplies the roll into the quaternion lookAt just set, in
    // the camera's own frame, which is what "roll the camera" means.
    camera.rotateZ(-camLead * 0.035);

    // World motion: cloud drift, sea swell, isle bob, wheeling gulls. The
    // sky dome rides with the camera (`createWorld`'s own `update`) so its
    // horizon never walks away as the camera tracks the squad.
    //
    // BACKDROP PARALLAX REVERTED (founder, 2026-09-05): Stage 1 briefly
    // drove near/mid/far backdrop-layer drift from `s.worldY` here.
    // Reverted -- 5.25 units of lateral offset on plates at distance
    // 150-270 subtends about 2 degrees over a 5s clip (below perception
    // by construction, at any rate), and what little IS visible reads as
    // the background strafing sideways, not the world moving forward.
    // `update()` is back to camera-locked plates only, its pre-Stage-1
    // 7-arg signature.
    skyWorld.update(
      dt, camera.position.x, camera.position.y, camera.position.z,
      lastLookAt.x, lastLookAt.y, lastLookAt.z);
    runFog.density = tuning.fogDensity;

    // GROUND SCROLL (Stage 1, 2026-09-05): the deck geometry itself never
    // moves (squad holds its ground, see STRIDE_M's comment above) -- UV
    // offset on the already-seamless-tiling deck textures is what fakes
    // the ground moving under it. Both maps share one offset so the
    // normal detail stays registered with the albedo speckle it was
    // generated from. Reads `rWorldY` (interpolated, fix C) rather than
    // `s.worldY` directly -- this is the scroll that was quantizing to
    // tick cadence and reading as judder.
    const groundOffset = (rWorldY * tuning.groundScrollRate) % 1;
    deckAlbedo.offset.y = groundOffset;
    deckNormal.offset.y = groundOffset;

    // PIER RECYCLE (Stage 1, 2026-09-05): each of the live `pierCount*2`
    // instances slides toward the camera with run distance and wraps
    // back out ahead once it passes PIER_WRAP_FLOOR (behind the camera in
    // every camera state this file computes -- see that constant's own
    // comment for why 15, the original floor, was a visible-pop stutter
    // and -15 isn't) -- "despawn behind, spawn ahead" is this single
    // modulo, not a separate pool. Count and spacing are
    // `tuning.pierCount`/`tuning.propSpawnDistance`, read fresh every
    // frame so the founder's live sliders actually move something
    // without a rebuild. `pierCount` is clamped to PIER_MAX_COUNT (the
    // buffer's real capacity, scene3d.ts's own construction comment
    // above); any instance beyond the live count is zero-scaled, not
    // left at a stale position.
    {
      const pierCount = Math.max(1, Math.min(PIER_MAX_COUNT, Math.round(tuning.pierCount)));
      const span = pierCount * tuning.propSpawnDistance;
      let pi = 0;
      for (let i = 0; i < pierCount; i++) {
        const raw = PIER_Z_START + i * tuning.propSpawnDistance - rWorldY;
        const z = PIER_WRAP_FLOOR + (((raw - PIER_WRAP_FLOOR) % span) + span) % span;
        for (const side of [-1, 1]) {
          pierMesh.setMatrixAt(pi++, m4.makeTranslation(side * (arenaW / 2) * SX, 0, z));
        }
      }
      for (let i = pi; i < PIER_MAX_COUNT * 2; i++) {
        pierMesh.setMatrixAt(i, m4.makeScale(0, 0, 0));
      }
      pierMesh.instanceMatrix.needsUpdate = true;
    }

    // THE SQUAD WAS BACKLIT. The key sat at pz + 10 — up-corridor, on the
    // far side of the squad from a camera that stands behind it — so the
    // only face of every soldier the player could ever see was its shadow
    // side. That is why the squad rendered as a black silhouette on a pale
    // road while the horde in front of it lit up fine.
    //
    // Moving it to pz - 3 puts it slightly BEHIND the squad, over the
    // camera's shoulder: the squad is lit on the face we look at, and the
    // horde further up the corridor still catches it three-quarter on.
    key.position.set(px - 8, 17, pz - 3);
    // Just above and slightly behind the formation, on the camera's side.
    heroFill.position.set(px, 3.2, pz - 2.6);
    key.target.position.set(px, 0, pz + 6);
    key.target.updateMatrixWorld();

    // R8: the dashed lane divider and the pylon/lamp streaming update that
    // lived here are deleted along with the meshes themselves (road
    // furniture, ART_BIBLE §Sky Archipelago Prop Vocabulary). The bridge
    // still simply stands still — enemies and gates advance on their own in
    // the sim (eY and gY decrease every tick), so no replacement motion is
    // needed here. INTEGRATION NOTE: char-pipeline's per-frame
    // `skyWorld.update(...)` call (cloud drift / sea swell / isle bob) also
    // lived here and is not adopted, for the same reason `environment.ts`
    // itself is not adopted (see the note beside `MAX_LANES`).

    /**
     * Squad animation state machine.
     *
     * Priority: dying > running > shooting > idle. A soldier that has just
     * been lost keeps its slot for the length of its death performance
     * instead of vanishing on the frame the count drops — an army that
     * blinks out of existence is the single least convincing thing a
     * formation can do, and the clip already exists.
     */
    function setClip(sol: Soldier, name: string, loop = true): void {
      if (sol.current === name) return;
      const next = sol.actions[name];
      if (!next) return;
      const prev = sol.actions[sol.current];
      next.reset();
      next.setLoop(loop ? LoopRepeat : LoopOnce, loop ? Infinity : 1);
      next.clampWhenFinished = !loop;
      next.enabled = true;
      next.setEffectiveWeight(1);
      // DESYNCHRONISE. A crossfade resets the new action to t=0, so every
      // soldier in the formation entered a run on exactly the same frame
      // and marched as one rigid object -- the shoot clip's load-time phase
      // offset was being thrown away the first time the squad moved.
      // Each body keeps its own place in the cycle and its own slightly
      // different tempo, which is the difference between a squad and a
      // clone stamp.
      next.time = (sol.phase * next.getClip().duration) % next.getClip().duration;
      // Uniform tempo: a march. Per-body tempo variation is what made the
      // squad drift OUT of the step the phase alignment put it in.
      next.setEffectiveTimeScale(1.0);
      if (prev && prev !== next) next.crossFadeFrom(prev, FADE_S, false);
      next.play();
      sol.current = name;
    }

    // --- squad: real skinned soldiers, capped, in a block formation
    // `drawn`/`shown`/`cols`/`deckHalf`/`cx`/`zAnchor`/etc. are computed
    // once, above, before the camera — see the comment there.

    // What the LIVING ranks should be doing this frame.
    frameNo++;
    barrelCount = 0;
    const movingRaw = Math.abs(lean) > 0.045;
    const firing = s.ep.count > 0;
    // 'shoot' is the resting state, NOT 'idle'. The rig ships three clips
    // -- die, shoot, run -- and an idle was deliberately never authored so
    // the rifle is always held. Asking for one anyway fell through to
    // `clips[0]`, which is DIE: for the first seconds of every run, before
    // a single enemy existed, the whole crowd was drawn as collapsed
    // bodies. A state that has no clip must resolve to a pose that does.
    // LOCOMOTION STATE MACHINE (founder 2026-08-14: the squad read as
    // erratic — flipping run/shoot at reflex rate with a stride that
    // sprinted). A state change must EARN itself by persisting 0.25s, and
    // the stride is slow, capped, and smoothed: the soldiers' job on
    // screen is to be steady while the PLAYER thinks about killing and
    // reward math.
    if (movingRaw !== locoWant) { locoWant = movingRaw; locoHold = 0; }
    else if (locoHold < 0.25) locoHold += dt;
    if (locoHold >= 0.25) locoState = locoWant;

    // --- FSM: events first, base state second -----------------------------
    // Read THIS TICK's events straight off the bus. The renderer keys its
    // transitions on event kinds and nothing else, so every transition
    // traces to exactly one event type by construction rather than by
    // convention.
    for (let i = 0; i < s.events.length; i += EVENT_STRIDE) {
      const kind = s.events[i]!;
      if (kind === Ev.SquadHit) fsmEnter('hit', HIT_HOLD_S);
    }
    if (s.squadSize <= 0) fsmEnter('die', 999);
    fsmHold = Math.max(0, fsmHold - dt);
    // WALK, NOT RUN (founder, 2026-08-30): "standing and shooting, or walk
    // on the sides" -- lateral movement is a side-step (the new strafe
    // clip), not a forward sprint. 'run' is left registered (still
    // consumed by fodder-tier borrowing elsewhere) but is no longer the
    // squad's own moving state.
    const base = locoState ? 'walk' : 'shoot';
    // A transient owns the body until its hold expires; then the base state
    // takes over through the same crossfade, so nothing ever snaps.
    if (fsmHold <= 0 && fsmState !== base) fsmEnter(base, 0);
    fsmFade = Math.min(1, fsmFade + dt / CROSSFADE_S);
    // The squad rig has no authored `hit`; fall back to the base clip rather
    // than drawing nothing. Named here so the gap is visible in the code
    // instead of appearing as a body that freezes when it is shot.
    const squadState = squadClips.has(fsmState) ? fsmState : base;
    const squadPrev = squadClips.has(fsmPrev) ? fsmPrev : base;
    // SLOW, DELIBERATE (founder, twice): the stride tempo tops out well
    // under clip speed. Soldiers are the calm in the frame; urgency
    // belongs to the horde and the counters.
    // M2 — NO SKATING. The playback rate is now derived from how fast the
    // squad is actually crossing the deck, not from a hand-picked constant.
    //
    // `STRIDE_M` is how far one cycle of the run clip carries a body at
    // playback rate 1. Driving rate = worldSpeed / STRIDE_M is what makes a
    // planted foot hold its ground: the clip advances exactly as far as the
    // body does. The old constant (0.5-0.85, tuned by eye) could only match
    // world speed by luck, which is precisely the skating the harness could
    // not settle.
    // M2 ON STATIC GROUND — corrected.
    //
    // B3c drove this from `scrollSpeed`, on the assumption that the deck moves
    // past the feet. It does not: scene3d sets `const scroll = 0` because the
    // squad HOLDS ITS GROUND and the horde comes to it. scrollSpeed is the
    // rate enemies and gates advance, which is a property of THEM.
    //
    // So the squad's ground speed is its LATERAL travel and nothing else, and
    // with root motion stripped the cycle is in place: the feet cannot
    // translate at all, and drift against absolute world position is zero by
    // construction rather than by tuning. The remaining job of the rate is
    // cosmetic cadence — how fast the legs cycle while side-stepping — so it
    // is driven by lateral speed, the only real motion the squad has.
    const worldSpeed = Math.abs(lean) * s.config.rules.moveSpeed;
    const rateTarget = locoState
      ? Math.max(0.35, Math.min(1.8, worldSpeed / STRIDE_M))
      : 0.75;
    strideRate += (rateTarget - strideRate) * Math.min(1, dt * 5);
    const squadRate = strideRate;
    // ONE CLOCK FOR EVERY MARCHING BODY (founder: ALL in sync). strideT is
    // the integral of the stride tempo; the skinned front ranks force
    // their clip playhead onto it and the GPU back ranks sample it, so
    // front and back can never drift apart, whatever the tempo does.
    strideT += dt * squadRate;
    void firing;

    // Bodies already mid-death this frame. Counted before any new death is
    // allowed to start, so the cap holds across the whole formation rather
    // than per-slot.
    let dyingNow = 0;
    for (const sol of soldiers) if (sol.dyingFor > 0) dyingNow++;
    for (const [i, sol] of soldiers.entries()) {
      if (i >= shown) {
        // Slot just lost: start the death performance and hold the body.
        //
        // BOUNDED. This is the "soldiers multiply" bug, measured rather
        // than guessed: a shrink over-drew by EXACTLY the number of bodies
        // held here, in every case (+12 with 12 held, +9 with 9, +23 with
        // 23). Growth was always exact, so neither the GPU crowd nor the
        // skinned pool ever leaked — the excess was always the death hold.
        //
        // Unbounded, a squad of 8 that had just been 30 draws 8 live
        // bodies plus 22 corpses standing in formation behind them for a
        // second and a half, and in real play — where soldiers die
        // continuously — the field never clears. That is what reads as the
        // squad multiplying.
        //
        // A death is punctuation, not a census. A handful of bodies going
        // down sells the loss; twenty-four is a second army.
        if (sol.root.visible && sol.dyingFor <= 0 && sol.current !== 'die'
            && dyingNow < MAX_DYING) {
          sol.dyingFor = DEATH_HOLD_S;
          dyingNow++;
          // DEATH IN PLACE (Contact and Impact, "squad bodies" part 2):
          // this index no longer gets a position.set below once it is
          // i >= shown, so this is the LAST read of the sim's position for
          // this body -- and step.ts's formation section specifically
          // wrote the nearest-to-blow slot into it this tick (see its
          // "DEATH IN PLACE" comment), rather than leaving whatever stale
          // value was there. Same sim-space -> world-space conversion the
          // living branch below uses.
          sol.root.position.set(
            ((s.sX[i] ?? 0) - arenaW / 2) * SX,
            sol.root.position.y,
            (s.sY[i] ?? 0) * S,
          );
          setClip(sol, 'die', false);
          // VARIETY (L5): each fall carries its own twist and tempo, so
          // three simultaneous deaths read as three men dying, not one
          // clip stamped thrice — the arms-up chorus that read as dancing.
          sol.root.rotation.y += ((i * 37) % 5 - 2) * 0.22;
          const die = sol.actions['die'];
          if (die) die.setEffectiveTimeScale(0.82 + ((i * 13) % 4) * 0.09);
        }
        if (sol.dyingFor > 0) {
          sol.dyingFor -= dt;
          // Always full rate: a death is a performance, and half-rate
          // deaths in the back ranks read as a stutter, not as distance.
          sol.mixer.update(dt);
          if (sol.dyingFor <= 0) { sol.root.visible = false; sol.current = ''; }
          continue;
        }
        sol.root.visible = false;
        continue;
      }
      // Re-recruited into a living rank: clear any death state.
      if (sol.dyingFor > 0) { sol.dyingFor = 0; sol.current = ''; }
      // C8/I10: a slot transitioning hidden -> visible is either the
      // opening squad or a freshly grown/recruited body -- both pop in
      // with a burst (settle's overshoot), never appear at full size on
      // the first frame they're drawn.
      if (!sol.root.visible) sol.joinFor = JOIN_DUR_S;
      if (sol.joinFor > 0) sol.joinFor = Math.max(0, sol.joinFor - dt);
      setClip(sol, squadState);
      sol.root.visible = true;
      // POSITION READ FROM THE SIM (Contact and Impact, "squad bodies"
      // part 1): step.ts's formation section computes this every tick
      // (the identical row/col/fileStep formula that lived here) and
      // writes s.sX/s.sY, so this is the single place that formula runs
      // now, not a second copy of it. Part 2 moved s.sX/s.sY to sim-space
      // (to collide against eX/eY through the same grid enemies use), so
      // this now converts to world-space the same way every other pooled
      // entity already does at draw time (e.g. scene3d.ts's own enemy
      // read, `(s.eX[i] - arenaW / 2) * SX, s.eY[i] * S`).
      sol.root.position.set(
        ((s.sX[i] ?? 0) - arenaW / 2) * SX,
        // Feet ON the deck: this rig's origin is its hips, so without the
        // lift every soldier stands buried to the waist. Scaled with the
        // body, since the lift was measured at rigFit alone. Vertical only
        // -- unrelated to squadSize/playerX/playerY, so not sim state.
        rigFootOffset * SOLDIER_SCALE * SQUAD_MULT,
        (s.sY[i] ?? 0) * S,
      );
      // Stockier than the raw rig: wider in x/z than in y reads as heavy.
      // A uniformly-scaled humanoid always looks spindly at distance.
      // 1.24 lateral (was 1.16): the chunky-hero stretch. Height carries
      // authority, width carries charm — a squat wide silhouette is the
      // hybrid-casual register the founder asked for.
      // TWO PATHS, ONE SIZE. `rigFit` converts the rig's own metres into
      // SOLDIER_WORLD_H, and this line used to omit it while the
      // GPU-skinned ranks behind applied it — so the front 24 and
      // everything behind them were different-sized armies, and the split
      // only became visible once the squad passed 24 bodies. That is the
      // "some are little giants" read: not growth, a scale mismatch at the
      // boundary between the two renderers.
      // settle() overshoots above 1 before returning, which is what makes
      // the pop-in read as weight arriving rather than a smooth grow.
      const joinK = sol.joinFor > 0 ? settle(1 - sol.joinFor / JOIN_DUR_S, 7, 2.6) : 1;
      const bodyScale = SOLDIER_SCALE * SQUAD_MULT * rigFit * crowdScale * joinK;
      // MIRROR FOR THE RIGHT STRAFE (founder, 2026-08-30): only a LEFT
      // strafe clip exists. Negating scale.x flips the body left-right
      // while it keeps facing forward (rotation stays zero, per the
      // "always square to the horde" law above), which is the standard,
      // correct way to cover the opposite direction from one asset. Only
      // while actually walking -- mirroring the shoot/idle pose would
      // put the rifle on the wrong shoulder for no reason.
      const mirror = squadState === 'walk' && lean > 0 ? -1 : 1;
      sol.root.scale.set(bodyScale * SOLDIER_BULK * mirror, bodyScale, bodyScale * SOLDIER_BULK);

      // Muzzle point, from the rig itself.
      const b = barrels[barrelCount] ?? (barrels[barrelCount] = { x: 0, y: 0, z: 0 });
      if (sol.lh && sol.rh) {
        sol.lh.getWorldPosition(handL);
        sol.rh.getWorldPosition(handR);
        // Barrel axis, extended past the support hand by the hand span —
        // scale-free, so it stays correct if the squad scale changes.
        muzzleV.subVectors(handL, handR);
        const span = muzzleV.length();
        if (span > 1e-4) muzzleV.multiplyScalar(1.15);
        else muzzleV.set(0, 0, 0.2);
        b.x = handL.x + muzzleV.x;
        b.y = handL.y + muzzleV.y;
        b.z = handL.z + muzzleV.z;
      } else {
        b.x = sol.root.position.x;
        b.y = 0.55;
        b.z = sol.root.position.z + 0.25;
      }
      barrelCount++;
      contact(sol.root.position.x, sol.root.position.z, SOLDIER_SCALE * SQUAD_MULT * 0.34);
      void rows;
      // Always square to the horde: lateral movement is a STRAFE. The old
      // yaw lean turned the whole formation, which read as the squad
      // wandering off rather than side-stepping while firing.
      sol.root.rotation.set(0, 0, 0);
      // Back ranks animate at half rate (see MAX_SOLDIERS).
      sol.mixer.update(dt);
      // UNISON: the locomotion playhead is the shared stride clock, set
      // after the mixer evaluates so crossfade weights still animate.
      // Deaths keep their own time — a fall is a performance, not a march.
      const act = sol.actions[sol.current];
      if (act && sol.current !== 'die') {
        act.time = strideT % Math.max(1e-4, act.getClip().duration);
      }

      // THE GUN IN THE HAND matches the gun in the HUD. The rig ships a
      // stock pistol; it is hidden and the current class's model rides in
      // its node, inheriting the exact grip animation for free.
      const profile = s.config.weapons[s.weaponIdx]?.vfxProfile ?? 'rifle';
      if (sol.gunMount && sol.mounted !== profile && gunModels[profile]) {
        // The socket is EMPTY by construction -- the stock pistol mesh is
        // stripped in the asset build -- so mounting is just parenting the
        // class model into the animated node.
        for (const child of [...sol.gunMount.children]) {
          if (child.name === 'classgun') sol.gunMount.remove(child);
        }
        const gm = gunModels[profile]!.clone(true);
        gm.name = 'classgun';
        // Sized in WORLD units, not parent units: the socket sits deep in
        // a scaled bone hierarchy, so a fixed local scale rendered the gun
        // at whatever the accumulated bone scale said -- in practice,
        // invisible. Measure the socket's world scale and cancel it.
        sol.gunMount.getWorldScale(tmpV);
        const worldPer = Math.max(1e-6, tmpV.x);
        gm.scale.setScalar((0.68 / 0.95) / worldPer);
        // MELEE RE-THEME (2026-08-20): every profile shares the one fixed
        // rifle-grip socket rotation (`gunMount`'s own comment, above) --
        // correct for ten guns modeled to be held level and pointed
        // forward, wrong for a sword/hammer whose generated long axis
        // runs vertical, which is the other half of "pointed at the sky"
        // (the first half was the x/z-only normalization, fixed above).
        // Pitched down onto the firing line rather than re-authoring a
        // separate socket, since only these two of twelve need it.
        if (profile === 'blade' || profile === 'hammer') {
          gm.rotation.x = -Math.PI / 2;
        }
        sol.gunMount.add(gm);
        sol.mounted = profile;
      }
    }

    // Ranks past the skinned pool: instanced frozen poses. One draw call
    // per pose instead of one skeleton per body.
    // Reset every instance cursor first. mesh.count IS the write cursor,
    // so leaving it set would append to last frame's matrices and the
    // count would climb until the pool overflowed.
    for (const [state, meshes] of Object.entries(crowd)) {
      crowdCount[state] = 0;
      for (const m of meshes) m.count = 0;
    }
    // GPU-SKINNED BACK RANKS. Same rig, same one draw call, but a real
    // clip at 30fps with a per-body phase instead of six frozen poses
    // cycled at nine. The baked path below is the fallback.
    // RESET THE BUFFER ON EVERY PATH, NOT ONE.
    //
    // begin()/end() lived inside the branch below, so on any frame that
    // took the fallback path the crowd mesh kept the instance count it was
    // left with — its bodies stayed on screen, unowned by any live slot,
    // and every later frame that came back through the fast path stacked a
    // new set on top. That is soldiers accumulating over time out of
    // nothing, which is what the founder is seeing.
    squadCrowd?.begin();
    if (squadCrowd && squadClips.has(squadState)) {
      const step = fileStep;
      const rankStep = rankStep2;
      // rigFit, NOT squadFit. They are both "convert this rig to
      // SOLDIER_WORLD_H", but they measure different things: rigFit uses
      // the MESH bounding box, squadFit uses the SKELETON's Y extent, and
      // a skeleton is always shorter than the mesh around it because no
      // bone reaches the top of the head. Two fit factors is two sizes,
      // which is the whole bug. One number, used by both renderers and by
      // the foot lift.
      const sq = SOLDIER_SCALE * SQUAD_MULT * rigFit * crowdScale;
      // `zAnchor`/`totalRows` (D1 centering) computed once, above, and
      // shared with the skinned loop — see the comment there.
      for (let i = shown; i < drawn; i++) {
        const row = Math.floor(i / cols);
        const inRow = i % cols;
        const inThisRow = Math.min(cols, drawn - row * cols);
        // V2 — M4 APPLIED TO THE FRIENDLIES.
        //
        // This deliberately reverses the earlier "ONE MARCH, zero offset"
        // rule. That was chosen to stop the back ranks shuffling out of step
        // with the front, but perfect lockstep on the largest, nearest bodies
        // on screen reads as one looping sprite rather than as people — M4's
        // banding, on the class M4 was never applied to.
        //
        // Offsets are a hash of the instance index, not a random draw: the
        // same soldier gets the same phase every frame, so nobody twitches,
        // and no PRNG is consumed. The jitter is small — +/-8% on rate, a
        // fraction of a cycle on phase — because the formation must still
        // read as a unit that happens to be made of individuals.
        const h = ((i * 2654435761) >>> 0) / 4294967296;
        const h2 = ((i * 40503 + 12345) >>> 0) / 4294967296;
        const rateJit = 1 + (h - 0.5) * 0.16;
        const phaseJit = h2 * squadCrowd.duration(squadState);
        const tBody = strideT * rateJit + phaseJit;
        // CROSSFADE (M1). Two rows and a blend — the mechanism the crowd rig
        // shipped with and nothing had ever used, which is why every state
        // change until now was a hard cut.
        const rowIdx = squadCrowd.row(squadState, tBody);
        const rowPrev = squadCrowd.row(squadPrev, tBody);
        // Positional jitter breaks the perfect lattice. Kept well under one
        // body width so the formation never reads as a rabble.
        tmpV.set(cx + (inRow - (inThisRow - 1) / 2) * step * squeeze + (h - 0.5) * step * 0.18,
          rigFootOffset * SOLDIER_SCALE * SQUAD_MULT,
          zAnchor - row * rankStep + (h2 - 0.5) * rankStep * 0.18);
        tmpE.set(0, 0, 0, 'YXZ');
        tmpQ.setFromEuler(tmpE);
        tmpS.set(sq * SOLDIER_BULK, sq, sq * SOLDIER_BULK);
        squadCrowd.push(tmpM.compose(tmpV, tmpQ, tmpS), rowPrev, rowIdx, fsmFade, squadTint);
        contact(tmpV.x, tmpV.z, SOLDIER_SCALE * SQUAD_MULT * 0.34);
      }
      for (const [state, meshes] of Object.entries(crowd)) {
        crowdCount[state] = 0;
        for (const m of meshes) { m.count = 0; m.visible = false; }
      }
    } else {
    const pool = crowd[squadState] ?? [];
    if (pool.length > 0) {
      const step = fileStep;
      const rankStep = rankStep2;
      tmpQ.identity();
      // The instanced ranks are the SAME body as the skinned ones. The
      // bakes are taken from the unscaled source, so they carry the rig's
      // own metres and need the identical fit factor -- a separate magic
      // number here is how the front rank and the crowd drift into two
      // different-sized armies.
      tmpS.setScalar(rigFit * crowdScale);
      // `zAnchor`/`totalRows` (D1 centering) computed once, above the
      // skinned loop, and shared with every rank-laying path.
      for (let i = shown; i < drawn; i++) {
        const row = Math.floor(i / cols);
        const inRow = i % cols;
        const inThisRow = Math.min(cols, drawn - row * cols);
        // The crowd's pose advances with TIME and carries a per-body phase.
        // It used to be `pool[i % pool.length]` -- pose chosen by position
        // in the formation -- so every soldier behind the skinned ranks was
        // frozen in one attitude for the entire run. Three static poses
        // arranged in stripes is what read as "the characters don't move":
        // the bodies were literally never animated.
        //
        // The phase offset matters as much as the motion. Cycling everyone
        // together makes the formation snap between poses as one block,
        // which reads as a strobing sprite rather than a squad.
        const phase = Math.floor(strideClock * CROWD_FPS + (i % 4) * 0.25);
        const mesh = pool[((phase % pool.length) + pool.length) % pool.length]!;
        const n = crowdCount[squadState]!;
        if (n >= MAX_DRAWN) break;
        // A little vertical bounce on the same phase, so the rank rises and
        // falls with its stride instead of sliding along at a fixed height.
        const bounce = squadState === 'walk' || squadState === 'run'
          ? Math.abs(Math.sin((strideClock * CROWD_FPS + i * 0.61) * 0.9)) * 0.045 : 0;
        // Same V1 centring as the GPU path. This fallback draws the same
        // formation, so it has the same near-plane problem and must not be
        // left with the old anchoring — a bug fixed on one of two renderers
        // is a bug that returns the moment the other one is used.
        tmpV.set(cx + (inRow - (inThisRow - 1) / 2) * step * squeeze,
          rigFootOffset + bounce, zAnchor - row * rankStep);
        mesh.setMatrixAt(mesh.count, tmpM.compose(tmpV, tmpQ, tmpS));
        mesh.count++;
        crowdCount[squadState] = n + 1;
      }
    }
    for (const [state, meshes] of Object.entries(crowd)) {
      for (const m of meshes) {
        m.visible = state === squadState && m.count > 0;
        if (m.visible) m.instanceMatrix.needsUpdate = true;
      }
    }
    }
    // Commit whatever was written this frame — including nothing at all,
    // which is the case that leaked bodies before.
    squadCrowd?.end();
    // Park the vehicle at the head of the formation while its timer runs.
    if (s.vehicleTicks > 0) {
      vehicle.visible = true;
      let vx2 = cx, vz2 = pz + 1.6;
      if (vehSlide.t < 0.7) {
        vehSlide.t += dt;
        const u = Math.min(1, vehSlide.t / 0.7);
        const k = u * u * (3 - 2 * u);
        vx2 = vehSlide.fx + (vx2 - vehSlide.fx) * k;
        vz2 = vehSlide.fz + (vz2 - vehSlide.fz) * k;
        if ((frameNo & 2) === 0) fx.hit(vx2 / SX + arenaW / 2, vz2 / S, 3, 0xc9d24f);
      }
      vehicle.position.set(vx2, 0, vz2);
      // Idle rock, and a hard flash in the last second so its expiry is
      // something the player SEES coming rather than discovers.
      const left = s.vehicleTicks / 60;
      const bob = Math.sin(frameNo * 0.13) * 0.03;
      vehicle.position.y = bob;
      contact(vehicle.position.x, vehicle.position.z, 1.6, 0.8);
      vehicle.rotation.z = Math.sin(frameNo * 0.09) * 0.02;
      const blink = left < 1.4 && (frameNo % 12) < 6;
      vehicle.scale.setScalar(blink ? 1.06 : 1);
      // THE TANK FIRES. It sat in the lane as scenery while the squad's
      // rifles did all the visible work, so the single biggest prize in the
      // game was also the one with no effect of its own. Its main gun barks
      // on a slow cadence -- a bright bore flash, a recoil shove and a
      // smoke ring -- so a tank run reads differently from every other run.
      if (frameNo % 26 === 0) {
        const mx = cx, my = s.playerY + 2.4;
        fx.muzzle(s.playerX, my, 0, 3.4);
        fx.ring(s.playerX, my + 1.0, 0xffd8a0, 3.0, 16);
        for (let k = 0; k < 16; k++) {
          const a = Math.random() * Math.PI * 2;
          emit(s.playerX, my, Math.cos(a) * 0.22, 0.05 + Math.random() * 0.10,
            0.10 + Math.random() * 0.34, 18 + Math.random() * 12,
            k & 1 ? 0xfff0c8 : 0x8a6a4a, 1.5);
        }
        void mx;
        // Recoil: the hull rocks back, which is the shot's proof.
        vehicle.position.z -= 0.22;
      }
    } else {
      vehicle.visible = false;
    }
    emitFlares();

    // --- enemies -------------------------------------------------------
    // defIdx -> art. 0..2 are the mass (instanced bind-pose silhouettes),
    // 3..5 are elites and bosses (real skinned actors from a small pool).
    strideClock += dt;
    const strideFrame = Math.floor(strideClock * MASS_FPS);
    for (const pool of massTiers) {
      if (!pool) continue;
      for (const mesh of pool) mesh.count = 0;
    }
    for (const p of actorPools) for (const a of p) a.busy = false;
    // DEATH BEAT: advance every actor mid-fall, release the ones whose
    // hold has expired, and re-claim busy=true for the rest so the live-
    // enemy loop below can never grab an actor still playing a death.
    for (let di = dyingActors.length - 1; di >= 0; di--) {
      const d = dyingActors[di]!;
      d.t += dt;
      if (d.t >= DEATH_HOLD_SEC) {
        d.actor.root.visible = false;
        d.actor.busy = false;
        dyingActors.splice(di, 1);
        continue;
      }
      d.actor.busy = true;
      const u = d.t / DEATH_HOLD_SEC;
      // Frame 0 is already `snapFrac` of the way through the knock and the
      // lean -- the blow's own force, not a standing start -- and the rest
      // eases in over each curve's original window, same end state as before.
      const frontLoad = (progress: number): number =>
        Math.min(1, d.snapFrac + (1 - d.snapFrac) * progress);
      // Knockback settles quickly; the fall runs the whole hold -- an
      // impact-then-collapse, not a slide.
      const slideU = frontLoad(Math.min(1, u / 0.4));
      d.actor.root.position.set(
        d.x0 + d.kux * DEATH_KNOCK_DIST * slideU, 0,
        d.z0 + d.kuz * DEATH_KNOCK_DIST * slideU,
      );
      // Pitching about the actor's own root (already at ground level, same
      // Y=0 the live claim loop clamps every reaction to) swings the body
      // DOWN, not through the deck -- Iron Rule 8, everything that falls
      // settles in a physically possible pose. The walk cycle freezes
      // rather than looping through the fall, same "the walk breaks" rule
      // the hit reaction above already uses (no mixer.update() here).
      d.actor.root.rotation.set(d.fallSign * (Math.PI / 2) * frontLoad(u), Math.PI, 0);
      d.actor.root.scale.set(d.sc, d.sc, d.sc);
      d.actor.root.visible = true;
    }
    let fallback = 0;
    // The crowd is filled fresh every frame: living bodies first, then the
    // dead, all into one instance buffer and one draw call.
    for (const h of hordes) h?.begin();
    contactCount = 0;

    for (let i = 0; i < s.ep.cap; i++) {
      if (!s.ep.alive[i]) {
        // A freed slot carries NOTHING into its next occupant. Without
        // this, a body killed mid-stagger hands the tail of that stagger
        // to whatever spawns into the slot, and a fresh grunt walks in
        // already reeling from a hit it never took.
        if (rTime[i]! > 0) { rTime[i] = 0; rKind[i] = ReactKind.None; }
        prevEHp[i] = -1;
        continue;
      }
      const def = s.config.enemies[s.eDefIdx[i]]!;
      const x = (s.eX[i] - arenaW / 2) * SX;
      const z = s.eY[i] * S;
      const flash = enemyFlash[i] ?? 0;
      if (flash > 0) enemyFlash[i] = flash - 1;

      // ALLY-STRUCK REACTION: an hp drop while inside an alive ally's
      // strike radius reads as a hit, the same way a player round does
      // via Ev.Hit -> enemyHit above. Severity uses the same formula
      // (damage over damage-plus-remaining); a lethal drop is skipped,
      // same as the player path -- the death event and corpse pool own
      // that frame instead. A body also hit by the player the same
      // frame may re-trigger this redundantly; enemyHit() already never
      // downgrades an in-flight reaction, so the worst case is a harmless
      // refresh of the same stagger, not a second, different one.
      {
        const curEHp = s.eHp[i]!;
        if (prevEHp[i]! >= 0 && curEHp > 0 && curEHp < prevEHp[i]!) {
          for (let li = 0; li < s.lp.cap; li++) {
            if (!s.lp.alive[li]) continue;
            const adx = s.eX[i]! - s.lX[li]!, ady = s.eY[i]! - s.lY[li]!;
            const arad = s.lRadius[li]!;
            if (adx * adx + ady * ady > arad * arad) continue;
            const dmg = prevEHp[i]! - curEHp;
            enemyHit(i, dmg / (dmg + curEHp), s.lX[li]!, s.lY[li]!, s.eX[i]!, s.eY[i]!);
            break;
          }
        }
        prevEHp[i] = curEHp;
      }

      // --- the reaction, advanced and shaped ---------------------------
      // Computed for EVERY body before the art path splits, because the
      // bodies that actually survive to react are the elites and bosses on
      // the actor path, not the instanced fodder. Measured, not assumed:
      // the Service Rifle does 52 damage and the three mass tiers carry
      // 5/22/40 base health, so at low threat every instanced body dies to
      // one round and never reaches a reaction at all. A reaction system
      // that only reads on one-shot fodder is a reaction system nobody
      // sees.
      let rt = rTime[i]!;
      const rk = rKind[i]!;
      if (rt > 0) {
        rt -= dt;
        if (rt <= 0) { rt = 0; rTime[i] = 0; rKind[i] = ReactKind.None; }
        else rTime[i] = rt;
      }
      const reacting = rt > 0 && rk !== ReactKind.None;
      // Progress through the reaction, 0 at the instant of the hit.
      const u = reacting ? 1 - rt / (rDur[i] || 1) : 0;
      // FAST ATTACK, SLOW RELEASE. A blow lands in a couple of frames and
      // is recovered from over the rest; a symmetric curve reads as a
      // body breathing rather than as a body being hit.
      const imp = !reacting ? 0
        : u < 0.18 ? u / 0.18 : Math.pow(1 - (u - 0.18) / 0.82, 1.6);
      // A REEL goes down, HOLDS there, and gets back up — the hold is
      // what separates it from a big flinch at a glance.
      const down = rk === ReactKind.Reel && reacting
        ? (u < 0.25 ? u / 0.25 : u < 0.62 ? 1 : 1 - (u - 0.62) / 0.38) : 0;
      const seed = rSeed[i]!;
      /** Sided per body, so a rank taking a volley does not turn as one. */
      const side = seed < 0.5 ? -1 : 1;

      if (s.eDefIdx[i] >= 3) {
        // Elite or boss: a genuine animated actor, scaled to its radius so
        // a Colossus reads as a Colossus rather than as a large grunt.
        // Per-SPAWN boss-ness, same as the sim. `def.isBoss` is 0 for Rift
        // Brute and Gale Colossus, so reading the def gave the chapter
        // bosses the elite pool and roughly 2.4x a soldier instead of 5x.
        const isBoss = s.eIsBoss[i] === 1;
        // THE BOSS BECOMES A SPECTACLE: the chapter's own boss (config
        // isBoss), never a mid-run beast -- `isBoss` above already
        // conflates the two, same as every other consumer of eIsBoss in
        // this file, which is exactly why the scale/numeral/damage-state
        // work below needs its OWN, narrower test.
        const trueBoss = def.isBoss;
        const isBeast = def.isBeast;
        const poolIdx = isBoss || s.eDefIdx[i] === 4 ? 1 : 0;
        const pool = actorPools[poolIdx] ?? [];
        const actor = pool.find((p) => !p.busy);
        if (actor) {
          actor.busy = true;
          actor.root.visible = true;
          // A boss stands FIVE TIMES a soldier; the chapter's own boss
          // stands taller again (TRUE_BOSS_SCALE) so it dwarfs even a
          // beast. Scale contrast is the whole reason a boss lands as an
          // event rather than as another body in the queue. Beasts get
          // their OWN scale (BEAST_SCALE, F2 audit) instead of falling
          // into the generic BOSS_SCALE bucket unrelated to their contact
          // radius -- see packages/run-engine/src/constants.ts.
          const sc = SOLDIER_SCALE * (trueBoss ? TRUE_BOSS_SCALE * TRUE_BOSS_VISUAL_MULT : isBeast ? BEAST_SCALE : isBoss ? BOSS_SCALE : 1.05 * (def.radius / 1.05) * FODDER_VISUAL_BULK);

          // A BIG BODY REACTS LESS. Reaction magnitude is inversely
          // proportional to mass, which is both the physically plausible
          // answer and the readable one: a five-soldier-tall boss snapping
          // around from a rifle round looks worse than a boss that does
          // not move at all. An elite takes the blow; a boss only leans
          // into it. The sim makes the same call for knockback, and the
          // presentation must not contradict it.
          const mass = isBoss ? 0.22 : 0.65;
          const lean = (rk === ReactKind.Reel ? 0.85 * down
            : rk === ReactKind.Stagger ? 0.46 * imp : 0.20 * imp) * mass;
          const shove = (rk === ReactKind.Reel ? 0.55 * imp
            : rk === ReactKind.Stagger ? 0.34 * imp : 0.12 * imp) * mass;
          const twist = side * (rk === ReactKind.Reel ? 0.55 * down
            : rk === ReactKind.Stagger ? 0.30 * imp : 0) * mass;
          const sag = (rk === ReactKind.Reel ? 0.26 * down
            : rk === ReactKind.Stagger ? 0.08 * imp : 0) * mass;

          // THE WALK BREAKS. This is the skinned equivalent of freezing a
          // baked pose: a body that is absorbing a real blow stops
          // striding for a moment and then picks the cycle back up. On a
          // rig playing a single retimeable clip, retiming that clip is
          // the only honest way to make it act. (CORRECTED 2026-08-25:
          // this used to claim "every elite, boss and titan in this game
          // ships exactly `walk`" -- backwards. boss-colossus.glb and
          // enemy-brute.glb each ship exactly `attack`; ACTOR GAIT, below
          // and in the pool-creation loop above, is what gives them `walk`
          // at all, borrowed from the same donor the crowd tiers use.)
          const slow = reacting && rk >= ReactKind.Stagger
            ? 1 - 0.85 * (rk === ReactKind.Reel ? down : imp) : 1;

          // A boss's scale is the whole reason it lands as an event, so
          // its squash is gentler than an elite's on top of the mass term.
          const sq = imp * mass;
          actor.root.scale.set(sc * 1.15 * (1 + 0.06 * sq),
            sc * (1 - 0.055 * sq), sc * 1.15 * (1 + 0.06 * sq));
          // RAIL LAW, MISSING UNTIL NOW (founder finding, live playtest,
          // 2026-09-05: "I need the boss to be inside the lane not
          // outside the road"). The mass-tier crowd and the squad both
          // clamp their world X to `railHalfX` minus their OWN rendered
          // half-width (Iron Rule 8: margins account for the rendered
          // body, not just its centre); this actor tier never got that
          // treatment at all -- `x` here is the sim's raw centre X with
          // no clamp of any kind. Invisible at soldier/elite scale, where
          // the flat corridor margin already covers a normal body's
          // width, but a boss stands 5-8x that size (`sc`), so its own
          // half-width can exceed the margin outright and push its
          // shoulders past the rail while its centre is still nominally
          // on the road. Same fractional-of-height half-width estimate
          // the crowd tier already uses (`crowdHalfW`), scaled by this
          // body's own `sc` instead of assumed flat.
          const actorHalfW = crowdHalfW * sc;
          const xClamped = Math.max(-(railHalfX - actorHalfW),
            Math.min(railHalfX - actorHalfW, x + rAxX[i]! * shove));
          // Never let a reaction push a body below the deck: everything
          // that falls settles in a possible pose (Iron Rule 8).
          actor.root.position.set(xClamped, Math.max(0, -sag * sc),
            z + rAxZ[i]! * shove);
          // Pitch about +x tips the head up-corridor; roll about +z tips
          // it toward -x, hence the sign on the lateral term.
          actor.root.rotation.set(rAxZ[i]! * lean, Math.PI + twist,
            -rAxX[i]! * lean * 0.7);
          // ACTOR GAIT (founder finding, 2026-08-25): walk while
          // approaching, attack only inside contact range. A plain
          // stop/play switch, no crossfade: this rig has exactly two
          // clips and the swing is quick enough that a hard cut reads as
          // a beat, not a pop.
          //
          // SCALE-AWARE RANGE (2026-08-31 founder finding: boss never
          // plays its attack clip in-game). The 1.6/-1.2 constants this
          // replaced were tuned against the elite tier's own stop
          // distance (rift_brute: radius 0.78 + playerHitRadius 0.45 -
          // 0.05 = 1.18, comfortably inside 1.6) and silently never
          // applied to the boss, because boss-colossus.glb had no walk
          // clip to switch away FROM (attackAction was the only action,
          // so it just played by default). boss-clown.glb has a real
          // walk clip, which exposed the gap: the boss's own true stop
          // distance (def.radius 4.9 + playerHitRadius - 0.05 ~= 5.3,
          // same formula step.ts uses for the sim's own stop point) is
          // more than 3x the hard-coded 1.6, so `inReach` never fired and
          // the boss walked in place at its stop point forever. Same
          // +0.42 margin the brute's own numbers imply, now derived per
          // actor instead of copy-pasted from one body's measurement.
          const reach = s.eY[i]! - s.playerY;
          const stopDist = def.radius + s.config.rules.playerHitRadius;
          const inReach = reach < stopDist + 0.42 && reach > -1.2;
          const wantGait = inReach ? 'attack' : 'walk';
          if (actor.gait !== wantGait) {
            if (wantGait === 'attack' && actor.attackAction) {
              actor.walkAction?.stop();
              actor.attackAction.reset().play();
              actor.gait = 'attack';
            } else if (wantGait === 'walk' && actor.walkAction) {
              actor.attackAction?.stop();
              actor.walkAction.reset().play();
              actor.gait = 'walk';
            }
          }
          // F4 AUDIT: no-skate law for the actor tier, matching the crowd
          // tier's own worldSpeed/STRIDE_M (`ACTOR GAIT` above already
          // notes this rig borrows the SAME authored walk clip the crowd
          // tiers use) -- without it, raising bossSpeedMult to close the
          // transit gap made a faster boss/beast slide across the ground
          // at a fixed stride cadence instead of striding harder. Only
          // the WALK gait is rate-scaled; the attack swing keeps its own
          // authored tempo, a swing rather than a stride.
          const scaleMult = trueBoss ? TRUE_BOSS_SCALE * TRUE_BOSS_VISUAL_MULT : isBeast ? BEAST_SCALE : isBoss ? BOSS_SCALE : 1;
          const strideRate = actor.gait === 'walk'
            ? Math.max(0.35, Math.min(2.5, (def.speed * s.config.rules.bossSpeedMult) / (STRIDE_M * scaleMult)))
            : 1;
          actor.mixer.update(dt * slow * strideRate);
          contact(x, z, sc * 0.55);

          // THE BOSS BECOMES A SPECTACLE: HP numeral + damage states.
          if (trueBoss) {
            const hpFrac = def.hp > 0 ? Math.max(0, Math.min(1, s.eHp[i]! / def.hp)) : 0;
            // WOW-LEGIBILITY FIX: a linear HP pool this large (isle 1:
            // 720,000 base x boss_hp_mult 8 = 5.76M) makes a single hit
            // (~130-2600 per volley) an imperceptible 0.007-0.04% of the
            // bar -- the numeral above ticks down, but nothing else ever
            // told the player a hit landed except at the two checkpoints
            // this used to have (67%/34%). BOSS_DMG_STATES gives the SAME
            // mechanism (shell darkens, throws debris on a transition,
            // never every tick) far more checkpoints across the fight, so
            // "did that hit register" has a real answer every ~1/Nth of
            // the boss's health instead of twice per fight. Debris size
            // is a flat, small amount per crossing (not scaled by state)
            // -- the payoff here is FREQUENCY, not size; a big burst every
            // crossing at this rate would be the same problem this session
            // has fixed twice already (D9: N simultaneous big effects
            // whiting out the screen), just self-inflicted by one actor.
            const state = Math.min(BOSS_DMG_STATES - 1, Math.floor((1 - hpFrac) * BOSS_DMG_STATES));
            if (actor.dmgState !== state) {
              actor.dmgState = state;
              // Visibly armoured in a shell that shatters progressively:
              // the body darkens each state, on its own per-slot material
              // clone (see the pool-creation comment for why a clone is
              // required here). This pool's asset is unlit MeshBasicMaterial
              // (no emissive channel to glow), so the read is colour alone.
              // Interpolated linearly 1.0 (intact) -> 0.55 (near death),
              // the same total darkening range the old 3-state version
              // covered, just reached in smaller, more frequent steps.
              const dark = 1 - (state / (BOSS_DMG_STATES - 1)) * 0.45;
              for (const bm of actor.bodyMats) bm.mat.color.copy(bm.color0).multiplyScalar(dark);
              // A shell shattering throws real debris, not a flash -- state
              // 0 is the initial claim (no transition to perform yet).
              if (state > 0) {
                fx.burst(s.eX[i]!, s.eY[i]!, 14, 0x8b8f96, 0.3);
                fx.debris(s.eX[i]!, s.eY[i]!, 5, 0x6b6f78, 0, -1, 1.2);
              }
              // SHELL-SHED MILESTONES (spectacle pass, 2026-09-01): the
              // "frequency not size" rule two comments up is deliberately
              // NOT broken here -- it governs the other 9 of 12 states,
              // which stay exactly as small as before. States 3/6/9 (the
              // 75%/50%/25%-HP-remaining crossings, 3 total across the
              // whole fight, never more) get ONE escalated beat each: a
              // bigger debris/burst (a visible CHUNK coming off, not the
              // same small chip) plus a camera punch, reusing punchCamera's
              // own eventPunch mechanism directly rather than routing
              // through the external handle. Staying at exactly 3 keeps
              // this rare -- the same "spectacular because it's scarce"
              // reasoning the mass-death retune (A-54) already applies.
              if (state === 3 || state === 6 || state === 9) {
                fx.burst(s.eX[i]!, s.eY[i]!, 40, 0xffc36a, 0.4);
                fx.debris(s.eX[i]!, s.eY[i]!, 14, 0x6b6f78, 0, -1, 2.2);
                eventPunch = Math.min(EVENT_PUNCH_MAX, eventPunch + 1.1);
              }
            }
            // Quantised relabel (gate numerals' own discipline): rounded to
            // the nearest 100 so a torrent of hits rebuilds this texture in
            // steps, not once a frame -- stormhulk's pool is authored at
            // 720000 base HP, so unquantised would relabel nearly every
            // tick under fire.
            const hpShown = Math.max(0, Math.round(s.eHp[i]! / 100) * 100);
            const hpText = hpShown.toLocaleString('en-US');
            if (actor.hpLabel !== hpText) {
              const mat = actor.hpSprite.material;
              mat.map?.dispose();
              mat.map = bossHpTexture(hpText);
              mat.needsUpdate = true;
              actor.hpLabel = hpText;
            }
            // Chest height: the model's own visual height back out of `sc`
            // (see TRUE_BOSS_SCALE's derivation from SOLDIER_HEIGHT), ~62%
            // of the way up -- torso, not head, not feet.
            const visualH = sc * (SOLDIER_HEIGHT / SOLDIER_SCALE);
            actor.hpSprite.position.set(x, visualH * 0.62, z);
            actor.hpSprite.visible = true;
          } else if (actor.dmgState !== -1) {
            // Slot reused by an elite or a beast after playing the true
            // boss: reset the shell tint and hide the numeral, or a beast
            // would inherit the boss's cracks.
            actor.dmgState = -1;
            for (const bm of actor.bodyMats) bm.mat.color.copy(bm.color0);
            actor.hpSprite.visible = false;
          }
          continue;
        }
      }

      const tier = Math.min(2, s.eDefIdx[i]);

      // ---- GPU-SKINNED PATH -------------------------------------------
      const hc = hordes[tier];
      const hcClips = hordeClips[tier];
      if (hc && hcClips) {
        const scC = massBodyScale(def.radius) * (crowdFit[tier] ?? 1) / SOLDIER_WORLD_H * growOf(i);
        const reach = s.eY[i]! - s.playerY;
        const inReach = reach < 1.6 && reach > -1.2;
        const gait = inReach && hcClips.has('attack')
          ? (seed < 0.5 || !hcClips.has('attack2') ? 'attack' : 'attack2')
          : hcClips.has('run') && def.speed > 4 ? 'run' : 'walk';
        // FEET THAT MATCH THE GROUND. The clip used to play at its authored
        // rate however fast the sim moved the body, so a fast enemy
        // moonwalked and a slow one skated. Retimed to the body's own
        // speed, a footfall lands where the foot actually is. Attacks are
        // not retimed — a swing has its own timing and is not locomotion.
        const rate = gait === 'walk' || gait === 'run'
          ? Math.max(0.45, Math.min(2.2, def.speed / 3.6)) : 1;
        const baseRow = hc.row(gait, strideClock * rate + i * 0.6180339887);

        // A REACTION PLAYS A CLIP IF THE RIG HAS ONE, and is expressed in
        // the matrix if it does not. Rigs that never got the M4 combat
        // clips (retarget skip, art still missing, etc.) ship only `walk`,
        // so the lean, twist and shove below are what a hit looks like on
        // them — the same severity tiers, carried by the transform instead
        // of by mocap.
        let rowA = baseRow, rowB = baseRow, blend = 0;
        const reactClip = HIT_VARIANTS[rVariant[i]! % HIT_VARIANTS.length] ?? '';
        const hasReactClip = reacting && reactClip !== '' && hcClips.has(reactClip);
        if (hasReactClip) {
          const into = rDur[i]! - rt;
          rowB = hc.row(reactClip, into, false);
          blend = Math.min(1, into / REACT_IN, rt / REACT_OUT);
        }
        const mtx = hasReactClip ? 0 : 1;   // matrix reaction only as fallback
        const leanAmt = (rk === ReactKind.Reel ? 1.05 * down
          : rk === ReactKind.Stagger ? 0.60 * imp : 0.28 * imp) * mtx;
        const twistC = side * (rk === ReactKind.Reel ? 0.85 * down
          : rk === ReactKind.Stagger ? 0.5 * imp : 0) * mtx;
        const shoveC = (rk === ReactKind.Reel ? 0.42 * imp
          : rk === ReactKind.Stagger ? 0.30 * imp : 0.11 * imp) * mtx;
        const dropC = (rk === ReactKind.Reel ? 0.30 * down
          : rk === ReactKind.Stagger ? 0.07 * imp : 0) * mtx * massBodyScale(def.radius);

        // THE RAIL LAW on the rendered edge, clamped unconditionally so a
        // body's shoulder can never cross the kerb (Iron Rule 8).
        const halfWC = crowdHalfW * massBodyScale(def.radius);
        const bxC = x + rAxX[i]! * shoveC;
        const limC = Math.max(0, railHalfX - halfWC);
        const fxC = Math.max(-limC, Math.min(limC, bxC));
        const over = Math.abs(fxC) + halfWC - railHalfX;
        if (over > reactOverhang) reactOverhang = over;

        tmpV.set(fxC, Math.max(0, -dropC), z + rAxZ[i]! * shoveC);
        tmpE.set(rAxZ[i]! * leanAmt, Math.PI + twistC + (seed - 0.5) * 0.28,
          -rAxX[i]! * leanAmt * 0.7, 'YXZ');
        tmpQ.setFromEuler(tmpE);
        const flC = flash > 0 ? 1.06 : 1;
        tmpS.set(scC * flC, scC * flC, scC * flC);
        // NO TINT (founder directive, 2026-08-30): the body's own original
        // colour, full stop -- the flash-scale bump above already carries
        // the hit feedback, so this instance colour has nothing left to do.
        hc.push(tmpM.compose(tmpV, tmpQ, tmpS), rowA, rowB, blend, WHITE_TINT);
        contact(fxC, z + rAxZ[i]! * shoveC, massBodyScale(def.radius) * 0.30);
        continue;
      }

      const pool = massTiers[tier];
      if (!pool || pool.length === 0) {
        // Art missing: fall back to the primitive so the horde is never
        // invisible, which would silently look like "no enemies spawned".
        tmpV.set(x, 0.4, z);
        tmpS.set(1, 1, 1);
        enemies.setMatrixAt(fallback++, tmpM.compose(tmpV, tmpQ.identity(), tmpS));
        continue;
      }
      // Which stride of the walk this body is on: global clock plus a
      // per-body offset, so the crowd is mid-cycle everywhere rather than
      // stepping in unison like a chorus line.
      //
      // A STAGGER OR A REEL STOPS THE WALK. This is the single clearest
      // read in the whole system: in a crowd where every body is stepping,
      // the one that has stopped stepping is the one that just took a
      // real hit, and it stays legible at corridor distance where a few
      // degrees of lean does not.
      let phaseIdx = (strideFrame + i) % pool.length;
      if (reacting && rk >= ReactKind.Stagger) {
        if (rHold[i]! < 0) rHold[i] = phaseIdx;
        phaseIdx = rHold[i]!;
      }
      const mesh = pool[phaseIdx]!;
      const n = mesh.count;
      if (n >= MAX_ENEMIES) continue;
      // Horde geometry is normalised to UNIT HEIGHT at load, so this
      // number is the enemy's height in world units directly. Sized
      // against a soldier: grunts clearly smaller, elites close to equal.
      // BULK. At 0.70-1.02 of a soldier the horde read as ants at corridor
      // distance -- the reference fodder stands nearly shoulder height with
      // the shooters. Grunts now start at 0.95 and the heavies pass 1.3.
      // THE HORDE IS SMALLER THAN THE SQUAD, except its heavies. At a 0.94
      // base every fodder body stood TALLER than the soldier killing it --
      // a skitterer at 1.08x, a wretch at 1.16x -- which is why the zombies
      // read as bigger and fluffier than the characters. Fodder now sits at
      // 0.78-0.86 of a soldier and only a brute out-tops one.
      const sc = massBodyScale(def.radius) * growOf(i);

      // LEAN OFF THE BLOW. The axis is the direction the round travelled,
      // so a body shot from behind pitches up-corridor and a body caught
      // from the side rolls away from the shooter — the reaction points
      // somewhere, instead of being the same nod every time.
      // Pitch about +x tips the head toward +z; roll about +z tips it
      // toward -x, hence the sign on the lateral term.
      const leanAmt = rk === ReactKind.Reel ? 1.05 * down
        : rk === ReactKind.Stagger ? 0.60 * imp
          : 0.28 * imp;
      const pitch = rAxZ[i]! * leanAmt;
      const roll = -rAxX[i]! * leanAmt * 0.7;
      // A twist off the vertical, sided per body so a rank taking a volley
      // does not turn as one piece.
      const twist = side
        * (rk === ReactKind.Reel ? 0.85 * down
          : rk === ReactKind.Stagger ? 0.5 * imp : 0);

      // Shoved along the shot, and — for a reel — DOWN onto a knee.
      const shove = rk === ReactKind.Reel ? 0.42 * imp
        : rk === ReactKind.Stagger ? 0.30 * imp
          : 0.11 * imp;
      const drop = (rk === ReactKind.Reel ? 0.30 * down
        : rk === ReactKind.Stagger ? 0.07 * imp : 0) * sc;

      // THE RAIL LAW, on the rendered EDGE (Iron Rule 8). The sim already
      // holds every body's CENTRE inside the corridor; a lateral shove is
      // new lateral travel the sim never sanctioned, so it is clamped here
      // against the body's own half-width. The limit never pulls a body
      // further in than the sim put it — this cannot create a reaction
      // that violates the rail, and it does not silently re-place bodies
      // that were not reacting.
      const halfW = (massHalfW[tier] ?? 0.24) * sc * 1.2;
      const bx = x + rAxX[i]! * shove;
      const lim = Math.max(Math.abs(x), railHalfX - halfW);
      const fx2 = Math.max(-lim, Math.min(lim, bx));
      if (reacting) {
        const over = Math.abs(fx2) + halfW - railHalfX;
        if (over > reactOverhang) reactOverhang = over;
      }

      tmpV.set(fx2, Math.max(0, -drop), z + rAxZ[i]! * shove);
      // YXZ: turn first, then pitch in the body's own frame, so a twisted
      // body leans off the blow rather than off the world axis.
      tmpE.set(pitch, Math.PI + twist, roll, 'YXZ');
      tmpQ.setFromEuler(tmpE);
      // ABSORB, don't inflate. The old read was a uniform 1.18 pop on
      // every hit, which reads as a balloon rather than as a body taking
      // a round. Widening while shortening is what impact looks like, and
      // it keeps the "I hit that one" signal at corridor distance.
      const sq = imp * (rk === ReactKind.Flinch ? 1 : 1.5);
      const fl = flash > 0 ? 1.06 : 1;
      tmpS.set(sc * 1.2 * (1 + 0.10 * sq) * fl, sc * (1 - 0.09 * sq) * fl,
        sc * 1.2 * (1 + 0.10 * sq) * fl);
      mesh.setMatrixAt(n, tmpM.compose(tmpV, tmpQ, tmpS));
      // NO TINT (founder directive, 2026-08-30): raw instance colour.
      mesh.setColorAt(n, WHITE_TINT);
      mesh.count = n + 1;
    }

    for (const pool of massTiers) {
      if (!pool) continue;
      for (const mesh of pool) {
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      }
    }

    // --- the dead ---------------------------------------------------------
    // A BODY THAT DIES NOW PERFORMS ITS DEATH. Two mocap death clips play
    // out on the GPU-skinned rig where the body fell, instead of a frozen
    // walk pose being thrown up-corridor and tumbled under a hand-written
    // gravity integrator. Mocap also settles in a physically possible pose
    // by construction, which is the thing the old settle code had to
    // approximate with a rotate-to-nearest-half-turn.
    //
    // The archetype survives as a SLIDE: a body blown off its feet still
    // travels, a crumple does not. The clip owns the vertical; the slide
    // owns the ground plane, and the rail law clamps it.
    for (const pool of corpseTiers) { if (pool) for (const m of pool) m.count = 0; }
    for (let i = 0; i < MAX_CORPSES; i++) {
      if (cLife[i]! <= 0 || cTier[i]! < 0) continue;
      cLife[i]! -= dt;
      if (cLife[i]! <= 0) { cTier[i] = -1; continue; }

      // A BODY THAT DIES PERFORMS ITS DEATH, now that the retarget has
      // given these rigs two mocap death clips. Mocap settles in a
      // physically possible pose by construction — the thing the tumble
      // below has to approximate by easing toward the nearest half-turn.
      //
      // The archetype survives as a SLIDE: a body blown off its feet still
      // travels, a crumple does not. The clip owns the vertical, the slide
      // owns the ground plane, and the rail law clamps it.
      if (cArch[i] === DeathArch.Breach) {
        // AUDIT A-13: no gravity, no grounding, no settle -- it never
        // fell, it ran past. Continues on its own heading (set at spawn
        // in `breach()`) until its short life ends, which happens well
        // off-frame at the speed it's given.
        cX[i]! += cVx[i]! * dt;
        cZ[i]! += cVz[i]! * dt;
        const poolB = corpseTiers[cTier[i]!];
        const meshB = poolB?.[cPhase[i]! % (poolB.length || 1)];
        if (!meshB || meshB.count >= MAX_CORPSES) continue;
        const scB = cScale[i]!;
        const cHalfB = (massHalfW[cTier[i]!] ?? 0.24) * scB * 1.2;
        const limB = railHalfX - cHalfB;
        if (cX[i]! < -limB) cX[i] = -limB;
        else if (cX[i]! > limB) cX[i] = limB;
        tmpV.set(cX[i]!, 0, cZ[i]!);
        tmpE.set(0, cYaw[i]!, 0, 'YXZ');
        tmpQ.setFromEuler(tmpE);
        tmpS.set(scB * 1.2, scB, scB * 1.2);
        meshB.setMatrixAt(meshB.count, tmpM.compose(tmpV, tmpQ, tmpS));
        meshB.count++;
        contact(cX[i]!, cZ[i]!, scB * 0.42, 1);
        continue;
      }

      const hcD = hordes[cTier[i]!];
      const hcDClips = hordeClips[cTier[i]!];
      if (hcD && hcDClips?.has('death')) {
        const age = cMax[i]! - cLife[i]!;
        const decay = Math.exp(-age * 3.4);
        cX[i]! += cVx[i]! * decay * dt;
        cZ[i]! += cVz[i]! * decay * dt;

        const halfD = crowdHalfW * massBodyScale(0.34);
        const limD = Math.max(0, railHalfX - halfD);
        if (cX[i]! < -limD) { cX[i] = -limD; cVx[i] = Math.abs(cVx[i]!) * 0.4; }
        else if (cX[i]! > limD) { cX[i] = limD; cVx[i] = -Math.abs(cVx[i]!) * 0.4; }
        const overD = Math.abs(cX[i]!) + halfD - railHalfX;
        if (overD > corpseOverhang) corpseOverhang = overD;

        // WHICH of the three M4 death clips (chosen once at spawn from the
        // dying body's own pool index — see `kill()` — never re-picked
        // here, or a corpse would swap its fall mid-flight every frame).
        // Non-looping: the clip HOLDS its last frame, which is what leaves
        // a body lying there.
        const clipD = DEATH_VARIANTS[cVariant[i]!] ?? 'death';
        const rowD = hcD.row(clipD, age, false);
        const tD = cLife[i]! / cMax[i]!;
        const sinkD = tD < 0.34 ? tD / 0.34 : 1;
        const scD = cScale[i]! * (crowdFit[cTier[i]!] ?? 1) / SOLDIER_WORLD_H;
        tmpV.set(cX[i]!, -(1 - sinkD) * 0.55 * cScale[i]!, cZ[i]!);
        tmpE.set(0, Math.PI + cYaw[i]!, 0, 'YXZ');
        tmpQ.setFromEuler(tmpE);
        tmpS.setScalar(scD);
        hcD.push(tmpM.compose(tmpV, tmpQ, tmpS), rowD, rowD, 0, WHITE_TINT);
        contact(cX[i]!, cZ[i]!, cScale[i]! * 0.42, sinkD);
        continue;
      }

      cVy[i]! -= 13 * dt;                       // gravity
      cX[i]! += cVx[i]! * dt;
      cY[i]! += cVy[i]! * dt;
      cZ[i]! += cVz[i]! * dt;
      cRot[i]! += cSpin[i]! * dt;
      const grounded = cY[i]! < 0.05;
      if (grounded) {                           // hit the deck: skid and settle
        cY[i] = 0.05; cVy[i] = -cVy[i]! * 0.28;
        cVx[i]! *= 0.55; cVz[i]! *= 0.55;
        // A grounded body SETTLES: spin dies and the rotation eases to the
        // nearest flat-on-the-deck pose instead of freezing mid-flip --
        // which is exactly how bodies ended up lying upside down in the
        // audit frames. Half-turn steps all read as "fallen" for a
        // silhouette; what mattered was stopping BETWEEN them.
        //
        // A CRUMPLE settles harder and faster than a body that was
        // thrown: it had almost no momentum to shed, so letting it skid
        // like a blown body is what would make the two look alike again.
        const brake = cArch[i] === DeathArch.Crumple ? 0.06 : 0.25;
        cSpin[i]! *= brake;
        if (cArch[i] === DeathArch.Crumple) { cVx[i]! *= 0.3; cVz[i]! *= 0.3; }
        const half = Math.PI;
        const target = Math.round(cRot[i]! / half) * half + half / 2;
        const rate = cArch[i] === DeathArch.Crumple ? 16 : 9;
        cRot[i]! += (target - cRot[i]!) * Math.min(1, dt * rate);
      }
      const pool = corpseTiers[cTier[i]!];
      const mesh = pool?.[cPhase[i]! % (pool.length || 1)];
      if (!mesh || mesh.count >= MAX_CORPSES) continue;
      const t = cLife[i]! / cMax[i]!;
      // Sink through the deck over the last third rather than popping out.
      const sink = t < 0.34 ? (t / 0.34) : 1;
      const sc = cScale[i]! * (0.86 + 0.14 * sink);

      // PHYSICS LAW: a flung body BOUNCES off the kerb, and it does so on
      // its OWN EDGE. This clamped the body's centre against a fixed 0.85
      // inset, so a wide body's shoulder passed through the rail while the
      // arithmetic said it had bounced. The half-width is measured from
      // the shipped geometry, and the tier is the corpse's own tier.
      const cHalf = (massHalfW[cTier[i]!] ?? 0.24) * sc * 1.2;
      const lim = railHalfX - cHalf;
      if (cX[i]! < -lim) { cX[i] = -lim; cVx[i] = Math.abs(cVx[i]!) * 0.45; }
      else if (cX[i]! > lim) { cX[i] = lim; cVx[i] = -Math.abs(cVx[i]!) * 0.45; }
      const over = Math.abs(cX[i]!) + cHalf - railHalfX;
      if (over > corpseOverhang) corpseOverhang = over;

      tmpV.set(cX[i]!, cY[i]! * sink - (1 - sink) * 0.3 * sc, cZ[i]!);
      // A settled body lies at its OWN resting yaw. Every corpse used to
      // face Math.PI, so a pile of them read as bodies laid out in a row
      // rather than as bodies that fell where they were hit.
      tmpE.set(cRot[i]! * 0.85, Math.PI + (grounded ? cYaw[i]! : 0),
        cRot[i]! * 0.4, 'YXZ');
      tmpQ.setFromEuler(tmpE);
      tmpS.set(sc * 1.2, sc, sc * 1.2);
      mesh.setMatrixAt(mesh.count, tmpM.compose(tmpV, tmpQ, tmpS));
      mesh.count++;
      // A fallen body is wider on the ground than a standing one, and its
      // patch fades with it rather than vanishing on the last frame.
      contact(cX[i]!, cZ[i]!, sc * 0.42, sink);
    }
    for (const pool of corpseTiers) {
      if (pool) for (const m of pool) m.instanceMatrix.needsUpdate = true;
    }
    for (const h of hordes) h?.end();
    contacts.count = contactCount;
    contacts.instanceMatrix.needsUpdate = true;
    if (contacts.instanceColor) contacts.instanceColor.needsUpdate = true;
    for (const p of actorPools) for (const a of p) {
      if (a.busy) continue;
      a.root.visible = false;
      // THE BOSS BECOMES A SPECTACLE: an unclaimed slot's numeral must not
      // float on screen after the body it belonged to is gone (dead, or
      // simply not respawned yet this frame).
      if (a.dmgState !== -1) {
        a.dmgState = -1;
        a.hpSprite.visible = false;
        for (const bm of a.bodyMats) bm.mat.color.copy(bm.color0);
      }
    }

    // HP counters over the big bodies. THE BOSS BECOMES A SPECTACLE: the
    // chapter's own boss now carries its own numeral (bossHpTexture, on
    // the actor itself) -- skip it here so the two do not both draw over
    // the same body. Beasts are untouched (still get this plain tag).
    let ht = 0;
    for (let i = 0; i < s.ep.cap && ht < hpTags.length; i++) {
      if (!s.ep.alive[i] || s.eIsBoss[i] !== 1) continue;
      const def = s.config.enemies[s.eDefIdx[i]]!;
      if (def.isBoss) continue;
      const tag = hpTags[ht]!;
      const text = String(Math.max(0, Math.round(s.eHp[i]!)));
      if (tag.text !== text) {
        tag.sp.material.map?.dispose();
        tag.sp.material.map = hpTexture(text);
        tag.sp.material.needsUpdate = true;
        tag.text = text;
      }
      const scale = def.isBoss ? BOSS_SCALE : 3.2;
      // UI LAW (founder, 2026-08-20): AT THE CHEST, not floating above the
      // head -- 1.15x body height (near/above the crown) dropped to 0.6x.
      tag.sp.position.set((s.eX[i]! - arenaW / 2) * SX,
        SOLDIER_SCALE * scale * 0.6, s.eY[i]! * S);
      tag.sp.scale.set(3.4, 1.13, 1);
      tag.sp.visible = true;
      ht++;
    }
    for (let i = ht; i < hpTags.length; i++) hpTags[i]!.sp.visible = false;

    // Lane head-counts. Counted over the APPROACHING band only: bodies
    // already level with the squad are a problem, not a forecast.
    {
      const lanes = lanesNow;
      const counts = new Array<number>(lanes).fill(0);
      const near = new Array<number>(lanes).fill(1e9);
      for (let i = 0; i < s.ep.cap; i++) {
        if (!s.ep.alive[i] || s.eIsBoss[i] === 1) continue;
        const dy = s.eY[i]! - s.playerY;
        if (dy < 2 || dy > 46) continue;
        const l = Math.min(lanes - 1, Math.max(0,
          Math.floor((s.eX[i]! / arenaW) * lanes)));
        counts[l]! += 1;
        if (dy < near[l]!) near[l] = dy;
      }
      for (let l = 0; l < laneTags.length; l++) {
        const tag = laneTags[l]!;
        if (l >= lanes || counts[l]! < 4) { tag.sp.visible = false; continue; }
        const text = String(counts[l]);
        if (tag.text !== text) {
          tag.sp.material.map?.dispose();
          tag.sp.material.map = hpTexture(text);
          tag.sp.material.needsUpdate = true;
          tag.text = text;
        }
        const lx = ((l + 0.5) / lanes - 0.5) * arenaW * SX;
        // Sits just ahead of that lane's leading rank, so the number
        // travels with the crowd it counts.
        tag.sp.position.set(lx, 3.4, pz + Math.min(40, near[l]! + 5) * S);
        tag.sp.scale.set(2.6, 0.87, 1);
        tag.sp.visible = true;
      }
    }
    enemies.count = fallback;
    enemies.instanceMatrix.needsUpdate = true;

    // --- fire: three additive layers + muzzle flares + a pulsing light
    // Every bolt on screen belongs to the CURRENT gun, so the palette is
    // set once per frame rather than per instance.
    lastProfile = s.config.weapons[s.weaponIdx]?.vfxProfile ?? '';
    const look = GUN_LOOKS[lastProfile] ?? DEFAULT_LOOK;
    // EFFECTIVE RANGE, mirrored from step.ts's own weapon-fire clamp --
    // same formula (floor, not round, matching the "a ceiling must never
    // round UP past itself" rule that fix documented), so the mortar's
    // arc lands exactly where the SIM's own range ceiling ends the
    // round's life, not at a separately-guessed constant that drifts the
    // moment either value changes. Read-only mirror; step.ts is untouched.
    const curWeapon = s.config.weapons[s.weaponIdx];
    let effectiveRange = 30;
    if (curWeapon) {
      const rangeCeilingTicks = Math.max(1, Math.floor(
        (s.config.rules.weaponRangeCeiling / curWeapon.projectileSpeed) * TICK_RATE));
      const lifeTicks = Math.min(rangeCeilingTicks, curWeapon.projectileLifetimeTicks);
      effectiveRange = (lifeTicks * curWeapon.projectileSpeed) / TICK_RATE;
    }
    // How high the mortar's arc rises at its own midpoint. A fixed world-
    // unit height (not scaled by effectiveRange) reads as consistently
    // "heavy" regardless of how far the shell happens to travel.
    const MORTAR_APEX = 2.6;
    // TRACER LATERAL-OFFSET CAP (founder correction, 2026-09-05, "bug A":
    // a tracer visually crosses an enemy and nothing happens; corrected
    // same date to split by axis). Root cause: several weapon shapes
    // displace the DRAWN bolt from its true sim position (`s.pX[i]`/
    // `s.pY[i]`, where collision actually happens) for visual variety --
    // see each `case` below. Only `ox` (render-X, LATERAL) can cause the
    // ground-plane hit-test mismatch this cap exists for, since the sim's
    // collision test reads `s.eX`/`s.eY` -> render-X/render-Z, never
    // render-Y (height). `spiral` (ox+oy, radius 0.30) / `rocket` (ox,
    // 0.18) / `shell` (ox, up to 0.65) are the three shapes that actually
    // set `ox` -- capped below. `sweep`/`slam` only ever set `oy`
    // (height, "RISES as it crosses" / "drops... hammers the deck") and
    // `lob`'s `ox` is never set either -- all three are EXEMPT, not
    // capped: height cannot cause this mismatch, and capping them
    // destroys the shapes (sweep's 0.90 rising arc, slam's 0.75 drop,
    // lob's 2.6-unit mortar arc are each that weapon's entire read).
    //
    // `minEnemyRadius` is read from the LIVE config, not hand-copied, so
    // this stays correct if enemies.csv changes. `tracerLateralOffsetMult`
    // (0=exactly on sim position, 1=historical unclamped amplitude for
    // any shape whose own amplitude was already <= minEnemyRadius, e.g.
    // spiral/rocket at today's roster) is live in the ?tune=1 panel;
    // default 0.5.
    let minEnemyRadius = Infinity;
    for (const def of s.config.enemies) if (def.radius < minEnemyRadius) minEnemyRadius = def.radius;
    const tracerOffsetCap = tuning.tracerLateralOffsetMult * minEnemyRadius;
    // Count the volley BEFORE drawing it. Additive blending is per-quad,
    // so at 80 streams the layers saturate to flat white and the corridor
    // fills with solid rectangles -- measured at squad 232, and it is the
    // single worst thing on screen. Girth and glow are divided down as the
    // stream count climbs, so a big volley reads as a dense curtain of
    // fire instead of a white wall.
    let live = 0;
    for (let i = 0; i < s.pp.cap; i++) if (s.pp.alive[i]) live++;
    // 1.0 up to ~12 bolts, falling off as the inverse square root after
    // that: total ink stays roughly constant as the count grows.
    const density = live <= 12 ? 1 : Math.sqrt(12 / live);

    // Girth is also bounded by how tightly the STREAMS are packed. The
    // curtain is a fixed width, so more streams means less room between
    // them: at 24 streams across a 1.22 half-width the spacing is 0.10
    // world units, and a bolt any fatter than that merges with its
    // neighbours into one solid band -- which is how the corridor filled
    // with white rectangles again even after the count-based falloff.
    const streams = volleySize(s.squadSize, s.statProjectiles,
      s.config.rules.maxProjectilesPerVolley);
    const halfW = fireHalfWidth(arenaW, lanesNow, s.squadSize);
    const spacing = streams > 1 ? (2 * halfW) / (streams - 1) : 2 * halfW;
    // The halo layer is the widest, at radius 0.2 * gw -- so 0.4 * gw
    // across. Keep that inside ~55% of the gap between streams.
    // Thin enough to stay separable, never so thin it stops reading as a
    // round. The old floor of 0.18 with an aggressive spacing clamp made a
    // dense volley into pale threads.
    const gw = Math.max(0.62, Math.min(look.w * density * 1.5, (spacing * 1.15) / 0.4));
    boltCore.material.color.setHex(look.core);
    boltMid.material.color.setHex(look.mid);
    boltHalo.material.color.setHex(look.halo);
    // Hotter than before: from the raised camera the bolts are the frame's
    // main light source, the way the reference ads read.
    boltCore.material.opacity = Math.max(0.45, density);
    boltMid.material.opacity = Math.min(1, 0.7 * look.glow) * Math.max(0.34, density);
    boltHalo.material.opacity = Math.min(1, 0.24 * look.glow) * Math.max(0.2, density);
    // ENERGY CLASSES LANCE, they do not lob. While a beam weapon has live
    // rounds in the air the barrels are drawn as solid lances reaching up
    // the lane; the rounds themselves are suppressed below, because showing
    // both gives a beam a visible bullet inside it.
    const isBeam = look.shape === 'beam';
    // A-52 FOLLOW-UP: bolt length was clamped only by projectile speed
    // (4.6 units max), never by how far apart consecutive shots actually
    // land. At a fast enough cooldown (isle 1's weapon0CooldownMult=0.2,
    // rebalanced 2026-09-01 for horde-density survival) shots fire closer
    // together than a single bolt is long, so consecutive bolts overlap
    // into a solid line -- a rifle reading as a beam weapon. Capping bolt
    // length to a fraction of the shot-to-shot travel gap leaves a visible
    // seam between bolts at any fire rate, without touching cooldownTicks,
    // damage, or anything else gameplay-relevant -- render-only.
    const shotGapUnits = curWeapon
      ? curWeapon.projectileSpeed * (curWeapon.cooldownTicks / TICK_RATE) : Infinity;
    const maxBoltLen = Math.min(4.6, shotGapUnits * 0.8);
    let bi2 = 0;
    if (isBeam && barrelCount > 0 && s.pp.count > 0) {
      // Reach = the furthest live round of this volley, so the lance ends
      // where the damage ends rather than at an arbitrary distance.
      let reach = 6;
      for (let i = 0; i < s.pp.cap; i++) {
        if (!s.pp.alive[i]) continue;
        const d = s.pY[i] - s.playerY;
        if (d > reach) reach = d;
      }
      const len = reach * S;
      for (let b = 0; b < barrelCount && bi2 < MAX_BEAMS; b++) {
        const br = barrels[b]!;
        tmpV.set(br.x, br.y + 0.1, br.z + len * 0.5);
        tmpQ.setFromEuler(new Euler(Math.PI / 2, 0, 0, 'YXZ'));
        tmpS.set(1, len, 1);
        tmpM.compose(tmpV, tmpQ, tmpS);
        beamCore.setMatrixAt(bi2, tmpM);
        beamHalo.setMatrixAt(bi2, tmpM);
        bi2++;
      }
      beamCore.material.color.setHex(look.core);
      beamHalo.material.color.setHex(look.mid);
      // A beam FLICKERS at its source rather than pulsing along its length.
      beamCore.material.opacity = 0.8 + Math.sin(frameNo * 0.9) * 0.15;
      beamHalo.material.opacity = 0.26 + Math.sin(frameNo * 0.7) * 0.06;
    }
    beamCore.count = bi2; beamHalo.count = bi2;
    beamCore.instanceMatrix.needsUpdate = true;
    beamHalo.instanceMatrix.needsUpdate = true;
    beamCore.visible = bi2 > 0; beamHalo.visible = bi2 > 0;

    // FORMATION-WIDE FALLBACK ONLY (2026-08-18): the closest-to-camera
    // rank's own world y. Used now only before any soldier has rendered a
    // barrel this frame (barrelCount === 0) -- once real barrels exist,
    // each bolt is clamped against its OWN assigned soldier below, not
    // this shared value, which is what let every bolt's tail line up on
    // one flat depth regardless of which soldier it came from ("fire
    // reads as a detached block floating in front of the squad").
    const fallbackMuzzleY = zAnchor - (totalRows - 1) * rankStep2;
    lastMuzzleY = fallbackMuzzleY;
    lastBoltDiag.length = 0;
    let ti = 0;
    let msi = 0; // mortarShadow instance cursor
    for (let i = 0; i < s.pp.cap && ti < MAX_TRACERS && !isBeam; i++) {
      if (!s.pp.alive[i]) continue;
      const vx = s.pVx[i], vy = s.pVy[i];
      const speed = Math.sqrt(vx * vx + vy * vy);
      // Bolts are CYLINDERS along their travel axis. A cylinder built on
      // +Y needs a quarter turn to lie down the corridor.
      const yaw = Math.atan2(vx, vy);
      // Per-bolt length jitter (index-derived, stable per stream): a rain
      // of uniform darts reads as a texture; varied lengths read as fire.
      // Widened 2026-08-18 (was 0.8-1.35): the per-soldier origin fix made
      // every column's bolts share one exact x, and at the old jitter span
      // they also came out nearly the same length -- four identical rails
      // instead of individual fire. A wider spread breaks that read at
      // range without touching where a bolt actually collides (`pY`,
      // untouched below).
      const jit = 0.55 + ((i * 37) % 13) / 13 * 1.05;
      const len = Math.max(1.4, Math.min(maxBoltLen, speed * 22)) * look.len * jit;
      // FIRING GEOMETRY, HEIGHT AXIS (founder VFX-overhaul audit,
      // 2026-08-20): X already varies per-projectile (the sim's own
      // rank-spread `ox`) and length already jitters (`jit` above), but
      // every bolt drew at the identical flat height (`1.02`, below) --
      // at 5+ soldiers a volley reads as one rigid sheet of parallel
      // lines regardless of weapon class, because the only two things
      // that DID vary (x, length) aren't enough on their own to break a
      // flat wall. Index-derived and stable per projectile, same
      // technique as `jit` -- NOT barrel-derived (see the two-strikes
      // note on X above; this is a new, narrower axis, not a retry).
      const yJit = ((i * 53) % 11) / 11 * 0.5 - 0.25;
      // PER-SOLDIER MUZZLE, Z ONLY (2026-08-18, narrowed 2026-08-18): the
      // sim spawns every projectile in a volley from ONE shared point
      // (step.ts, s.playerY + 0.35) -- correct for hit detection, which
      // never asked "which rifle" -- but rendering every bolt's tail
      // against that single point drew every stream tailing to the same
      // depth regardless of which soldier's rifle it belongs to: fire
      // read as a detached block floating in front of the squad. `i` is
      // the PROJECTILE POOL INDEX, stable frame to frame for a bolt's
      // whole lifetime, so `i % barrelCount` is a deterministic,
      // non-shimmering soldier assignment for the Z clamp below.
      //
      // X IS NOT TAKEN FROM THE BARREL (reverted 2026-08-18, two-strikes:
      // a52a2db's barrel-x snap read as 4 rigid parallel rails: a squad of
      // N shares only `barrelCount` real x positions, so every bolt from
      // one soldier stacked on that soldier's exact x with no variation
      // between neighbours. 32fcbff's lateral jitter was a second attempt
      // at papering over the same snap and did not survive review either
      // -- reverting to the sim's own per-projectile x below, which never
      // had this problem because every LIVE bolt already carries its own
      // real x from step.ts's rank-spread fire (`ox = playerX + rank *
      // width`), the same variation a real volley has.
      const myBarrel = barrelCount > 0 ? barrels[i % barrelCount] : undefined;
      const myMuzzleY = myBarrel ? myBarrel.z : fallbackMuzzleY;
      const myMuzzleX = (s.pX[i] - arenaW / 2) * SX;
      // CLAMPED to how far this bolt has actually travelled past ITS OWN
      // soldier's muzzle (2026-08-18): centering a full-length bolt at
      // pY-len*0.5 put its TAIL behind the firing soldier -- for a bolt
      // only 0.35u out of the barrel, len (1.4-4.6u) reaches yards past
      // the squad and off the bottom of frame, so fire read as
      // originating from the bottom edge of the screen. `Math.max(0, ...)`
      // is deliberate, not a floor for safety: a bolt that hasn't cleared
      // the muzzle yet draws at zero length (invisible) rather than
      // poking out backward by even an epsilon -- "tail never behind the
      // muzzle" has to hold exactly, not approximately. It grows to the
      // full `len` once travelled far enough, which is also what a tracer
      // does.
      const effectiveLen = Math.max(0, Math.min(len, s.pY[i] - myMuzzleY));
      lastBoltDiag.push({ tip: s.pY[i], tail: s.pY[i] - effectiveLen, muzzleY: myMuzzleY, originX: myMuzzleX });
      // Offset BACKWARD by half the (now clamped) length (2026-08-18, was
      // forward): the geometry is centred on its origin, so centering at
      // pY put the TRAILING edge at pY and the bright leading tip a full
      // `len` ahead of it -- but pY is the collision point (Lane B
      // measured hits landing within 0.75u of a live projectile's pY,
      // mean 0.33u, zero tick delay). The tip has to BE the collision
      // point: a tracer's bright head is the round, the tail is the
      // streak behind it, and the round is wherever the sim says it is.
      // Offsetting backward instead still keeps the geometry off the
      // muzzle (the original "shots pass through the shooters" problem
      // this offset exists to solve), because the tail trails toward the
      // shooter, not the tip.
      // FLIGHT PATH BY FAMILY. Presentation only -- the sim's straight line
      // is untouched, so nothing here can move a hitbox; this displaces the
      // DRAWN bolt around its true position. Without it every one of the ten
      // classes flew the identical dead-straight dart and the library read as
      // one gun in ten colours.
      //
      // Travel distance drives the phase rather than wall-clock, so the
      // helix is fixed in space: a time-driven wobble makes the whole stream
      // shimmer sideways at once, which reads as a rendering fault.
      const travel = s.pY[i] - s.playerY;
      let ox = 0, oy = 0, roll = 0;
      // Set only by 'lob': the arc's own tangent slope (d(height)/d(travel))
      // at this instant, so the drawn shell can nose over along its real
      // path instead of staying axis-aligned like a dart. 0 for every
      // other shape (no pitch applied).
      let lobSlope = 0;
      switch (look.shape) {
        case 'spiral': {
          // Tesla: paired arcs corkscrewing around the shot axis. The
          // instance index picks the phase so neighbouring streams braid
          // instead of moving as one ribbon. Radius capped to
          // `tracerOffsetCap` (bug A) -- at today's roster/default 0.5
          // mult this is already below 0.30, so the corkscrew tightens
          // rather than disappears.
          const r = Math.min(0.30, tracerOffsetCap);
          ox = Math.sin(travel * 1.5 + i * 2.2) * r;
          oy = Math.cos(travel * 1.5 + i * 2.2) * r;
          roll = travel * 1.5;
          break;
        }
        case 'lob': {
          // Mortar: a shell ARCS over its FULL flight -- scaled to the
          // weapon's own effective range (computed above, mirrored from
          // step.ts) so the arc completes exactly where the round dies,
          // not at a fixed distance that leaves a longer-range weapon's
          // tail flying flat. `u` is 0 at the muzzle, 1 at effective range.
          // EXEMPT FROM THE LATERAL CAP (bug A): `ox` is never set in
          // this case (stays 0), so a mortar shell's LATERAL (render-X)
          // position always matches its true sim position exactly -- it
          // cannot cause the ground-plane hit-test mismatch the cap
          // exists for. `oy` here is a HEIGHT arc only; the round's real
          // ground position is already shown by its own shadow decal
          // below, specifically so the player never has to guess it from
          // the floating shell. The full MORTAR_APEX(2.6) arc is this
          // weapon's entire read and stays untouched.
          const u = Math.min(1, travel / effectiveRange);
          oy = Math.max(0, Math.sin(u * Math.PI)) * MORTAR_APEX;
          // Tangent slope at this instant (d(height)/d(travel)) -- used
          // below to pitch the drawn shell along its real path instead of
          // holding it axis-aligned like a dart. Sampled at the SAME
          // clamped `u` as `oy`, so at the far edge (u=1) it reads as a
          // steep dive into the ground rather than snapping level.
          lobSlope = ((MORTAR_APEX * Math.PI) / effectiveRange) * Math.cos(u * Math.PI);
          // SHADOW: the round's REAL ground position (pX, pY), never the
          // drawn/arced one -- an ellipse under a floating shell is what
          // sells "this is going to land here." Shrinks toward the apex,
          // full size at muzzle height and at touchdown.
          const rx = (s.pX[i] - arenaW / 2) * SX;
          const rz = s.pY[i] * S;
          lobLastPos.set(i, { x: rx, z: rz, travel });
          if (msi < MAX_MORTAR_SHADOWS) {
            const shrink = 1 - Math.min(0.55, (oy / MORTAR_APEX) * 0.55);
            // FADE IN OVER THE FIRST FEW UNITS OF TRAVEL: a round still
            // basically at the muzzle is also basically at the SQUAD's own
            // depth -- right next to the camera -- and a near-full-size
            // ellipse there reads as a huge disc parked on the formation's
            // own heads rather than a shadow out on the road. Full size is
            // reached once the round has actually cleared the squad.
            const ramp = Math.min(1, Math.max(0, travel) / 3);
            tmpV.set(rx, 0.03, rz);
            tmpQ.setFromEuler(new Euler(-Math.PI / 2, 0, 0));
            tmpS.set(0.55 * shrink * ramp, 0.75 * shrink * ramp, 1);
            mortarShadow.setMatrixAt(msi, tmpM.compose(tmpV, tmpQ, tmpS));
            msi++;
          }
          break;
        }
        case 'rocket':
          // Missiles wander slightly as they chase, and drop a smoke puff
          // behind them every few units of travel. Wander capped (bug A).
          ox = Math.sin(travel * 0.55 + i) * Math.min(0.18, tracerOffsetCap);
          if (((i + Math.floor(travel * 0.6)) & 7) === 0) {
            emit(s.pX[i], s.pY[i] - 0.6, (Math.random() - 0.5) * 0.05,
              0.02 + Math.random() * 0.03, -0.02,
              16 + Math.random() * 10, look.halo, 0.5);
          }
          break;
        case 'shell':
          // FIRING GEOMETRY (2026-08-20): shotgun/cannon/flak previously
          // fell through to the same dead-straight dart as a rifle bolt
          // despite having their own GunLook -- at density they read as
          // an identical wall, cosmetics aside. A shell FANS outward as
          // it clears the muzzle, alternating side by pool index (stable,
          // not flickering) so a volley reads as spreading buckshot
          // rather than a rail of parallel rounds. Plateau capped (bug A)
          // -- this is the one LATERAL (render-X) offset among sweep/
          // slam/shell, so it's the one of the three that actually could
          // read as "visually overlapping, didn't register."
          ox = (i % 2 === 0 ? 1 : -1) * Math.min(0.65, tracerOffsetCap, travel * 0.10);
          break;
        case 'sweep':
          // ARC BLADE: the round is a flat crescent that RISES as it
          // crosses, so a volley reads as one wide scything cut instead of
          // a spray of pellets. Rolled flat so its width faces the camera.
          // EXEMPT FROM THE LATERAL CAP (bug A, corrected 2026-09-05):
          // this is a HEIGHT (render-Y) effect only -- `ox` is never set
          // in this case -- so it cannot cause the ground-plane hit-test
          // mismatch the cap exists for. The 0.35-to-0.90 rising arc is
          // the entire read of this weapon; capping it was corrected
          // after confirming it was never the actual bug.
          oy = 0.35 + Math.min(1, travel / 5) * 0.55;
          roll = Math.PI / 2;
          break;
        case 'slam':
          // STORM HAMMER: a heavy slab that drops as it goes out and
          // hammers the deck, throwing sparks where it lands. EXEMPT FROM
          // THE LATERAL CAP for the same reason as sweep -- height only,
          // `ox` never set, the 0.75 drop is the weapon's whole read.
          oy = 0.75 - Math.min(1, travel / 4.5) * 0.85;
          if (travel > 3.4 && ((i + Math.floor(travel * 2)) & 3) === 0) {
            emit(s.pX[i], s.pY[i], (Math.random() - 0.5) * 0.22, 0.12, 0.04,
              12, look.mid, 1.6);
          }
          break;
        default:
          break;
      }
      // MELEE ANCHOR (2026-08-20): sweep/slam streaks otherwise draw at
      // the same flat 1.02 height as every other weapon, unrelated to
      // where the held blade/hammer prop actually sits on the animated
      // rig -- "streams emit from nowhere" was this, not a typo. Leans
      // toward the real barrel height (damped, not snapped, since barrel
      // resolution is coarse -- same caution as the X two-strikes note,
      // just on a different axis where a coarse read is far less visible:
      // height doesn't need per-soldier granularity to look right, only
      // rank granularity, which barrels already give it).
      if ((look.shape === 'sweep' || look.shape === 'slam') && myBarrel) {
        oy += (myBarrel.y - 1.02) * 0.6;
      }
      tmpV.set(myMuzzleX + ox, 1.02 + oy + yJit,
        (s.pY[i] - effectiveLen * 0.5) * S);
      if (look.shape === 'lob') {
        // NOSE OVER ALONG THE ARC TANGENT, not axis-aligned like a dart.
        // Local +Y (the cylinder's own axis) is pointed directly at the
        // real 3D direction of travel -- lateral (yaw, unchanged from
        // every other shape) plus the arc's own vertical rate (`lobSlope`)
        // -- via setFromUnitVectors rather than composing another Euler
        // rotation onto the existing yaw/roll one, which stays exact at
        // any slope instead of needing the two rotations' axes untangled.
        tmpDir.set(Math.sin(yaw), lobSlope, Math.cos(yaw)).normalize();
        tmpQ.setFromUnitVectors(UNIT_Y, tmpDir);
      } else {
        tmpQ.setFromEuler(new Euler(Math.PI / 2, roll, -yaw, 'YXZ'));
      }
      tmpS.set(gw, effectiveLen, gw);
      boltCore.setMatrixAt(ti, tmpM.compose(tmpV, tmpQ, tmpS));
      tmpS.set(gw, effectiveLen * 0.82, gw);
      boltMid.setMatrixAt(ti, tmpM.compose(tmpV, tmpQ, tmpS));
      tmpS.set(gw, effectiveLen * 0.6, gw);
      boltHalo.setMatrixAt(ti, tmpM.compose(tmpV, tmpQ, tmpS));
      ti++;
    }
    for (const m of [boltCore, boltMid, boltHalo]) {
      m.count = ti;
      m.instanceMatrix.needsUpdate = true;
    }
    mortarShadow.count = msi;
    mortarShadow.instanceMatrix.needsUpdate = true;

    // MORTAR MISS BURST: a round that expired at range never fires
    // Ev.Hit (main.ts only bursts on an actual enemy hit), so a shell
    // landing on empty ground would vanish silently. Diffed against
    // `s.pp.alive` rather than tracked separately -- any pool index this
    // map still remembers that is no longer alive died THIS frame, either
    // by hitting something (already bursts via Ev.Hit, close enough to
    // this same position that a second burst here would double it -- skip
    // it by checking it actually reached near its own range ceiling) or
    // by running out of road.
    for (const [idx, last] of lobLastPos) {
      if (s.pp.alive[idx]) continue;
      if (last.travel >= effectiveRange - 0.5) {
        fx.hit(last.x / SX + arenaW / 2, last.z / S, 10, look.mid);
      }
      lobLastPos.delete(idx);
    }

    // Muzzle flares fade fast; the light they cast fades with them.
    for (const [i, sp] of muzzles.entries()) {
      const l = muzzleLife[i]!;
      if (l <= 0) { if (sp.visible) sp.visible = false; continue; }
      // Clamped so a long frame cannot swallow the whole flash: on a slow
      // device the volley must still read, and on a fast one this is a
      // no-op (dt is well under the clamp at 60fps).
      muzzleLife[i] = l - Math.min(dt, MUZZLE_LIFE_S * 0.5);
      sp.material.opacity = l / MUZZLE_LIFE_S;
      // Flare sprite tinted by the class -- the barrel line is where a
      // weapon change should be visible FIRST.
      sp.material.color.setHex(look.mid);
      const b = barrels[muzzleBarrel[i]!];
      if (b) sp.position.set(b.x, b.y, b.z);
    }
    gunFlash *= 0.82;
    // The volley light belongs on the guns, not on the formation origin.
    const bl = barrels[0];
    gunLight.position.set(bl ? bl.x + 1.2 : px, bl ? bl.y + 0.35 : 1.3, bl ? bl.z : pz + 0.6);
    gunLight.intensity = gunFlash * 11;

    // --- gems
    let gi = 0;
    for (let i = 0; i < s.kp.cap && gi < 220; i++) {
      if (!s.kp.alive[i]) continue;
      tmpV.set((s.kX[i] - arenaW / 2) * SX, 0.5, s.kY[i] * S);
      tmpS.set(1, 1, 1);
      gems.setMatrixAt(gi++, tmpM.compose(tmpV, tmpQ.identity(), tmpS));
    }
    for (let i = gi; i < 220; i++) {
      gems.setMatrixAt(i, tmpM.compose(HIDDEN, tmpQ, tmpS.set(0, 0, 0)));
    }
    gems.instanceMatrix.needsUpdate = true;

    // --- gates
    let pi = 0;
    let si = 0;
    let wi = 0;
    let pyi = 0;
    for (let i = 0; i < s.gp.cap && pi < gatePanels.length; i++) {
      if (!s.gp.alive[i]) continue;
      const op = s.gOp[i];
      const lanes = s.config.gateGroups[s.gGroup[i]]!.lanes.length;
      const lx = ((s.gLane[i] + 0.5) / lanes - 0.5) * arenaW * SX;
      // COLOUR FOLLOWS THE REWARD, NOT THE OP (founder, 2026-08-24). A
      // pumpable multiply lane (isle 12's dial) can start at x0.5 -- a
      // penalty -- and climb past x1.0 as fire raises gValue; colouring by
      // op alone (or by a static threshold on the AUTHORED value) cannot
      // track that, because the live value drifts away from what was
      // authored. armory/barrier/armscrate/vehicle/ally/armour hand over a
      // real prize regardless of crowd size -- always a gain, always green
      // -- mirroring verify-level's own CROWD_OPS carve-out (laneRaw's doc
      // comment: their `value` column is vestigial). add/subtract/multiply
      // are the only ops whose crowd-in transform can go either way, so
      // THEY are coloured by comparing applyGateOp's raw result -- the
      // SAME arithmetic verify-level's check H sweeps -- against the crowd
      // walking in right now (s.squadSize), not a per-op static threshold.
      // ALL-NEGATIVE GATES (founder, 2026-08-22): an add lane can author a
      // negative starting value (a penalty), which this subsumes -- a
      // negative-value add's raw result is below squadSize, same as any
      // other loss.
      const good = op === 3 || op === 4 || op === 5 || op === 6 || op === 7 || op === 8
        || applyGateOp(s.squadSize, op, s.gValue[i], Infinity) >= s.squadSize;
      // A crate NAMES the gun locked inside it and counts its own armour
      // down, so the player can see what they are shooting for and how
      // close they are -- that is the whole appeal of the mechanic.
      const broken = s.gHp[i] <= 0;
      // The PRIZE is the 3D model on the podium; the panel is only the
      // armour still in the way. Spelling the gun's name across a billboard
      // was arcade-era signage -- it shouted the one thing the player can
      // already see and buried the number that actually changes.
      //
      // Ceil'd to a whole HP number so the texture is rebuilt at most once
      // per point of damage rather than tracking fractional HP: at 90
      // streams a per-hit relabel with no quantisation was allocating a
      // canvas texture every frame, which is what made heavy fire stutter.
      // A crate or a tank sits BEHIND its wall, so the armour number reads
      // as belonging to the thing blocking the lane.
      const isWall = op === 4 || op === 5 || op === 6 || op === 7 || op === 8;
      // ISLE-12 LEGIBILITY PASS (2026-08-22): raw HP, not percent, on the
      // armoured face -- "its remaining HP as a large numeral", counting
      // down as it takes fire, matching what the player is actually
      // shooting at rather than a derived ratio.
      const hpLeft = Math.max(0, Math.ceil(s.gHp[i]));
      // THE DIAL (founder, 2026-08-22): a pumpable lane's gValue can climb
      // continuously (0.1/hit, gates.csv pump_per_hit) -- same quantised-
      // relabel discipline as hpLeft above (rounded to a fixed step) so
      // the texture rebuilds on a real digit change, not every bullet.
      // Every other lane (gPump===0, every chapter but 12) keeps the exact
      // raw-value formatting it always has -- untouched, byte-identical.
      //
      // GATE SIGN RENDERING FIX (founder finding, 2026-08-25): `.toFixed(1)`
      // fires for EVERY chapter-12 add lane regardless of whether the live
      // value is currently a whole number, because `pumped` only tests
      // whether pump_per_hit is authored nonzero (true for all ten) --
      // not whether the value has actually accumulated a fraction yet.
      // Chapter 12's gate values are whole numbers by design (check J);
      // rounding to the nearest integer for display keeps the quantised-
      // relabel discipline above (still rebuilds on a real digit change,
      // not every bullet) while never showing a decimal point.
      const pumped = s.gPump[i] > 0;
      const gVal = pumped ? String(Math.round(s.gValue[i])) : String(s.gValue[i]);
      // A negative gVal already carries its own minus sign (String(-1) ===
      // "-1") -- prefixing "+" unconditionally for op 0 produced "+-1" for
      // a penalty add lane. Only prepend "+" when the value is actually
      // non-negative.
      const label = isWall
        ? (broken ? 'OPEN' : String(hpLeft))
        : op === 3 ? 'ARMS'
        : op === 0 ? (s.gValue[i] < 0 ? gVal : `+${gVal}`)
        : op === 1 ? `-${gVal}`
        : `x${gVal}`;
      // ONE BAND (founder, 2026-08-22): each lane's segment now spans
      // (nearly) its full lane width and the group's segments sit
      // edge-to-edge, reading as one band across the corridor rather than
      // a small chip floating mid-lane. Uses SX (the corridor's actual
      // widened render scale) for width -- the prior 0.33-factor version
      // scaled width by the UN-widened S, so even a factor of 1.0 there
      // covered only ~53% of the true lane.
      const widthFactor = 0.98;
      const panelW = (arenaW / lanes) * widthFactor * SX;
      const panelH = isWall ? 1.7 : 2.0;
      const aspect = panelW / panelH;
      const panel = gatePanels[pi]!;
      // BARRIER PAYOUT (founder, 2026-08-24): this was a barrier's ONLY
      // panel, and it relabelled itself "OPEN" the instant the wall broke
      // -- exactly the moment the player earned the payout, the one thing
      // that panel had been reporting, became illegible. Every other
      // armoured type (crate/vehicle/ally/armour) already keeps its payout
      // visible for the whole approach via the separate PRIZE hologram
      // (`stations`, below) -- a barrier has no hologram, so this was its
      // only signal. It now disappears at zero instead of relabelling; the
      // payout moves to its own persistent sign (gatePayoutPanels, below)
      // that never says "OPEN".
      const barrierBroken = op === 4 && broken;
      if (barrierBroken) {
        panel.mesh.visible = false;
      } else {
        // `good` is part of the cache key so a colour flip (the dial
        // crossing breakeven) invalidates the cached texture even on a
        // tick where the label text itself happens not to change --
        // otherwise a stale colour could sit under a fresh number.
        const cacheKey = `${label}|${aspect.toFixed(2)}|${good}`;
        if (panel.label !== cacheKey) {
          panel.mesh.material.map?.dispose();
          panel.mesh.material.map = gateTexture(label, good, aspect);
          panel.mesh.material.needsUpdate = true;
          panel.label = cacheKey;
        }
        panel.mesh.visible = true;
      }
      // Armoured segments sit at wall/station HEIGHT (~1.7, matching the
      // rock wall's own span) so the numeral overlays the object it's
      // reporting on -- previously floated at y=2.9, well above the wall,
      // which is what made it read as a disconnected sign rather than the
      // object's own face. Portal (add/sub/multiply/armory) segments sit
      // a little higher, at drive-through height.
      const panelWorldY = isWall ? 1.5 : 1.6;
      panel.mesh.position.set(lx, panelWorldY, s.gY[i] * S);
      // GATE SIGN RENDERING REVERT (founder finding, 2026-08-25): 1c99b11's
      // distance-compensated shrink hit its own bounding-box target by
      // making the sign illegible -- ~25x8px at 560x940, t=2s, judged by
      // eye as unreadable and sitting off the road. Reverted outright
      // rather than re-tuned: the founder's own framing is "signs return
      // to their pre-1c99b11 world size", not "shrink less". The overlap/
      // clip finding that motivated 1c99b11 is being addressed structurally
      // instead, in the same session -- chapter 12's gates are being
      // rebuilt as ONE object per group (see FIX 2, isle-12 monolith
      // rebuild), which removes the two-lane pair whose SECOND lane was
      // half of what was overlapping/clipping in the first place.
      // CLIP GUARD REMOVED (2026-08-22): the 54-cell depth x CAM_BACK x FOV
      // sweep showed that, unclamped, the two segments straddle opposite
      // frame edges at resolve and each renders its own INNER half -- the
      // guard was collapsing one segment to width 0 (removing information)
      // rather than preserving the readable half each already has.
      panel.mesh.scale.set(panelW, panelH, 1);

      const armor0 = isWall ? Math.max(1, gateArmor(s, i)) : 1;
      const frac = Math.max(0, Math.min(1, s.gHp[i] / armor0));
      if (op === 4 && wi < walls.length) {
        // BARRIER: the rock wall, shot down block by block.
        const w = walls[wi]!;
        const want = s.gHp[i] <= 0 ? 0 : Math.max(1, Math.ceil(frac * w.rocks.length));
        if (want !== w.standing) {
          // Every block that just went throws its own rubble, so breaking
          // through is a sequence of impacts rather than a number ticking.
          for (let k = want; k < w.standing; k++) {
            const rock = w.rocks[k];
            if (!rock) continue;
            const gx = ((s.gLane[i] + 0.5) / lanes) * arenaW;
            for (let d = 0; d < 5; d++) {
              const a = Math.random() * Math.PI * 2;
              emit(gx + rock.position.x / SX, s.gY[i], Math.cos(a) * 0.16,
                0.10 + Math.random() * 0.16, Math.sin(a) * 0.16,
                22 + Math.random() * 14, 0x8b8f96, 1.1);
            }
          }
          for (const [k, rock] of w.rocks.entries()) rock.visible = k < want;
          w.standing = want;
        }
        w.root.position.set(lx, 0, s.gY[i] * S);
        w.root.visible = true;
        wi++;
        // PAYOUT SIGN: persistent, never "OPEN". Floats above the wall
        // (y=2.9 -- the height a numeral used to float at before it was
        // moved onto the wall's own face, see the comment below; here
        // that "disconnected sign" read is exactly what's wanted, since
        // this sign reports the prize, not the wall's own condition) from
        // the moment the gate enters frame through to the group's
        // resolution -- `s.gp.alive[i]` goes false, and this loop's own
        // `continue` above stops updating this slot, at the same tick the
        // countdown panel's object does.
        if (pyi < gatePayoutPanels.length) {
          const pp = gatePayoutPanels[pyi]!;
          const payoutLabel = s.gValue[i] < 0 ? gVal : `+${gVal}`;
          const payoutH = 1.1;
          const payoutAspect = panelW / payoutH;
          const payoutCacheKey = `${payoutLabel}|${payoutAspect.toFixed(2)}|${good}`;
          if (pp.label !== payoutCacheKey) {
            pp.mesh.material.map?.dispose();
            pp.mesh.material.map = gateTexture(payoutLabel, good, payoutAspect);
            pp.mesh.material.needsUpdate = true;
            pp.label = payoutCacheKey;
          }
          pp.mesh.visible = true;
          // AUDIT (frame-audit 2026-08-30-live, t05/gate stills): the payout
          // sign's y was chosen to clear the WALL PROP, but the barrier's
          // own numeral panel (panelWorldY=1.5, panelH=1.7) tops out at
          // exactly 2.35 -- edge-to-edge with this sign's old y=2.9 minus
          // its own half-height (payoutH=1.1 -> 0.55), zero world-space gap.
          // Two real, correctly-rendering numerals (cost above, reward
          // below) reading as one fused, ambiguous panel in a live capture.
          // Nudged to 3.05 for a real ~0.15 unit gap -- small enough that
          // "floats above, disconnected from the wall" (the original intent)
          // is unchanged, large enough that the two numerals visibly part.
          pp.mesh.position.set(lx, 3.05, s.gY[i] * S);
          pp.mesh.scale.set(panelW, payoutH, 1);
          pyi++;
        }
      } else if ((op === 5 || op === 6 || op === 7 || op === 8) && si < stations.length) {
        // PRIZE: the charge station. The hologram shows the prize the
        // whole approach; the capacitor heats blue -> magenta as it takes
        // fire; a full charge materialises the real thing.
        const st = stations[si]!;
        const kind = op === 6 ? 'tank'
          : op === 7 ? 'ally'
          : op === 8 ? 'armour'
          : (s.config.weapons[Math.min(s.config.weapons.length - 1,
              s.weaponIdx + 1)]?.vfxProfile ?? 'rifle');
        // FOUNDER FINDING (2026-09-04, screenshot supplied): "hollogramic
        // geometric shape of the monster reward... like a glitch or bug
        // visual" -- a purple box-torso/box-limb humanoid (buildAllyPrize,
        // the SAME procedural placeholder the field-ally system used
        // before this session's fix) floating as this gate wall's prize
        // hologram, next to its "OPEN"/payout signs. This is a THIRD,
        // separate reward-preview system from the monolith preview and
        // the field-ally rig -- missed twice already because both prior
        // searches stayed inside the monolith rendering block; this one
        // belongs to the gate/wall system (`op` 5-8, `stations[]`) instead.
        // `guardianSrc`/riggedSrc's colossus are real, already-proven
        // Mixamo rigs (this session's own work) -- reused here rather
        // than building a fourth asset. Skip creating anything this tick
        // if neither has finished loading yet (retried every subsequent
        // tick via `st.kind !== kind` staying true) instead of falling
        // back to the box placeholder this fix exists to remove.
        const allySrc = kind === 'ally' ? (guardianSrc ?? riggedSrc.get(2)) : undefined;
        if (kind === 'ally' && !allySrc) {
          // not loaded yet -- try again next tick, no placeholder shown
        } else if (st.kind !== kind) {
          if (st.holo) { st.holoSlot.remove(st.holo); st.holo = null; }
          if (st.real) { st.realSlot.remove(st.real); st.real = null; }
          if (st.solid) { st.holoSlot.remove(st.solid); st.solid = null; }
          st.solidMeshes.length = 0; st.holoMeshes.length = 0;
          const src = kind === 'tank'
            ? buildAetherSled(0.5)
            : kind === 'ally' ? (cloneSkinned(allySrc!) as Group)
            : kind === 'armour' ? buildArmourPrize()
            : (gunModels[kind] ? (gunModels[kind]!.clone(true) as Group) : buildGun(kind));
          if (kind === 'ally') {
            // SAME METALNESS FIX AS EVERY OTHER USE OF THIS ASSET FAMILY
            // this session: the FBX->glTF converter guesses a non-zero
            // metallic value for this diffuse zombie-skin texture, which
            // reads as a washed-out near-uniform blob under this scene's
            // lighting otherwise.
            src.traverse((o) => {
              if (!(o as Mesh).isMesh) return;
              const m = (o as Mesh).material as MeshStandardMaterial | MeshStandardMaterial[];
              for (const mat of Array.isArray(m) ? m : [m]) {
                if ((mat as MeshStandardMaterial).isMeshStandardMaterial) (mat as MeshStandardMaterial).metalness = 0;
              }
            });
          }
          st.real = src;
          st.realSlot.add(st.real);
          // NORMALISE BY MEASURED HEIGHT. One magic multiplier cannot serve
          // a 0.6-unit pistol and a 2-unit monster: 3.1x made the gun read
          // right and turned the ally into a six-unit slab that filled the
          // sky. Every prize is scaled to the same advertised height, so
          // the podium always reads the same regardless of what is on it.
          const bb = new Box3().setFromObject(src);
          const h = Math.max(0.001, bb.max.y - bb.min.y);
          const showScale = 2.3 / h;
          st.holo = holoify(src);
          st.holo.scale.multiplyScalar(showScale);
          st.holoSlot.add(st.holo); glow(st.holo);
          st.holo.traverse((o) => { if ((o as Mesh).isMesh) st.holoMeshes.push(o as Mesh); });
          // The solid twin, same size as the ghost, revealed part by part.
          // cloneSkinned, same reason as holoify's own clone above -- a
          // plain .clone(true) breaks skinning on a real rigged source.
          st.solid = cloneSkinned(src) as Group;
          st.solid.scale.multiplyScalar(showScale);
          st.holoSlot.add(st.solid);
          st.solid.traverse((o) => { if ((o as Mesh).isMesh) st.solidMeshes.push(o as Mesh); });
          // Deterministic reveal order, largest part last so the payoff
          // builds: sort by bounding-sphere radius ascending.
          st.solidMeshes.sort((a, b) => {
            a.geometry.computeBoundingSphere(); b.geometry.computeBoundingSphere();
            return (a.geometry.boundingSphere?.radius ?? 0) - (b.geometry.boundingSphere?.radius ?? 0);
          });
          for (const m of st.solidMeshes) m.visible = false;
          st.kind = kind;
          st.wasCharged = false;
        }
        const charged = s.gHp[i] <= 0;
        // charge: 0 = untouched hologram, 1 = fully materialised.
        const chargeF = 1 - frac;
        if (st.holo) {
          st.holo.visible = !charged;
          st.holoSlot.position.y = 3.0 + Math.sin(frameNo * 0.05 + si) * 0.14;
          st.holoSlot.rotation.y = frameNo * 0.02;
          // PROGRESSIVE MATERIALISATION -- the station's entire hook. The
          // ghost starts as wireframe; every point of damage reveals more
          // of the SOLID prize inside it, smallest parts first, so the
          // thing you are buying visibly takes shape while you fight for
          // it. At the Partial threshold the wireframe becomes surface; at
          // Full the ghost is gone and the object is real.
          const reveal = Math.floor(chargeF * st.solidMeshes.length + 1e-6);
          for (const [k, m] of st.solidMeshes.entries()) m.visible = !charged && k < reveal;
          const flick = (0.46 - chargeF * 0.3) + Math.sin(frameNo * 0.31 + si * 2.1) * 0.05
            + (frameNo % 47 === 0 ? 0.18 : 0);
          for (const hm of st.holoMeshes) {
            const m = hm.material as MeshBasicMaterial;
            m.opacity = Math.max(0.08, flick);
          }
          if (st.solid) st.solid.visible = !charged;
        }
        if (st.real) {
          st.real.visible = charged;
          if (charged) {
            // The materialised prize turns proudly, larger than the ghost
            // that promised it.
            st.realSlot.rotation.y = frameNo * 0.03;
            st.realSlot.scale.setScalar(1.4);
            st.realSlot.position.y = 2.5 + Math.sin(frameNo * 0.07 + si) * 0.08;
          }
        }
        // Capacitor heat: cold blue at zero charge, hot magenta at full.
        st.capMat.emissive.setHex(0x2242c8).lerp(tmpColor.setHex(0xff2fa8), chargeF);
        st.capMat.emissiveIntensity = 1.2 + chargeF * 2.2;
        // The rim reads the TIER, not just the amount: red means arriving
        // now pays nothing, amber means a weakened prize, green means the
        // full one. The player can price the next second of fire at a
        // glance -- that readability is what makes the threshold worth
        // fighting for.
        const rm = st.ring.material as MeshStandardMaterial;
        rm.emissive.setHex(charged || chargeF >= 0.85 ? 0x2fe86b
          : chargeF >= 0.4 ? 0xe8a22f : 0xe83a2f);
        rm.emissiveIntensity =
          1.6 + chargeF * 2.4 + (charged ? Math.sin(frameNo * 0.4) * 1.2 + 1.2 : 0);
        if (charged && !st.wasCharged) {
          st.wasCharged = true;
          // MATERIALISATION. The one moment this machine exists for.
          const gx = ((s.gLane[i] + 0.5) / lanes) * arenaW;
          fx.ring(gx, s.gY[i], 0x9fe8ff, 4.0, 26);
          fx.burst(gx, s.gY[i], 30, 0x7ce4ff, 0.3);
        }
        st.root.position.set(lx, 0, s.gY[i] * S);
        st.root.visible = true;
        si++;
      }
      pi++;
    }
    for (let i = pi; i < gatePanels.length; i++) gatePanels[i]!.mesh.visible = false;
    for (let i = pyi; i < gatePayoutPanels.length; i++) gatePayoutPanels[i]!.mesh.visible = false;
    for (let i = wi; i < walls.length; i++) walls[i]!.root.visible = false;
    for (let i = si; i < stations.length; i++) stations[i]!.root.visible = false;

    // Birth scale decay (L2), once per frame.
    for (let i = 0; i < enemyGrow.length; i++) if (enemyGrow[i]! > 0) enemyGrow[i]!--;

    // --- block theatre: shards fly, bounce once, settle, fade ------------
    for (const sh of shards) {
      if (!sh.m.visible) continue;
      sh.life -= dt;
      if (sh.life <= 0) { sh.m.visible = false; continue; }
      sh.vy -= 14 * dt;
      sh.m.position.x += sh.vx * dt;
      sh.m.position.y += sh.vy * dt;
      sh.m.position.z += sh.vz * dt;
      sh.m.rotation.x += sh.sx * dt; sh.m.rotation.z += sh.sz * dt;
      if (sh.m.position.y < 0.2 && sh.vy < 0) {
        // One bounce, then rest ON the deck — debris settles, never sinks
        // (Iron Rule 8) and never slides forever.
        sh.m.position.y = 0.2;
        sh.vy = Math.abs(sh.vy) > 1.6 ? Math.abs(sh.vy) * 0.35 : 0;
        sh.vx *= 0.55; sh.vz *= 0.55; sh.sx *= 0.4; sh.sz *= 0.4;
      }
      if (sh.life < 0.5) {
        (sh.m.material as MeshStandardMaterial).opacity = sh.life / 0.5;
      }
    }
    for (const hf of hitFlashes) {
      if (!hf.sp.visible) continue;
      hf.t += dt;
      // `tuning.impactFlashDuration` (default 0.14, matching `hf.dur`'s
      // own pool-creation default exactly) overrides the fixed per-entry
      // value live -- `hf.dur` itself is left on each entry unused,
      // rather than removed, since nothing else reads it.
      const dur = tuning.impactFlashDuration;
      if (hf.t >= dur) { hf.sp.visible = false; continue; }
      const u = hf.t / dur;
      // Punch in fast, settle, fade -- a hit reads as an instant, not a
      // glow that eases in. Shrinks slightly over its life so it never
      // looks like a static decal sitting on the body.
      hf.sp.material.opacity = 1 - u * u;
      hf.sp.scale.setScalar(hf.size * (1.3 - u * 0.5));
    }
    for (const st of streaks) {
      if (!st.sp.visible) continue;
      st.t += dt;
      if (st.t < 0) continue;
      const u = Math.min(1, st.t / st.dur);
      // Ease-out arc: fast leave, soft land, a lifted mid-point.
      const e = 1 - (1 - u) * (1 - u);
      st.sp.position.set(
        st.fx + (st.tx - st.fx) * e,
        1.1 + Math.sin(u * Math.PI) * 2.2,
        st.fz + (st.tz - st.fz) * e);
      st.sp.scale.setScalar(0.9 + Math.sin(u * Math.PI) * 0.5);
      if (u >= 1) {
        st.sp.visible = false;
        // Quality-audit L-tier: a bare `hit` flash read as a hard pop --
        // this small burst gives the landing a few frames of body (a puff)
        // without adding a new asset or texture, same tint as the flash.
        fx.hit(st.tx / SX + arenaW / 2, st.tz / S, 3, 0x9fffe8);
        fx.burst(st.tx / SX + arenaW / 2, st.tz / S, 10, 0x9fffe8, 0.16);
      }
    }

    if (gunArc.obj) {
      gunArc.t += dt;
      const u = Math.min(1, gunArc.t / 0.7);
      const e = 1 - (1 - u) * (1 - u);
      const tx = (s.playerX - arenaW / 2) * SX, tz = s.playerY * S;
      gunArc.obj.position.set(
        gunArc.fx + (tx - gunArc.fx) * e,
        1.4 + Math.sin(u * Math.PI) * 2.6,
        gunArc.fz + (tz - gunArc.fz) * e);
      gunArc.obj.rotation.y += dt * 9;
      // Motion trail (the founder's reference bar: moving prizes leave light).
      if ((frameNo & 2) === 0) {
        fx.hit(gunArc.obj.position.x / SX + arenaW / 2, gunArc.obj.position.z / S, 2, 0xffb84a);
      }
      if (u >= 1) {
        fx.hit(s.playerX, s.playerY, 6, 0xffb84a);
        world.remove(gunArc.obj);
        gunArc.obj = null;
      }
    }

    // --- monoliths: the rock, the fissure, the counter, the ghost inside
    for (let i = 0; i < monoRigs.length; i++) {
      const rig = monoRigs[i]!;
      if (i >= s.mp.cap || !s.mp.alive[i]) {
        // BLOCK THEATRE: leaving the field is a STAGED exit, never a
        // disappearance. Broken (hp reached 0) -> the break burst;
        // expired with hp left -> the fizzle (gray dust, the loss shown).
        if (rig.lastAlive) {
          const tint = PAYLOAD_TINT[rig.lastOp] ?? 0xb47aff;
          if (rig.lastHp <= 1) {
            burstShards(rig.lastWX, rig.lastWZ, tint, 12);
            fx.ring((rig.lastWX / SX) + arenaW / 2, rig.lastWZ / S, tint, 7.5, 26);
            lastBreak.wx = rig.lastWX; lastBreak.wz = rig.lastWZ;
            lastBreak.at = strideClock; lastBreak.op = rig.lastOp;
            if (onBlockBreakCb) onBlockBreakCb(Math.min(1, rig.counterVal > 20000 ? 1 : 0.5));
            // bodies: teal comets fly INTO the formation.
            if (rig.lastOp === 1) {
              launchStreaks(rig.lastWX, rig.lastWZ,
                (s.playerX - arenaW / 2) * SX, s.playerY * S, 5, tint);
            }
            // armour: the shield blooms from the block onto the squad.
            if (rig.lastOp === 3) {
              launchStreaks(rig.lastWX, rig.lastWZ,
                (s.playerX - arenaW / 2) * SX, s.playerY * S, 4, tint);
            }
            // crate: the WON GUN arcs home (L6a).
            if (rig.lastOp === 2) {
              if (gunArc.obj) world.remove(gunArc.obj);
              const g0 = genCache.get('gun-mk') ?? genCache.get('gun-rifle');
              if (g0) {
                gunArc.obj = g0.clone(true) as Group;
                gunArc.obj.scale.multiplyScalar(2.2);
                world.add(gunArc.obj);
                gunArc.t = 0; gunArc.fx = rig.lastWX; gunArc.fz = rig.lastWZ;
              }
            }
            // vehicle: the tank SLIDES IN from the block (L6b).
            if (rig.lastOp === 4) {
              vehSlide.t = 0; vehSlide.fx = rig.lastWX; vehSlide.fz = rig.lastWZ;
            }
          } else {
            // FIZZLE: gray dust, no reward — the loss is shown.
            fx.hit((rig.lastWX / SX) + arenaW / 2, rig.lastWZ / S, 8, 0x8a8f98);
          }
          rig.lastAlive = false;
        }
        rig.root.visible = false; continue;
      }
      rig.root.visible = true;
      rig.root.position.set((s.mX[i] - arenaW / 2) * SX, 0, s.mY[i] * S);
      const left = Math.max(0, s.mHp[i]);
      const frac = left / Math.max(1, s.mHpMax[i]);
      const broken = 1 - frac;
      const tint = PAYLOAD_TINT[s.mOp[i]] ?? 0xb47aff;
      // CHIPPING: every hp drop this frame throws sparks and rock chips at
      // the impact face, escalating as the block empties (the destruction
      // canon: each hit feels bigger than the last).
      if (rig.lastAlive && rig.lastHp > left) {
        const sev = Math.min(1, (rig.lastHp - left) / Math.max(1, s.mHpMax[i] * 0.02));
        if ((frameNo & 1) === 0) {
          fx.hit(s.mX[i], s.mY[i] - 1.0, Math.round(2 + sev * 3 + broken * 4), tint);
        }
        // WRECKAGE (item 4): rock chunks off the cracking face, sparser
        // than the spark chip above -- a block sheds rock, not sparks.
        if ((frameNo & 3) === 0) {
          fx.debris(s.mX[i], s.mY[i] - 1.0, 2 + Math.round(broken * 3), 0x9a917f, 0, -1, 0.6 + broken * 0.6);
        }
        rig.root.scale.setScalar(1 + 0.03 * sev * (0.5 + broken));
        // ONE-BONE SPRING (M6): the titan flinches at the exact moment its
        // shell takes fire, the same "reacts, then settles" read every
        // other hit reaction in the game already has. Impulse, not a set
        // value -- back-to-back hits stack the kick instead of resetting
        // it, which is what makes a sustained volley read as a beating
        // rather than a series of unrelated taps.
        rig.holoSpringVel -= (0.16 + sev * 0.22);
      } else {
        rig.root.scale.setScalar(1);
      }
      // ANTICIPATION: under 12% the block TREMBLES and the light surges —
      // the player is told the payoff is one burst away.
      if (frac < 0.12) {
        rig.root.position.x += Math.sin(frameNo * 1.7 + i) * 0.05;
        rig.root.position.z += Math.cos(frameNo * 2.3 + i) * 0.04;
        (rig.fissure.material as MeshBasicMaterial).opacity =
          0.6 + Math.sin(frameNo * 0.35) * 0.35;
      }
      // L7 RAISE: the block rises out of the deck under dust — an
      // entrance, not a pop.
      if (!rig.lastAlive) {
        rig.raiseT = 0;
        fx.hit(s.mX[i], s.mY[i] - 1.0, 10, 0x9a917f);
      }
      if (rig.raiseT < 0.6) {
        rig.raiseT += dt;
        const u = Math.min(1, rig.raiseT / 0.6);
        // Back-out ease: the rock overshoots its stand by a few percent
        // and SETTLES — mass arriving, not a lift reaching its floor.
        // (CrazyGames feel bar: nothing moves linearly, nothing pops.)
        const c1 = 1.7, c3 = c1 + 1;
        const k = 1 + c3 * Math.pow(u - 1, 3) + c1 * Math.pow(u - 1, 2);
        rig.root.position.y = -3.2 * (1 - k);
        if ((frameNo & 3) === 0 && u < 0.85) fx.hit(s.mX[i], s.mY[i] - 1.2, 3, 0x9a917f);
      } else rig.root.position.y = 0;
      // Ground the block (L3): a standing megalith needs a shadow.
      contact(rig.root.position.x, rig.root.position.z, 2.1, 0.85);
      rig.lastAlive = true; rig.lastHp = left; rig.lastOp = s.mOp[i];
      rig.lastWX = rig.root.position.x; rig.lastWZ = rig.root.position.z;
      (rig.fissure.material as MeshBasicMaterial).color.set(tint);
      // CREATURE REVEAL is BLOCK_TITAN only. BLOCK_RACE was folded in here
      // on 2026-08-27 to fix a real bug (buildPreview had no case for it,
      // so the "prize" rendered as nothing) -- but the fix over-corrected:
      // it made isle 12 show a titan/creature ghost for a payload that
      // (step.ts breakBlock, BLOCK_RACE branch) always pays gainSquad, never
      // releaseTitan. FOUNDER FINDING (2026-08-28): "isle 12 holograms show
      // monsters, then reward soldiers" -- a real mismatch between what the
      // ghost promised and what broke. buildPreview now has its own case
      // for BLOCK_RACE (soldier silhouettes, same as BLOCK_BODIES), so the
      // original bug this was patching is fixed at its actual source and
      // BLOCK_RACE can go back to the static-prize family it belongs to.
      const isCreatureReveal = s.mOp[i] === 0;
      // ORPHANED PREVIEW MESH (founder-reported): `preview` (non-creature
      // ops) and `holo` (creature-reveal ops) are cleared by two SEPARATE
      // guards below, each keyed to its own op range -- so crossing the
      // boundary left the PREVIOUS op's mesh parented on rig.root forever
      // (a rig that used to hold a titan kept showing its ghost after
      // becoming an armour block, and vice versa). The two are mutually
      // exclusive on one rig, so whichever domain the op just left has its
      // mesh torn down here, unconditionally, before either guard runs.
      if (isCreatureReveal && rig.preview) {
        rig.root.remove(rig.preview); rig.preview = null;
        rig.previewMeshes.length = 0; rig.previewOp = -1;
      }
      if (!isCreatureReveal && rig.holo) {
        rig.root.remove(rig.holo); rig.holo = null;
        rig.holoMeshes.length = 0; rig.titan = -1;
      }
      // THE PRIZE, SHOWN: non-creature payloads levitate their actual reward.
      if (!isCreatureReveal && rig.previewOp !== s.mOp[i]) {
        if (rig.preview) { rig.root.remove(rig.preview); rig.preview = null; }
        rig.previewMeshes.length = 0;
        const pv = holoify(buildPreview(s.mOp[i], s.mValue[i]));
        pv.position.set(0, monoH + monolithDressingYOffset(monoH, rig.root.position.z - pz),
          MONOLITH_PREVIEW_Z_OFFSET);
        // THE REWARD IS THE READ, NOT THE NUMBER (2026-08-18): at 0.38 the
        // preview was a keychain next to the counter's five-figure number
        // -- the player read a digit, not a prize. Raised to 0.6, but
        // that was tuned against the OLD, far spawn distance (dz~33) --
        // once monoliths spawn close enough to read at all (VISIBLE-KILL
        // AUDIT PART 2), the same world-space scale projects large enough
        // to overrun the HUD in the corner. 0.48 keeps the reward the
        // larger silhouette on the rock without doing that at the closer
        // distance.
        // RETUNED AGAIN, DIFFERENTLY (2026-08-28, camera pull-in): a flat
        // multiply on the outer group was never going to be uniform --
        // `buildPreview()`'s own op branches each bake in their OWN scale
        // before this ever runs (armour x1.9, the crate gun x2.6, the
        // vehicle sled x1.35), so the SAME outer factor produced wildly
        // different effective sizes per payload. Confirmed via
        // `previewDiag()` against the new closer camera: isle 12 (crowd
        // dots, no internal multiplier) cleared at a flat 0.28, isle 1
        // (armour/crate/vehicle, all internally scaled) still clipped
        // -0.07 to -0.15 at the SAME flat factor. Measure-and-normalize
        // instead, the same pattern this file already uses to fit the
        // monolith model itself onto a known target -- every payload's
        // preview lands at the same effective on-screen size regardless
        // of what its own builder scaled it to internally.
        const pvBox = new Box3().setFromObject(pv);
        const pvRawH = Math.max(0.001, pvBox.max.y - pvBox.min.y);
        pv.scale.setScalar(Math.max(0.001, (monoH * 0.13) / pvRawH));
        rig.root.add(pv); glow(pv);
        pv.traverse((o) => { if ((o as Mesh).isMesh) rig.previewMeshes.push(o as Mesh); });
        rig.preview = pv; rig.previewOp = s.mOp[i];
      }
      if (rig.preview) {
        rig.preview.position.y = monoH + monolithDressingYOffset(monoH, rig.root.position.z - pz)
          + Math.sin(frameNo * 0.045 + i) * 0.18;
        rig.preview.rotation.y = frameNo * 0.016;
        for (const pm of rig.previewMeshes) {
          const m = pm.material as MeshBasicMaterial;
          // UNVEILING, NOT A STATIC ICON (founder finding, 2026-08-27,
          // "creative reward unveiling... refer to lastZ-latest"): every
          // reference frame studied shows the SAME arc -- a ghost that
          // solidifies as the object takes fire, never a prize that's
          // already fully rendered at full health. The 0.86-base "primary
          // read" fix (2026-08-18) solved a real legibility problem
          // (reward as a faint wisp next to a loud number) but solved it
          // by removing the arc entirely instead of by feeding it more
          // contrast. Now shares the SAME solidify curve every creature-
          // reveal payload already uses (0.34 at full health -> 0.92 at
          // the last hit) -- legible from the first frame (0.34 is not
          // invisible, just clearly a ghost) and dramatically clearer
          // by the time it breaks, matching the reference arc instead of
          // skipping it.
          m.opacity = 0.34 + broken * 0.58 + Math.sin(frameNo * 0.3 + i * 1.3) * 0.03;
        }
      }
      // GUARDIAN — REVERTED to strict payload accuracy, 2026-09-04,
      // same-day founder correction. A 2026-09-04 earlier change widened
      // this to `isCreatureReveal || mOp===3 (BLOCK_ARMOUR)`, reasoning
      // that the guardian is "just decoration, not a reward promise" and
      // therefore safe on any payload. That reasoning was wrong: the
      // PLAYER has no way to see that internal distinction — a monster
      // visibly standing on/emerging from the rock reads as "this is
      // what I'm about to get," full stop, regardless of what the code
      // privately categorises it as. Confirmed directly: BLOCK_ARMOUR
      // pays a shield charge (`step.ts`'s `breakBlock`), not a creature —
      // showing a guardian there is the EXACT "promised monster,
      // delivered something else" bug this file's own adjacent comment
      // on `isCreatureReveal` already documents as a previously-fixed
      // class of defect (isle 12, monsters-then-soldiers). Reintroduced
      // it by trying to be clever about "decoration vs. promise"; not
      // trying again without a way to make that distinction visible to
      // the player, which does not exist today. Guardian now strictly
      // matches `isCreatureReveal` — the only payload it can ever
      // decorate is the one that actually delivers a creature.
      const hasGuardian = isCreatureReveal;
      if (hasGuardian && rig.titan !== s.mTitan[i]) {
        if (isCreatureReveal) {
          // Ghost cleanup stays scoped to the payload that can actually
          // show one — an armour block never had a ghost to remove.
          if (rig.holo) { rig.root.remove(rig.holo); rig.holo = null; }
          rig.holoMeshes.length = 0;
        }
        if (rig.guardian) { rig.root.remove(rig.guardian); rig.guardian = null; rig.guardianMixer = null; }
        const kind = renderTitanKind(s.mTitan[i]);
        // Prefer the guardian's own dedicated character (Parasite L
        // Starkie); fall back to the field-ally titan model only while
        // the dedicated asset is still loading, so nothing regresses to
        // the ghost during that brief window.
        const rsrc = guardianSrc ?? riggedSrc.get(kind);
        if (rsrc) {
          // GUARDIAN (Z-ROUTE BUILD PLAN, Priority 1): the reference's
          // strongest, most-repeated beat is a living creature standing
          // on the reward, actively fighting to defend it — not a
          // translucent ghost. Reuses the SAME rigged source and clone
          // technique the field ally titan already pays for elsewhere in
          // this file (`cloneSkinned`, not `holoify`'s plain `.clone()`,
          // which silently breaks skinning on a real bone-rigged GLB).
          const guardian = cloneSkinned(rsrc) as Group;
          guardian.traverse((o) => {
            if (!(o as Mesh).isMesh) return;
            o.castShadow = true;
            // SAME FIX AS THE BOSS'S OWN SHININESS BUG, same root cause:
            // the FBX->glTF converter guessed a non-zero metallic value
            // (0.4) for a real diffuse zombie-skin texture -- a metallic
            // surface reflects mostly the bright sky/environment under
            // this scene's strong lighting, not its own texture, which is
            // why the guardian rendered as a near-uniform pale blob
            // despite the texture image and every other material
            // property (wrap mode, texCoord, baseColorFactor) checking
            // out correct on direct inspection. Zombie flesh is never
            // metallic; force it off, same as the boss fix already did.
            const m = (o as Mesh).material as MeshStandardMaterial | MeshStandardMaterial[];
            for (const mat of Array.isArray(m) ? m : [m]) {
              if ((mat as MeshStandardMaterial).isMeshStandardMaterial) {
                (mat as MeshStandardMaterial).metalness = 0;
                (mat as MeshStandardMaterial).roughness = 0.85;
              }
            }
          });
          // MEASURE AND NORMALISE (same pattern as the reward-preview
          // scaling above): `rsrc` carries the field-ally titan's OWN
          // fit (8.0 world units, sized to stand on open ground). Placed
          // on top of an already-elevated monolith instead, that towers
          // out of frame — verified live (t=40 capture: only the
          // guardian's feet were visible at the very top edge, the rest
          // extended above the camera). Rescale so the guardian's own
          // height is proportionate to the ROCK it stands on, not to the
          // ground it would otherwise stand on.
          // REAL ROOT CAUSE, found by tracing the model-load code (not
          // another live guess): the monolith's own model is deliberately
          // re-skinned to a tall glass/crystal spire (`monoH = rawH*fitY`,
          // the "creative reward holder" pass) -- `monoH` is tuned for
          // THAT dramatic silhouette, not a small rock, and can be well
          // over 15 world units. Scaling the guardian off `monoH` inherits
          // that same dramatic tallness regardless of Y-position, which is
          // the actual reason the earlier position-only fix had zero
          // visible effect. Target a FIXED, camera-appropriate absolute
          // height instead, off the same `SOLDIER_WORLD_H` reference this
          // file already uses to camera-tune the squad itself -- roughly
          // 2.5x a soldier's own height, matching the reference's own
          // guardian-vs-soldier proportion (ASSESSMENT.md §6).
          const gBox = new Box3().setFromObject(guardian);
          const gRawH = Math.max(0.001, gBox.max.y - gBox.min.y);
          const GUARDIAN_TARGET_H = SOLDIER_WORLD_H * 2.5;
          const fitScale = GUARDIAN_TARGET_H / gRawH;
          guardian.scale.multiplyScalar(fitScale);
          rig.guardianBaseScale = guardian.scale.x; // the FULL baked-fit x fitScale, not just fitScale alone
          const mixer = new AnimationMixer(guardian);
          const clips = (rsrc as Object3D & { userData: { clips?: AnimationClip[] } }).userData.clips ?? [];
          const attack = clips.find((c) => c.name === 'attack') ?? clips[0];
          if (attack) {
            const act = mixer.clipAction(attack);
            act.setLoop(LoopRepeat, Infinity);
            act.time = (i * 0.41) % Math.max(0.001, attack.duration); // per-slot phase, same as the field titan
            act.play();
          }
          // `rsrc.position.y` already carries the loader's own
          // feet-at-zero offset (`-bb.min.y * fit`, baked at load time
          // for the ORIGINAL 8.0-unit fit) — but that offset does not
          // auto-rescale when `guardian.scale` above changes, and a hard
          // `.position.set()` would discard it outright rather than
          // silently mis-scale it. Re-measure where the feet actually
          // land AFTER the rescale and correct by the difference, so
          // this is exact regardless of what scale factor was chosen.
          // ROOT CAUSE #1 (position), found live: `monolithDressingYOffset`
          // deliberately pushes a FLOATING GHOST HOLOGRAM toward the top
          // few percent of screen -- wrong to reuse for a standing body.
          // ROOT CAUSE #2 (the one that actually mattered), found by
          // tracing the model-load code: `monoH` is the CROWN of a
          // deliberately tall glass/crystal spire (the "creative reward
          // holder" re-skin, `monoH = rawH*fitY`), not a modest rock top
          // -- standing a guardian AT monoH means standing it at the
          // spire's own peak, which is why fixing only the position
          // formula (root cause #1) had zero visible effect: the TARGET
          // height itself was still wrong. The reference's own guardians
          // stand at the BASE of a much shorter pedestal, not the crown
          // of a sky-reaching spire (ASSESSMENT.md §6) -- anchor near
          // ground level instead, matching where `CRYSTAL_LAYOUT`'s own
          // decorations sit (y range ~1.2-3.0), not the spire's crown.
          const feetY = new Box3().setFromObject(guardian).min.y;
          const targetFootY = 0.3;
          guardian.position.y += targetFootY - feetY;
          guardian.position.x = 0;
          // Z=0, NOT `MONOLITH_HOLO_Z_OFFSET`: that constant exists to
          // pull a TRANSLUCENT GHOST toward the camera so it composites
          // in front of the opaque rock instead of being hidden behind it
          // (its own D4 fix comment). An opaque physical body standing at
          // the rock's own base doesn't have that occlusion problem in
          // the first place -- reusing the ghost's fix for a body that
          // never had the ghost's disease just pushed it to the wrong
          // side of the rock's silhouette instead (found live: guardian
          // reads as a sliver beside/behind the crystal, not centered).
          guardian.position.z = 0;
          // FACING (founder finding, live playtest): the guardian faced
          // the horde, not the squad it's meant to be confronting/
          // defending against. Never explicitly set before this — it
          // inherited the source asset's own unrotated default, the same
          // orientation that's correct for the field-ally titan (which
          // WALKS in that direction, away from the player) but backward
          // for a stationary guardian that should be facing the player's
          // side of the corridor.
          guardian.rotation.y = Math.PI;
          rig.root.add(guardian);
          rig.guardian = guardian; rig.guardianMixer = mixer;
          // Store the constant so the per-frame update (which re-derives
          // `targetFootY` every tick as the monolith's own relative depth
          // changes) can keep the feet correctly grounded without
          // re-measuring a bounding box every frame.
          rig.guardianFootOffset = guardian.position.y - targetFootY;
        }
        // GHOST FALLBACK REMOVED ENTIRELY (founder directive, 2026-09-04):
        // "remove the purple hologram, it's a regression -- a real
        // monster, not some stupid geometrical shape." The procedural
        // `buildTitan()` box-primitive ghost this branch used to fall
        // back to (when neither `guardianSrc` nor `riggedSrc` had loaded
        // yet) is exactly that placeholder. Per the founder's own stated
        // alternative ("if you can't do that, remove it altogether"): if
        // the real guardian asset isn't ready yet, the monolith now shows
        // nothing extra for that one tick rather than a placeholder —
        // strictly better than shipping a shape the founder has now
        // twice called out as reading as broken, not as a monster.
        rig.titan = s.mTitan[i];
      }
      if (rig.guardianMixer) rig.guardianMixer.update(dt);
      // The ghost SOLIDIFIES as the rock opens: at full hp it is a faint
      // suggestion, at the last blow it is nearly the thing itself. That
      // rising clarity is what makes the next magazine feel worth spending.
      //
      // Base raised 0.34 -> 0.55 (founder finding, 2026-09-02, live capture
      // review): at 35 units out with the AETHER WARD CRYSTALS (opacity
      // 0.82, emissive 1.3+) glowing right next to it, a 0.34-opacity ghost
      // lost the read entirely -- the crystal decoration (an explicit
      // stopgap for "the rocks look like black rocks" pending a real
      // MESHY-generated asset, see MonolithRig.crystals) became the
      // dominant shape on screen and the actual prize -- the reason this
      // hologram exists at all -- was an unreadable smear behind it. The
      // monster must win that contrast fight from the first frame, not
      // just at the last hit.
      for (const hm of rig.holoMeshes) {
        const m = hm.material as MeshBasicMaterial;
        m.opacity = 0.55 + broken * 0.4
          + Math.sin(frameNo * 0.29 + i * 1.7) * 0.04;
      }
      // ONE-BONE SPRING (M6), integrated every frame regardless of `rig.holo`
      // existing yet -- a titan revealed mid-decay must not inherit a
      // stale kick from whatever this rig showed before it. Semi-implicit
      // Euler, critically-damped-ish (damping^2 close to 4*stiffness):
      // settles in ~0.3s with one clean overshoot, never a sustained
      // wobble that would read as the model glitching rather than reacting.
      rig.holoSpringVel += (-42 * rig.holoSpringOffset - 13 * rig.holoSpringVel) * dt;
      rig.holoSpringOffset += rig.holoSpringVel * dt;
      if (rig.holo) {
        rig.holo.position.y = monoH + monolithDressingYOffset(monoH, rig.root.position.z - pz)
          + Math.sin(frameNo * 0.04 + i) * 0.16;
        rig.holo.rotation.y = frameNo * 0.014;
        rig.holo.scale.setScalar((0.72 + broken * 0.3) * (1 + rig.holoSpringOffset));
      }
      // GUARDIAN: a standing creature, not a drifting hologram -- no slow
      // spin or float, just the same M6 hit-reaction spring the ghost
      // already earns, so taking damage still reads as a reaction.
      if (rig.guardian) {
        rig.guardian.position.y = 0.3 + rig.guardianFootOffset;
        rig.guardian.scale.setScalar(rig.guardianBaseScale * (1 + rig.holoSpringOffset));
      }
      // The fissure widens and brightens with every point taken off —
      // capped narrow: a crack that outgrows the rock face stops reading
      // as a crack and starts reading as a glass pane glued to the block.
      rig.fissure.scale.set(1 + broken * 2.4, 1, 1 + broken * 1.4);
      (rig.fissure.material as MeshBasicMaterial).opacity = 0.4 + broken * 0.6;
      // AETHER WARD CRYSTALS: brighten and creep outward with damage, same
      // "the fight is visibly working" law as the fissure above -- a
      // crystal cluster that looked identical at 100% and 5% health would
      // read as decoration, not as part of what the player is doing.
      for (const [ci, cm] of rig.crystals.entries()) {
        const cmat = cm.material as MeshStandardMaterial;
        cmat.emissiveIntensity = 0.75 + broken * 1.6;
        const grow = 1 + broken * 0.22;
        cm.scale.set(0.85 * grow, cm.userData['baseStretch'] as number ?? 1, 0.85 * grow);
        cm.rotation.y += (0.003 + broken * 0.01) * (ci % 2 === 0 ? 1 : -1);
      }
      if (rig.model) {
        // ONE mesh cannot shed sub-boulders, so the mass itself is eaten
        // away: the rock loses a third of its bulk as the counter empties
        // and settles lower on the deck, which reads as being chipped
        // rather than as being deleted.
        const bulk = 1 - broken * 0.34;
        rig.model.scale.setScalar(bulk);
        rig.model.rotation.y = 0.35 + broken * 0.12;
      } else {
        // Placeholder path, used only until the asset finishes loading.
        const standing = Math.max(1, Math.ceil(frac * rig.rocks.length));
        for (const [k, rock] of rig.rocks.entries()) rock.visible = k < standing;
      }
      // THE COUNTER. Five figures, beside the rock, the single most-read
      // element of the reference frame. Redrawn only when the number
      // changes; scaled against camera distance so it reads the same from
      // the second the rock is raised until it drifts past.
      const shown = Math.ceil(left);
      if (rig.counterVal !== shown) {
        rig.counterVal = shown;
        rig.counter.material.map?.dispose();
        // UI LAW (founder, 2026-08-20): same billboard + outlined-numeral
        // treatment as the gate signs (gateTexture, green panel -- a
        // monolith is always a reward) so costs and rewards read as one
        // visual language. Was labelTexture + per-payload tint; that
        // function is untouched and still serves its other callers
        // (damage numbers, lane counts) unmodified.
        rig.counter.material.map = gateTexture(String(shown), true);
        rig.counter.material.needsUpdate = true;
      }
      const camDist = Math.max(8, rig.root.position.z - (pz - 8.2));
      // Width in world units: near-linear with distance holds the number at
      // roughly constant SCREEN size, clamped so it can neither swallow the
      // frame up close nor die to a speck at the horizon.
      // SECONDARY ELEMENT (2026-08-18): the clamp band was 3.2-8.5, wide
      // enough that the counter alone could be nearly as wide as the whole
      // rock. Still legible, no longer the headline -- the reward preview
      // above it (scaled ~monoH*0.6) is now the larger silhouette.
      const cw = Math.min(3.4, Math.max(1.3, camDist * 0.062));
      // Aspect matches gateTexture's own 256x128 canvas (2:1) now that this
      // sprite draws from it instead of labelTexture's 256x96.
      rig.counter.scale.set(cw, cw * 0.5, 1);
    }

    // --- allies + shield
    for (let i = 0; i < allyActors.length; i++) {
      const a = allyActors[i]!;
      if (i < s.lp.cap && s.lp.alive[i]) {
        // THE FREED TITAN MARCHES TO THE WAR. The sim deploys it at the
        // horde lane instantly; the renderer bridges the crossing from
        // the broken block so the screen never shows a teleport
        // (animation bible: cause travels to effect).
        if (allyEase[i] === undefined || allyEase[i] === null) {
          if (strideClock - lastBreak.at < 1.5 && lastBreak.op === 0) {
            allyEase[i] = { fx: lastBreak.wx, fz: lastBreak.wz, t: 0 };
          } else allyEase[i] = { fx: 0, fz: 0, t: 9 };
        }
        // SECOND MONSTER FOR THE FIELD ALLY (founder, 2026-09-04: "add
        // another monster character so there is at least 2 of them").
        // `renderTitanKind()` hardcodes every sim kind to the SAME rigged
        // asset (kind 2, boss-colossus) — see its own comment, a
        // deliberate movement-over-theme-purity call, not a bug, so left
        // unchanged for every OTHER call site (monolith preview, guardian
        // reveal). The field ally alone alternates on top of that: even
        // `lKind` keeps the colossus (kind 2, `riggedSrc`), odd `lKind`
        // uses the guardian-parasite rig instead — a virtual "kind 3"
        // slot, unused by `riggedMix`/`allyRigs` until now (TITAN_ASSET's
        // real kind-3 entry, titan-artillery, has no rig and was never
        // reachable as a field ally in the first place, so this claims
        // dead array space rather than colliding with anything live).
        const useParasite = s.lKind[i] % 2 === 1 && !!guardianSrc;
        const kind = useParasite ? 3 : renderTitanKind(s.lKind[i]);
        // A rigged titan WALKS; prefer it over the static generated mesh.
        const rsrc = useParasite ? guardianSrc : riggedSrc.get(kind);
        if (rsrc && !riggedMix[kind]![i]) {
          const root = cloneSkinned(rsrc);
          // SCALE MISMATCH (found while wiring this in): `guardianSrc` is
          // normalised to a 1-unit span at load (the monolith guardian
          // re-fits it to its own target height at ITS creation site) --
          // `riggedSrc`'s colossus is normalised to an 8-unit span AT
          // LOAD instead (this file, RIGGED_TITAN's own loader, above).
          // Cloning the parasite straight in without re-fitting it here
          // would render it at roughly 1/8 the colossus's size, not a
          // second monster of comparable presence. Matched to the SAME
          // 8-unit longest-axis target so both field-ally kinds read as
          // the same class of prize.
          if (useParasite) {
            root.position.y = 0;
            const gBox = new Box3().setFromObject(root);
            const gSpan = Math.max(gBox.max.x - gBox.min.x, gBox.max.y - gBox.min.y, gBox.max.z - gBox.min.z);
            root.scale.multiplyScalar(8.0 / Math.max(0.001, gSpan));
            root.position.y = -new Box3().setFromObject(root).min.y;
          }
          const mixer = new AnimationMixer(root);
          const clips = (rsrc as Object3D & { userData: { clips?: AnimationClip[] } })
            .userData.clips ?? [];
          const walk = clips.find((c) => c.name === 'walk') ?? clips[0];
          if (walk) {
            const act = mixer.clipAction(walk);
            act.setLoop(LoopRepeat, Infinity);
            // Per-body phase, so two titans never march in lockstep.
            act.time = (i * 0.37) % Math.max(0.001, walk.duration);
            act.play();
          }
          // Per-slot materials: `rsrc` is the one cached source every
          // ally of this kind clones from, and Object3D.clone() copies a
          // Mesh's `material` by REFERENCE, not by value. Without this,
          // the hurt-readout opacity below (keyed to THIS slot's hp)
          // would mutate the shared material and desync every other
          // titan of the same kind -- including the monolith preview's
          // own hologram, which draws from the same cached asset.
          root.traverse((o) => {
            const mm = (o as Mesh).material;
            if (!mm) return;
            const clones = (Array.isArray(mm) ? mm : [mm]).map((mat) => mat.clone());
            (o as Mesh).material = clones.length === 1 ? clones[0]! : clones;
            // WHITE MONSTER FIX (founder finding, 2026-09-02): the rigged
            // titan-bulwark asset carries no base-colour texture on at
            // least some materials, and MeshStandardMaterial's own default
            // colour is plain white -- rendered lit, that reads as a
            // stark, untextured white mannequin, not a monster. Tint any
            // texture-less near-white material to a plausible stony-metal
            // albedo so it reads as a body, not a placeholder. A material
            // that already carries a real map or a real (non-white)
            // colour is left exactly as authored.
            for (const cm of clones) {
              const sm = cm as MeshStandardMaterial;
              if (sm.isMeshStandardMaterial && !sm.map
                && sm.color.r > 0.85 && sm.color.g > 0.85 && sm.color.b > 0.85) {
                sm.color.setHex(0x8a7a6a);
              }
            }
          });
          root.visible = false;
          world.add(root);
          riggedMix[kind]![i] = { root, mixer };
        }
        const rig = riggedMix[kind]?.[i];
        const gen = rig ? null : genCache.get(TITAN_ASSET[kind] ?? '');
        if (gen && !allyRigs[kind]![i]) {
          const m = gen.clone(true) as Group;
          // Same per-slot material split as the rigged branch above.
          m.traverse((o) => {
            const mm = (o as Mesh).material;
            if (mm) (o as Mesh).material = Array.isArray(mm) ? mm.map((mat) => mat.clone()) : mm.clone();
          });
          m.scale.setScalar(1.35);
          m.visible = false;
          world.add(m);
          allyRigs[kind]![i] = m;
        }
        const real = rig ? rig.root : allyRigs[kind]?.[i];
        if (rig) rig.mixer.update(dt);
        // Hide every rigged body that is not this ally's kind.
        for (const [k, arr] of riggedMix.entries()) {
          const r = arr[i]; if (r) r.root.visible = k === kind && !!rig;
        }
        for (const [k, arr] of allyRigs.entries()) {
          const r = arr[i]; if (r) r.visible = k === kind;
        }
        if (real) {
          real.position.copy(a.position);
          real.rotation.copy(a.rotation);
          // PROCEDURAL GAIT. A generated mesh has no clips, so without this
          // a two-tonne titan SLIDES up the corridor like furniture -- the
          // single most artificial thing a large body can do. Weight is
          // sold by four things a static mesh can still be given: a stride
          // bounce, a lateral sway against it, a lean into the walk, and a
          // hard recoil on the frame its swing lands.
          const ph = frameNo * 0.075 + i * 1.7;
          const step = Math.abs(Math.sin(ph));
          // A rigged body animates its OWN legs; adding the procedural
          // bounce on top would double the stride and read as a limp.
          real.position.y = rig ? 0 : step * 0.20;
          real.rotation.z = rig ? 0 : Math.sin(ph * 0.5) * 0.055;
          // Swing: lCooldown counts DOWN from the strike, so a high value
          // is a fresh hit. Wind up, drive through, settle.
          const sw = s.lCooldown[i] > 20 ? (s.lCooldown[i] - 20) / 13 : 0;
          real.rotation.x = -sw * 0.55 + step * 0.03;
          if (!rig) real.scale.setScalar(1.35 * (1 + sw * 0.06));
          // Each footfall throws dust where the foot actually lands.
          if (step > 0.97 && (frameNo & 3) === 0) {
            for (let d = 0; d < 3; d++) {
              emit(s.lX[i], s.lY[i], (Math.random() - 0.5) * 0.2, 0.05,
                (Math.random() - 0.5) * 0.2, 16, 0x9a8f80, 1.4);
            }
          }
        }
        a.visible = !real;
        // Ease the visual from the broken block to the sim's position:
        // the crossing is a 0.9s MARCH, not a teleport.
        const ease = allyEase[i];
        let ax = (s.lX[i] - arenaW / 2) * SX;
        let az = s.lY[i] * S;
        if (ease && ease.t < 0.9) {
          ease.t += dt;
          const u = Math.min(1, ease.t / 0.9);
          const k = u * u * (3 - 2 * u);
          ax = ease.fx + (ax - ease.fx) * k;
          az = ease.fz + (az - ease.fz) * k;
        }
        a.position.set(ax, 0, az);
        if (real) real.position.copy(a.position);
        // Stomp cycle: it strides, swings on its cooldown beat, and its
        // whole body sells the hit.
        const swing = s.lCooldown[i] > 24 ? (s.lCooldown[i] - 24) / 9 : 0;
        a.rotation.x = swing * 0.5;
        a.position.y = Math.abs(Math.sin(frameNo * 0.11 + i)) * 0.08;
        // FACE THE WAY YOU WALK. The ally advances UP the corridor (lY
        // increases every tick) and was rotated a half turn to face the
        // camera, so it marched backwards into the horde for its entire
        // life. A body moving one way and facing the other is the most
        // basic physical-plausibility failure there is, and it read as the
        // monster being broken rather than as a rendering choice.
        a.rotation.y = Math.sin(frameNo * 0.05 + i) * 0.10;
        // Hurt readout: it darkens as it wears down, so its death is
        // never a surprise. `hf` was computed and discarded here (`void
        // hf`) — the opacity was pinned to 1 regardless of hp, so the
        // ally never showed damage while alive. Also, `a` (the
        // placeholder) is hidden the instant `real` exists (`a.visible =
        // !real` above, effectively always once assets load), so wiring
        // `hf` into `a`'s material alone would still be invisible in the
        // common case -- it has to reach whichever of `real`/`a` is
        // actually on screen. Safe to mutate directly: the per-slot
        // material clone above means this hp-fraction only ever touches
        // THIS ally's own material, not the shared cached asset.
        const hf = Math.max(0, Math.min(1, s.lHp[i] / Math.max(1, s.lHpMax[i])));
        (real ?? a).traverse((o) => {
          const m = (o as Mesh).material as MeshStandardMaterial | undefined;
          if (m && m.color) {
            m.transparent = true;
            m.opacity = 0.4 + 0.6 * hf;
          }
        });
      } else {
        a.visible = false;
        for (const arr of allyRigs) { const r = arr[i]; if (r) r.visible = false; }
        // D3 LIFETIME BUG: this branch hid the static/procedural ally
        // mesh (`allyRigs`) but never the RIGGED one (`riggedMix`) — its
        // `.root.visible` was only ever set inside the `if` branch above
        // (the per-kind "hide every rigged body that is not this ally's
        // kind" loop), which never runs once the ally stops being alive.
        // A rigged titan, once shown, stayed visible forever after —
        // through the ally's own death, through every restart — because
        // nothing ever ran the code that would have turned it back off.
        // This explains "persists across restarts": it was never really
        // about restart teardown, it was that the object was NEVER
        // rehidden in the first place, restart or not.
        for (const arr of riggedMix) { const r = arr[i]; if (r) r.root.visible = false; }
      }
    }
    if (s.shieldHp > 0) {
      shieldBubble.visible = true;
      // ABILITY AURA (founder, 2026-09-05, "washes out the deck and hides
      // the squad"): this is the shield bubble -- the only filled-volume
      // ability effect in the file (every other ability visual is a thin
      // ring or a one-shot particle burst). `auraRadiusMult`/
      // `auraOpacityMult` default to 1.0, reproducing today's exact
      // sizing/opacity; the panel multiplies rather than replaces so the
      // existing shield-strength "breathe" and squad-size scaling stay
      // intact underneath the live dial.
      const spanS = Math.max(1.6, formHalf * 2.2 / S) * tuning.auraRadiusMult;
      shieldBubble.position.set(cx, 0.9, pz);
      shieldBubble.scale.set(spanS, spanS * 0.72, spanS);
      // Breathes with remaining strength; a thin shield is visibly thin.
      shieldMat.opacity = (0.08 + 0.14 * Math.min(1, s.shieldHp / Math.max(1, s.shieldMax))
        + Math.sin(frameNo * 0.12) * 0.02) * tuning.auraOpacityMult;
    } else shieldBubble.visible = false;

    // --- fx particles
    for (let i = 0; i < PARTS; i++) {
      if (pState.life[i]! <= 0) {
        parts.setMatrixAt(i, tmpM.compose(HIDDEN, tmpQ.identity(), tmpS.set(0, 0, 0)));
        continue;
      }
      pState.life[i]!--;
      pState.x[i]! += pState.vx[i]!; pState.y[i]! += pState.vy[i]!; pState.z[i]! += pState.vz[i]!;
      pState.vy[i]! -= 0.006;
      pState.vx[i]! *= 0.94; pState.vz[i]! *= 0.94;
      if (pState.y[i]! < 0.06) { pState.y[i] = 0.06; pState.vy[i] = 0; }
      const t = 1 - pState.life[i]! / pState.max[i]!;
      const sc = pState.sc[i]! * (1 - t * 0.65);
      tmpV.set(pState.x[i]!, pState.y[i]!, pState.z[i]!);
      parts.setMatrixAt(i, tmpM.compose(tmpV, tmpQ.identity(), tmpS.set(sc, sc, sc)));
      pColor.setRGB(pState.col[i * 3]!, pState.col[i * 3 + 1]!, pState.col[i * 3 + 2]!);
      parts.setColorAt(i, pColor);
    }
    parts.instanceMatrix.needsUpdate = true;
    if (parts.instanceColor) parts.instanceColor.needsUpdate = true;

    // --- impact debris (item 4): same integration shape as the particle
    // pool above, plus tumble and a harder ground settle (a chunk stops
    // dead, a spark does not).
    for (let i = 0; i < DEBRIS; i++) {
      if (dState.life[i]! <= 0) {
        debrisMesh.setMatrixAt(i, tmpM.compose(HIDDEN, tmpQ.identity(), tmpS.set(0, 0, 0)));
        continue;
      }
      dState.life[i]!--;
      dState.x[i]! += dState.vx[i]!; dState.y[i]! += dState.vy[i]!; dState.z[i]! += dState.vz[i]!;
      dState.vy[i]! -= 0.007;
      dState.vx[i]! *= 0.985; dState.vz[i]! *= 0.985;
      if (dState.y[i]! < 0.12) {
        dState.y[i] = 0.12; dState.vy[i]! *= -0.3;
        dState.vx[i]! *= 0.55; dState.vz[i]! *= 0.55;
        dState.rvx[i]! *= 0.4; dState.rvy[i]! *= 0.4; dState.rvz[i]! *= 0.4;
      }
      dState.rx[i]! += dState.rvx[i]! * 0.05;
      dState.ry[i]! += dState.rvy[i]! * 0.05;
      dState.rz[i]! += dState.rvz[i]! * 0.05;
      const t = 1 - dState.life[i]! / dState.max[i]!;
      const sc = dState.sc[i]! * (1 - t * 0.3);
      tmpV.set(dState.x[i]!, dState.y[i]!, dState.z[i]!);
      tmpE.set(dState.rx[i]!, dState.ry[i]!, dState.rz[i]!);
      tmpQ.setFromEuler(tmpE);
      debrisMesh.setMatrixAt(i, tmpM.compose(tmpV, tmpQ, tmpS.set(sc, sc, sc)));
      dColor.setRGB(dState.col[i * 3]!, dState.col[i * 3 + 1]!, dState.col[i * 3 + 2]!);
      debrisMesh.setColorAt(i, dColor);
    }
    debrisMesh.instanceMatrix.needsUpdate = true;
    if (debrisMesh.instanceColor) debrisMesh.instanceColor.needsUpdate = true;

    for (const l of labels) {
      if (l.life <= 0) { if (l.sp.visible) l.sp.visible = false; continue; }
      l.life--;
      l.y += l.rise * 0.014;
      const t = 1 - l.life / l.max;
      l.sp.position.set(l.x, l.y, l.z);
      l.sp.material.opacity = Math.min(1, (1 - t) * 2.4);
    }

    // HIDE ENTITIES (?hideents=1, residual-hitch test, 2026-09-05):
    // forced AFTER every update block above has already run and set
    // whatever visibility state it normally would -- this only
    // overrides the DRAWN result for this frame, it does not skip or
    // alter any update logic (sim state, positions, animation) above.
    // Reuses the existing hideEnemies/hideGatePanels/hideMonoliths
    // hide-and-difference machinery (dev(), below) for the pools they
    // already cover; projectiles (bolt tracers + mortar shadow) and
    // pickups (gems) had no existing toggle, added here.
    if (HIDE_ENTITIES) {
      for (const pool of massTiers) if (pool) for (const m of pool) m.visible = false;
      for (const pool of corpseTiers) if (pool) for (const m of pool) m.visible = false;
      for (const h of hordes) if (h) h.mesh.visible = false;
      for (const pool of actorPools) for (const a of pool) a.root.visible = false;
      for (const rig of monoRigs) rig.root.visible = false;
      for (const p of gatePanels) p.mesh.visible = false;
      for (const p of gatePayoutPanels) p.mesh.visible = false;
      boltCore.visible = false;
      boltMid.visible = false;
      boltHalo.visible = false;
      mortarShadow.visible = false;
      gems.visible = false;
    }

    // TWO RENDERS. The bloom render is camera-layer-masked (BLOOM_LAYER
    // only, background cleared) rather than the old darken-traverse/
    // restore-traverse dance -- see the long comment beside
    // `bloomComposer`'s construction for why this is a different, much
    // cheaper mechanism than what was measured at ~8.9ms and removed.
    const prevBg = scene.background;
    scene.background = null;
    camera.layers.set(BLOOM_LAYER);
    bloomComposer.render();
    camera.layers.set(0);
    scene.background = prevBg;
    gradePass.uniforms['uVignette'].value = dangerVignette;
    gradePass.uniforms['uDesaturate'].value = dangerDesaturate;
    finalComposer.render();
    // HITCH INVESTIGATION: `renderer.info.autoReset` (three's default,
    // true) resets the counters before EVERY internal render() call an
    // EffectComposer chain makes -- reading `renderer.info` from OUTSIDE
    // this function, on the next animation frame, only ever sees
    // whatever the LAST internal pass drew (OutputPass: one fullscreen
    // triangle, one draw call -- exactly the bogus "1, 1" a naive
    // external read reported). Snapshotting here, once per LOGICAL
    // frame after the whole composer chain has run, is what makes
    // `__scene.perf()` report real numbers.
    lastFrameInfo.calls = renderer.info.render.calls;
    lastFrameInfo.triangles = renderer.info.render.triangles;
  }

  window.addEventListener('resize', resize);

  // Debug probe: lets a harness ask what the renderer actually built,
  // instead of inferring it from a screenshot.
  (window as unknown as Record<string, unknown>)['__scene'] = {
    /**
     * DEV-ONLY OBJECT HANDLES. Every other probe here returns plain data,
     * because every other probe only had to MEASURE the frame. Placing a
     * candidate body INSIDE the shipped frame needs the frame itself.
     *
     * The harness that did this by copying constants measured its own copy:
     * it rebuilt the camera as fov 57 / back 7.3 and a flat grey plane, and
     * kept rendering that after the live camera became fov 50 / back 13.0
     * with a carved-stone deck and piers. Handing out the real scene is the
     * only version that cannot go stale.
     *
     * Nothing in the game reads this; it exists for `meshy-inframe.mjs`.
     */
    dev: () => ({
      scene, camera, renderer, S, SX, arenaW,
      load: (url: string) => loader.loadAsync(url),
      mixer: (o: Object3D) => new AnimationMixer(o),
      Box3, Vector3,
      /**
       * Hide every ENEMY body — instanced mass, GPU crowd, corpses, elite
       * actors — leaving deck, squad, backdrop and lighting untouched.
       *
       * This is the control plate for measuring the horde's figure-ground
       * against the deck. The horde is already in the scene when a capture
       * starts, so it cannot be isolated by differencing a before/after
       * pair the way an injected body can; hiding it inverts the trick and
       * gives the same exact mask. Sampling projected enemy centres instead
       * would mix deck pixels into the body reading, which is the one thing
       * the measurement must not do.
       *
       * Returns what it actually toggled, so a harness can assert the plate
       * really is empty rather than trust that it is.
       */
      hideEnemies: (on: boolean) => {
        let n = 0;
        for (const pool of massTiers) if (pool) for (const m of pool) { m.visible = !on; n++; }
        for (const pool of corpseTiers) if (pool) for (const m of pool) { m.visible = !on; n++; }
        for (const h of hordes) if (h) { h.mesh.visible = !on; n++; }
        // Elite/boss actors are deliberately NOT touched: they are a
        // different tier with their own materials, and folding them into a
        // "the horde" reading would average two populations. They are also
        // pool-managed per frame, so a blanket visible=false here would be
        // overwritten anyway.
        return n;
      },
      /**
       * Same hide-and-difference trick as hideEnemies, for ONE named
       * backdrop layer (window.__scene.backdropInfo()'s own `name` field
       * — "sky", "clouds", "underClouds", "island1".."island6"). Added
       * for COLOR_SCRIPT.md's value-hierarchy re-measurement (islands'
       * own reading kept coming back contaminated by neighbouring cloud/
       * sky pixels at blind-coordinate sampling resolution).
       *
       * CAUTION, learned the hard way (2026-08-30): isolating the layer
       * is necessary but not sufficient. Each island's PlaneGeometry is
       * far larger than its painted silhouette (mostly transparent
       * margin), so a naive diff-and-average over every changed pixel is
       * dominated by that margin, not the silhouette — a first attempt
       * at this read 188-192 luma (a false "too bright" finding) when
       * the silhouette is visibly dark grey by eye. Threshold the diff
       * mask to near-opaque pixels only (or weight by alpha) before
       * trusting a luma reading from this — a large changed-pixel count
       * is not the same as a clean signal on a mostly-transparent plate.
       * Returns false if no layer matches the name (so a harness can
       * assert the toggle really landed, same contract as hideEnemies).
       */
      hideBackdropLayer: (name: string, on: boolean) => {
        const layer = skyWorld.backdrop.find((l) => l.name === name);
        if (!layer) return false;
        layer.mesh.visible = !on;
        return true;
      },
      /**
       * TEMP DIAGNOSTIC (color-audit, 2026-08-30): reads raw texture
       * pixels directly off the material's own canvas, bypassing the
       * whole render/composite/screenshot pipeline entirely -- for
       * verifying a regrade filter (saturate/brightness) actually landed
       * on the source pixels, independent of anything a screenshot-based
       * measurement could get wrong (compositing, alpha blending, UI
       * overlap, camera framing).
       */
      sampleBackdropTexture: (name: string, gridN = 6) => {
        const layer = skyWorld.backdrop.find((l) => l.name === name);
        if (!layer) return null;
        const img = layer.texture.image as { width?: number; height?: number; getContext?: (t: string) => CanvasRenderingContext2D | null };
        if (!img?.getContext) return { error: 'not canvas-backed', hasImage: !!img };
        const ctx = img.getContext('2d');
        if (!ctx || !img.width || !img.height) return { error: 'no 2d context' };
        const out: { x: number; y: number; r: number; g: number; b: number; a: number }[] = [];
        for (let gy = 0; gy < gridN; gy++) {
          for (let gx = 0; gx < gridN; gx++) {
            const x = Math.min(img.width - 1, Math.floor((gx + 0.5) * img.width / gridN));
            const y = Math.min(img.height - 1, Math.floor((gy + 0.5) * img.height / gridN));
            const d = ctx.getImageData(x, y, 1, 1).data;
            out.push({ x, y, r: d[0]!, g: d[1]!, b: d[2]!, a: d[3]! });
          }
        }
        return out;
      },
      /**
       * DIAGNOSTIC (2026-08-30): actorPools[poolIdx] slot state -- built
       * while chasing a suspected "true chapter boss never visibly
       * renders" defect (isle 1, t=45-70s). RESOLVED, not a bug: paired
       * with `actorFrame()`/`projectWorld` and a screenshot taken in the
       * same evaluate() call, the boss was confirmed rendering correctly
       * at t=51s (dist=26.3, scale 4.1, HP numeral visible, bbox spanning
       * screen y=[0.001, 0.238] as predicted). The earlier "invisible"
       * read came from a prior real-browser screenshot set whose frame
       * indices were never actually correlated to sim time -- a smaller,
       * unrelated elite entity was mistaken for the boss. Kept as
       * permanent debug infra alongside `hideBackdropLayer`/
       * `sampleBackdropTexture`, not because this specific bug was real.
       */
      actorPoolDiag: (poolIdx: number) => actorPools[poolIdx]?.map((a, k) => ({
        k, busy: a.busy, visible: a.root.visible, gait: a.gait,
        pos: { x: a.root.position.x, y: a.root.position.y, z: a.root.position.z },
        scale: a.root.scale.x,
      })) ?? null,
      /**
       * Hide tracers and muzzle flares. These keep animating on render time
       * even with the sim paused, so they move between two captures taken
       * seconds apart: the first horde measurement had to discard 34,884
       * moving pixels to keep 4,597 stable body ones, which biases the
       * sample toward whatever the fire was not crossing. Hidden in BOTH
       * frames, they cancel instead of contaminating.
       */
      hideFire: (on: boolean) => {
        let n = 0;
        for (const m of [boltCore, boltMid, boltHalo]) { m.visible = !on; n++; }
        for (const m of muzzles) { m.visible = on ? false : m.visible; n++; }
        return n;
      },
      /**
       * Z-ROUTE COLOR AUDIT (2026-09-03): same hide-and-difference trick as
       * hideEnemies, for the player squad. Needed to isolate the squad's
       * own figure-ground reading (Ev.PickedUp cage highlight, ATK-callout
       * work) without contaminating the sample with deck/tracer pixels.
       */
      hideSquad: (on: boolean) => {
        let n = 0;
        for (const sol of soldiers) { sol.root.visible = !on; n++; }
        return n;
      },
      /** Same trick, for monolith rigs (rock body only, not the holo/preview reveal — those are their own overlay and would average two populations). */
      hideMonoliths: (on: boolean) => {
        let n = 0;
        for (const rig of monoRigs) { rig.root.visible = !on; n++; }
        return n;
      },
      /** Same trick, for gate/reward panels. */
      hideGatePanels: (on: boolean) => {
        let n = 0;
        for (const p of gatePanels) { p.mesh.visible = !on; n++; }
        for (const p of gatePayoutPanels) { p.mesh.visible = !on; n++; }
        return n;
      },
    }),
    tiers: () => massTiers.map((pool) => (pool && pool.length
      ? { phases: pool.length, count: pool.reduce((a, m) => a + m.count, 0), verts: pool[0]!.geometry.attributes['position']?.count ?? 0 }
      : null)),
    actors: () => actorPools.map((p) => p.length),
    soldiers: () => soldiers.length,
    mounts: () => soldiers.slice(0, 4).map((x) => ({
      hasMount: !!x.gunMount, mounted: x.mounted,
      kids: x.gunMount ? x.gunMount.children.map((c) => c.name).join(',') : '',
      vis: x.gunMount?.visible ?? false,
    })),
    gunsLoaded: () => Object.keys(gunModels).join(','),
    // Which clip each visible soldier is actually running. Lets a harness
    // prove the state machine fires rather than infer it from pixels.
    clips: () => soldiers.filter((x) => x.root.visible).map((x) => x.current),
    // M2 VERIFICATION: a visible soldier's Hips bone, so a harness can
    // sample it across a full run cycle and measure drift directly rather
    // than trust the source. Same bone-name match as `stripRootMotion`
    // (`clip-shape.ts`), so this checks the exact joint the fix targets.
    //
    // Reports BOTH world position and the LOCAL offset from the soldier's
    // own root object (hip world minus root world). World position alone
    // is confounded by formation layout (rank/file reflow as squad size
    // changes, camera-relative anchoring) and is NOT a clean root-motion
    // reading — `local` isolates the animated offset actually produced by
    // the clip inside the rig, decoupled from where the formation places
    // the body in the world. `local` is what `stripRootMotion` targets;
    // grade drift on `local`, not `world`.
    hipWorld: (i = 0) => {
      const sol = soldiers.filter((x) => x.root.visible)[i];
      if (!sol) return null;
      let hip: Object3D | null = null;
      sol.root.traverse((o) => { if (!hip && /Hips$/.test(o.name)) hip = o; });
      if (!hip) return null;
      const p = new Vector3();
      (hip as Object3D).getWorldPosition(p);
      const r = new Vector3();
      sol.root.getWorldPosition(r);
      return {
        world: { x: p.x, y: p.y, z: p.z },
        local: { x: p.x - r.x, y: p.y - r.y, z: p.z - r.z },
        root: { x: r.x, y: r.y, z: r.z },
        clip: sol.current,
      };
    },
    // TEMP DIAGNOSTIC (gun-orientation report, 2026-09-04): overrides the
    // gun socket's local rotation for empirical sweep testing, so a
    // harness can find the correct constant by measuring instead of
    // guessing blind and redeploying each try.
    setGunSocketRot: (x: number, y: number, z: number) => {
      for (const sol of soldiers) if (sol.gunMount) sol.gunMount.rotation.set(x, y, z);
    },
    setGunLocalRot: (x: number, y: number, z: number) => {
      for (const sol of soldiers) {
        const gm = sol.gunMount?.children.find((c) => c.name === 'classgun');
        if (gm) gm.rotation.set(x, y, z);
      }
    },
    highlightGun: (scale: number) => {
      for (const sol of soldiers) {
        const gm = sol.gunMount?.children.find((c) => c.name === 'classgun');
        if (!gm) continue;
        gm.scale.multiplyScalar(scale);
        gm.traverse((o) => {
          if ((o as Mesh).isMesh) {
            (o as Mesh).material = new MeshBasicMaterial({ color: 0xff00ff });
          }
        });
      }
    },
    // TEMP DIAGNOSTIC (gun-orientation report, 2026-09-04): the classgun
    // mesh's world-space bounding box and its socket/hand world quaternion,
    // so the "horizontal gun" complaint can be root-caused from real
    // numbers instead of guessed at from a screenshot.
    gunWorld: (i = 0) => {
      const sol = soldiers.filter((x) => x.root.visible)[i];
      if (!sol) return null;
      const mount = sol.gunMount as Object3D | null;
      const gm = mount?.children.find((c) => c.name === 'classgun') ?? null;
      const q = new Quaternion();
      mount?.getWorldQuaternion(q);
      const box = gm ? new Box3().setFromObject(gm) : null;
      return {
        hasMount: !!mount, hasGun: !!gm,
        mountWorldQuat: { x: q.x, y: q.y, z: q.z, w: q.w },
        mountLocalRot: mount ? { x: mount.rotation.x, y: mount.rotation.y, z: mount.rotation.z } : null,
        gunLocalRot: gm ? { x: gm.rotation.x, y: gm.rotation.y, z: gm.rotation.z } : null,
        box: box ? { min: { x: box.min.x, y: box.min.y, z: box.min.z }, max: { x: box.max.x, y: box.max.y, z: box.max.z } } : null,
        gunName: gm?.name ?? null,
      };
    },
    dying: () => soldiers.filter((x) => x.dyingFor > 0).length,
    // HITCH INVESTIGATION: `lastFrameInfo` (a per-logical-frame snapshot
    // of renderer.info, taken once after the whole composer chain has
    // rendered — see the note beside `finalComposer.render()`; a live
    // external read of `renderer.info` mid-frame only ever sees the last
    // internal pass, autoReset resetting the counts before each one)
    // plus the two pools a mass-death burst actually touches — particles
    // (`pState`, ring-buffer, `emit()` on every hit/kill) and corpses
    // (`cTier`, one slot claimed per death). Lets a harness correlate a
    // bad frame with what was live in it, instead of guessing between
    // particle spawn / corpse allocation / draw calls.
    perf: () => ({
      drawCalls: lastFrameInfo.calls,
      triangles: lastFrameInfo.triangles,
      particlesAlive: (() => {
        let n = 0;
        for (let i = 0; i < PARTS; i++) if (pState.life[i]! > 0) n++;
        return n;
      })(),
      corpsesAlive: (() => {
        let n = 0;
        for (let i = 0; i < MAX_CORPSES; i++) if (cTier[i]! >= 0) n++;
        return n;
      })(),
      dyingSoldiers: soldiers.filter((x) => x.dyingFor > 0).length,
    }),
    // D9: render-side pool occupancy, so a restart-teardown claim is
    // checkable against real counts. Sim-side counts are read from
    // window.__bonwarsy.state() (the two probes live in different
    // modules by construction — main.ts owns the sim's global, scene3d
    // owns this one) so a caller gets both halves from one call.
    liveCounts: () => {
      const bw = (window as unknown as {
        __bonwarsy?: { state: () => RunState };
      }).__bonwarsy;
      const s = bw?.state();
      const sim = s ? {
        tick: s.tick,
        squadSize: s.squadSize,
        enemiesAlive: (() => { let n = 0; for (let i = 0; i < s.ep.cap; i++) if (s.ep.alive[i]) n++; return n; })(),
        projectilesAlive: (() => { let n = 0; for (let i = 0; i < s.pp.cap; i++) if (s.pp.alive[i]) n++; return n; })(),
        gatesAlive: (() => { let n = 0; for (let i = 0; i < s.gp.cap; i++) if (s.gp.alive[i]) n++; return n; })(),
        gemsAlive: (() => { let n = 0; for (let i = 0; i < s.kp.cap; i++) if (s.kp.alive[i]) n++; return n; })(),
        alliesAlive: (() => { let n = 0; for (let i = 0; i < s.lp.cap; i++) if (s.lp.alive[i]) n++; return n; })(),
        monolithsAlive: (() => { let n = 0; for (let i = 0; i < s.mp.cap; i++) if (s.mp.alive[i]) n++; return n; })(),
        // The sim has no corpse concept at all — a body leaves `ep` on
        // death and becomes a render-only object. Reported as null,
        // not 0, so "the sim doesn't model this" isn't confused with
        // "the sim modelled it and found zero".
        corpses: null as number | null,
      } : null;
      const render = {
        soldiersVisible: soldiers.filter((x) => x.root.visible).length,
        soldiersDying: soldiers.filter((x) => x.dyingFor > 0).length,
        fodderDrawn: hordes.map((h) => h?.drawn ?? 0),
        corpsesAlive: (() => { let n = 0; for (let i = 0; i < MAX_CORPSES; i++) if (cTier[i]! >= 0) n++; return n; })(),
        particlesAlive: (() => { let n = 0; for (let i = 0; i < PARTS; i++) if (pState.life[i]! > 0) n++; return n; })(),
        labelsAlive: labels.filter((l) => l.life > 0).length,
        gatePanelsVisible: gatePanels.filter((p) => p.mesh.visible).length,
      };
      return { sim, render };
    },
    // D9: per-soldier diagnostic for the "sideways at restart" claim —
    // rotation (a stale death-twist would show a nonzero y that a living
    // soldier's unconditional `rotation.set(0,0,0)` never leaves behind),
    // dyingFor (nonzero = still mid-death-performance), and whether the
    // CURRENT clip's action is actually running (mixer still driving it)
    // vs the object sitting frozen. Distinguishes "frozen stale transform"
    // from "animating with a wrong root rotation" from a still frame.
    soldierDiag: () => soldiers.map((x, i) => ({
      i,
      visible: x.root.visible,
      current: x.current,
      dyingFor: x.dyingFor,
      rotation: { x: x.root.rotation.x, y: x.root.rotation.y, z: x.root.rotation.z },
      actionRunning: x.actions[x.current]?.isRunning() ?? false,
    })),
    // Flares must sit on rifles, not near the formation centre — this
    // reports both so the claim is checkable rather than asserted.
    barrels: () => barrels.slice(0, barrelCount)
      .map((b) => `${b.x.toFixed(2)},${b.y.toFixed(2)},${b.z.toFixed(2)}`),
    // What actually reached each fodder tier's GPU-skinned rig, so a claim
    // about M4 variety (or its absence) is checkable against the loaded
    // asset instead of asserted from reading the loader code.
    hordeClips: () => hordeClips.map((s) => [...s]),
    // D2: real material/geometry facts for the gull meshes, so a "still
    // squares" claim (or a "fixed" one) is checkable against what's
    // actually bound, not asserted from reading the source.
    // D3: every persistent-lifetime, non-terrain render pool, so a
    // "no banned prop" claim is checkable against what's actually
    // instantiated/visible rather than the (unfiltered) source roster —
    // TITAN_ASSET still lists 'titan-mech' by name because that string
    // is a real asset on disk, but `renderTitanKind` means kind 1 is
    // never the kind actually passed to anything that builds or shows a
    // mesh, so no ally/hologram entry below can ever report it.
    propAudit: () => {
      const out: { name: string; type: string; visible: boolean; clearedByResetForNewRun: boolean }[] = [];
      out.push({
        name: 'soldiers', type: 'SkinnedMesh pool (front rank)',
        visible: soldiers.some((x) => x.root.visible), clearedByResetForNewRun: true,
      });
      out.push({
        name: 'corpses', type: 'InstancedMesh pool',
        visible: (() => { for (let i = 0; i < MAX_CORPSES; i++) if (cTier[i]! >= 0) return true; return false; })(),
        clearedByResetForNewRun: true,
      });
      out.push({
        name: 'particles', type: 'InstancedMesh pool',
        visible: (() => { for (let i = 0; i < PARTS; i++) if (pState.life[i]! > 0) return true; return false; })(),
        clearedByResetForNewRun: true,
      });
      out.push({
        name: 'labels', type: 'Sprite pool',
        visible: labels.some((l) => l.life > 0), clearedByResetForNewRun: true,
      });
      out.push({
        name: 'gatePanels', type: 'Sprite pool',
        visible: gatePanels.some((p) => p.mesh.visible), clearedByResetForNewRun: true,
      });
      // Ally titans, one entry per kind actually reachable at render
      // time — i.e. post-`renderTitanKind` redirect, which is why kind
      // 1 never appears here under its own name (kind 0's entry is
      // visible if EITHER a genuine kind-0 ally or a redirected kind-1
      // one is live — they are indistinguishable in the scene, by
      // design, once redirected). Kinds 2 and 3 are unredirected and
      // reported as themselves — D10, not this fix, covers whether they
      // should be.
      for (const k of [0, 2, 3]) {
        const rigged = riggedMix[k] ?? [];
        const gen = allyRigs[k] ?? [];
        const visible = rigged.some((r) => r?.root.visible) || gen.some((r) => r?.visible);
        out.push({
          name: `ally:${TITAN_ASSET[k]}${k === 0 ? ' (also covers kind 1, redirected from titan-mech)' : ''}`,
          type: 'Object3D (rigged or generated titan clone)',
          visible, clearedByResetForNewRun: true,
        });
      }
      // Monolith reward holograms — same redirect applies.
      const holoVisible = monoRigs.some((r) => r.holo && r.holo.visible);
      out.push({
        name: 'monolithHolograms', type: 'Group (holoify preview)',
        visible: holoVisible, clearedByResetForNewRun: false,
      });
      // The item 1 investigation (founder rejection of Part A) found
      // `monolithHolograms` above only ever audits `rig.holo` (the TITAN
      // ghost, op===0) — it never covered `rig.preview` (the non-titan
      // payload's own "PRIZE, SHOWN" hologram, op!==0), which is most of
      // isle 1's monoliths (bodies/crate/armour/vehicle). Added rather
      // than silently left blind.
      const previewVisible = monoRigs.some((r) => r.preview && r.preview.visible);
      out.push({
        name: 'monolithPreviews', type: 'Group (holoify buildPreview, non-titan payload)',
        visible: previewVisible, clearedByResetForNewRun: false,
      });
      return out;
    },
    // Item 1 (founder rejection of Part A, "no hologram visible"): the
    // monolith preview/holo groups' full world bounding box projected to
    // screen, per corner — not just the pivot (`propAudit()`'s
    // `monolithPreviews`/`monolithHolograms` only check `.visible`, which
    // was true the whole time this was broken; the object was on the
    // scene graph and "visible" while still rendering entirely above the
    // frustum's top edge). `bboxScreenY.min < 0` means part of the object
    // is cropped above frame — this is what caught it.
    previewDiag: () => {
      const report = (g: Group | null): unknown => {
        if (!g) return null;
        const wp = new Vector3();
        g.getWorldPosition(wp);
        const proj = wp.clone().project(camera);
        const bb = new Box3().setFromObject(g);
        const corners = [
          new Vector3(bb.min.x, bb.min.y, bb.min.z), new Vector3(bb.max.x, bb.max.y, bb.max.z),
          new Vector3(bb.min.x, bb.max.y, bb.min.z), new Vector3(bb.max.x, bb.min.y, bb.max.z),
        ].map((c) => c.project(camera));
        const ys = corners.map((c) => (1 - c.y) / 2);
        return {
          groupVisible: g.visible,
          worldPos: { x: wp.x, y: wp.y, z: wp.z },
          worldBBox: { min: { x: bb.min.x, y: bb.min.y, z: bb.min.z },
            max: { x: bb.max.x, y: bb.max.y, z: bb.max.z } },
          screen01: { x: (proj.x + 1) / 2, y: (1 - proj.y) / 2 },
          bboxScreenY: { min: Math.min(...ys), max: Math.max(...ys) },
          offscreen: proj.x < -1 || proj.x > 1 || proj.y < -1 || proj.y > 1 || proj.z > 1,
        };
      };
      const previewRig = monoRigs.find((r) => r.preview);
      const holoRig = monoRigs.find((r) => r.holo);
      return {
        cameraY: camera.position.y,
        preview: report(previewRig?.preview ?? null),
        holo: report(holoRig?.holo ?? null),
      };
    },
    // Founder DoD verification: a specific rig's preview mesh count, so a
    // harness can force an op change and confirm the orphaned-mesh fix
    // (`preview`/`holo` are mutually exclusive on one rig) rather than
    // inferring it from a screenshot.
    rigPreviewCount: (idx: number) => {
      const rig = monoRigs[idx];
      if (!rig) return null;
      return {
        op: -1, // caller already knows the op it forced; not read from sim here
        hasPreview: !!rig.preview, hasHolo: !!rig.holo,
        count: (rig.preview ? 1 : 0) + (rig.holo ? 1 : 0),
        rootChildrenIncludesPreview: rig.preview ? rig.root.children.includes(rig.preview) : false,
        rootChildrenIncludesHolo: rig.holo ? rig.root.children.includes(rig.holo) : false,
      };
    },
    // Founder DoD verification: an ally actor's live material opacity, so
    // a harness can force lHp and confirm the hurt-readout fix reaches
    // whichever of real/placeholder is actually on screen.
    allyOpacity: (idx: number) => {
      const a = allyActors[idx];
      if (!a) return null;
      let opacity: number | null = null;
      const target: Object3D[] = [];
      for (const arr of riggedMix) { const r = arr[idx]; if (r?.root.visible) target.push(r.root); }
      for (const arr of allyRigs) { const r = arr[idx]; if (r?.visible) target.push(r); }
      if (target.length === 0 && a.visible) target.push(a);
      for (const t of target) {
        t.traverse((o) => {
          const m = (o as Mesh).material as MeshStandardMaterial | undefined;
          if (m && typeof m.opacity === 'number') opacity = m.opacity;
        });
      }
      return { opacity, sourcesChecked: target.length };
    },
    // D4: real material/texture facts for the monolith fissure, so a
    // "lit core" or "bright bar" grading is checkable against what's
    // actually bound rather than asserted from reading the source.
    fissureInfo: () => {
      const f = monoRigs[0]?.fissure;
      const m = f?.material as MeshBasicMaterial | undefined;
      return {
        path: 'scene3d.ts: fissureGlowTex (texture) + PlaneGeometry + MeshBasicMaterial',
        materialType: m?.type ?? null,
        // MeshBasicMaterial has no `emissive` channel at all — the glow
        // is additive blending + a gradient alpha map, not PBR emissive.
        emissive: false,
        emissiveIntensity: null,
        hasGradientMap: !!m?.map,
        mapWidth: (m?.map?.image as { width?: number } | undefined)?.width ?? null,
        mapHeight: (m?.map?.image as { height?: number } | undefined)?.height ?? null,
        blending: m?.blending ?? null,
        opacity: m?.opacity ?? null,
        colorHex: m?.color ? `#${m.color.getHexString()}` : null,
        // Diagnostic-only (D4 Step 1): whether the sim actually thinks
        // slot 0 is alive vs. whether the render rig believes it's alive
        // — these can disagree if a test injects into s.mp directly
        // without going through poolAlloc's free-list bookkeeping.
        simAlive0: (() => {
          const bw = (window as unknown as {
            __bonwarsy?: { state: () => RunState };
          }).__bonwarsy;
          const s = bw?.state();
          return s ? !!s.mp.alive[0] : null;
        })(),
        rigRootVisible: monoRigs[0]?.root.visible ?? null,
        rigRootPos: monoRigs[0]
          ? { x: monoRigs[0].root.position.x, y: monoRigs[0].root.position.y, z: monoRigs[0].root.position.z }
          : null,
      };
    },
    monoRigDebug: () => monoRigs.map((r, idx) => ({
      idx,
      rootVisible: r.root.visible,
      lastOp: r.lastOp,
      lastHp: r.lastHp,
      hasPreview: !!r.preview,
      previewVisible: r.preview?.visible ?? null,
      previewOp: r.previewOp,
      previewMeshCount: r.previewMeshes.length,
      previewOpacities: r.previewMeshes.map((m) => (m.material as MeshBasicMaterial).opacity),
      hasHolo: !!r.holo,
      holoVisible: r.holo?.visible ?? null,
      holoMeshCount: r.holoMeshes.length,
      holoOpacities: r.holoMeshes.map((m) => (m.material as MeshBasicMaterial).opacity),
      titan: r.titan,
    })),
    gullInfo: () => {
      const gulls = skyWorld.gulls;
      const m = gulls[0]?.material as MeshBasicMaterial | undefined;
      const geo = gulls[0]?.geometry;
      return {
        count: gulls.length,
        objectType: gulls[0]?.type ?? null,
        materialType: m?.type ?? null,
        hasMap: !!m?.map,
        mapWidth: (m?.map?.image as { width?: number } | undefined)?.width ?? null,
        mapHeight: (m?.map?.image as { height?: number } | undefined)?.height ?? null,
        transparent: m?.transparent ?? null,
        alphaTest: m?.alphaTest ?? null,
        depthWrite: m?.depthWrite ?? null,
        blending: m?.blending ?? null,
        vertexCount: geo?.getAttribute('position')?.count ?? null,
      };
    },
    // D2 verification aid, kept separate from gullInfo() since the
    // founder specified that probe's exact shape — screen01 position of
    // each gull, so a still can be cropped to the right pixels instead
    // of guessed at.
    gullScreens: () => skyWorld.gulls.map((g) => {
      const v = g.position.clone().project(camera);
      return { screen01: { x: (v.x + 1) / 2, y: (1 - v.y) / 2 }, behindCamera: v.z > 1 };
    }),
    // Layered painted backdrop, Part A DoD #1/#2, extended for item 3
    // (founder rejection: "screenCoverage measured the PLANE's geometry,
    // not where the TEXTURE's content lands on screen"). `screenCoverage`
    // is the plate's 4 local corners (PlaneGeometry(1,1) centred at
    // origin, so ±0.5 on each axis) carried through the mesh's live world
    // transform and the camera projection — a bounding box in screen01,
    // so "full coverage, no gap" is a number to check, not a claim from
    // reading the code. `textureAspect`/`planeAspect` and `repeat`/
    // `offset` are the item-3 numbers: aspects must match (the whole point
    // of the cover-crop fix) and repeat/offset show which axis got cropped
    // and by how much.
    backdropInfo: () => skyWorld.backdrop.map((layer) => {
      const mat = layer.mesh.material as MeshBasicMaterial;
      const img = layer.texture.image as HTMLImageElement | undefined;
      const corners = [
        new Vector3(-0.5, -0.5, 0), new Vector3(0.5, -0.5, 0),
        new Vector3(-0.5, 0.5, 0), new Vector3(0.5, 0.5, 0),
      ].map((c) => layer.mesh.localToWorld(c).project(camera));
      const xs = corners.map((c) => (c.x + 1) / 2);
      const ys = corners.map((c) => (1 - c.y) / 2);
      const planeAspect = layer.mesh.scale.x / layer.mesh.scale.y;
      return {
        name: layer.name,
        textureLoaded: !!(img && img.complete && img.naturalWidth > 0),
        width: img?.naturalWidth ?? null,
        height: img?.naturalHeight ?? null,
        textureAspect: layer.nativeAspect,
        planeAspect,
        aspectsMatchAfterCrop: Math.abs(layer.nativeAspect - planeAspect) < 1e-6
          || layer.texture.repeat.x < 1 || layer.texture.repeat.y < 1,
        planeScale: { x: layer.mesh.scale.x, y: layer.mesh.scale.y },
        textureRepeat: { x: layer.texture.repeat.x, y: layer.texture.repeat.y },
        textureOffset: { x: layer.texture.offset.x, y: layer.texture.offset.y },
        screenCoverage: {
          xMin: Math.min(...xs), xMax: Math.max(...xs),
          yMin: Math.min(...ys), yMax: Math.max(...ys),
        },
        renderOrder: layer.mesh.renderOrder,
        side: mat.side,
        parallaxFactor: layer.parallaxFactor,
      };
    }),
    // D6 diagnostic. Every HUD element's screen01 box (top-left origin,
    // 0-1 in both axes against the actual viewport, matching soldierFrame's
    // convention) plus pairwise overlap area for every pair, so a collision
    // claim is checkable against real getBoundingClientRect() numbers
    // rather than asserted from reading the CSS. Pure DOM query — this
    // reads no scene3d state, it only exists here because the founder
    // asked for it under window.__scene alongside the other probes.
    // D7: per-frame screen-space displacement caused by camKick alone
    // (camera shake), over the last 300 frames — a ring buffer, oldest
    // first from the CURRENT write cursor. Units: fraction of viewport
    // HEIGHT (the x component of the offset is rescaled by camera.aspect
    // so both axes share one unit before combining).
    shakeTrace: () => {
      const out: number[] = [];
      for (let i = 0; i < shakeTrace.length; i++) {
        out.push(shakeTrace[(shakeTraceCur + i) % shakeTrace.length]!);
      }
      return out;
    },
    // D1: project a SIM-space (x = corridor lateral, y = corridor depth)
    // coordinate through the same world-scaling (SX/S) and camera every
    // other probe uses, so a caller with raw enemy positions from
    // window.__bonwarsy.state() (eX/eY) doesn't have to duplicate the
    // scaling math to ask "where does this land on screen".
    projectWorld: (x: number, y: number) => {
      const v = new Vector3((x - arenaW / 2) * SX, 0.3, y * S).project(camera);
      return { screen01: { x: (v.x + 1) / 2, y: (1 - v.y) / 2 } };
    },
    // Twin of projectWorld, but for an already-in-world-units point (no
    // SX/S sim-scaling) — the horizon (a fixed world Y at a very large Z,
    // never a sim-space enemy/soldier position) has no sim coordinate to
    // project through projectWorld's scaling.
    projectRaw: (x: number, y: number, z: number) => {
      const v = new Vector3(x, y, z).project(camera);
      return { screen01: { x: (v.x + 1) / 2, y: (1 - v.y) / 2 } };
    },
    hudBoxes: () => {
      // NORMALISE AGAINST THE CANVAS, NOT THE WINDOW. `#hud` is sized to
      // `--play-w`/`--play-x` — the SAME letterboxed play-box the canvas
      // fills (`#stage` carries the identical left/width rule) — so the
      // HUD is positioned relative to the canvas/play-box, not the raw
      // window. Dividing by `window.innerWidth/innerHeight` is only
      // numerically correct when the window happens to be narrow enough
      // that no letterboxing triggers (`play-w === innerWidth`), which
      // masked this at the one viewport this was tested against.
      const canvasRect = renderer.domElement.getBoundingClientRect();
      const windowRect = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight,
        width: window.innerWidth, height: window.innerHeight };
      const cw = canvasRect.width, ch = canvasRect.height;
      const els: { name: string; el: Element }[] = [];
      document.querySelectorAll('#hud [data-hud]').forEach((el) => {
        els.push({ name: el.getAttribute('data-hud') ?? '?', el });
      });
      const pause = document.getElementById('pause');
      const mute = document.getElementById('mute');
      if (pause && pause.style.visibility !== 'hidden') els.push({ name: 'pause', el: pause });
      if (mute) els.push({ name: 'mute', el: mute });
      const boxes = els.map(({ name, el }) => {
        const r = el.getBoundingClientRect();
        return {
          name,
          x0: (r.left - canvasRect.left) / cw, y0: (r.top - canvasRect.top) / ch,
          x1: (r.right - canvasRect.left) / cw, y1: (r.bottom - canvasRect.top) / ch,
        };
      });
      const overlaps: { a: string; b: string; area: number }[] = [];
      for (let i = 0; i < boxes.length; i++) {
        for (let j = i + 1; j < boxes.length; j++) {
          const a = boxes[i]!, b = boxes[j]!;
          const ox = Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0));
          const oy = Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));
          overlaps.push({ a: a.name, b: b.name, area: ox * oy });
        }
      }
      return {
        boxes, overlaps, aspect: camera.aspect,
        canvasRect: { left: canvasRect.left, top: canvasRect.top, width: cw, height: ch },
        windowRect,
      };
    },
    // D6 correction: the "90"/"135" collision evidence is NOT the
    // ephemeral `labels[]` pool (those sit near the player/squad, never
    // near the HUD band in practice) — it is `gatePanels[]`, the gate's
    // own SIGN, a persistent Sprite whose position IS the gate's world
    // position (`s.gY[i]`, sim-driven). Reports every currently-visible
    // panel's screen01 position and label text.
    gatePanelScreens: () => gatePanels
      .filter((p) => p.mesh.visible)
      .map((p) => {
        const v = p.mesh.position.clone().project(camera);
        const halfW = p.mesh.scale.x / 2;
        const halfH = p.mesh.scale.y / 2;
        const leftV = new Vector3(p.mesh.position.x - halfW, p.mesh.position.y, p.mesh.position.z).project(camera);
        const rightV = new Vector3(p.mesh.position.x + halfW, p.mesh.position.y, p.mesh.position.z).project(camera);
        // GATE SIGN RENDERING (2026-08-25): top/bottom added alongside
        // left/right -- the original left/right-only version could not
        // answer "does this board's own height overlap the group behind
        // it", which is exactly the failure mode reported.
        const topV = new Vector3(p.mesh.position.x, p.mesh.position.y + halfH, p.mesh.position.z).project(camera);
        const botV = new Vector3(p.mesh.position.x, p.mesh.position.y - halfH, p.mesh.position.z).project(camera);
        return {
          screen01y: (1 - v.y) / 2, label: p.label,
          worldWidth: p.mesh.scale.x, worldHeight: p.mesh.scale.y,
          screen01xLeft: (1 + leftV.x) / 2, screen01xRight: (1 + rightV.x) / 2,
          screen01yTop: (1 - topV.y) / 2, screen01yBottom: (1 - botV.y) / 2,
        };
      }),
    // D1 diagnostic. World position of every VISIBLE skinned soldier plus
    // its screen-projected position, normalised 0-1 in both axes with a
    // top-left origin (matches CSS/DOM convention, not three's NDC). Camera
    // state is reported alongside so a claim about framing is checkable
    // against the actual numbers instead of re-derived from the formula.
    soldierFrame: () => {
      const wp = new Vector3();
      const box = new Box3();
      const soldiersOut = soldiers.map((sol, i) => {
        if (!sol.root.visible) return null;
        sol.root.getWorldPosition(wp);
        const proj = wp.clone().project(camera);
        // Body height ON SCREEN, not just the pivot's position: the actual
        // rendered bounding box, foot to head, projected top and bottom.
        // Rank-to-rank pivot spread alone understates "how big the squad
        // reads" for a shallow formation (few rows) — most of a soldier's
        // screen presence is their OWN height, not the gap to the next rank.
        box.setFromObject(sol.root);
        const footProj = new Vector3(wp.x, box.min.y, wp.z).project(camera);
        const headProj = new Vector3(wp.x, box.max.y, wp.z).project(camera);
        const bodyHeight01 = Math.abs(
          (1 - footProj.y) / 2 - (1 - headProj.y) / 2);
        return {
          i,
          world: { x: wp.x, y: wp.y, z: wp.z },
          screen01: { x: (proj.x + 1) / 2, y: (1 - proj.y) / 2 },
          bodyHeight01,
          offscreen: proj.x < -1 || proj.x > 1 || proj.y < -1 || proj.y > 1 || proj.z > 1,
        };
      }).filter((v): v is NonNullable<typeof v> => v !== null);
      return {
        soldiers: soldiersOut,
        camera: {
          position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
          lookAt: { x: lastLookAt.x, y: lastLookAt.y, z: lastLookAt.z },
          fov: camera.fov, near: camera.near, far: camera.far,
          aspect: camera.aspect,
        },
        arenaW,
      };
    },
    // Diagnostic twin of soldierFrame(), for the elite/boss actor pools
    // instead of the soldier rig array — a harness measuring boss framing
    // (head clipped by the HUD, top-of-body screen position) has no other
    // way to reach an actor's world bounding box; `actors()` above reports
    // only pool occupancy counts, not geometry.
    actorFrame: () => {
      const box = new Box3();
      const out: { poolIdx: number; screen01Top: number; screen01Bottom: number;
        world: { x: number; y: number; z: number }; height: number;
        // F2 AUDIT: full box bounds, so a contact radius can be checked
        // against the mesh's OWN drawn extent (its furthest reaching limb
        // at whatever point in its clip loop this frame lands on), not
        // just the pivot-to-pivot distance a scalar radius assumes.
        boxMin: { x: number; y: number; z: number }; boxMax: { x: number; y: number; z: number };
      }[] = [];
      actorPools.forEach((pool, poolIdx) => {
        for (const a of pool) {
          if (!a.busy || !a.root.visible) continue;
          box.setFromObject(a.root);
          const wp = new Vector3();
          a.root.getWorldPosition(wp);
          const topProj = new Vector3(wp.x, box.max.y, wp.z).project(camera);
          const botProj = new Vector3(wp.x, box.min.y, wp.z).project(camera);
          out.push({
            poolIdx,
            screen01Top: (1 - topProj.y) / 2,
            screen01Bottom: (1 - botProj.y) / 2,
            world: { x: wp.x, y: wp.y, z: wp.z },
            height: box.max.y - box.min.y,
            boxMin: { x: box.min.x, y: box.min.y, z: box.min.z },
            boxMax: { x: box.max.x, y: box.max.y, z: box.max.z },
          });
        }
      });
      return out;
    },
    flares: () => muzzles.filter((m, i) => m.visible && muzzleLife[i]! > 0)
      .map((m) => `${m.position.x.toFixed(2)},${m.position.y.toFixed(2)},${m.position.z.toFixed(2)}`),
    // 2026-08-18: every live bolt's drawn tip/tail (world Y) as of the
    // last frame, plus the PER-BOLT muzzle (Z) and origin (X) it was
    // individually drawn against (one real soldier's barrel each, since
    // the per-soldier origin fix -- `fallbackMuzzleY` below is only what
    // an unassigned bolt would fall back to before any barrel exists),
    // so a harness can check "tip == pY", "tail >= its own muzzleY", and
    // "N distinct origin x" directly rather than inferring it from a
    // screenshot.
    tracerDiag: () => ({ fallbackMuzzleY: lastMuzzleY, bolts: lastBoltDiag.slice() }),
    // MORTAR SHADOW VERIFICATION: the shadow's render position for every
    // live 'lob' round, alongside the SAME sim pX/pY it was derived from
    // (`rx`/`rz` in the render loop, re-derived here rather than re-read
    // from a stored value, so this checks the actual conversion holds
    // live, not a cached claim). `delta` is 0 by construction -- the
    // shadow's x/z IS `(pX-arenaW/2)*SX` / `pY*S`, no offset -- but
    // reported as a measured number, not asserted.
    mortarShadowDiag: () => {
      const bw = (window as unknown as { __bonwarsy?: { state: () => RunState } }).__bonwarsy;
      const s = bw?.state();
      if (!s) return [];
      const out: { i: number; shadowX: number; shadowZ: number; simX: number; simY: number; deltaX: number; deltaZ: number }[] = [];
      for (const [i, pos] of lobLastPos) {
        if (!s.pp.alive[i]) continue;
        const simX = (s.pX[i]! - arenaW / 2) * SX;
        const simZ = s.pY[i]! * S;
        out.push({
          i, shadowX: pos.x, shadowZ: pos.z, simX, simY: s.pY[i]!,
          deltaX: pos.x - simX, deltaZ: pos.z - simZ,
        });
      }
      return out;
    },
    massBounds: () => massBounds,
    /** Body half-widths the rail law is enforced against, per tier. */
    massHalfW: () => massHalfW,
    /**
     * EVERY POOL THAT CAN HOLD A SQUAD BODY, counted separately.
     *
     * Three fixes were aimed at "the soldiers multiply" without this
     * existing, and all three were guesses at WHICH pool was leaking.
     * Four pools can put a soldier on screen, and a body that outlives its
     * slot can be stuck in any of them:
     *
     *   skinned  - the 24 real skeletons, gated on root.visible
     *   dying    - a subset of those, held alive by DEATH_HOLD_S
     *   gpu      - the GPU-skinned back ranks (instance count)
     *   baked    - the frozen-pose fallback meshes (instance counts)
     *
     * Compare `total` against s.squadSize each frame. If the delta grows,
     * the pool that grew with it IS the bug — no theory required.
     */
    squadBodies: () => {
      const skinned = soldiers.filter((x) => x.root.visible).length;
      const dying = soldiers.filter((x) => x.dyingFor > 0).length;
      const gpu = squadCrowd?.drawn ?? 0;
      const baked = Object.values(crowd)
        .reduce((a, ms) => a + ms.reduce((b, m) => b + m.count, 0), 0);
      return { skinned, dying, gpu, baked, total: skinned + gpu + baked };
    },
    /**
     * Is the horde GPU-SKINNED this run, or did it fall back to the frozen
     * baked poses? The whole point of the rebuild is invisible from a
     * screenshot if the fallback silently took over on some device.
     */
    gpuCrowd: () => hordes.map((h, i) => (h
      ? { tier: i, on: true, clips: h.clipNames, drawn: h.drawn,
          verts: h.mesh.geometry.getAttribute('position').count,
          halfW: +h.halfW.toFixed(4) }
      : { tier: i, on: false })),
    /**
     * Worst distance, in scene units, that any REACTING body or corpse has
     * put its rendered edge past the inside of the kerb, for the whole run.
     * Iron Rule 8 says these stay at zero; a harness can assert it instead
     * of a frame being argued over.
     */
    overhang: () => ({ react: +reactOverhang.toFixed(5), corpse: +corpseOverhang.toFixed(5) }),
    /** How many bodies are mid-reaction, by tier, right now. */
    reacting: () => {
      const by = [0, 0, 0, 0];
      for (let i = 0; i < REACT_CAP; i++) if (rTime[i]! > 0) by[rKind[i]!]! += 1;
      return { flinch: by[1], stagger: by[2], reel: by[3] };
    },
    /** One enemy slot's own reaction state, for a harness testing a
     * specific scripted body rather than reading the ambient aggregate. */
    slotReacting: (idx: number) => ({ rTime: rTime[idx], rKind: rKind[idx], prevEHp: prevEHp[idx] }),
    /** Live corpses by death archetype — proves the variety is reachable. */
    deaths: () => {
      const by = [0, 0, 0, 0];
      for (let i = 0; i < MAX_CORPSES; i++) if (cTier[i]! >= 0) by[cArch[i]!]! += 1;
      return { blown: by[0], crumple: by[1], spinOut: by[2], pitch: by[3] };
    },
    // Bodies actually drawn: skinned front ranks + instanced crowd.
    crowd: () => soldiers.filter((x) => x.root.visible).length
      + Object.values(crowd).reduce((n, ms) => n + ms.reduce((a, m) => a + m.count, 0), 0),
    heads: () => soldiers.filter((x) => x.root.visible).slice(0, 3)
      .map((x) => `${x.root.position.x.toFixed(2)},${x.root.position.z.toFixed(2)}`),
  };

  /**
   * One flare per FIRING RIFLE, placed on that soldier's own barrel.
   * Soldiers are laid out front rank first, so taking the first N barrels
   * lights the ranks that are actually shooting.
   */
  function flash(streams: number): void {
    // Only RECORD the volley. Several sim ticks can land between two
    // rendered frames, and the formation moves every frame — emitting
    // here would pin the flare to whatever the layout happened to be
    // last time round, which is how it ended up a rank ahead of the
    // rifles. The render loop emits, immediately after laying out.
    pendingStreams = Math.max(pendingStreams, Math.max(1, streams));
  }

  function emitFlares(): void {
    if (pendingStreams <= 0) return;
    gunFlash = Math.min(1, gunFlash + 0.7);
    const n = Math.min(barrelCount, MUZZLES, pendingStreams);
    pendingStreams = 0;
    // Put the volley light on the front rank too, so the flash lights the
    // squad from where the guns actually are.
    for (let k = 0; k < n; k++) {
      const b = barrels[k];
      if (!b) break;
      const sp = muzzles[muzzleCur]!;
      sp.position.set(b.x, b.y, b.z);
      // Slight per-barrel size variation so a wall of flares does not
      // read as one stamped-out row.
      sp.scale.setScalar(0.44 + ((k * 7) % 5) * 0.05);
      sp.visible = true;
      sp.material.opacity = 1;
      muzzleBarrel[muzzleCur] = k;
      muzzleLife[muzzleCur] = MUZZLE_LIFE_S;
      muzzleCur = (muzzleCur + 1) % MUZZLES;
      // THE PER-CLASS DISCHARGE, finally connected.
      //
      // fx.muzzle() carries a distinct shape for every weapon family --
      // brass ejecting from kinetics, a debris cone from shells, backblast
      // from rockets, arcs off the tesla coil -- and it was reachable from
      // exactly ONE call site in the whole codebase: the tank. The squad's
      // own flare was this sprite and nothing else: one shared texture,
      // tinted, identical for all twelve classes. That is why every weapon
      // fired the same however different its bolt was.
      //
      // Thinned to every other barrel because a thirty-strong squad firing
      // nine particles per barrel per volley buries the frame.
      if ((k & 1) === 0) fx.muzzle(b.x / SX + arenaW / 2, b.z / S, 0, 0.5);
    }
  }

  /**
   * D9 fix. Clears every persistent render-side pool's TRANSIENT
   * per-object state — not the pools themselves, which stay allocated
   * and reused exactly as before. Each pool gets the same treatment its
   * own per-frame code already gives a naturally-expired entry (a dying
   * soldier whose hold ran out, a corpse whose life ran out), just
   * forced immediately instead of waiting for the timer.
   */
  function resetForNewRun(): void {
    for (const sol of soldiers) {
      sol.dyingFor = 0;
      sol.joinFor = 0;
      sol.current = '';
      sol.root.visible = false;
      // The death-twist rotation (see the `i >= shown` branch above) is
      // the specific stale value D9 was reported against — cleared
      // directly rather than trusting next frame's living-soldier path
      // to overwrite it, since a soldier that stays out of formation
      // (squad still small next run) would never reach that code at all.
      sol.root.rotation.set(0, 0, 0);
      sol.mixer.stopAllAction();
    }
    for (let i = 0; i < MAX_CORPSES; i++) { cTier[i] = -1; cLife[i] = 0; }
    for (let i = 0; i < PARTS; i++) pState.life[i] = 0;
    for (const l of labels) { l.life = 0; l.sp.visible = false; }
    for (const p of gatePanels) p.mesh.visible = false;
    // D9, same rule as everything above: a death beat in flight when the
    // run ends must not carry into the next run's tick 0.
    for (const d of dyingActors) { d.actor.root.visible = false; d.actor.busy = false; }
    dyingActors.length = 0;
    // D3: freed-titan allies. Belt-and-suspenders with the per-frame
    // else-branch fix (the actual bug — see the comment there) rather
    // than a substitute for it: explicit here so the invariant doesn't
    // depend on that branch continuing to run correctly forever.
    for (const a of allyActors) a.visible = false;
    for (const arr of allyRigs) for (const r of arr) if (r) r.visible = false;
    for (const arr of riggedMix) for (const r of arr) if (r) r.root.visible = false;
    // Camera-feel accumulators: an old run's last kill or lane-change
    // must not be felt as the new run's first frame.
    camKick = 0;
    eventPunch = 0;
    camLead = 0;
    camSquadPull = 0;
    camBossT = 0;
    camIsleCompleteT = 0;
    bossRevealT = 0;
    lastBossNear = false;
  }

  return {
    render, resize, flash,
    resetForNewRun,
    setLanes: (n: number) => { lanesNow = Math.max(1, n); },
    setTeamColor: (hex: number) => {
      // NO TINT (founder directive, 2026-08-30): character selection no
      // longer repaints the soldier's base colour -- it ships in its own
      // original asset colour regardless of pick. The rim accent is a
      // dynamic light-response effect, not a static texture tint, so it
      // still tracks the selection (these Color objects back live shader
      // uniforms, addRimLight's own return contract -- mutating in place
      // is enough, no material recompile needed).
      for (const rc of friendlyRimColors) rc.setHex(hex);
    },
    gunBoom: () => (GUN_LOOKS[lastProfile] ?? DEFAULT_LOOK).boom,
    onBlockBreak: (cb: (mass: number) => void) => { onBlockBreakCb = cb; },
    punchCamera: (strength: number) => {
      eventPunch = Math.min(EVENT_PUNCH_MAX, eventPunch + strength);
    },
    setDanger: (vignette: number, desaturate: number) => {
      dangerVignette = vignette;
      dangerDesaturate = desaturate;
    },
    domElement: renderer.domElement,
    get ready() { return ready; },
    fx,
    enemyFlash,
    enemyGrow,
    enemyHit,
    killActor,
  };
}
